import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import {
  FiUsers, FiBook, FiVideo, FiBarChart2, FiSettings, FiHome, FiLogOut,
  FiPlus, FiEdit, FiTrash2, FiEye, FiChevronDown, FiChevronRight, FiChevronLeft,
  FiGrid, FiMapPin, FiTag, FiAward, FiPackage, FiCreditCard, FiKey, FiLink,
  FiRefreshCw, FiLoader, FiBookOpen, FiCalendar, FiUsers as FiUsersGroup,
  FiArrowLeft, FiHelpCircle, FiMessageSquare, FiArrowUp, FiArrowDown, FiStar
} from 'react-icons/fi';
import Header from '../components/Header';
import BlogContentEditor from '../components/BlogContentEditor';
import RichTextEditor from '../components/RichTextEditor';
// VideoLearningManagement removed - deprecated
import EnhancedContentStructure from '../components/EnhancedContentStructure';
import BookContentLinks from '../components/BookContentLinks';
import PackageManagement from '../components/PackageManagement';
import CouponManagement from '../components/CouponManagement';
import PaymentSettings from '../components/PaymentSettings';
import { apiUrls, getHttpHeaders } from '../config/api';
import { presignVideoUpload, presignContentUpload, getContentSignedUrl } from '../api/uploads';

// Content Library Component with Full Content Management
const ContentLibrary: React.FC = () => {
  const [grades, setGrades] = useState<Array<{ id: number, name: string }>>([]);
  const [subjects, setSubjects] = useState<Array<{ id: number, name: string }>>([]);
  const [contentTypes, setContentTypes] = useState<Array<{ id: number, name: string }>>([]);
  const [content, setContent] = useState<Array<any>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedGrade, setSelectedGrade] = useState<string>('');
  const [selectedSubject, setSelectedSubject] = useState<string>('');
  const [selectedContentType, setSelectedContentType] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [filteredSubjects, setFilteredSubjects] = useState<Array<{ id: number, name: string }>>([]);
  const [filteredContent, setFilteredContent] = useState<any[]>([]);

  // Pagination states for Content Library
  const [contentCurrentPage, setContentCurrentPage] = useState<number>(1);
  const [contentItemsPerPage, setContentItemsPerPage] = useState<number>(10);

  // Sorting states for Content Library
  const [contentSortColumn, setContentSortColumn] = useState<string>('');
  const [contentSortDirection, setContentSortDirection] = useState<'asc' | 'desc'>('asc');

  // Content management states - CHANGED: Replace modal with inline page
  const [showAddPage, setShowAddPage] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedContent, setSelectedContent] = useState<any>(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    content_type_id: '',
    file_url: '',
    duration_sec: '',
    is_downloadable: false,
    subject_id: '',
    grade_id: '',
    is_published: false,
    is_reusable: true
  });

  // File upload states
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  // Preview states
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewContent, setPreviewContent] = useState<any>(null);
  const [previewVideoUrl, setPreviewVideoUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    const fetchFilterData = async () => {
      try {
        setLoading(true);
        console.log('🔄 Fetching Content Library filter data...');

        // Fetch grades from grades table
        console.log('📚 Fetching grades from:', `${apiUrls.books.grades}`);
        const gradesResponse = await fetch(`${apiUrls.books.grades}`);
        console.log('📚 Grades response status:', gradesResponse.status);
        if (gradesResponse.ok) {
          const gradesData = await gradesResponse.json();
          console.log('📚 Grades data received:', gradesData);
          setGrades(gradesData.grades || []);
        } else {
          console.warn('⚠️ Grades response not ok:', gradesResponse.status, gradesResponse.statusText);
        }

        // Fetch subjects from subjects table
        console.log('📖 Fetching subjects from subjects table...');
        try {
          const subjectsResponse = await fetch(`${apiUrls.books.subjects}`);
          console.log('📖 Subjects response status:', subjectsResponse.status);
          if (subjectsResponse.ok) {
            const subjectsData = await subjectsResponse.json();
            console.log('📖 Subjects data received:', subjectsData);
            setSubjects(subjectsData.subjects || []);
          } else {
            console.error('❌ Subjects endpoint failed:', subjectsResponse.status, subjectsResponse.statusText);
            setSubjects([]);
          }
        } catch (subjectsErr) {
          console.error('❌ Subjects fetch failed:', subjectsErr);
          setSubjects([]);
        }

        // Fetch content types from content_types table
        console.log('📋 Fetching content types from:', `${apiUrls.books.list}/content-types`);
        const contentTypesResponse = await fetch(`${apiUrls.books.list}/content-types`);
        console.log('📋 Content types response status:', contentTypesResponse.status);
        if (contentTypesResponse.ok) {
          const contentTypesData = await contentTypesResponse.json();
          console.log('📋 Content types data received:', contentTypesData);
          setContentTypes(contentTypesData.contentTypes || []);
        } else {
          console.warn('⚠️ Content types response not ok:', contentTypesResponse.status, contentTypesResponse.statusText);
          // Fallback: Use common content types
          setContentTypes([
            { id: 1, name: 'Chapters' },
            { id: 2, name: 'Units' },
            { id: 3, name: 'Lessons' },
            { id: 4, name: 'Videos' }
          ]);
        }

        console.log('✅ Filter data fetch completed');

      } catch (err) {
        console.error('❌ Error fetching filter data:', err);
        setError('Failed to load filter options');
      } finally {
        setLoading(false);
      }
    };

    fetchFilterData();
  }, []);

  // Function to fetch content from unified content API
  const fetchContent = async () => {
    try {
      setLoading(true);
      const response = await fetch(apiUrls.content.list);
      if (response.ok) {
        const data = await response.json();
        setContent(data.content || []);
      } else {
        console.error('Failed to fetch content:', response.status);
        setError('Failed to load content');
      }
    } catch (err) {
      console.error('Error fetching content:', err);
      setError('Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  // Function to filter content based on selected criteria
  const filterContent = () => {
    let filtered = [...content];

    // Filter by search term (title and description)
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(item =>
        item.title?.toLowerCase().includes(searchLower) ||
        item.description?.toLowerCase().includes(searchLower)
      );
    }

    // Filter by grade
    if (selectedGrade && selectedGrade !== '') {
      filtered = filtered.filter(item =>
        item.grade_id === parseInt(selectedGrade)
      );
    }

    // Filter by subject (topic_id in content_assets corresponds to subject)
    if (selectedSubject && selectedSubject !== '') {
      filtered = filtered.filter(item =>
        item.topic_id === parseInt(selectedSubject)
      );
    }

    // Filter by content type
    if (selectedContentType && selectedContentType !== '') {
      filtered = filtered.filter(item =>
        item.content_type_id === parseInt(selectedContentType)
      );
    }

    setFilteredContent(filtered);
  };

  // Fetch content when component mounts
  useEffect(() => {
    fetchContent();
  }, []);

  // Initialize filtered content when content is loaded
  useEffect(() => {
    setFilteredContent(content);
  }, [content]);

  // Apply filters whenever filter criteria change
  useEffect(() => {
    filterContent();
  }, [content, searchTerm, selectedGrade, selectedSubject, selectedContentType]);

  // Function to handle grade selection and filter subjects
  const handleGradeChange = async (gradeId: string) => {
    setSelectedGrade(gradeId);
    setContentCurrentPage(1); // Reset to first page when filter changes

    if (!gradeId || gradeId === '') {
      // If "All Grades" is selected, show all subjects
      setFilteredSubjects(subjects);
    } else {
      try {
        // Fetch subjects filtered by the selected grade
        const response = await fetch(`${apiUrls.books.subjects}/by-grade/${gradeId}`);
        if (response.ok) {
          const data = await response.json();
          setFilteredSubjects(data.subjects || []);
        } else {
          console.error('Failed to fetch subjects for grade:', gradeId);
          setFilteredSubjects([]);
        }
      } catch (error) {
        console.error('Error fetching subjects for grade:', error);
        setFilteredSubjects([]);
      }
    }
  };

  // Initialize filtered subjects with all subjects
  useEffect(() => {
    setFilteredSubjects(subjects);
  }, [subjects]);

  // Handler for subject selection
  const handleSubjectChange = (subjectId: string) => {
    setSelectedSubject(subjectId);
    setContentCurrentPage(1); // Reset to first page when filter changes
  };

  // Handler for content type selection
  const handleContentTypeChange = (contentTypeId: string) => {
    setSelectedContentType(contentTypeId);
    setContentCurrentPage(1); // Reset to first page when filter changes
  };

  // Handler for search term change
  const handleSearchChange = (search: string) => {
    setSearchTerm(search);
    setContentCurrentPage(1); // Reset to first page when search changes
  };

  // Handle form input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  // Handle file selection
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type (video and PDF files)
      const allowedTypes = [
        // Video types
        'video/mp4', 'video/webm', 'video/ogg', 'video/avi', 'video/mov', 'video/wmv',
        // PDF types
        'application/pdf'
      ];
      if (!allowedTypes.includes(file.type)) {
        setUploadError('Please select a valid video file (MP4, WebM, OGG, AVI, MOV, WMV) or PDF file');
        return;
      }

      // Validate file size (max 500MB)
      const maxSize = 500 * 1024 * 1024; // 500MB in bytes
      if (file.size > maxSize) {
        setUploadError('File size must be less than 500MB');
        return;
      }

      setSelectedFile(file);
      setUploadError(null);
      setFormData(prev => ({ ...prev, file_url: file.name })); // Show filename in form
    }
  };

  // Handle file upload to S3
  const handleFileUpload = async (): Promise<string | null> => {
    if (!selectedFile) return null;

    // Validate that content type is selected
    if (!formData.content_type_id) {
      setUploadError('Please select a content type before uploading');
      return null;
    }

    const contentTypeId = parseInt(formData.content_type_id);
    
    // Additional validation
    if (isNaN(contentTypeId) || contentTypeId <= 0) {
      setUploadError('Invalid content type selected. Please select a valid content type.');
      return null;
    }

    try {
      setIsUploading(true);
      setUploadProgress(0);
      setUploadError(null);

      console.log('📤 Starting upload with contentTypeId:', contentTypeId);
      console.log('📤 File:', selectedFile.name, selectedFile.type, selectedFile.size);

      // Step 1: Get presigned URL for S3 upload using content type
      const presigned = await presignContentUpload(contentTypeId, selectedFile);
      
      console.log('✅ Got presigned URL:', presigned);

      setUploadProgress(30);

      // Step 2: Upload directly to S3
      const putResponse = await fetch(presigned.url, {
        method: 'PUT',
        headers: {
          'Content-Type': presigned.contentType || 'application/octet-stream'
        },
        body: selectedFile
      });

      if (!putResponse.ok) {
        throw new Error(`S3 upload failed with status ${putResponse.status}`);
      }

      setUploadProgress(100);
      setIsUploading(false);

      // Return the S3 key that can be stored in the database
      return presigned.key;

    } catch (error) {
      console.error('File upload failed:', error);
      setUploadError(error instanceof Error ? error.message : 'Upload failed');
      setIsUploading(false);
      setUploadProgress(0);
      return null;
    }
  };

  // Handle Add Content
  const handleAddContent = async () => {
    if (!formData.title || !formData.content_type_id) {
      alert('Please fill in all required fields');
      return;
    }

    if (!selectedFile) {
      alert('Please upload a file before saving content');
      return;
    }

    try {
      let finalFileUrl = formData.file_url;

      // If a file is selected, upload it first
      if (selectedFile) {
        const uploadedUrl = await handleFileUpload();
        if (!uploadedUrl) {
          alert('File upload failed. Please try again.');
          return;
        }
        finalFileUrl = uploadedUrl;
      }

      const response = await fetch(`${process.env.REACT_APP_VIDEO_SERVICE_URL || 'http://localhost:3003'}/api/content`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          file_url: finalFileUrl, // Use uploaded file URL
          file_type: 's3', // Since we're only dealing with S3 uploads
          created_by_user_id: 18, // Default admin user ID
          content_type_id: parseInt(formData.content_type_id),
          grade_id: formData.grade_id ? parseInt(formData.grade_id) : null,
          subject_id: formData.subject_id ? parseInt(formData.subject_id) : null,
          duration_sec: formData.duration_sec ? parseInt(formData.duration_sec) : null,
        }),
      });

      if (response.ok) {
        setShowAddPage(false);
        // Reset form and file upload states
        setFormData({
          title: '',
          description: '',
          content_type_id: '',
          file_url: '',
          duration_sec: '',
          is_downloadable: false,
          subject_id: '',
          grade_id: '',
          is_published: false,
          is_reusable: true
        });
        setSelectedFile(null);
        setUploadProgress(0);
        setIsUploading(false);
        setUploadError(null);
        fetchContent(); // Refresh content list
      } else {
        const errorData = await response.json();
        alert(`Failed to add content: ${errorData.error || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Error adding content:', err);
      alert('Failed to add content');
    }
  };

  // Handle Preview Content
  const handlePreviewContent = async (item: any) => {
    console.log('Preview content clicked:', item);
    setPreviewContent(item);
    setPreviewVideoUrl(null);
    setPreviewLoading(true);
    setShowPreviewModal(true);

    try {
      console.log('Calling getContentSignedUrl for ID:', item.id);
      // Get signed URL for the content
      const { signedUrl, fileType } = await getContentSignedUrl(item.id);
      console.log('Received signed URL:', signedUrl, 'fileType:', fileType);
      setPreviewVideoUrl(signedUrl);
    } catch (error) {
      console.error('Error getting signed URL:', error);
      alert('Failed to load video preview');
      setShowPreviewModal(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  // Handle Edit Modal
  const openEditModal = (item: any) => {
    setSelectedContent(item);
    setFormData({
      title: item.title || '',
      description: item.description || '',
      content_type_id: item.content_type_id?.toString() || '',
      file_url: item.file_url || '',

      duration_sec: item.duration_sec?.toString() || '',
      is_downloadable: item.is_downloadable || false,
      subject_id: item.subject_id?.toString() || '',
      grade_id: item.grade_id?.toString() || '',
      is_published: item.is_published || false,
      is_reusable: item.is_reusable || true
    });
    setShowEditModal(true);
  };

  // Handle Delete Modal
  const openDeleteModal = (item: any) => {
    setSelectedContent(item);
    setShowDeleteModal(true);
  };

  // Handle Edit Content
  const handleEditContent = async () => {
    if (!selectedContent) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_VIDEO_SERVICE_URL || 'http://localhost:3003'}/api/content/${selectedContent.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          content_type_id: parseInt(formData.content_type_id),
          grade_id: formData.grade_id ? parseInt(formData.grade_id) : null,
          subject_id: formData.subject_id ? parseInt(formData.subject_id) : null,
          duration_sec: formData.duration_sec ? parseInt(formData.duration_sec) : null,
        }),
      });

      if (response.ok) {
        setShowEditModal(false);
        setSelectedContent(null);
        setFormData({
          title: '',
          description: '',
          content_type_id: '',
          file_url: '',
          duration_sec: '',
          is_downloadable: false,
          subject_id: '',
          grade_id: '',
          is_published: false,
          is_reusable: true
        });
        fetchContent(); // Refresh content list
      } else {
        const errorData = await response.json();
        alert(`Failed to update content: ${errorData.error || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Error updating content:', err);
      alert('Failed to update content');
    }
  };

  // Handle Delete Content
  const handleDeleteContent = async () => {
    if (!selectedContent) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_VIDEO_SERVICE_URL || 'http://localhost:3003'}/api/content/${selectedContent.id}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        setShowDeleteModal(false);
        setSelectedContent(null);
        fetchContent(); // Refresh content list
      } else {
        const errorData = await response.json();
        alert(`Failed to delete content: ${errorData.error || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Error deleting content:', err);
      alert('Failed to delete content');
    }
  };




  if (loading) {
    return (
      <div style={{ padding: '24px', background: '#f8fafc', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '24px', marginBottom: '16px' }}>⏳</div>
          <p style={{ color: '#6b7280', fontSize: '16px' }}>Loading Content Library...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '24px', background: '#f8fafc', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '24px', marginBottom: '16px' }}>⚠️</div>
          <p style={{ color: '#ef4444', fontSize: '16px' }}>{error}</p>
        </div>
      </div>
    );
  }

  // CHANGED: Return Add Content page when showAddPage is true
  if (showAddPage) {
    return (
      <div style={{ padding: '24px', background: '#f8fafc', minHeight: '100vh' }}>
        {/* Header with Content Library banner colors and layout */}
        <div style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          padding: '20px 24px',
          borderRadius: '12px',
          marginBottom: '24px',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{ fontSize: '24px' }}>➕</div>
            <div>
              <h1 style={{ margin: 0, fontSize: '24px', fontWeight: '600' }}>Content Details</h1>
              <p style={{ margin: '4px 0 0 0', opacity: 0.9, fontSize: '14px' }}>
                Fill in the information for your new content
              </p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={() => setShowAddPage(false)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px 16px',
                border: 'none',
                borderRadius: '8px',
                background: 'rgba(255, 255, 255, 0.2)',
                color: 'white',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                transition: 'all 0.2s ease',
                backdropFilter: 'blur(10px)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)';
                e.currentTarget.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <FiArrowLeft size={16} />
              Back to Content Library
            </button>
          </div>
        </div>

        {/* Form Content with Video Learning colors */}
        <div style={{
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          overflow: 'hidden'
        }}>


          {/* Form Body */}
          <div style={{ padding: '24px' }}>
            <div style={{ display: 'grid', gap: '16px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '16px' }}>
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontWeight: '600',
                    color: '#374151'
                  }}>Title *</label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '8px',
                      fontSize: '14px',
                      background: 'white',
                      color: '#374151',
                      transition: 'all 0.2s ease'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#3b82f6';
                      e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e5e7eb';
                      e.target.style.boxShadow = 'none';
                    }}
                    required
                  />
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontWeight: '600',
                    color: '#374151'
                  }}>Content Type *</label>
                  <select
                    name="content_type_id"
                    value={formData.content_type_id}
                    onChange={handleInputChange}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '8px',
                      fontSize: '14px',
                      background: 'white',
                      color: '#374151',
                      transition: 'all 0.2s ease'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#3b82f6';
                      e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e5e7eb';
                      e.target.style.boxShadow = 'none';
                    }}
                    required
                  >
                    <option value="">Select Type</option>
                    {contentTypes.map(type => (
                      <option key={type.id} value={type.id}>{type.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: '600',
                  color: '#374151'
                }}>Description</label>
                <RichTextEditor
                  value={formData.description}
                  onChange={(value) => setFormData(prev => ({ ...prev, description: value }))}
                  placeholder="Enter content description with formatting..."
                  showImageUpload={false}
                  minHeight="150px"
                />
              </div>

              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: '600',
                  color: '#374151'
                }}>Upload File * <span style={{ color: '#ef4444' }}>(Required)</span></label>

                {/* File Upload Area */}
                <div style={{
                  border: `2px dashed ${selectedFile ? '#10b981' : '#e5e7eb'}`,
                  borderRadius: '8px',
                  padding: '24px',
                  textAlign: 'center',
                  background: selectedFile ? '#f0fdf4' : '#f9fafb',
                  transition: 'all 0.2s ease',
                  cursor: 'pointer'
                }}
                  onClick={() => document.getElementById('file-upload')?.click()}
                >
                  <input
                    id="file-upload"
                    type="file"
                    accept="video/*,application/pdf,.pdf"
                    onChange={handleFileSelect}
                    style={{ display: 'none' }}
                  />

                  {selectedFile ? (
                    <div>
                      <div style={{ fontSize: '24px', marginBottom: '8px' }}>✅</div>
                      <div style={{ fontWeight: '600', color: '#10b981', marginBottom: '4px' }}>
                        {selectedFile.name}
                      </div>
                      <div style={{ fontSize: '14px', color: '#6b7280' }}>
                        {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB
                      </div>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedFile(null);
                          setFormData(prev => ({ ...prev, file_url: '' }));
                          setUploadError(null);
                        }}
                        style={{
                          marginTop: '8px',
                          padding: '4px 8px',
                          background: '#ef4444',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          fontSize: '12px',
                          cursor: 'pointer'
                        }}
                      >
                        Remove
                      </button>
                    </div>
                  ) : (
                    <div>
                      <div style={{ fontSize: '24px', marginBottom: '8px' }}>📁</div>
                      <div style={{ fontWeight: '600', color: '#374151', marginBottom: '4px' }}>
                        Click to upload file
                      </div>
                      <div style={{ fontSize: '14px', color: '#6b7280' }}>
                        Supports videos (MP4, WebM, OGG, AVI, MOV, WMV) and PDF files (max 500MB)
                      </div>
                    </div>
                  )}
                </div>

                {/* Upload Progress */}
                {isUploading && (
                  <div style={{ marginTop: '12px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                      <span style={{ fontSize: '14px', color: '#374151' }}>Uploading...</span>
                      <span style={{ fontSize: '14px', color: '#374151' }}>{uploadProgress}%</span>
                    </div>
                    <div style={{
                      width: '100%',
                      height: '8px',
                      background: '#e5e7eb',
                      borderRadius: '4px',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        width: `${uploadProgress}%`,
                        height: '100%',
                        background: '#10b981',
                        transition: 'width 0.2s ease'
                      }} />
                    </div>
                  </div>
                )}

                {/* Upload Error */}
                {uploadError && (
                  <div style={{
                    marginTop: '8px',
                    padding: '8px 12px',
                    background: '#fef2f2',
                    border: '1px solid #fecaca',
                    borderRadius: '6px',
                    color: '#dc2626',
                    fontSize: '14px'
                  }}>
                    {uploadError}
                  </div>
                )}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontWeight: '600',
                    color: '#374151'
                  }}>Grade</label>
                  <select
                    name="grade_id"
                    value={formData.grade_id}
                    onChange={async (e) => {
                      const gradeId = e.target.value;
                      setFormData(prev => ({ ...prev, grade_id: gradeId, subject_id: '' }));

                      // Fetch subjects filtered by the selected grade
                      if (!gradeId || gradeId === '') {
                        setFilteredSubjects(subjects);
                      } else {
                        try {
                          const response = await fetch(`${apiUrls.books.subjects}/by-grade/${gradeId}`);
                          if (response.ok) {
                            const data = await response.json();
                            setFilteredSubjects(data.subjects || []);
                          } else {
                            console.error('Failed to fetch subjects for grade:', gradeId);
                            setFilteredSubjects([]);
                          }
                        } catch (error) {
                          console.error('Error fetching subjects for grade:', error);
                          setFilteredSubjects([]);
                        }
                      }
                    }}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '8px',
                      fontSize: '14px',
                      background: 'white',
                      color: '#374151',
                      transition: 'all 0.2s ease'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#3b82f6';
                      e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e5e7eb';
                      e.target.style.boxShadow = 'none';
                    }}
                  >
                    <option value="">Select Grade</option>
                    {grades.map(grade => (
                      <option key={grade.id} value={grade.id}>{grade.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontWeight: '600',
                    color: '#374151'
                  }}>Subject</label>
                  <select
                    name="subject_id"
                    value={formData.subject_id}
                    onChange={handleInputChange}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '8px',
                      fontSize: '14px',
                      background: 'white',
                      color: '#374151',
                      transition: 'all 0.2s ease'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#3b82f6';
                      e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e5e7eb';
                      e.target.style.boxShadow = 'none';
                    }}
                  >
                    <option value="">Select Subject</option>
                    {filteredSubjects.map(subject => (
                      <option key={subject.id} value={subject.id}>{subject.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: '600',
                  color: '#374151'
                }}>Duration (seconds)</label>
                <input
                  type="number"
                  name="duration_sec"
                  value={formData.duration_sec}
                  onChange={handleInputChange}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px',
                    background: 'white',
                    color: '#374151',
                    transition: 'all 0.2s ease'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#3b82f6';
                    e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e5e7eb';
                    e.target.style.boxShadow = 'none';
                  }}
                  placeholder="Optional"
                />
              </div>

              <div style={{
                display: 'flex',
                gap: '24px',
                alignItems: 'center',
                padding: '16px',
                background: '#f8fafc',
                borderRadius: '8px',
                border: '1px solid #e5e7eb'
              }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  cursor: 'pointer'
                }}>
                  <input
                    type="checkbox"
                    name="is_downloadable"
                    checked={formData.is_downloadable}
                    onChange={handleInputChange}
                    style={{
                      width: '16px',
                      height: '16px',
                      accentColor: '#3b82f6'
                    }}
                  />
                  Downloadable
                </label>

                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  cursor: 'pointer'
                }}>
                  <input
                    type="checkbox"
                    name="is_published"
                    checked={formData.is_published}
                    onChange={handleInputChange}
                    style={{
                      width: '16px',
                      height: '16px',
                      accentColor: '#3b82f6'
                    }}
                  />
                  Published
                </label>

                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  cursor: 'pointer'
                }}>
                  <input
                    type="checkbox"
                    name="is_reusable"
                    checked={formData.is_reusable}
                    onChange={handleInputChange}
                    style={{
                      width: '16px',
                      height: '16px',
                      accentColor: '#3b82f6'
                    }}
                  />
                  Reusable
                </label>
              </div>
            </div>
          </div>

          {/* Action Buttons with Video Learning colors */}
          <div style={{
            padding: '20px 24px',
            background: '#f8fafc',
            borderTop: '1px solid #e5e7eb',
            display: 'flex',
            gap: '12px',
            justifyContent: 'flex-end'
          }}>
            <button
              onClick={() => setShowAddPage(false)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px 20px',
                background: '#e5e7eb',
                color: '#374151',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#d1d5db';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#e5e7eb';
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleAddContent}
              disabled={!selectedFile}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px 20px',
                background: selectedFile ? '#10b981' : '#9ca3af',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: selectedFile ? 'pointer' : 'not-allowed',
                fontSize: '14px',
                fontWeight: '500',
                transition: 'all 0.2s ease',
                opacity: selectedFile ? 1 : 0.6
              }}
              onMouseEnter={(e) => {
                if (selectedFile) {
                  e.currentTarget.style.background = '#059669';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.4)';
                }
              }}
              onMouseLeave={(e) => {
                if (selectedFile) {
                  e.currentTarget.style.background = '#10b981';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }
              }}
            >
              <FiPlus size={16} />
              Save Content
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Calculate pagination for display
  const totalPages = Math.ceil(filteredContent.length / contentItemsPerPage);
  const safePage = contentCurrentPage > totalPages && totalPages > 0 ? 1 : contentCurrentPage;

  return (
    <div style={{ padding: '24px', background: '#f8fafc', minHeight: '100vh' }}>
      {/* Top Header with Create Button and Title */}
      <div style={{
        background: 'white',
        padding: '20px 24px',
        borderRadius: '12px',
        marginBottom: '16px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '16px',
        flexWrap: 'wrap'
      }}>
        {/* Left side: Create button and title */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button
            onClick={() => setShowAddPage(true)}
            style={{
              backgroundColor: '#059669',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontWeight: 'bold',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              flexShrink: 0
            }}
          >
            <FiPlus size={18} /> Create
          </button>
          <h2 style={{ margin: 0, color: '#253C8C', fontSize: '28px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px' }}>
            📚 Content Library
          </h2>
        </div>

        {/* Right side: Pagination controls */}
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Items per page selector */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <label style={{ fontSize: '14px', color: '#374151', fontWeight: '500' }}>
              Show:
            </label>
            <select
              value={contentItemsPerPage}
              onChange={(e) => {
                setContentItemsPerPage(Number(e.target.value));
                setContentCurrentPage(1);
              }}
              style={{
                padding: '6px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                backgroundColor: 'white',
                cursor: 'pointer',
                fontWeight: '500'
              }}
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <span style={{ fontSize: '14px', color: '#6b7280' }}>per page</span>
          </div>

          {totalPages > 1 && (
            <>
              <div style={{ width: '1px', height: '24px', backgroundColor: '#e5e7eb', margin: '0 4px' }} />
              <button
                onClick={() => setContentCurrentPage(p => Math.max(1, p - 1))}
                disabled={safePage === 1}
                style={{
                  padding: '8px 16px',
                  backgroundColor: safePage === 1 ? '#e5e7eb' : '#3b82f6',
                  color: safePage === 1 ? '#9ca3af' : 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: safePage === 1 ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  transition: 'all 0.2s'
                }}
              >
                <FiChevronLeft size={16} />
                Previous
              </button>

              <div style={{ 
                padding: '8px 16px', 
                backgroundColor: 'white',
                border: '2px solid #3b82f6',
                borderRadius: '6px',
                color: '#3b82f6',
                fontSize: '14px',
                fontWeight: '700',
                whiteSpace: 'nowrap'
              }}>
                Page {safePage} of {totalPages}
              </div>

              <button
                onClick={() => setContentCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={safePage === totalPages}
                style={{
                  padding: '8px 16px',
                  backgroundColor: safePage === totalPages ? '#e5e7eb' : '#3b82f6',
                  color: safePage === totalPages ? '#9ca3af' : 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: safePage === totalPages ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  transition: 'all 0.2s'
                }}
              >
                Next
                <FiChevronRight size={16} />
              </button>
            </>
          )}
        </div>
      </div>

      {/* Filter Section - Matching Video Library with Real Data */}
      <div style={{
        background: 'white',
        padding: '20px',
        borderRadius: '8px',
        marginBottom: '20px',
        border: '1px solid #e5e7eb'
      }}>
        <h3 style={{
          fontWeight: '600',
          color: '#374151',
          marginBottom: '16px',
          fontSize: '16px'
        }}>
          Filters
        </h3>
        {/* Search Input */}
        <div style={{ marginBottom: '16px' }}>
          <label style={{
            fontWeight: '600',
            color: '#374151',
            marginBottom: '8px',
            display: 'block',
            fontSize: '14px'
          }}>
            🔍 Search Content
          </label>
          <input
            type="text"
            placeholder="Search by title or description..."
            value={searchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
            style={{
              padding: '8px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px',
              background: 'white',
              color: '#374151',
              width: '100%',
              height: '40px'
            }}
          />
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '20px',
          marginTop: '16px'
        }}>
          <div>
            <label style={{
              fontWeight: '600',
              color: '#374151',
              marginBottom: '8px',
              display: 'block',
              fontSize: '14px'
            }}>
              Grade Level
            </label>
            <select
              value={selectedGrade}
              onChange={(e) => handleGradeChange(e.target.value)}
              style={{
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                background: 'white',
                color: '#374151',
                width: '100%',
                height: '40px'
              }}
            >
              <option value="">All Grades</option>
              {grades.map(grade => (
                <option key={grade.id} value={grade.id}>{grade.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={{
              fontWeight: '600',
              color: '#374151',
              marginBottom: '8px',
              display: 'block',
              fontSize: '14px'
            }}>
              Subject
            </label>
            <select
              value={selectedSubject}
              onChange={(e) => handleSubjectChange(e.target.value)}
              style={{
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                background: 'white',
                color: '#374151',
                width: '100%',
                height: '40px'
              }}
            >
              <option value="">All Subjects</option>
              {filteredSubjects.map(subject => (
                <option key={subject.id} value={subject.id}>{subject.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={{
              fontWeight: '600',
              color: '#374151',
              marginBottom: '8px',
              display: 'block',
              fontSize: '14px'
            }}>
              Content Type
            </label>
            <select
              value={selectedContentType}
              onChange={(e) => handleContentTypeChange(e.target.value)}
              style={{
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                background: 'white',
                color: '#374151',
                width: '100%',
                height: '40px'
              }}
            >
              <option value="">All Types</option>
              {contentTypes.map(contentType => (
                <option key={contentType.id} value={contentType.id}>{contentType.name}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Clear Filters Button */}
        {(searchTerm || selectedGrade || selectedSubject || selectedContentType) && (
          <div style={{ marginTop: '16px', textAlign: 'right' }}>
            <button
              onClick={() => {
                setSearchTerm('');
                setSelectedGrade('');
                setSelectedSubject('');
                setSelectedContentType('');
                setContentCurrentPage(1); // Reset to first page when clearing filters
              }}
              style={{
                padding: '8px 16px',
                background: '#f3f4f6',
                color: '#374151',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                cursor: 'pointer',
                fontWeight: '500'
              }}
            >
              🗑️ Clear All Filters
            </button>
          </div>
        )}
      </div>

      {/* Calculate pagination and sorting */}
      {(() => {
        // Apply sorting to filtered content
        let sortedContent = [...filteredContent];
        if (contentSortColumn) {
          sortedContent.sort((a, b) => {
            let aValue: any = '';
            let bValue: any = '';

            switch (contentSortColumn) {
              case 'Title':
                aValue = (a.title || '').toLowerCase();
                bValue = (b.title || '').toLowerCase();
                break;
              case 'Type':
                const aType = contentTypes.find(ct => ct.id === a.content_type_id)?.name || '';
                const bType = contentTypes.find(ct => ct.id === b.content_type_id)?.name || '';
                aValue = aType.toLowerCase();
                bValue = bType.toLowerCase();
                break;
              case 'Grade':
                const aGrade = grades.find(g => g.id === a.grade_id)?.name || '';
                const bGrade = grades.find(g => g.id === b.grade_id)?.name || '';
                aValue = aGrade.toLowerCase();
                bValue = bGrade.toLowerCase();
                break;
              case 'Status':
                aValue = a.is_published ? 'published' : 'not published';
                bValue = b.is_published ? 'published' : 'not published';
                break;
              default:
                return 0;
            }

            if (typeof aValue === 'number' && typeof bValue === 'number') {
              return contentSortDirection === 'asc' ? aValue - bValue : bValue - aValue;
            }
            
            return contentSortDirection === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
          });
        }

        const startIndex = (safePage - 1) * contentItemsPerPage;
        const endIndex = startIndex + contentItemsPerPage;
        const paginatedContent = sortedContent.slice(startIndex, endIndex);

        return (
          <>
            {/* Content Table */}
            <div style={{
              background: 'white',
              borderRadius: '8px',
              border: '1px solid #e5e7eb',
              overflow: 'hidden'
            }}>
              {/* Table Header */}
              <div style={{
                padding: '20px',
                borderBottom: '1px solid #e5e7eb'
              }}>
                <h3 style={{
                  fontWeight: '600',
                  color: '#374151',
                  margin: 0,
                  fontSize: '16px'
                }}>
                  Content Assets ({filteredContent.length})
                </h3>
              </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{
            width: '100%',
            borderCollapse: 'collapse'
          }}>
            <thead>
              <tr style={{
                background: '#f9fafb',
                borderBottom: '1px solid #e5e7eb'
              }}>
                {['Title', 'Type', 'Grade', 'Status'].map(column => (
                  <th 
                    key={column}
                    onClick={() => {
                      if (contentSortColumn === column) {
                        setContentSortDirection(contentSortDirection === 'asc' ? 'desc' : 'asc');
                      } else {
                        setContentSortColumn(column);
                        setContentSortDirection('asc');
                      }
                    }}
                    style={{
                      padding: '12px 16px',
                      textAlign: 'left',
                      fontWeight: '600',
                      fontSize: '14px',
                      color: '#374151',
                      cursor: 'pointer',
                      userSelect: 'none',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#f3f4f6';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                    title={`Click to sort by ${column}`}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span>{column}</span>
                      <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minWidth: '16px' }}>
                        {contentSortColumn === column ? (
                          contentSortDirection === 'asc' ? (
                            <FiArrowUp size={14} color="#059669" />
                          ) : (
                            <FiArrowDown size={14} color="#059669" />
                          )
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0px', fontSize: '10px', color: '#9ca3af', lineHeight: '1' }}>
                            <span>▲</span>
                            <span>▼</span>
                          </div>
                        )}
                      </span>
                    </div>
                  </th>
                ))}
                <th style={{
                  padding: '12px 16px',
                  textAlign: 'left',
                  fontWeight: '600',
                  fontSize: '14px',
                  color: '#374151'
                }}>Actions</th>
              </tr>
            </thead>
              <tbody>
                {filteredContent.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={{
                      padding: '40px',
                      textAlign: 'center',
                      color: '#6b7280',
                      fontSize: '16px'
                    }}>
                      {(searchTerm || selectedGrade || selectedSubject || selectedContentType) ?
                        '🔍 No content matches your filter criteria' :
                        '📄 No content available'
                      }
                    </td>
                  </tr>
                ) : (
                  paginatedContent.map((item, index) => (
                  <tr key={item.id} style={{
                    borderBottom: '1px solid #e5e7eb'
                  }}>
                    <td style={{
                      padding: '12px 16px',
                      fontSize: '14px',
                      color: '#374151'
                    }}>
                      <div>
                        <div style={{ fontWeight: '500' }}>{item.title}</div>
                        <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                          {item.description?.substring(0, 60)}...
                        </div>
                      </div>
                    </td>
                    <td style={{
                      padding: '12px 16px',
                      fontSize: '14px',
                      color: '#374151'
                    }}>
                      <span style={{
                        padding: '4px 8px',
                        background: '#dbeafe',
                        color: '#1e40af',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: '500'
                      }}>
                        {contentTypes.find(ct => ct.id === item.content_type_id)?.name || 'Unknown'}
                      </span>
                    </td>
                    <td style={{
                      padding: '12px 16px',
                      fontSize: '14px',
                      color: '#374151'
                    }}>
                      {item.grade?.name || (() => {
                        const grade = grades.find(g => g.id === item.grade_id);
                        return grade ? grade.name : 'N/A';
                      })()}
                    </td>
                    <td style={{
                      padding: '12px 16px',
                      fontSize: '14px',
                      color: '#374151'
                    }}>
                      <span style={{
                        padding: '4px 8px',
                        background: item.is_published ? '#d1fae5' : '#fef3c7',
                        color: item.is_published ? '#065f46' : '#92400e',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: '500'
                      }}>
                        {item.is_published ? 'Published' : 'Not Published'}
                      </span>
                    </td>
                    <td style={{
                      padding: '12px 16px',
                      fontSize: '14px',
                      color: '#374151'
                    }}>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          onClick={() => handlePreviewContent(item)}
                          style={{
                            padding: '6px 12px',
                            background: '#10b981',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            fontSize: '12px',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = '#059669';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = '#10b981';
                          }}
                        >
                          👁️ Preview
                        </button>
                        <button
                          onClick={() => openEditModal(item)}
                          style={{
                            padding: '6px 12px',
                            background: '#3b82f6',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            fontSize: '12px',
                            cursor: 'pointer'
                          }}
                        >
                          ✏️ Edit
                        </button>
                        <button
                          onClick={() => openDeleteModal(item)}
                          style={{
                            padding: '6px 12px',
                            background: '#ef4444',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            fontSize: '12px',
                            cursor: 'pointer'
                          }}
                        >
                          🗑️ Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
        );
      })()}

      {/* Old Add Content Modal removed - now using inline page */}

      {/* Edit Content Modal */}
      {showEditModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'white',
          zIndex: 1000,
          transform: showAddPage ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
          overflowY: 'auto'
        }}>
          {/* Header with back button */}
          <div style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            padding: '24px 32px',
            color: 'white',
            position: 'sticky',
            top: 0,
            zIndex: 10,
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '20px'
            }}>
              <button
                onClick={() => setShowAddPage(false)}
                style={{
                  background: 'rgba(255, 255, 255, 0.2)',
                  border: 'none',
                  borderRadius: '12px',
                  padding: '12px 16px',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.2s ease',
                  backdropFilter: 'blur(10px)'
                }}
                onMouseEnter={(e) => {
                  (e.target as HTMLElement).style.background = 'rgba(255, 255, 255, 0.3)';
                }}
                onMouseLeave={(e) => {
                  (e.target as HTMLElement).style.background = 'rgba(255, 255, 255, 0.2)';
                }}
              >
                ← Back to Content Library
              </button>

              <div style={{ flex: 1 }}>
                <h1 style={{
                  margin: 0,
                  fontSize: '32px',
                  fontWeight: '700',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px'
                }}>
                  <span style={{ fontSize: '36px' }}>✨</span>
                  Add New Content
                </h1>
                <p style={{
                  margin: '12px 0 0 0',
                  opacity: 0.9,
                  fontSize: '18px',
                  fontWeight: '300'
                }}>
                  Create engaging content for your learning platform
                </p>
              </div>
            </div>
          </div>

          {/* Form Content */}
          <div style={{
            padding: '40px',
            maxWidth: '800px',
            margin: '0 auto',
            background: 'white'
          }}>
            <div style={{ display: 'grid', gap: '32px' }}>
              {/* Title Section */}
              <div style={{
                background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                padding: '24px',
                borderRadius: '16px',
                border: '1px solid #e2e8f0'
              }}>
                <label style={{
                  display: 'block',
                  marginBottom: '16px',
                  fontWeight: '600',
                  fontSize: '18px',
                  color: '#1e293b'
                }}>
                  Content Title *
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  style={{
                    width: '100%',
                    padding: '18px 24px',
                    border: '2px solid #e2e8f0',
                    borderRadius: '12px',
                    fontSize: '16px',
                    background: 'white',
                    transition: 'all 0.2s ease',
                    boxSizing: 'border-box'
                  }}
                  placeholder="Enter a compelling title for your content"
                  required
                  onFocus={(e) => {
                    (e.target as HTMLElement).style.borderColor = '#667eea';
                    (e.target as HTMLElement).style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                  }}
                  onBlur={(e) => {
                    (e.target as HTMLElement).style.borderColor = '#e2e8f0';
                    (e.target as HTMLElement).style.boxShadow = 'none';
                  }}
                />
              </div>

              {/* Description Section */}
              <div style={{
                background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                padding: '24px',
                borderRadius: '16px',
                border: '1px solid #e2e8f0'
              }}>
                <label style={{
                  display: 'block',
                  marginBottom: '16px',
                  fontWeight: '600',
                  fontSize: '18px',
                  color: '#1e293b'
                }}>
                  Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={6}
                  style={{
                    width: '100%',
                    padding: '18px 24px',
                    border: '2px solid #e2e8f0',
                    borderRadius: '12px',
                    fontSize: '16px',
                    background: 'white',
                    resize: 'vertical',
                    transition: 'all 0.2s ease',
                    boxSizing: 'border-box',
                    fontFamily: 'inherit'
                  }}
                  placeholder="Describe your content to help learners understand what they'll gain"
                  onFocus={(e) => {
                    (e.target as HTMLElement).style.borderColor = '#667eea';
                    (e.target as HTMLElement).style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                  }}
                  onBlur={(e) => {
                    (e.target as HTMLElement).style.borderColor = '#e2e8f0';
                    (e.target as HTMLElement).style.boxShadow = 'none';
                  }}
                />
                {/* Content Type & File Type Row */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '32px' }}>
                  <div style={{
                    background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                    padding: '24px',
                    borderRadius: '16px',
                    border: '1px solid #e2e8f0'
                  }}>
                    <label style={{
                      display: 'block',
                      marginBottom: '16px',
                      fontWeight: '600',
                      fontSize: '18px',
                      color: '#1e293b'
                    }}>
                      Content Type *
                    </label>
                    <select
                      name="content_type_id"
                      value={formData.content_type_id}
                      onChange={handleInputChange}
                      style={{
                        width: '100%',
                        padding: '18px 24px',
                        border: '2px solid #e2e8f0',
                        borderRadius: '12px',
                        fontSize: '16px',
                        background: 'white',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        boxSizing: 'border-box'
                      }}
                      required
                      onFocus={(e) => {
                        (e.target as HTMLElement).style.borderColor = '#667eea';
                        (e.target as HTMLElement).style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                      }}
                      onBlur={(e) => {
                        (e.target as HTMLElement).style.borderColor = '#e2e8f0';
                        (e.target as HTMLElement).style.boxShadow = 'none';
                      }}
                    >
                      <option value="">Select Content Type</option>
                      {contentTypes.map(type => (
                        <option key={type.id} value={type.id}>{type.name}</option>
                      ))}
                    </select>
                  </div>

                  <div style={{
                    background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                    padding: '24px',
                    borderRadius: '16px',
                    border: '1px solid #e2e8f0'
                  }}>

                  </div>
                </div>

                {/* S3 Location Section */}
                <div style={{
                  background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                  padding: '24px',
                  borderRadius: '16px',
                  border: '1px solid #e2e8f0'
                }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '16px',
                    fontWeight: '600',
                    fontSize: '18px',
                    color: '#1e293b'
                  }}>
                    S3 Location *
                  </label>
                  <input
                    type="text"
                    name="file_url"
                    value={formData.file_url}
                    onChange={handleInputChange}
                    style={{
                      width: '100%',
                      padding: '18px 24px',
                      border: '2px solid #e2e8f0',
                      borderRadius: '12px',
                      fontSize: '16px',
                      background: 'white',
                      transition: 'all 0.2s ease',
                      boxSizing: 'border-box'
                    }}
                    placeholder="Enter file URL or S3 key"
                    required
                    onFocus={(e) => {
                      (e.target as HTMLElement).style.borderColor = '#667eea';
                      (e.target as HTMLElement).style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                    }}
                    onBlur={(e) => {
                      (e.target as HTMLElement).style.borderColor = '#e2e8f0';
                      (e.target as HTMLElement).style.boxShadow = 'none';
                    }}
                  />
                </div>

                {/* Grade & Duration Row */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '32px' }}>
                  <div style={{
                    background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                    padding: '24px',
                    borderRadius: '16px',
                    border: '1px solid #e2e8f0'
                  }}>
                    <label style={{
                      display: 'block',
                      marginBottom: '16px',
                      fontWeight: '600',
                      fontSize: '18px',
                      color: '#1e293b'
                    }}>
                      Grade Level
                    </label>
                    <select
                      name="grade_id"
                      value={formData.grade_id}
                      onChange={handleInputChange}
                      style={{
                        width: '100%',
                        padding: '18px 24px',
                        border: '2px solid #e2e8f0',
                        borderRadius: '12px',
                        fontSize: '16px',
                        background: 'white',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        boxSizing: 'border-box'
                      }}
                      onFocus={(e) => {
                        (e.target as HTMLElement).style.borderColor = '#667eea';
                        (e.target as HTMLElement).style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                      }}
                      onBlur={(e) => {
                        (e.target as HTMLElement).style.borderColor = '#e2e8f0';
                        (e.target as HTMLElement).style.boxShadow = 'none';
                      }}
                    >
                      <option value="">Select Grade</option>
                      {grades.map(grade => (
                        <option key={grade.id} value={grade.id}>{grade.name}</option>
                      ))}
                    </select>
                  </div>

                  <div style={{
                    background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                    padding: '24px',
                    borderRadius: '16px',
                    border: '1px solid #e2e8f0'
                  }}>
                    <label style={{
                      display: 'block',
                      marginBottom: '16px',
                      fontWeight: '600',
                      fontSize: '18px',
                      color: '#1e293b'
                    }}>
                      Duration (seconds)
                    </label>
                    <input
                      type="number"
                      name="duration_sec"
                      value={formData.duration_sec}
                      onChange={handleInputChange}
                      style={{
                        width: '100%',
                        padding: '18px 24px',
                        border: '2px solid #e2e8f0',
                        borderRadius: '12px',
                        fontSize: '16px',
                        background: 'white',
                        transition: 'all 0.2s ease',
                        boxSizing: 'border-box'
                      }}
                      placeholder="Optional"
                      onFocus={(e) => {
                        (e.target as HTMLElement).style.borderColor = '#667eea';
                        (e.target as HTMLElement).style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                      }}
                      onBlur={(e) => {
                        (e.target as HTMLElement).style.borderColor = '#e2e8f0';
                        (e.target as HTMLElement).style.boxShadow = 'none';
                      }}
                    />
                  </div>
                </div>

                {/* Checkboxes Section */}
                <div style={{
                  background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                  padding: '24px',
                  borderRadius: '16px',
                  border: '1px solid #e2e8f0'
                }}>
                  <h4 style={{
                    margin: '0 0 20px 0',
                    fontWeight: '600',
                    fontSize: '18px',
                    color: '#1e293b'
                  }}>
                    Content Settings
                  </h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px' }}>
                    <label style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      cursor: 'pointer',
                      padding: '12px',
                      borderRadius: '8px',
                      transition: 'all 0.2s ease'
                    }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'rgba(102, 126, 234, 0.05)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'transparent';
                      }}>
                      <input
                        type="checkbox"
                        name="is_downloadable"
                        checked={formData.is_downloadable}
                        onChange={handleInputChange}
                        style={{
                          width: '20px',
                          height: '20px',
                          accentColor: '#667eea'
                        }}
                      />
                      <span style={{ fontWeight: '500', color: '#374151' }}>📥 Downloadable</span>
                    </label>

                    <label style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      cursor: 'pointer',
                      padding: '12px',
                      borderRadius: '8px',
                      transition: 'all 0.2s ease'
                    }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'rgba(102, 126, 234, 0.05)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'transparent';
                      }}>
                      <input
                        type="checkbox"
                        name="is_published"
                        checked={formData.is_published}
                        onChange={handleInputChange}
                        style={{
                          width: '20px',
                          height: '20px',
                          accentColor: '#667eea'
                        }}
                      />
                      <span style={{ fontWeight: '500', color: '#374151' }}>🌐 Published</span>
                    </label>

                    <label style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      cursor: 'pointer',
                      padding: '12px',
                      borderRadius: '8px',
                      transition: 'all 0.2s ease'
                    }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'rgba(102, 126, 234, 0.05)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'transparent';
                      }}>
                      <input
                        type="checkbox"
                        name="is_reusable"
                        checked={formData.is_reusable}
                        onChange={handleInputChange}
                        style={{
                          width: '20px',
                          height: '20px',
                          accentColor: '#667eea'
                        }}
                      />
                      <span style={{ fontWeight: '500', color: '#374151' }}>🔄 Reusable</span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div style={{
                display: 'flex',
                gap: '16px',
                justifyContent: 'flex-end',
                marginTop: '32px',
                paddingTop: '24px',
                borderTop: '1px solid #e2e8f0'
              }}>
                <button
                  onClick={() => setShowAddPage(false)}
                  style={{
                    padding: '14px 28px',
                    background: 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    fontSize: '16px',
                    fontWeight: '600',
                    transition: 'all 0.2s ease',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                  }}
                  onMouseEnter={(e) => {
                    (e.target as HTMLElement).style.transform = 'translateY(-2px)';
                    (e.target as HTMLElement).style.boxShadow = '0 8px 15px -3px rgba(0, 0, 0, 0.2)';
                  }}
                  onMouseLeave={(e) => {
                    (e.target as HTMLElement).style.transform = 'translateY(0)';
                    (e.target as HTMLElement).style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddContent}
                  style={{
                    padding: '14px 28px',
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    fontSize: '16px',
                    fontWeight: '600',
                    transition: 'all 0.2s ease',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                  }}
                  onMouseEnter={(e) => {
                    (e.target as HTMLElement).style.transform = 'translateY(-2px)';
                    (e.target as HTMLElement).style.boxShadow = '0 8px 15px -3px rgba(0, 0, 0, 0.2)';
                  }}
                  onMouseLeave={(e) => {
                    (e.target as HTMLElement).style.transform = 'translateY(0)';
                    (e.target as HTMLElement).style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
                  }}
                >
                  ✨ Add Content
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Content Modal */}
      {showEditModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            padding: '24px',
            borderRadius: '8px',
            width: '90%',
            maxWidth: '600px',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: '600' }}>
              Edit Content: {selectedContent?.title}
            </h3>

            <div style={{ display: 'grid', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Title *</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                  required
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Content Type *</label>
                  <select
                    name="content_type_id"
                    value={formData.content_type_id}
                    onChange={handleInputChange}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                    required
                  >
                    <option value="">Select Type</option>
                    {contentTypes.map(type => (
                      <option key={type.id} value={type.id}>{type.name}</option>
                    ))}
                  </select>
                </div>


              </div>

              {/* S3 Location Section */}
              <div style={{
                background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                padding: '24px',
                borderRadius: '16px',
                border: '1px solid #e2e8f0'
              }}>
                <label style={{
                  display: 'block',
                  marginBottom: '16px',
                  fontWeight: '600',
                  fontSize: '18px',
                  color: '#1e293b'
                }}>
                  S3 Location *
                </label>
                <input
                  type="text"
                  name="file_url"
                  value={formData.file_url}
                  onChange={handleInputChange}
                  style={{
                    width: '100%',
                    padding: '18px 24px',
                    border: '2px solid #e2e8f0',
                    borderRadius: '12px',
                    fontSize: '16px',
                    background: 'white',
                    transition: 'all 0.2s ease',
                    boxSizing: 'border-box'
                  }}
                  placeholder="Enter file URL or S3 key"
                  required
                  onFocus={(e) => {
                    (e.target as HTMLElement).style.borderColor = '#667eea';
                    (e.target as HTMLElement).style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                  }}
                  onBlur={(e) => {
                    (e.target as HTMLElement).style.borderColor = '#e2e8f0';
                    (e.target as HTMLElement).style.boxShadow = 'none';
                  }}
                />
              </div>

              {/* Grade & Duration Row */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '32px' }}>
                <div style={{
                  background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                  padding: '24px',
                  borderRadius: '16px',
                  border: '1px solid #e2e8f0'
                }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '16px',
                    fontWeight: '600',
                    fontSize: '18px',
                    color: '#1e293b'
                  }}>
                    Grade Level
                  </label>
                  <select
                    name="grade_id"
                    value={formData.grade_id}
                    onChange={handleInputChange}
                    style={{
                      width: '100%',
                      padding: '18px 24px',
                      border: '2px solid #e2e8f0',
                      borderRadius: '12px',
                      fontSize: '16px',
                      background: 'white',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e) => {
                      (e.target as HTMLElement).style.borderColor = '#667eea';
                      (e.target as HTMLElement).style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                    }}
                    onBlur={(e) => {
                      (e.target as HTMLElement).style.borderColor = '#e2e8f0';
                      (e.target as HTMLElement).style.boxShadow = 'none';
                    }}
                  >
                    <option value="">Select Grade</option>
                    {grades.map(grade => (
                      <option key={grade.id} value={grade.id}>{grade.name}</option>
                    ))}
                  </select>
                </div>

                <div style={{
                  background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                  padding: '24px',
                  borderRadius: '16px',
                  border: '1px solid #e2e8f0'
                }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '16px',
                    fontWeight: '600',
                    fontSize: '18px',
                    color: '#1e293b'
                  }}>
                    Duration (seconds)
                  </label>
                  <input
                    type="number"
                    name="duration_sec"
                    value={formData.duration_sec}
                    onChange={handleInputChange}
                    style={{
                      width: '100%',
                      padding: '18px 24px',
                      border: '2px solid #e2e8f0',
                      borderRadius: '12px',
                      fontSize: '16px',
                      background: 'white',
                      transition: 'all 0.2s ease',
                      boxSizing: 'border-box'
                    }}
                    placeholder="Optional"
                    onFocus={(e) => {
                      (e.target as HTMLElement).style.borderColor = '#667eea';
                      (e.target as HTMLElement).style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                    }}
                    onBlur={(e) => {
                      (e.target as HTMLElement).style.borderColor = '#e2e8f0';
                      (e.target as HTMLElement).style.boxShadow = 'none';
                    }}
                  />
                </div>
              </div>

              {/* Checkboxes Section */}
              <div style={{
                background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                padding: '24px',
                borderRadius: '16px',
                border: '1px solid #e2e8f0'
              }}>
                <h4 style={{
                  margin: '0 0 20px 0',
                  fontWeight: '600',
                  fontSize: '18px',
                  color: '#1e293b'
                }}>
                  Content Settings
                </h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px' }}>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    cursor: 'pointer',
                    padding: '12px',
                    borderRadius: '8px',
                    transition: 'all 0.2s ease'
                  }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(102, 126, 234, 0.05)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent';
                    }}>
                    <input
                      type="checkbox"
                      name="is_downloadable"
                      checked={formData.is_downloadable}
                      onChange={handleInputChange}
                      style={{
                        width: '20px',
                        height: '20px',
                        accentColor: '#667eea'
                      }}
                    />
                    <span style={{ fontWeight: '500', color: '#374151' }}>📥 Downloadable</span>
                  </label>

                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    cursor: 'pointer',
                    padding: '12px',
                    borderRadius: '8px',
                    transition: 'all 0.2s ease'
                  }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(102, 126, 234, 0.05)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent';
                    }}>
                    <input
                      type="checkbox"
                      name="is_published"
                      checked={formData.is_published}
                      onChange={handleInputChange}
                      style={{
                        width: '20px',
                        height: '20px',
                        accentColor: '#667eea'
                      }}
                    />
                    <span style={{ fontWeight: '500', color: '#374151' }}>🌐 Published</span>
                  </label>

                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    cursor: 'pointer',
                    padding: '12px',
                    borderRadius: '8px',
                    transition: 'all 0.2s ease'
                  }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(102, 126, 234, 0.05)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent';
                    }}>
                    <input
                      type="checkbox"
                      name="is_reusable"
                      checked={formData.is_reusable}
                      onChange={handleInputChange}
                      style={{
                        width: '20px',
                        height: '20px',
                        accentColor: '#667eea'
                      }}
                    />
                    <span style={{ fontWeight: '500', color: '#374151' }}>🔄 Reusable</span>
                  </label>
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
              <button
                onClick={() => setShowEditModal(false)}
                style={{
                  padding: '8px 16px',
                  background: '#6b7280',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleEditContent}
                style={{
                  padding: '8px 16px',
                  background: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                Update Content
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {showPreviewModal && previewContent && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            maxWidth: '90vw',
            maxHeight: '90vh',
            width: '800px',
            overflow: 'hidden',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
          }}>
            {/* Modal Header */}
            <div style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              padding: '20px 24px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>
                  📹 Preview: {previewContent.title}
                </h3>
                <p style={{ margin: '4px 0 0 0', opacity: 0.9, fontSize: '14px' }}>
                  {previewContent.description}
                </p>
              </div>
              <button
                onClick={() => setShowPreviewModal(false)}
                style={{
                  background: 'rgba(255, 255, 255, 0.2)',
                  border: 'none',
                  borderRadius: '6px',
                  color: 'white',
                  padding: '8px 12px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                ✕ Close
              </button>
            </div>

            {/* Modal Content */}
            <div style={{ padding: '24px' }}>
              {previewLoading ? (
                // Loading State
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '400px',
                  background: '#f8fafc',
                  borderRadius: '8px'
                }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{
                      fontSize: '24px',
                      marginBottom: '12px',
                      animation: 'spin 1s linear infinite'
                    }}>⏳</div>
                    <div style={{ color: '#6b7280' }}>Loading content...</div>
                  </div>
                </div>
              ) : previewVideoUrl ? (
                (() => {
                  // Debug logging to understand what data we have
                  console.log('=== PDF DETECTION DEBUG ===');
                  console.log('Preview content:', previewContent);
                  console.log('Preview video URL:', previewVideoUrl);
                  console.log('Content type ID:', previewContent?.content_type_id);
                  console.log('File URL:', previewContent?.file_url);
                  console.log('File type:', previewContent?.file_type);

                  // Determine file type based on multiple sources
                  const originalUrl = previewContent?.file_url || '';
                  const signedUrl = previewVideoUrl || '';
                  const contentTypeId = previewContent?.content_type_id;

                  console.log('Original URL:', originalUrl);
                  console.log('Signed URL:', signedUrl);

                  // Check each condition individually for debugging
                  const hasPdfInOriginalUrl = originalUrl.toLowerCase().includes('.pdf');
                  const hasPdfInSignedUrl = signedUrl.toLowerCase().includes('.pdf');
                  const hasWorksheetsFolder = signedUrl.toLowerCase().includes('/worksheets/') || originalUrl.toLowerCase().includes('/worksheets/');
                  const isWorksheetContentType = contentTypeId === 4;
                  const isPdfContentType = [1, 2, 4, 7, 9].includes(contentTypeId);

                  console.log('Has PDF in original URL:', hasPdfInOriginalUrl);
                  console.log('Has PDF in signed URL:', hasPdfInSignedUrl);
                  console.log('Has worksheets folder:', hasWorksheetsFolder);
                  console.log('Is worksheet content type (4):', isWorksheetContentType);
                  console.log('Is PDF content type (1,2,4,7,9):', isPdfContentType);

                  // Determine if this is a PDF file
                  const isPDF = hasPdfInOriginalUrl || hasPdfInSignedUrl || hasWorksheetsFolder || isPdfContentType;

                  console.log('=== FINAL RESULT: Is PDF detected:', isPDF, '===');

                  if (previewContent.file_type === 'youtube') {
                    // YouTube Video
                    return (
                      <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0 }}>
                        <iframe
                          src={previewVideoUrl.replace('watch?v=', 'embed/')}
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            border: 'none',
                            borderRadius: '8px'
                          }}
                          allowFullScreen
                          title="Video Preview"
                        />
                      </div>
                    );
                  } else if (isPDF) {
                    // PDF Viewer
                    return (
                      <div style={{
                        width: '100%',
                        height: '600px',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        overflow: 'hidden'
                      }}>
                        <iframe
                          src={`${previewVideoUrl}#toolbar=1&navpanes=1&scrollbar=1`}
                          style={{
                            width: '100%',
                            height: '100%',
                            border: 'none'
                          }}
                          title="PDF Preview"
                        />
                        <div style={{
                          marginTop: '12px',
                          textAlign: 'center',
                          fontSize: '14px',
                          color: '#6b7280'
                        }}>
                          <a
                            href={previewVideoUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              color: '#3b82f6',
                              textDecoration: 'none',
                              fontWeight: '500'
                            }}
                          >
                            📄 Open PDF in new tab
                          </a>
                        </div>
                      </div>
                    );
                  } else {
                    // Video Player
                    return (
                      <div>
                        <video
                          controls
                          style={{
                            width: '100%',
                            maxHeight: '400px',
                            borderRadius: '8px',
                            background: '#000'
                          }}
                        >
                          <source src={previewVideoUrl} type="video/mp4" />
                          Your browser does not support the video tag.
                        </video>
                      </div>
                    );
                  }
                })()
              ) : (
                // Error State
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '400px',
                  background: '#fef2f2',
                  borderRadius: '8px',
                  border: '1px solid #fecaca'
                }}>
                  <div style={{ textAlign: 'center', color: '#dc2626' }}>
                    <div style={{ fontSize: '24px', marginBottom: '12px' }}>❌</div>
                    <div>Failed to load content</div>
                  </div>
                </div>
              )}

              {/* Content Info */}
              <div style={{ marginTop: '20px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <strong style={{ color: '#374151' }}>Content Type:</strong>
                  <div style={{ color: '#6b7280', marginTop: '4px' }}>
                    {contentTypes.find(ct => ct.id === previewContent.content_type_id)?.name || 'Unknown'}
                  </div>
                </div>
                <div>
                  <strong style={{ color: '#374151' }}>Grade:</strong>
                  <div style={{ color: '#6b7280', marginTop: '4px' }}>
                    {previewContent.grade?.name || 'N/A'}
                  </div>
                </div>
                <div>
                  <strong style={{ color: '#374151' }}>File Type:</strong>
                  <div style={{ color: '#6b7280', marginTop: '4px' }}>
                    {previewContent.file_type.toUpperCase()}
                  </div>
                </div>
                <div>
                  <strong style={{ color: '#374151' }}>Status:</strong>
                  <div style={{ color: '#6b7280', marginTop: '4px' }}>
                    {previewContent.is_published ? '✅ Published' : '📝 Not Published'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            padding: '24px',
            borderRadius: '8px',
            width: '90%',
            maxWidth: '400px'
          }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '600' }}>
              Delete Content
            </h3>
            <p style={{ margin: '0 0 20px 0', color: '#6b7280' }}>
              Are you sure you want to delete "{selectedContent?.title}"? This action cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowDeleteModal(false)}
                style={{
                  padding: '8px 16px',
                  background: '#6b7280',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteContent}
                style={{
                  padding: '8px 16px',
                  background: '#ef4444',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

interface AdminPageProps {
  onHomeClick: () => void;
  onBooksClick: () => void;
  onVideoLearningClick: () => void;
  onBlogsClick: () => void;
  onLoginClick: () => void;
  onAdminClick: () => void;
  onMkAcademyOnlineClick?: () => void;
  onMkAcademyInPersonClick?: () => void;
  isAuthenticated?: boolean;
  isAdmin?: boolean;
  user?: any;
  onLogout?: () => void;
}

interface Stats {
  users: number;
  books: number;
  blogs: number;
  subjects: number;
  grades: number;
  countries: number;
  states: number;
  packages: number;
  subscriptions: number;
  coupons: number;
  onlineClasses: number;
  inPersonClasses: number;
  totalBatches: number;
}

interface MenuItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  color: string;
  expanded?: boolean;
  subItems?: Array<{
    id: string;
    label: string;
    action: string;
  }>;
}

// Styled Components
const AdminContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #f8f9ff 0%, #ffffff 100%);
  padding-top: 120px;
  display: flex;
  position: relative;
`;

const Sidebar = styled.div`
  width: 300px;
  background: white;
  box-shadow: 3px 0 15px rgba(37, 60, 140, 0.15);
  height: calc(100vh - 120px);
  position: fixed;
  left: 0;
  top: 120px;
  overflow-y: auto;
  z-index: 100;
  border-right: 3px solid #253C8C;
`;

const SidebarHeader = styled.div`
  padding: 20px;
  border-bottom: 1px solid #e5e7eb;
  background: #f8f9ff;
`;

const SidebarTitle = styled.h3`
  font-size: 1.2rem;
  color: #253C8C;
  margin: 0;
  font-weight: 600;
  text-align: center;
`;

const MenuSection = styled.div`
  margin-bottom: 10px;
`;

const MenuItem = styled.div<{ isActive?: boolean; isExpanded?: boolean }>`
  padding: 15px 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  border-left: 4px solid transparent;
  background: ${props => props.isActive ? '#f0f4ff' : 'transparent'};
  border-left-color: ${props => props.isActive ? '#253C8C' : 'transparent'};
  border-bottom: 1px solid #f3f4f6;
  font-weight: ${props => props.isActive ? '600' : '500'};

  &:hover {
    background: #f8f9ff;
    border-left-color: #253C8C;
  }
`;

const MenuItemContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const MenuItemLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const MenuIcon = styled.div<{ color: string }>`
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.color};
`;

const MenuLabel = styled.span`
  font-size: 0.95rem;
  color: #374151;
  font-weight: 500;
`;

const SubMenu = styled.div<{ isExpanded: boolean }>`
  max-height: ${props => props.isExpanded ? '500px' : '0'};
  overflow: hidden;
  transition: max-height 0.3s ease;
`;

const SubMenuItem = styled.div`
  padding: 12px 20px 12px 60px;
  cursor: pointer;
  font-size: 0.9rem;
  color: #6b7280;
  transition: all 0.3s ease;
  border-left: 2px solid transparent;

  &:hover {
    background: #f3f4f6;
    color: #253C8C;
    border-left-color: #253C8C;
    font-weight: 500;
  }
`;

const MainContent = styled.div`
  flex: 1;
  margin-left: 300px;
  padding: 20px;
  min-height: calc(100vh - 120px);
  border-left: 1px solid #e5e7eb;
`;

const ContentArea = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const AdminHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const AdminTitle = styled.h1`
  font-size: 2rem;
  color: #253C8C;
  margin: 0;
  font-weight: 700;
`;

const AdminSubtitle = styled.p`
  font-size: 0.9rem;
  color: #718096;
  margin: 5px 0 0 0;
`;

const DashboardControls = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
`;

const RefreshButton = styled.button`
  background: #253C8C;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 8px 16px;
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.3s ease;

  &:hover {
    background: #1e3a8a;
    transform: translateY(-1px);
  }

  &:disabled {
    background: #9ca3af;
    cursor: not-allowed;
    transform: none;
  }
`;

const LoadingSpinner = styled.div`
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const ErrorMessage = styled.div`
  background: #fee2e2;
  border: 1px solid #fecaca;
  color: #dc2626;
  padding: 8px 12px;
  border-radius: 6px;
  margin-bottom: 15px;
  text-align: center;
  font-size: 0.8rem;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 12px;
  margin-bottom: 20px;
`;

const StatCard = styled.div<{ clickable?: boolean }>`
  background: white;
  border-radius: 8px;
  padding: 12px;
  box-shadow: 0 2px 8px rgba(37, 60, 140, 0.1);
  text-align: center;
  transition: all 0.3s ease;
  cursor: ${props => props.clickable ? 'pointer' : 'default'};
  position: relative;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 20px rgba(37, 60, 140, 0.15);
    ${props => props.clickable && `
      background: linear-gradient(135deg, #f8f9ff 0%, #ffffff 100%);
      border: 2px solid #253C8C;
    `}
  }

  ${props => props.clickable && `
    &:after {
      content: '→';
      position: absolute;
      top: 10px;
      right: 15px;
      color: #253C8C;
      font-weight: bold;
      font-size: 1.2rem;
      opacity: 0;
      transition: opacity 0.3s ease;
    }

    &:hover:after {
      opacity: 1;
    }
  `}
`;

const StatIcon = styled.div<{ color: string }>`
  width: 35px;
  height: 35px;
  border-radius: 50%;
  background: ${props => props.color};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 8px;
  color: white;
  font-size: 16px;
`;

const StatNumber = styled.div`
  font-size: 1.4rem;
  font-weight: 700;
  color: #253C8C;
  margin-bottom: 3px;
  line-height: 1.2;
`;

const StatLabel = styled.div`
  font-size: 0.75rem;
  color: #718096;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  line-height: 1.1;
`;

const CRUDSection = styled.div`
  background: white;
  border-radius: 15px;
  padding: 30px;
  box-shadow: 0 4px 20px rgba(37, 60, 140, 0.1);
  margin-bottom: 30px;
`;

const CRUDTitle = styled.h2`
  font-size: 1.5rem;
  color: #253C8C;
  margin-bottom: 20px;
  font-weight: 600;
`;

const CRUDGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 15px;
`;

const CRUDButton = styled.button<{ variant: 'create' | 'read' | 'update' | 'delete' }>`
  padding: 12px 20px;
  border: none;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 0.9rem;

  ${props => {
    switch (props.variant) {
      case 'create':
        return `
          background: #10b981;
          color: white;
          &:hover { background: #059669; }
        `;
      case 'read':
        return `
          background: #3b82f6;
          color: white;
          &:hover { background: #2563eb; }
        `;
      case 'update':
        return `
          background: #f59e0b;
          color: white;
          &:hover { background: #d97706; }
        `;
      case 'delete':
        return `
          background: #ef4444;
          color: white;
          &:hover { background: #dc2626; }
        `;
    }
  }}

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  }
`;

// Full-page form styled components
const FullPageContainer = styled.div`
  min-height: calc(100vh - 120px);
  background: #f8f9ff;
  padding: 20px;
`;

const FullPageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  padding: 16px 20px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(37, 60, 140, 0.1);
`;

const FullPageTitle = styled.h1`
  font-size: 1.5rem;
  color: #253C8C;
  margin: 0;
  font-weight: 600;
`;

const BackButton = styled.button`
  background: #6b7280;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 10px 16px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;

  &:hover {
    background: #4b5563;
    transform: translateY(-1px);
  }
`;

const FormContainer = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(37, 60, 140, 0.1);
  max-width: 1100px;
  margin: 0 auto;
`;

const FormSection = styled.div`
  margin-bottom: 24px;
  padding: 16px;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
`;

const FormSectionTitle = styled.h4`
  margin: 0 0 16px 0;
  color: #1e40af;
  font-size: 16px;
  font-weight: 600;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormField = styled.div`
  margin-bottom: 16px;
`;

const FormLabel = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: bold;
  color: #374151;
  font-size: 14px;
`;

const FormInput = styled.input`
  width: 100%;
  padding: 12px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: #253C8C;
  }
`;

const FormTextarea = styled.textarea`
  width: 100%;
  padding: 12px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  resize: vertical;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: #253C8C;
  }
`;

const FormSelect = styled.select`
  width: 100%;
  padding: 12px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  background: white;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: #253C8C;
  }
`;

const FormActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #e5e7eb;
`;

const SubmitButton = styled.button<{ loading?: boolean }>`
  background: #059669;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: ${props => props.loading ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.loading ? 0.6 : 1};
  transition: all 0.3s ease;

  &:hover {
    background: ${props => props.loading ? '#059669' : '#047857'};
    transform: ${props => props.loading ? 'none' : 'translateY(-1px)'};
  }
`;

const CancelButton = styled.button<{ loading?: boolean }>`
  background: #6b7280;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: ${props => props.loading ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.loading ? 0.6 : 1};
  transition: all 0.3s ease;

  &:hover {
    background: ${props => props.loading ? '#6b7280' : '#4b5563'};
    transform: ${props => props.loading ? 'none' : 'translateY(-1px)'};
  }
`;

const AdminPage: React.FC<AdminPageProps> = ({
  onHomeClick,
  onBooksClick,
  onVideoLearningClick,
  onBlogsClick,
  onLoginClick,
  onAdminClick,
  onMkAcademyOnlineClick,
  onMkAcademyInPersonClick,
  isAuthenticated,
  isAdmin,
  user,
  onLogout
}) => {
  const [stats, setStats] = useState<Stats>({
    users: 0,
    books: 0,
    blogs: 0,
    subjects: 0,
    grades: 0,
    countries: 0,
    states: 0,
    packages: 0,
    subscriptions: 0,
    coupons: 0,
    onlineClasses: 0,
    inPersonClasses: 0,
    totalBatches: 0
  });

  const [activeSection, setActiveSection] = useState<string>('dashboard');
  const [expandedMenus, setExpandedMenus] = useState<Set<string>>(new Set());

  // Data states for admin sections
  const [users, setUsers] = useState<any[]>([]);
  const [books, setBooks] = useState<any[]>([]);
  const [videos, setVideos] = useState<any[]>([]);
  const [packages, setPackages] = useState<any[]>([]);
  const [subscriptions, setSubscriptions] = useState<any[]>([]);
  const [blogs, setBlogs] = useState<any[]>([]);
  const [faqs, setFaqs] = useState<any[]>([]);
  const [feedback, setFeedback] = useState<any[]>([]);
  const [subjects, setSubjects] = useState<any[]>([]);
  const [grades, setGrades] = useState<any[]>([]);
  const [countries, setCountries] = useState<any[]>([]);
  const [states, setStates] = useState<any[]>([]);

  // Academy states
  const [courses, setCourses] = useState<any[]>([]);
  const [onlineClasses, setOnlineClasses] = useState<any[]>([]);
  const [inPersonClasses, setInPersonClasses] = useState<any[]>([]);
  const [batches, setBatches] = useState<any[]>([]);
  const [batchesCourses, setBatchesCourses] = useState<any[]>([]);
  const [batchStudents, setBatchStudents] = useState<any[]>([]);

  // Online Class Form Data Interface
  interface OnlineClassFormData {
    course_name: string;
    program: string;
    instructor: string;
    level: string;
    day: string;
    time1: string;
    time2: string;
    date1: string;
    date2: string;
    fee: string;
    description: string;
    slots: number;
    zoom_link: string;
    total_hours: string;
    year: string;
    mode: string;
  }

  // Batch Form Data Interface
  interface BatchFormData {
    batch_name: string;
    program: string;
    instructor: string;
    description: string;
    start_date: string;
    end_date: string;
    max_students: number;
    status: string;
    courses: string[]; // Array of course IDs
  }

  // Batch Student Form Data Interface
  interface BatchStudentFormData {
    student_name: string;
    email: string;
    batch_id: string;
    enrollment_date: string;
    status: string;
    progress: number;
    grade: string;
  }

  // Online Classes Modal States
  const [showCreateOnlineClassModal, setShowCreateOnlineClassModal] = useState<boolean>(false);
  const [showEditOnlineClassModal, setShowEditOnlineClassModal] = useState<boolean>(false);
  const [showDeleteOnlineClassModal, setShowDeleteOnlineClassModal] = useState<boolean>(false);
  const [selectedOnlineClass, setSelectedOnlineClass] = useState<any>(null);
  const [onlineClassFormData, setOnlineClassFormData] = useState<OnlineClassFormData>({
    course_name: '',
    program: '',
    instructor: '',
    level: '',
    day: '',
    time1: '',
    time2: '',
    date1: '',
    date2: '',
    fee: '',
    description: '',
    slots: 20,
    zoom_link: '',
    total_hours: '',
    year: new Date().getFullYear().toString(),
    mode: 'online'
  });

  // In-Person Classes Modal States
  const [showCreateInPersonClassModal, setShowCreateInPersonClassModal] = useState<boolean>(false);
  const [showEditInPersonClassModal, setShowEditInPersonClassModal] = useState<boolean>(false);
  const [showDeleteInPersonClassModal, setShowDeleteInPersonClassModal] = useState<boolean>(false);
  const [selectedInPersonClass, setSelectedInPersonClass] = useState<any>(null);
  const [inPersonClassFormData, setInPersonClassFormData] = useState<OnlineClassFormData>({
    course_name: '',
    program: '',
    instructor: '',
    level: '',
    day: '',
    time1: '',
    time2: '',
    date1: '',
    date2: '',
    fee: '',
    description: '',
    slots: 20,
    zoom_link: '',
    total_hours: '',
    year: new Date().getFullYear().toString(),
    mode: 'in-person'
  });

  // Batch Form Data
  const [batchFormData, setBatchFormData] = useState<BatchFormData>({
    batch_name: '',
    program: '',
    instructor: '',
    description: '',
    start_date: '',
    end_date: '',
    max_students: 20,
    status: 'Active',
    courses: []
  });

  // Batch Student Form Data
  const [batchStudentFormData, setBatchStudentFormData] = useState<BatchStudentFormData>({
    student_name: '',
    email: '',
    batch_id: '',
    enrollment_date: new Date().toISOString().split('T')[0],
    status: 'Active',
    progress: 0,
    grade: ''
  });

  // Selected batch and batch student for editing
  const [selectedBatch, setSelectedBatch] = useState<any>(null);
  const [selectedBatchStudent, setSelectedBatchStudent] = useState<any>(null);

  // Available courses for batch assignment
  const [availableCourses, setAvailableCourses] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [statsLoading, setStatsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

  // Modal and editing states
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [showPasswordResetModal, setShowPasswordResetModal] = useState<boolean>(false);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  
  // Book Management specific filters
  const [bookGradeFilter, setBookGradeFilter] = useState<string>('all');
  const [bookCountryFilter, setBookCountryFilter] = useState<string>('all');
  const [bookStateFilter, setBookStateFilter] = useState<string>('all');
  
  // Book Management tabs (browser-like tabs within Book Management)
  interface BookTab {
    id: string;
    type: 'list' | 'edit';
    title: string;
    book?: any;
  }
  const [bookTabs, setBookTabs] = useState<BookTab[]>([{ id: 'list', type: 'list', title: '📋 Books List' }]);
  const [activeBookTab, setActiveBookTab] = useState<string>('list');
  
  // User form states
  const [newUserForm, setNewUserForm] = useState({
    name: '',
    email: '',
    password: '',
    user_role: 1
  });
  const [editUserForm, setEditUserForm] = useState({
    name: '',
    email: '',
    role_id: 1
  });
  const [resetPasswordForm, setResetPasswordForm] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [userFormError, setUserFormError] = useState<string>('');
  const [userFormSuccess, setUserFormSuccess] = useState<string>('');
  const [userFormLoading, setUserFormLoading] = useState<boolean>(false);
  const [tempPasswordDisplay, setTempPasswordDisplay] = useState<string>('');
  
  // Pagination state for table views
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);
  const [currentPage, setCurrentPage] = useState<number>(1);

  // Sorting state
  const [sortColumn, setSortColumn] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // Subject-specific states
  const [showCreateSubjectModal, setShowCreateSubjectModal] = useState<boolean>(false);
  const [showEditSubjectModal, setShowEditSubjectModal] = useState<boolean>(false);
  const [showDeleteSubjectModal, setShowDeleteSubjectModal] = useState<boolean>(false);
  const [selectedSubject, setSelectedSubject] = useState<any>(null);
  const [subjectFormData, setSubjectFormData] = useState<{
    name: string;
    selectedGradeIds: number[];
  }>({
    name: '',
    selectedGradeIds: []
  });
  const [subjectLoading, setSubjectLoading] = useState<boolean>(false);

  // Subject-Grade relationship states
  const [showManageSubjectGradesModal, setShowManageSubjectGradesModal] = useState<boolean>(false);
  const [selectedSubjectForGrades, setSelectedSubjectForGrades] = useState<any>(null);
  const [subjectGrades, setSubjectGrades] = useState<any[]>([]);
  const [availableGrades, setAvailableGrades] = useState<any[]>([]);
  const [selectedGradeIds, setSelectedGradeIds] = useState<number[]>([]);
  const [subjectGradesLoading, setSubjectGradesLoading] = useState<boolean>(false);

  // Location (Countries and States) states
  const [showCreateCountryModal, setShowCreateCountryModal] = useState<boolean>(false);
  const [showEditCountryModal, setShowEditCountryModal] = useState<boolean>(false);
  const [showDeleteCountryModal, setShowDeleteCountryModal] = useState<boolean>(false);
  const [selectedCountry, setSelectedCountry] = useState<any>(null);
  const [countryFormData, setCountryFormData] = useState<{
    name: string;
    active: boolean;
  }>({
    name: '',
    active: true
  });
  const [countryLoading, setCountryLoading] = useState<boolean>(false);

  const [showCreateStateModal, setShowCreateStateModal] = useState<boolean>(false);
  const [showEditStateModal, setShowEditStateModal] = useState<boolean>(false);
  const [showDeleteStateModal, setShowDeleteStateModal] = useState<boolean>(false);
  const [selectedState, setSelectedState] = useState<any>(null);
  const [stateFormData, setStateFormData] = useState<{
    name: string;
    country_id: string;
    active: boolean;
  }>({
    name: '',
    country_id: '',
    active: true
  });
  const [stateLoading, setStateLoading] = useState<boolean>(false);

  // Full-page description editor states
  const [showDescriptionEditor, setShowDescriptionEditor] = useState<boolean>(false);
  const [tempDescription, setTempDescription] = useState<string>('');

  // Blog content editor states
  const [showBlogContentEditor, setShowBlogContentEditor] = useState<boolean>(false);
  const [tempBlogContent, setTempBlogContent] = useState<string>('');

  // Blog-specific states
  const [showCreateBlogModal, setShowCreateBlogModal] = useState<boolean>(false);
  const [showEditBlogModal, setShowEditBlogModal] = useState<boolean>(false);
  const [showDeleteBlogModal, setShowDeleteBlogModal] = useState<boolean>(false);
  const [selectedBlog, setSelectedBlog] = useState<any>(null);
  const [blogFormData, setBlogFormData] = useState<{
    title: string;
    description: string;
    author: string;
    status: string;
    seo: string;
    image: string;
    content_images: Array<{ filename: string; alt_text: string }>;
  }>({
    title: '',
    description: '',
    author: '',
    status: 'Active',
    seo: '',
    image: '',
    content_images: []
  });

  // Blog image upload states
  const [selectedBlogImageFile, setSelectedBlogImageFile] = useState<File | null>(null);
  const [isUploadingBlogImage, setIsUploadingBlogImage] = useState<boolean>(false);
  const [blogImageUploadProgress, setBlogImageUploadProgress] = useState<number>(0);
  const [blogImageUploadError, setBlogImageUploadError] = useState<string | null>(null);
  const [blogLoading, setBlogLoading] = useState<boolean>(false);

  // Blog image upload function
  const handleBlogImageUpload = async (): Promise<string | null> => {
    if (!selectedBlogImageFile) return null;

    setIsUploadingBlogImage(true);
    setBlogImageUploadError(null);
    setBlogImageUploadProgress(0);

    try {
      console.log('🔍 Starting blog image upload for:', selectedBlogImageFile.name);

      // Create FormData for file upload
      const formData = new FormData();
      formData.append('image', selectedBlogImageFile);

      setBlogImageUploadProgress(30);

      // Upload to blog service (uses blog-specific upload endpoint)
      const uploadResponse = await fetch(`${process.env.REACT_APP_BLOG_SERVICE_URL || 'http://localhost:3011'}/api/blogs/upload-image`, {
        method: 'POST',
        body: formData
      });

      setBlogImageUploadProgress(70);

      if (!uploadResponse.ok) {
        const errorData = await uploadResponse.json();
        throw new Error(errorData.error || 'Failed to upload image');
      }

      const responseData = await uploadResponse.json();
      setBlogImageUploadProgress(100);

      const uploadedFileName = responseData.filename || responseData.fileName;
      console.log('✅ Blog image uploaded successfully:', uploadedFileName);
      return uploadedFileName; // Return just the filename (e.g., "blog-123.jpg")

    } catch (error) {
      console.error('❌ Blog image upload error:', error);
      setBlogImageUploadError(error instanceof Error ? error.message : 'Upload failed');
      return null;
    } finally {
      setIsUploadingBlogImage(false);
      setTimeout(() => setBlogImageUploadProgress(0), 2000);
    }
  };

  // FAQ-specific states
  const [showCreateFaqModal, setShowCreateFaqModal] = useState<boolean>(false);
  const [showEditFaqModal, setShowEditFaqModal] = useState<boolean>(false);
  const [showDeleteFaqModal, setShowDeleteFaqModal] = useState<boolean>(false);
  const [showPreviewFaqModal, setShowPreviewFaqModal] = useState<boolean>(false);
  const [selectedFaq, setSelectedFaq] = useState<any>(null);
  const [faqFormData, setFaqFormData] = useState<{
    question: string;
    answer_html: string;
    category: string;
    tags: string[];
    status: string;
    display_order: number;
    locale: string;
  }>({
    question: '',
    answer_html: '',
    category: 'General',
    tags: [],
    status: 'active',
    display_order: 0,
    locale: 'en'
  });
  const [faqLoading, setFaqLoading] = useState<boolean>(false);
  const [faqCategories, setFaqCategories] = useState<string[]>([]);

  // Feedback-specific states
  const [showViewFeedbackModal, setShowViewFeedbackModal] = useState<boolean>(false);
  const [showDeleteFeedbackModal, setShowDeleteFeedbackModal] = useState<boolean>(false);
  const [selectedFeedback, setSelectedFeedback] = useState<any>(null);
  const [feedbackLoading, setFeedbackLoading] = useState<boolean>(false);

  // Success Stories states
  const [successStories, setSuccessStories] = useState<any[]>([]);
  const [showCreateSuccessStoryModal, setShowCreateSuccessStoryModal] = useState<boolean>(false);
  const [showEditSuccessStoryModal, setShowEditSuccessStoryModal] = useState<boolean>(false);
  const [showDeleteSuccessStoryModal, setShowDeleteSuccessStoryModal] = useState<boolean>(false);
  const [selectedSuccessStory, setSelectedSuccessStory] = useState<any>(null);
  const [successStoryFormData, setSuccessStoryFormData] = useState<{
    name: string;
    grade: string;
    avatar: string;
    content: string;
    improvement: string;
    time_spent: string;
    display_order: number;
    is_active: boolean;
  }>({
    name: '',
    grade: '',
    avatar: '',
    content: '',
    improvement: '',
    time_spent: '',
    display_order: 0,
    is_active: true
  });
  const [successStoryLoading, setSuccessStoryLoading] = useState<boolean>(false);

  // Grade-specific states
  const [showCreateGradeModal, setShowCreateGradeModal] = useState<boolean>(false);
  const [showEditGradeModal, setShowEditGradeModal] = useState<boolean>(false);
  const [showDeleteGradeModal, setShowDeleteGradeModal] = useState<boolean>(false);
  const [selectedGrade, setSelectedGrade] = useState<any>(null);
  const [gradeFormData, setGradeFormData] = useState<{
    name: string;
  }>({
    name: ''
  });
  const [gradeLoading, setGradeLoading] = useState<boolean>(false);

  // Book-specific states

  const [showDeleteBookModal, setShowDeleteBookModal] = useState<boolean>(false);
  const [selectedBook, setSelectedBook] = useState<any>(null);
  const [bookFormData, setBookFormData] = useState<{
    title: string;
    image: string;
    description: string;
    price: string;
    discount_price: string;
    referal_link: string;
    video_link: string;
    file: string;
    subject_id: string;
    shared_content_group_id: string;
    sub_subject_id: string;
    country: string;
    state_id: string;
    state_ids: string;
    grade_id: string;
    short_description: string;
    seo: string;
    is_best_seller: boolean;
    vendor_id: string;
    vendors: string[]; // New: array of selected vendor names
    other_link: string;
  }>({
    title: '',
    image: '',
    description: '',
    price: '',
    discount_price: '',
    referal_link: '',
    video_link: '',
    file: '',
    subject_id: '',
    shared_content_group_id: '',
    sub_subject_id: '',
    country: '',
    state_id: '',
    state_ids: '',
    grade_id: '',
    short_description: '',
    seo: '',
    is_best_seller: false,
    vendor_id: '',
    vendors: [], // New: array of selected vendor names
    other_link: ''
  });
  const [bookLoading, setBookLoading] = useState<boolean>(false);

  // Image upload states for book creation
  const [selectedBookImageFile, setSelectedBookImageFile] = useState<File | null>(null);
  const [isUploadingBookImage, setIsUploadingBookImage] = useState<boolean>(false);
  const [bookImageUploadProgress, setBookImageUploadProgress] = useState<number>(0);
  const [bookImageUploadError, setBookImageUploadError] = useState<string | null>(null);

  // Helper function to parse vendor list from book data
  const parseVendorsFromBook = (book: any): string[] => {
    if (book.vendorInfo?.vendorlist) {
      return book.vendorInfo.vendorlist.toLowerCase().split(',').map((v: string) => v.trim());
    }
    // Fallback mapping for vendor_id to vendor names
    const vendorIdToNames: { [key: number]: string[] } = {
      1: ['mathknots'],
      2: ['amazon'],
      3: ['barnesandnoble'],
      4: ['amazon', 'barnesandnoble'],
      5: ['amazon', 'mathknots'],
      6: ['barnesandnoble', 'mathknots'],
      7: ['amazon', 'barnesandnoble', 'mathknots']
    };
    return vendorIdToNames[book.vendor_id] || [];
  };

  // Image upload function for book creation
  const handleBookImageUpload = async (): Promise<string | null> => {
    if (!selectedBookImageFile) return null;

    setIsUploadingBookImage(true);
    setBookImageUploadError(null);
    setBookImageUploadProgress(0);

    try {
      console.log('🔍 Starting book image upload for:', selectedBookImageFile.name);
      console.log('📁 File size:', selectedBookImageFile.size, 'bytes');
      console.log('📁 File type:', selectedBookImageFile.type);

      // Create FormData for file upload
      const formData = new FormData();
      formData.append('image', selectedBookImageFile);
      formData.append('folder', 'images'); // Store in public/images folder

      setBookImageUploadProgress(30);

      // Upload to server (book service will handle saving to public folder)
      const uploadUrl = `${process.env.REACT_APP_BOOK_SERVICE_URL || 'http://localhost:5001'}/api/books/upload-image`;
      console.log('📤 Uploading to:', uploadUrl);
      
      const uploadResponse = await fetch(uploadUrl, {
        method: 'POST',
        body: formData
      });

      setBookImageUploadProgress(70);

      console.log('📥 Upload response status:', uploadResponse.status, uploadResponse.statusText);
      console.log('📥 Upload response content-type:', uploadResponse.headers.get('content-type'));

      if (!uploadResponse.ok) {
        let errorMessage = `Upload failed with status ${uploadResponse.status}`;
        try {
          const errorText = await uploadResponse.text();
          console.error('❌ Server error response:', errorText.substring(0, 500)); // Log first 500 chars
          
          // Check if response is HTML (service not running or wrong endpoint)
          if (errorText.includes('<!DOCTYPE') || errorText.includes('<html')) {
            errorMessage = `Backend service error (status ${uploadResponse.status}). The book-service may not be running or the endpoint is incorrect. Please check: pm2 status`;
          } else {
            // Try to parse as JSON
            try {
              const errorData = JSON.parse(errorText);
              errorMessage = errorData.error || errorData.message || errorMessage;
            } catch (jsonError) {
              // If not JSON and not HTML, use the text as error message
              errorMessage = errorText.substring(0, 200) || errorMessage;
            }
          }
        } catch (readError) {
          console.error('❌ Could not read error response:', readError);
        }
        throw new Error(errorMessage);
      }

      // Check if response is actually JSON
      const contentType = uploadResponse.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        console.error('❌ Response is not JSON, content-type:', contentType);
        const responseText = await uploadResponse.text();
        console.error('❌ Response text:', responseText.substring(0, 500));
        throw new Error('Server returned non-JSON response. Service may not be configured correctly.');
      }

      const responseData = await uploadResponse.json();
      console.log('✅ Upload response data:', responseData);
      
      const { fileName, filePath } = responseData;
      setBookImageUploadProgress(100);

      console.log('✅ Book image uploaded successfully:', fileName);
      return fileName; // Return just the filename (e.g., "book-123.jpg")

    } catch (error) {
      console.error('❌ Book image upload error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Upload failed';
      console.error('❌ Error details:', errorMessage);
      setBookImageUploadError(errorMessage);
      alert(`Failed to upload image: ${errorMessage}`);
      return null;
    } finally {
      setIsUploadingBookImage(false);
      setTimeout(() => setBookImageUploadProgress(0), 2000);
    }
  };

  // Video-specific states
  const [showDeleteVideoModal, setShowDeleteVideoModal] = useState<boolean>(false);
  const [selectedVideo, setSelectedVideo] = useState<any>(null);
  const [videoFormData, setVideoFormData] = useState<{
    book_details_id: string;
    subject_id: string;
    grade_id: string;
    six_month: string;
    twelve_month: string;
    is_publish: boolean;
  }>({
    book_details_id: '',
    subject_id: '',
    grade_id: '',
    six_month: '',
    twelve_month: '',
    is_publish: false
  });
  const [videoLoading, setVideoLoading] = useState<boolean>(false);

  // Package-specific states
  const [showDeletePackageModal, setShowDeletePackageModal] = useState<boolean>(false);
  const [selectedPackage, setSelectedPackage] = useState<any>(null);
  const [packageFormData, setPackageFormData] = useState<{
    name: string;
    description: string;
    annual_price: string;
    discount_price: string;
    country_id: string;
    state_id: string;
    grade_id: string;
    subject_id: string;
    book_id: string; // Legacy single book field
    is_active: boolean;
    features: string;
    image: string;
    metadata: string;
    deleted_at: string;
    book_ids: string[]; // Many-to-many book associations
    sort_orders: string[]; // Sort orders for each book
    junction_active: boolean[]; // Active status for each book association
  }>({
    name: '',
    description: '',
    annual_price: '',
    discount_price: '',
    country_id: '',
    state_id: '',
    grade_id: '',
    subject_id: '',
    book_id: '', // Legacy single book field
    is_active: true,
    features: '',
    image: '',
    metadata: '',
    deleted_at: '',
    book_ids: [], // Many-to-many book associations
    sort_orders: [], // Sort orders for each book
    junction_active: [] // Active status for each book association
  });
  const [packageLoading, setPackageLoading] = useState<boolean>(false);

  // Fetch dynamic stats from all APIs
  const fetchStats = async (): Promise<void> => {
    try {
      setStatsLoading(true);
      setError('');
      console.log('📊 Fetching dashboard stats...');

      // Initialize stats object
      const newStats: Stats = {
        users: 0,
        books: 0,
        blogs: 0,
        subjects: 0,
        grades: 0,
        countries: 0,
        states: 0,
        packages: 0,
        subscriptions: 0,
        coupons: 0,
        onlineClasses: 0,
        inPersonClasses: 0,
        totalBatches: 0
      };

      // Fetch users count
      try {
        const endpoints = [
          apiUrls.admin.users,
          apiUrls.users.admin,
          apiUrls.users.general
        ];

        for (const endpoint of endpoints) {
          try {
            const response = await fetch(endpoint, { headers: getHttpHeaders() });
            if (response.ok) {
              const data = await response.json();
              console.log(`👥 Users API response from ${endpoint}:`, {
                paginationTotal: data.pagination?.total_items,
                total: data.total,
                usersArrayLength: Array.isArray(data.users) ? data.users.length : 'N/A'
              });
              // Use total count from API if available (check multiple formats)
              if (data.pagination?.total_items !== undefined) {
                // admin-service format: { users: [...], pagination: { total_items: X } }
                newStats.users = data.pagination.total_items;
                console.log(`✅ Using pagination.total_items: ${newStats.users}`);
              } else if (data.total !== undefined) {
                // user-service format: { users: [...], total: X }
                newStats.users = data.total;
                console.log(`✅ Using total: ${newStats.users}`);
              } else {
                // Fallback: count array length
                const usersArray = Array.isArray(data.users || data.data || data) ?
                  (data.users || data.data || data) : [];
                newStats.users = usersArray.length;
                console.log(`⚠️ Using array length fallback: ${newStats.users}`);
              }
              break;
            }
          } catch (err) {
            console.log(`Failed to fetch from ${endpoint}:`, err);
          }
        }
      } catch (error) {
        console.error('Error fetching users count:', error);
      }

      // Fetch books count
      try {
        const response = await fetch(apiUrls.books.list, { headers: getHttpHeaders() });
        if (response.ok) {
          const data = await response.json();
          const booksArray = Array.isArray(data.books || data) ? (data.books || data) : [];
          newStats.books = booksArray.length;
        }
      } catch (error) {
        console.error('Error fetching books count:', error);
      }

      // Video count removed - Video Learning System deprecated

      // Fetch blogs count
      try {
        const response = await fetch(apiUrls.blogs.list, { headers: getHttpHeaders() });
        if (response.ok) {
          const data = await response.json();
          const blogsArray = Array.isArray(data.blogs || data.data || data) ?
            (data.blogs || data.data || data) : [];
          newStats.blogs = blogsArray.length;
        }
      } catch (error) {
        console.error('Error fetching blogs count:', error);
      }

      // Fetch subjects count
      try {
        const response = await fetch(apiUrls.books.subjectsAdmin, { headers: getHttpHeaders() });
        if (response.ok) {
          const data = await response.json();
          const subjectsArray = Array.isArray(data.subjects || data) ? (data.subjects || data) : [];
          newStats.subjects = subjectsArray.length;
        }
      } catch (error) {
        console.error('Error fetching subjects count:', error);
      }

      // Fetch grades count
      try {
        const response = await fetch(apiUrls.books.gradesAdmin, { headers: getHttpHeaders() });
        if (response.ok) {
          const data = await response.json();
          const gradesArray = Array.isArray(data.grades || data) ? (data.grades || data) : [];
          newStats.grades = gradesArray.length;
        }
      } catch (error) {
        console.error('Error fetching grades count:', error);
      }

      // Fetch countries count
      try {
        const response = await fetch(apiUrls.countries.list, { headers: getHttpHeaders() });
        if (response.ok) {
          const data = await response.json();
          const countriesArray = Array.isArray(data.countries || data) ? (data.countries || data) : [];
          newStats.countries = countriesArray.length;
        }
      } catch (error) {
        console.error('Error fetching countries count:', error);
      }

      // Fetch packages count
      try {
        const response = await fetch(apiUrls.packages.list, { headers: getHttpHeaders() });
        if (response.ok) {
          const data = await response.json();
          const packagesArray = Array.isArray(data.packages || data.data || data) ?
            (data.packages || data.data || data) : [];
          newStats.packages = packagesArray.length;
        }
      } catch (error) {
        console.error('Error fetching packages count:', error);
      }

      // Note: States count would require knowing which country to query
      // For now, we'll keep it as 0 or implement a total count endpoint

      // Fetch online classes count (courses with mode=online)
      try {
        const response = await fetch(apiUrls.academy.coursesOnline, { headers: getHttpHeaders() });
        if (response.ok) {
          const data = await response.json();
          const onlineCoursesArray = Array.isArray(data.courses || data.data || data) ?
            (data.courses || data.data || data) : [];
          newStats.onlineClasses = onlineCoursesArray.length;
        }
      } catch (error) {
        console.error('Error fetching online courses count:', error);
      }

      // Fetch in-person classes count (courses with mode=in-person)
      try {
        const response = await fetch(apiUrls.academy.coursesInPerson, { headers: getHttpHeaders() });
        if (response.ok) {
          const data = await response.json();
          const inPersonCoursesArray = Array.isArray(data.courses || data.data || data) ?
            (data.courses || data.data || data) : [];
          newStats.inPersonClasses = inPersonCoursesArray.length;
        }
      } catch (error) {
        console.error('Error fetching in-person courses count:', error);
      }

      // Fetch batches count
      try {
        const response = await fetch(apiUrls.academy.batches, { headers: getHttpHeaders() });
        if (response.ok) {
          const data = await response.json();
          const batchesArray = Array.isArray(data.batches || data.data || data) ?
            (data.batches || data.data || data) : [];
          newStats.totalBatches = batchesArray.length;
        }
      } catch (error) {
        console.error('Error fetching batches count:', error);
      }

      // TODO: Implement subscription count API endpoint
      newStats.subscriptions = 0;

      // Update stats with real data
      setStats(newStats);
      console.log('✅ Stats updated:', newStats);

    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      setError('Failed to load dashboard statistics');
    } finally {
      setStatsLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const getMenuItems = (): MenuItem[] => [
    {
      id: 'dashboard',
      label: '📊 Dashboard',
      icon: <FiGrid />,
      color: '#253C8C'
    },
    {
      id: 'users',
      label: '👥 User Management',
      icon: <FiUsers />,
      color: '#3b82f6'
    },
    {
      id: 'books',
      label: '📚 Book Management',
      icon: <FiBook />,
      color: '#10b981'
    },

    // Video Learning System menu item removed - deprecated
    {
      id: 'content-management',
      label: '📋 Content Management',
      icon: <FiBook />,
      color: '#059669',
      subItems: [
        {
          id: 'content-library',
          label: '📚 Content Library',
          action: 'content-library'
        },
        {
          id: 'content-structure',
          label: '🏗️ Content Structure',
          action: 'content-structure'
        },
        {
          id: 'book-content',
          label: '📖 Book Content',
          action: 'book-content'
        },
        {
          id: 'packages',
          label: '📦 Content Packages',
          action: 'packages'
        },
        {
          id: 'grades',
          label: '🏆 Grades',
          action: 'grades'
        },
        {
          id: 'subjects',
          label: '🏷️ Subjects',
          action: 'subjects'
        }
      ]
    },
    {
      id: 'academy',
      label: '🎓 MK Academy',
      icon: <FiBookOpen />,
      color: '#7c3aed',
      subItems: [
        {
          id: 'courses',
          label: '📚 Course Management',
          action: 'courses'
        },
        {
          id: 'online-classes',
          label: '💻 Online Classes',
          action: 'online-classes'
        },
        {
          id: 'inperson-classes',
          label: '🏫 InPerson Classes',
          action: 'inperson-classes'
        },
        {
          id: 'batches',
          label: '📚 Batches',
          action: 'batches'
        },
        {
          id: 'batch-students',
          label: '👥 Batch Students',
          action: 'batch-students'
        }
      ]
    },
    {
      id: 'subscriptions',
      label: '💳 Subscriptions',
      icon: <FiCreditCard />,
      color: '#06b6d4'
    },
    {
      id: 'coupons',
      label: '🎫 Coupon Management',
      icon: <FiTag />,
      color: '#10b981'
    },
    {
      id: 'payment-settings',
      label: '💳 Payment Settings',
      icon: <FiSettings />,
      color: '#8b5cf6'
    },
    {
      id: 'blogs',
      label: '📝 Blog Management',
      icon: <FiBarChart2 />,
      color: '#ec4899'
    },

    {
      id: 'locations',
      label: '📍 Locations',
      icon: <FiMapPin />,
      color: '#6366f1'
    },

    {
      id: 'faqs',
      label: '❓ FAQ Management',
      icon: <FiHelpCircle />,
      color: '#8b5cf6'
    },

    {
      id: 'feedback',
      label: '💬 Feedback Management',
      icon: <FiMessageSquare />,
      color: '#10b981'
    },
    {
      id: 'success-stories',
      label: '⭐ Success Stories',
      icon: <FiStar />,
      color: '#f59e0b'
    }
  ];

  // Fetch functions for admin data
  const fetchUsers = async (): Promise<void> => {
    try {
      setLoading(true);
      setError('');
      console.log('🔍 Fetching users...');

      // Try multiple endpoints to get user data
      const endpoints = [
        apiUrls.admin.users, // Admin service
        apiUrls.users.admin, // User service admin endpoint
        apiUrls.users.general // User service general endpoint
      ];

      let userData = null;
      let lastError = null;

      for (const endpoint of endpoints) {
        try {
          console.log(`🔗 Trying endpoint: ${endpoint}`);
          const response = await fetch(endpoint, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json'
            }
          });

          if (response.ok) {
            const data = await response.json();
            console.log(`✅ Success from ${endpoint}:`, data);
            userData = data.users || data.data || data || [];
            break;
          } else {
            console.log(`❌ Failed ${endpoint}: ${response.status}`);
            lastError = `HTTP ${response.status}`;
          }
        } catch (err) {
          console.log(`❌ Error ${endpoint}:`, err);
          lastError = err instanceof Error ? err.message : 'Network error';
        }
      }

      if (userData) {
        // Ensure userData is an array
        const usersArray = Array.isArray(userData) ? userData : [userData];
        
        // Remove duplicates based on user ID
        const uniqueUsers = usersArray.filter((user, index, self) => 
          index === self.findIndex((u) => u.id === user.id)
        );
        
        setUsers(uniqueUsers);
        console.log(`📊 Loaded ${usersArray.length} users (${uniqueUsers.length} unique)`);
        
        if (usersArray.length !== uniqueUsers.length) {
          console.warn(`⚠️ Removed ${usersArray.length - uniqueUsers.length} duplicate user entries`);
        }
      } else {
        throw new Error(lastError || 'All user endpoints failed');
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      setError(`Failed to fetch users: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchBooks = async (): Promise<void> => {
    try {
      setLoading(true);
      setError('');
      console.log('📚 Fetching books...');

      const response = await fetch(apiUrls.books.list, {
        headers: getHttpHeaders()
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('📚 Books data:', data);
      setBooks(data.books || data || []);
    } catch (error) {
      console.error('Error fetching books:', error);
      setError('Failed to fetch books. Please try again.');
      setBooks([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchVideos = async (): Promise<void> => {
    try {
      setLoading(true);
      setError('');
      console.log('🎥 Fetching videos...');

      const response = await fetch(apiUrls.videos.admin, {
        headers: getHttpHeaders()
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('🎥 Videos data:', data);
      setVideos(data.videos || data || []);
    } catch (error) {
      console.error('Error fetching videos:', error);
      setError('Failed to fetch videos. Please try again.');
      setVideos([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch functions for all modules
  const fetchPackages = async (): Promise<void> => {
    try {
      setLoading(true);
      setError('');
      console.log('🔍 Fetching content packages...');

      const response = await fetch(apiUrls.packages.list, {
        headers: getHttpHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Content packages data:', data);
        setPackages(data.packages || data.data || data || []);
      } else {
        throw new Error(`HTTP ${response.status}`);
      }
    } catch (error) {
      console.error('Error fetching content packages:', error);
      setError(`Failed to fetch content packages: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setPackages([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscriptions = async (): Promise<void> => {
    try {
      setLoading(true);
      setError('');
      console.log('🔍 Fetching subscriptions...');

      // TODO: Implement subscription API endpoint
      setSubscriptions([]);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
      setError(`Failed to fetch subscriptions: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setSubscriptions([]);
      setLoading(false);
    }
  };

  const fetchBlogs = async (): Promise<void> => {
    try {
      setLoading(true);
      setError('');
      console.log('🔍 Fetching blogs for admin...');

      const response = await fetch(apiUrls.blogs.admin, {
        headers: getHttpHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Blogs admin data:', data);

        // Handle the new blog service response format
        if (data.success && Array.isArray(data.blogs)) {
          setBlogs(data.blogs);
        } else if (Array.isArray(data)) {
          setBlogs(data);
        } else {
          console.warn('Unexpected blog data format:', data);
          setBlogs([]);
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }
    } catch (error) {
      console.error('Error fetching blogs:', error);
      setError(`Failed to fetch blogs: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setBlogs([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchFaqs = async (): Promise<void> => {
    try {
      setLoading(true);
      setError('');
      console.log('🔍 Fetching FAQs for admin...');

      const response = await fetch(apiUrls.faqs.admin, {
        headers: getHttpHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        console.log('✅ FAQs admin data:', data);

        if (data.success && Array.isArray(data.faqs)) {
          setFaqs(data.faqs);
        } else if (Array.isArray(data)) {
          setFaqs(data);
        } else {
          console.warn('Unexpected FAQ data format:', data);
          setFaqs([]);
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }
    } catch (error) {
      console.error('Error fetching FAQs:', error);
      setError(`Failed to fetch FAQs: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setFaqs([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchFeedback = async (): Promise<void> => {
    try {
      setLoading(true);
      setError('');
      console.log('🔍 Fetching feedback for admin...');

      const response = await fetch(apiUrls.feedback.admin, {
        headers: getHttpHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Feedback admin data:', data);

        if (data.success && Array.isArray(data.feedbacks)) {
          setFeedback(data.feedbacks);
        } else if (Array.isArray(data)) {
          setFeedback(data);
        } else {
          console.warn('Unexpected feedback data format:', data);
          setFeedback([]);
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }
    } catch (error) {
      console.error('Error fetching feedback:', error);
      setError(`Failed to fetch feedback: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setFeedback([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchSubjects = async (): Promise<void> => {
    try {
      setLoading(true);
      setError('');
      console.log('🔍 Fetching subjects with grades...');

      const response = await fetch(apiUrls.books.subjectsAdmin, {
        headers: getHttpHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Subjects with grades data:', data);
        setSubjects(data.subjects || data || []);
      } else {
        throw new Error(`HTTP ${response.status}`);
      }
    } catch (error) {
      console.error('Error fetching subjects with grades:', error);
      setError(`Failed to fetch subjects: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setSubjects([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchGrades = async (): Promise<void> => {
    try {
      setLoading(true);
      setError('');
      console.log('🔍 Fetching grades from grades service...');

      const response = await fetch(apiUrls.grades.admin, {
        headers: getHttpHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Grades data:', data);
        setGrades(data.grades || data || []);
      } else {
        throw new Error(`HTTP ${response.status}`);
      }
    } catch (error) {
      console.error('Error fetching grades:', error);
      setError(`Failed to fetch grades: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setGrades([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchLocations = async (): Promise<void> => {
    try {
      setLoading(true);
      setError('');
      console.log('🔍 Fetching locations...');

      // Fetch countries using admin endpoint
      const countriesResponse = await fetch(apiUrls.countries.admin, {
        headers: getHttpHeaders()
      });

      if (countriesResponse.ok) {
        const countriesData = await countriesResponse.json();
        console.log('✅ Countries data:', countriesData);
        setCountries(countriesData.countries || []);
      } else {
        console.error('Failed to fetch countries');
        setCountries([]);
      }

      // Fetch all states using admin endpoint
      const statesResponse = await fetch(apiUrls.countries.statesAdmin, {
        headers: getHttpHeaders()
      });

      if (statesResponse.ok) {
        const statesData = await statesResponse.json();
        console.log('✅ States data:', statesData);
        setStates(statesData.states || []);
      } else {
        console.error('Failed to fetch states');
        setStates([]);
      }
    } catch (error) {
      console.error('Error fetching locations:', error);
      setError(`Failed to fetch locations: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setCountries([]);
      setStates([]);
    } finally {
      setLoading(false);
    }
  };

  // Academy fetch functions
  const fetchCourses = async (): Promise<void> => {
    try {
      setLoading(true);
      setError('');
      console.log('📚 Fetching courses...');

      const response = await fetch(apiUrls.academy.courses, {
        headers: getHttpHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Courses data:', data);
        
        const coursesArray = data.courses || data || [];
        
        // Remove duplicates based on course ID
        const uniqueCourses = coursesArray.filter((course: any, index: number, self: any[]) => 
          index === self.findIndex((c: any) => c.id === course.id || c.course_id === course.course_id)
        );
        
        setCourses(uniqueCourses);
        console.log(`📊 Loaded ${coursesArray.length} courses (${uniqueCourses.length} unique)`);
        
        if (coursesArray.length !== uniqueCourses.length) {
          console.warn(`⚠️ Removed ${coursesArray.length - uniqueCourses.length} duplicate course entries`);
        }
      } else {
        throw new Error(`HTTP ${response.status}`);
      }
    } catch (error) {
      console.error('Error fetching courses:', error);
      setError(`Failed to fetch courses: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setCourses([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchOnlineClasses = async (): Promise<void> => {
    try {
      setLoading(true);
      setError('');
      console.log('💻 Fetching online classes with enrollment data...');

      const response = await fetch(apiUrls.academy.onlineClassesWithEnrollment, {
        headers: getHttpHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Online classes with enrollment data:', data);
        
        const classesArray = data.classes || data || [];
        
        // Remove duplicates based on course_id
        const uniqueClasses = classesArray.filter((classItem: any, index: number, self: any[]) => 
          index === self.findIndex((c: any) => c.course_id === classItem.course_id)
        );
        
        setOnlineClasses(uniqueClasses);
        console.log(`📊 Loaded ${classesArray.length} online classes (${uniqueClasses.length} unique)`);
        
        if (classesArray.length !== uniqueClasses.length) {
          console.warn(`⚠️ Removed ${classesArray.length - uniqueClasses.length} duplicate online class entries`);
        }
      } else {
        throw new Error(`HTTP ${response.status}`);
      }
    } catch (error) {
      console.error('Error fetching online classes:', error);
      setError(`Failed to fetch online classes: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setOnlineClasses([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchInPersonClasses = async (): Promise<void> => {
    try {
      setLoading(true);
      setError('');
      console.log('🏫 Fetching in-person classes with enrollment data...');

      const response = await fetch(apiUrls.academy.inPersonClassesWithEnrollment, {
        headers: getHttpHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        console.log('✅ In-person classes with enrollment data:', data);
        
        const classesArray = data.classes || data || [];
        
        // Remove duplicates based on course_id
        const uniqueClasses = classesArray.filter((classItem: any, index: number, self: any[]) => 
          index === self.findIndex((c: any) => c.course_id === classItem.course_id)
        );
        
        setInPersonClasses(uniqueClasses);
        console.log(`📊 Loaded ${classesArray.length} in-person classes (${uniqueClasses.length} unique)`);
        
        if (classesArray.length !== uniqueClasses.length) {
          console.warn(`⚠️ Removed ${classesArray.length - uniqueClasses.length} duplicate in-person class entries`);
        }
      } else {
        throw new Error(`HTTP ${response.status}`);
      }
    } catch (error) {
      console.error('Error fetching in-person classes:', error);
      setError(`Failed to fetch in-person classes: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setInPersonClasses([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchBatches = async (): Promise<void> => {
    try {
      setLoading(true);
      setError('');
      console.log('📚 Fetching batches with course information...');

      const response = await fetch(apiUrls.academy.batchesWithCourses, {
        headers: getHttpHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Batches with courses data:', data);
        
        const batchesArray = data.batches || data || [];
        
        // Remove duplicates based on batch id
        const uniqueBatches = batchesArray.filter((batch: any, index: number, self: any[]) => 
          index === self.findIndex((b: any) => b.id === batch.id)
        );
        
        setBatches(uniqueBatches);
        console.log(`📊 Loaded ${batchesArray.length} batches (${uniqueBatches.length} unique)`);
        
        if (batchesArray.length !== uniqueBatches.length) {
          console.warn(`⚠️ Removed ${batchesArray.length - uniqueBatches.length} duplicate batch entries`);
        }
      } else {
        throw new Error(`HTTP ${response.status}`);
      }
    } catch (error) {
      console.error('Error fetching batches:', error);
      setError(`Failed to fetch batches: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setBatches([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchBatchStudents = async (): Promise<void> => {
    try {
      setLoading(true);
      setError('');
      console.log('👥 Fetching all batch students enrollment data...');

      // Fetch all batch student enrollments across all batches
      const response = await fetch(apiUrls.academy.batchStudentsAdmin, {
        headers: getHttpHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        console.log('✅ All batch students enrollment data:', data);
        
        const studentsArray = data.batchStudents || data || [];
        
        // Remove duplicates based on student enrollment id
        const uniqueStudents = studentsArray.filter((student: any, index: number, self: any[]) => 
          index === self.findIndex((s: any) => s.id === student.id)
        );
        
        setBatchStudents(uniqueStudents);
        console.log(`📊 Loaded ${studentsArray.length} batch students (${uniqueStudents.length} unique)`);
        
        if (studentsArray.length !== uniqueStudents.length) {
          console.warn(`⚠️ Removed ${studentsArray.length - uniqueStudents.length} duplicate batch student entries`);
        }
      } else {
        throw new Error(`HTTP ${response.status}`);
      }
    } catch (error) {
      console.error('Error fetching batch students:', error);
      setError(`Failed to fetch batch students: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setBatchStudents([]);
    } finally {
      setLoading(false);
    }
  };

  const handleMenuToggle = (itemId: string): void => {
    if (itemId === 'dashboard') {
      setActiveSection('dashboard');
      return;
    }

    const newExpandedMenus = new Set(expandedMenus);
    if (newExpandedMenus.has(itemId)) {
      newExpandedMenus.delete(itemId);
    } else {
      newExpandedMenus.add(itemId);
    }
    setExpandedMenus(newExpandedMenus);
  };

  // Menu click handlers
  const handleMenuClick = (section: string): void => {
    setActiveSection(section);
    console.log(`Admin section: ${section}`);

    // Auto-fetch data when section changes
    switch (section) {
      case 'users':
        fetchUsers();
        break;
      case 'books':
        fetchBooks();
        fetchSubjects();
        fetchGrades();
        fetchCountries();
        // Don't call fetchStates() without a country - states will be loaded when country is selected
        break;
      // 'videos' case removed - Video Learning System deprecated
      case 'packages':
        fetchPackages();
        break;
      case 'subscriptions':
        fetchSubscriptions();
        break;
      case 'coupons':
        // Coupon data is fetched within the CouponManagement component
        break;
      case 'payment-settings':
        // Payment settings data is fetched within the PaymentSettings component
        break;
      case 'blogs':
        fetchBlogs();
        break;
      case 'faqs':
        fetchFaqs();
        break;
      case 'feedback':
        fetchFeedback();
        break;
      case 'success-stories':
        fetchSuccessStories();
        break;
      case 'subjects':
        fetchSubjects();
        break;
      case 'grades':
        fetchGrades();
        break;
      case 'locations':
        fetchLocations();
        break;
    }
  };

  // Handle stat card clicks to navigate to corresponding sections
  const handleStatCardClick = (statType: string): void => {
    console.log(`📊 Stat card clicked: ${statType}`);

    // Map stat types to admin sections
    const sectionMap: Record<string, string> = {
      'users': 'users',
      'books': 'books',
      'blogs': 'blogs',
      'categories': 'categories',
      'subjects': 'subjects',
      'grades': 'grades',
      'countries': 'locations',
      'states': 'locations',
      'packages': 'packages',
      'subscriptions': 'subscriptions',
      'coupons': 'coupons',
      'onlineClasses': 'online-classes',
      'inPersonClasses': 'inperson-classes',
      'totalBatches': 'batches'
    };

    const targetSection = sectionMap[statType];
    if (targetSection) {
      // Academy sections use handleSubMenuClick instead of handleMenuClick
      if (['online-classes', 'inperson-classes', 'batches'].includes(targetSection)) {
        handleSubMenuClick(targetSection);
      } else {
        handleMenuClick(targetSection);
      }
    }
  };

  const handleSubMenuClick = (action: string): void => {
    setActiveSection(action);
    console.log(`Admin action: ${action}`);

    // Fetch data based on the action
    switch (action) {
      case 'view-books':
        fetchBooks();
        break;
      // 'view-videos' case removed - Video Learning System deprecated
      case 'courses':
        fetchCourses();
        break;
      case 'online-classes':
        fetchOnlineClasses();
        break;
      case 'inperson-classes':
        fetchInPersonClasses();
        break;
      case 'batches':
        fetchBatches();
        break;
      case 'batch-students':
        fetchBatchStudents();
        break;
      case 'book-content':
        // Book Content management - no additional data fetching needed
        console.log('Book Content management selected');
        break;
      case 'grades':
        fetchGrades();
        break;
      case 'subjects':
        fetchSubjects();
        break;
      case 'packages':
        fetchPackages();
        break;
      default:
        console.log(`Action ${action} will be implemented soon`);
    }
  };

  // User action handlers
  const handleEditUser = (user: any): void => {
    console.log('✏️ Edit user:', user);
    setSelectedUser(user);
    setEditUserForm({
      name: user.name || '',
      email: user.email || '',
      role_id: user.role_id || user.user_role || 1
    });
    setUserFormError('');
    setUserFormSuccess('');
    setShowEditModal(true);
  };

  const handleDeleteUser = (user: any): void => {
    console.log('🗑️ Delete user:', user);
    setSelectedUser(user);
    setUserFormError('');  // Clear any previous errors
    setShowDeleteModal(true);
  };

  const confirmDeleteUser = async (): Promise<void> => {
    if (!selectedUser) return;
    
    setUserFormLoading(true);
    setUserFormError('');
    
    try {
      console.log('🗑️ Deleting user:', selectedUser.id);
      
      // Try admin-service first, then user-service
      const endpoints = [
        `${apiUrls.admin.users}/${selectedUser.id}`,
        `${apiUrls.users.admin}/${selectedUser.id}`
      ];
      
      let success = false;
      let lastError = '';
      
      for (const endpoint of endpoints) {
        try {
          const response = await fetch(endpoint, {
            method: 'DELETE',
            headers: getHttpHeaders()
          });
          
          if (response.ok) {
            success = true;
            console.log('✅ User deleted successfully via:', endpoint);
            break;
          } else {
            const errorData = await response.json();
            lastError = errorData.message || errorData.error || `HTTP ${response.status}`;
          }
        } catch (err) {
          lastError = err instanceof Error ? err.message : 'Network error';
        }
      }
      
      if (success) {
        // Refresh users list
        await fetchUsers();
        setShowDeleteModal(false);
        setSelectedUser(null);
        alert(`User "${selectedUser.name}" has been deleted successfully.`);
      } else {
        setUserFormError(`Failed to delete user: ${lastError}`);
      }
    } catch (error) {
      console.error('❌ Error deleting user:', error);
      setUserFormError(`Error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setUserFormLoading(false);
    }
  };

  const confirmUpdateUser = async (): Promise<void> => {
    if (!selectedUser) return;
    
    setUserFormLoading(true);
    setUserFormError('');
    
    try {
      console.log('✏️ Updating user:', selectedUser.id, editUserForm);
      
      // Validation
      if (!editUserForm.name.trim() || !editUserForm.email.trim()) {
        setUserFormError('Name and email are required');
        setUserFormLoading(false);
        return;
      }
      
      const response = await fetch(`${apiUrls.admin.users}/${selectedUser.id}`, {
        method: 'PUT',
        headers: getHttpHeaders(),
        body: JSON.stringify({
          name: editUserForm.name.trim(),
          email: editUserForm.email.trim(),
          user_role: parseInt(String(editUserForm.role_id))  // Backend expects user_role, maps to role_id
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        console.log('✅ User updated successfully');
        await fetchUsers();
        setShowEditModal(false);
        setSelectedUser(null);
        alert(`User "${editUserForm.name}" has been updated successfully.`);
      } else {
        setUserFormError(data.message || data.error || 'Failed to update user');
      }
    } catch (error) {
      console.error('❌ Error updating user:', error);
      setUserFormError(`Error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setUserFormLoading(false);
    }
  };

  const handleCreateUser = (): void => {
    console.log('➕ Create new user');
    setSelectedUser(null);
    setNewUserForm({ name: '', email: '', password: '', user_role: 1 });
    setUserFormError('');
    setUserFormSuccess('');
    setShowCreateModal(true);
  };

  const handlePasswordReset = (user: any): void => {
    console.log('🔑 Reset password for user:', user);
    setSelectedUser(user);
    setResetPasswordForm({ newPassword: '', confirmPassword: '' });
    setUserFormError('');
    setUserFormSuccess('');
    setTempPasswordDisplay('');
    setShowPasswordResetModal(true);
  };

  const handleSubmitCreateUser = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setUserFormLoading(true);
    setUserFormError('');
    setUserFormSuccess('');

    try {
      // Validation
      if (!newUserForm.name || !newUserForm.email || !newUserForm.password) {
        setUserFormError('All fields are required');
        setUserFormLoading(false);
        return;
      }

      if (newUserForm.password.length < 6) {
        setUserFormError('Password must be at least 6 characters');
        setUserFormLoading(false);
        return;
      }

      const response = await fetch(`${apiUrls.admin.users}`, {
        method: 'POST',
        headers: getHttpHeaders(),
        body: JSON.stringify(newUserForm)
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to create user');
      }

      setUserFormSuccess(`User "${data.user.name}" created successfully!`);
      
      // Refresh users list
      await fetchUsers();
      
      // Close modal after 2 seconds
      setTimeout(() => {
        setShowCreateModal(false);
        setUserFormSuccess('');
      }, 2000);

    } catch (error: any) {
      console.error('Error creating user:', error);
      setUserFormError(error.message || 'Failed to create user');
    } finally {
      setUserFormLoading(false);
    }
  };

  const handleSubmitPasswordReset = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setUserFormLoading(true);
    setUserFormError('');
    setTempPasswordDisplay('');

    try {
      if (!selectedUser) {
        setUserFormError('No user selected');
        setUserFormLoading(false);
        return;
      }

      // Generate random password if not provided
      const passwordToUse = resetPasswordForm.newPassword || generateRandomPassword();

      const response = await fetch(`${apiUrls.admin.users}/${selectedUser.id}/reset-password`, {
        method: 'POST',
        headers: getHttpHeaders(),
        body: JSON.stringify({ newPassword: passwordToUse })
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to reset password');
      }

      // Show the temporary password to admin
      setTempPasswordDisplay(data.temporaryPassword);
      setUserFormSuccess('Password reset successfully! Please save the temporary password below.');

    } catch (error: any) {
      console.error('Error resetting password:', error);
      setUserFormError(error.message || 'Failed to reset password');
    } finally {
      setUserFormLoading(false);
    }
  };

  const generateRandomPassword = (): string => {
    const chars = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$%';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return password;
  };

  // Subject CRUD handlers
  const handleCreateSubject = async (): Promise<void> => {
    console.log('➕ Create new subject');
    setSubjectFormData({
      name: '',
      selectedGradeIds: []
    });

    // Load available grades for selection
    try {
      const response = await fetch(apiUrls.books.gradesAdmin, {
        headers: getHttpHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        setAvailableGrades(data.grades || []);
      } else {
        console.error('Failed to load grades for subject creation');
        setAvailableGrades([]);
      }
    } catch (error) {
      console.error('Error loading grades:', error);
      setAvailableGrades([]);
    }

    setShowCreateSubjectModal(true);
  };

  const handleEditSubject = (subject: any): void => {
    console.log('✏️ Edit subject:', subject);
    setSelectedSubject(subject);
    setSubjectFormData({
      name: subject.name || '',
      selectedGradeIds: [] // Not used in edit modal, only for create
    });
    setShowEditSubjectModal(true);
  };

  const handleDeleteSubject = (subject: any): void => {
    console.log('🗑️ Delete subject:', subject);
    setSelectedSubject(subject);
    setShowDeleteSubjectModal(true);
  };

  const handleManageSubjectGrades = async (subject: any): Promise<void> => {
    console.log('🔗 Manage grades for subject:', subject);
    setSelectedSubjectForGrades(subject);
    setSubjectGradesLoading(true);

    try {
      // Fetch current grades for this subject
      const subjectGradesResponse = await fetch(apiUrls.books.subjectGrades(subject.id), {
        headers: getHttpHeaders()
      });

      // Fetch all available grades
      const allGradesResponse = await fetch(apiUrls.books.gradesAdmin, {
        headers: getHttpHeaders()
      });

      if (subjectGradesResponse.ok && allGradesResponse.ok) {
        const subjectGradesData = await subjectGradesResponse.json();
        const allGradesData = await allGradesResponse.json();

        console.log('✅ Subject grades data:', subjectGradesData);
        console.log('✅ All grades data:', allGradesData);

        setSubjectGrades(subjectGradesData.grades || []);
        setAvailableGrades(allGradesData.grades || []);

        // Set currently selected grade IDs
        const currentGradeIds = (subjectGradesData.grades || []).map((grade: any) => grade.id);
        setSelectedGradeIds(currentGradeIds);

        setShowManageSubjectGradesModal(true);
      } else {
        throw new Error('Failed to fetch grade data');
      }
    } catch (error) {
      console.error('❌ Error fetching subject grades:', error);
      alert(`Error loading grade data: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setSubjectGradesLoading(false);
    }
  };

  const submitCreateSubject = async (): Promise<void> => {
    if (!subjectFormData.name.trim()) {
      alert('Subject name is required');
      return;
    }

    setSubjectLoading(true);
    try {
      // First, create the subject
      const response = await fetch(apiUrls.books.subjectsAdmin, {
        method: 'POST',
        headers: getHttpHeaders(),
        body: JSON.stringify({
          name: subjectFormData.name.trim()
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        console.log('✅ Subject created successfully');

        // If grades are selected, link them to the subject
        if (subjectFormData.selectedGradeIds.length > 0) {
          try {
            const linkResponse = await fetch(apiUrls.books.linkSubjectGrades(data.subject.id), {
              method: 'POST',
              headers: getHttpHeaders(),
              body: JSON.stringify({
                grade_ids: subjectFormData.selectedGradeIds
              })
            });

            const linkData = await linkResponse.json();
            if (!linkResponse.ok || !linkData.success) {
              console.warn('Subject created but failed to link grades:', linkData.error);
              alert('Subject created successfully, but failed to link some grades. You can link them later using "Manage Grades".');
            } else {
              console.log('✅ Grades linked successfully');
            }
          } catch (linkError) {
            console.warn('Subject created but failed to link grades:', linkError);
            alert('Subject created successfully, but failed to link grades. You can link them later using "Manage Grades".');
          }
        }

        setShowCreateSubjectModal(false);
        setSubjectFormData({ name: '', selectedGradeIds: [] });
        fetchSubjects(); // Refresh the list
        alert('Subject created successfully!');
      } else {
        throw new Error(data.error || 'Failed to create subject');
      }
    } catch (error) {
      console.error('❌ Error creating subject:', error);
      alert(`Error creating subject: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setSubjectLoading(false);
    }
  };

  const submitEditSubject = async (): Promise<void> => {
    if (!subjectFormData.name.trim()) {
      alert('Subject name is required');
      return;
    }

    setSubjectLoading(true);
    try {
      const response = await fetch(apiUrls.booksExtra.subjectById(selectedSubject.id), {
        method: 'PUT',
        headers: getHttpHeaders(),
        body: JSON.stringify({
          name: subjectFormData.name.trim()
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        console.log('✅ Subject updated successfully');
        setShowEditSubjectModal(false);
        setSubjectFormData({ name: '', selectedGradeIds: [] });
        setSelectedSubject(null);
        fetchSubjects(); // Refresh the list
        alert('Subject updated successfully!');
      } else {
        throw new Error(data.error || 'Failed to update subject');
      }
    } catch (error) {
      console.error('❌ Error updating subject:', error);
      alert(`Error updating subject: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setSubjectLoading(false);
    }
  };

  const submitDeleteSubject = async (): Promise<void> => {
    setSubjectLoading(true);
    try {
      const response = await fetch(apiUrls.booksExtra.subjectById(selectedSubject.id), {
        method: 'DELETE',
        headers: getHttpHeaders()
      });

      const data = await response.json();

      if (response.ok && data.success) {
        console.log('✅ Subject deleted successfully');
        setShowDeleteSubjectModal(false);
        setSelectedSubject(null);
        fetchSubjects(); // Refresh the list
        alert('Subject deleted successfully!');
      } else {
        throw new Error(data.error || 'Failed to delete subject');
      }
    } catch (error) {
      console.error('❌ Error deleting subject:', error);
      alert(`Error deleting subject: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setSubjectLoading(false);
    }
  };

  const submitSubjectGrades = async (): Promise<void> => {
    if (!selectedSubjectForGrades) return;

    setSubjectGradesLoading(true);
    try {
      const response = await fetch(apiUrls.books.linkSubjectGrades(selectedSubjectForGrades.id), {
        method: 'POST',
        headers: getHttpHeaders(),
        body: JSON.stringify({
          grade_ids: selectedGradeIds
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        console.log('✅ Subject grades updated successfully');
        setShowManageSubjectGradesModal(false);
        setSelectedSubjectForGrades(null);
        setSelectedGradeIds([]);
        fetchSubjects(); // Refresh the list to show updated grade counts
        alert('Subject grades updated successfully!');
      } else {
        throw new Error(data.error || 'Failed to update subject grades');
      }
    } catch (error) {
      console.error('❌ Error updating subject grades:', error);
      alert(`Error updating subject grades: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setSubjectGradesLoading(false);
    }
  };

  const handleGradeToggle = (gradeId: number): void => {
    setSelectedGradeIds(prev => {
      if (prev.includes(gradeId)) {
        return prev.filter(id => id !== gradeId);
      } else {
        return [...prev, gradeId];
      }
    });
  };

  const handleCreateSubjectGradeToggle = (gradeId: number): void => {
    setSubjectFormData(prev => ({
      ...prev,
      selectedGradeIds: prev.selectedGradeIds.includes(gradeId)
        ? prev.selectedGradeIds.filter(id => id !== gradeId)
        : [...prev.selectedGradeIds, gradeId]
    }));
  };

  // Grade CRUD handlers
  const handleCreateGrade = (): void => {
    console.log('➕ Create new grade');
    setGradeFormData({
      name: ''
    });
    setShowCreateGradeModal(true);
  };

  const handleEditGrade = (grade: any): void => {
    console.log('✏️ Edit grade:', grade);
    setSelectedGrade(grade);
    setGradeFormData({
      name: grade.name || ''
    });
    setShowEditGradeModal(true);
  };

  const handleDeleteGrade = (grade: any): void => {
    console.log('🗑️ Delete grade:', grade);
    setSelectedGrade(grade);
    setShowDeleteGradeModal(true);
  };

  const submitCreateGrade = async (): Promise<void> => {
    if (!gradeFormData.name.trim()) {
      alert('Grade name is required');
      return;
    }

    setGradeLoading(true);
    try {
      console.log('🔍 Creating grade with grades service URL:', apiUrls.grades.create);
      console.log('🔍 Grade name:', gradeFormData.name.trim());

      const response = await fetch(apiUrls.grades.create, {
        method: 'POST',
        headers: getHttpHeaders(),
        body: JSON.stringify({
          name: gradeFormData.name.trim()
        })
      });

      console.log('🔍 Response status:', response.status);

      const data = await response.json();

      if (response.ok && data.success) {
        console.log('✅ Grade created successfully');
        setShowCreateGradeModal(false);
        setGradeFormData({ name: '' });
        fetchGrades(); // Refresh the list
        alert('Grade created successfully!');
      } else {
        throw new Error(data.error || 'Failed to create grade');
      }
    } catch (error) {
      console.error('❌ Error creating grade:', error);
      alert(`Error creating grade: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setGradeLoading(false);
    }
  };

  const submitEditGrade = async (): Promise<void> => {
    if (!gradeFormData.name.trim()) {
      alert('Grade name is required');
      return;
    }

    setGradeLoading(true);
    try {
      console.log('🔍 Updating grade with URL:', apiUrls.grades.update(selectedGrade.id));
      const response = await fetch(apiUrls.grades.update(selectedGrade.id), {
        method: 'PUT',
        headers: getHttpHeaders(),
        body: JSON.stringify({
          name: gradeFormData.name.trim()
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        console.log('✅ Grade updated successfully');
        setShowEditGradeModal(false);
        setGradeFormData({ name: '' });
        setSelectedGrade(null);
        fetchGrades(); // Refresh the list
        alert('Grade updated successfully!');
      } else {
        throw new Error(data.error || 'Failed to update grade');
      }
    } catch (error) {
      console.error('❌ Error updating grade:', error);
      alert(`Error updating grade: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setGradeLoading(false);
    }
  };

  const submitDeleteGrade = async (): Promise<void> => {
    setGradeLoading(true);
    try {
      console.log('🔍 Deleting grade with URL:', apiUrls.grades.delete(selectedGrade.id));
      const response = await fetch(apiUrls.grades.delete(selectedGrade.id), {
        method: 'DELETE',
        headers: getHttpHeaders()
      });

      const data = await response.json();

      if (response.ok && data.success) {
        console.log('✅ Grade deleted successfully');
        setShowDeleteGradeModal(false);
        setSelectedGrade(null);
        fetchGrades(); // Refresh the list
        alert('Grade deleted successfully!');
      } else {
        throw new Error(data.error || 'Failed to delete grade');
      }
    } catch (error) {
      console.error('❌ Error deleting grade:', error);
      alert(`Error deleting grade: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setGradeLoading(false);
    }
  };


  // ============================================================================
  // LOCATION (COUNTRIES & STATES) CRUD HANDLERS
  // ============================================================================

  // Country CRUD handlers
  const handleCreateCountry = (): void => {
    console.log('➕ Create new country');
    setCountryFormData({
      name: '',
      active: true
    });
    setShowCreateCountryModal(true);
  };

  const handleEditCountry = (country: any): void => {
    console.log('✏️ Edit country:', country);
    setSelectedCountry(country);
    setCountryFormData({
      name: country.name || '',
      active: country.active !== undefined ? country.active : true
    });
    setShowEditCountryModal(true);
  };

  const handleDeleteCountry = (country: any): void => {
    console.log('🗑️ Delete country:', country);
    setSelectedCountry(country);
    setShowDeleteCountryModal(true);
  };

  const submitCreateCountry = async (): Promise<void> => {
    if (!countryFormData.name.trim()) {
      alert('Country name is required');
      return;
    }

    setCountryLoading(true);
    try {
      const response = await fetch(apiUrls.countries.create, {
        method: 'POST',
        headers: getHttpHeaders(),
        body: JSON.stringify({
          name: countryFormData.name.trim()
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        console.log('✅ Country created successfully');
        setShowCreateCountryModal(false);
        setCountryFormData({ name: '', active: true });
        fetchLocations(); // Refresh the list
        alert('Country created successfully!');
      } else {
        throw new Error(data.error || 'Failed to create country');
      }
    } catch (error) {
      console.error('❌ Error creating country:', error);
      alert(`Error creating country: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setCountryLoading(false);
    }
  };

  const submitEditCountry = async (): Promise<void> => {
    if (!countryFormData.name.trim()) {
      alert('Country name is required');
      return;
    }

    setCountryLoading(true);
    try {
      const response = await fetch(apiUrls.countries.update(selectedCountry.id), {
        method: 'PUT',
        headers: getHttpHeaders(),
        body: JSON.stringify({
          name: countryFormData.name.trim(),
          active: countryFormData.active
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        console.log('✅ Country updated successfully');
        setShowEditCountryModal(false);
        setCountryFormData({ name: '', active: true });
        setSelectedCountry(null);
        fetchLocations(); // Refresh the list
        alert('Country updated successfully!');
      } else {
        throw new Error(data.error || 'Failed to update country');
      }
    } catch (error) {
      console.error('❌ Error updating country:', error);
      alert(`Error updating country: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setCountryLoading(false);
    }
  };

  const submitDeleteCountry = async (): Promise<void> => {
    setCountryLoading(true);
    try {
      const response = await fetch(apiUrls.countries.delete(selectedCountry.id), {
        method: 'DELETE',
        headers: getHttpHeaders()
      });

      const data = await response.json();

      if (response.ok && data.success) {
        console.log('✅ Country deleted successfully');
        setShowDeleteCountryModal(false);
        setSelectedCountry(null);
        fetchLocations(); // Refresh the list
        alert('Country deleted successfully!');
      } else {
        throw new Error(data.error || 'Failed to delete country');
      }
    } catch (error) {
      console.error('❌ Error deleting country:', error);
      alert(`Error deleting country: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setCountryLoading(false);
    }
  };

  // State CRUD handlers
  const handleCreateState = (): void => {
    console.log('➕ Create new state');
    setStateFormData({
      name: '',
      country_id: '',
      active: true
    });
    setShowCreateStateModal(true);
  };

  const handleEditState = (state: any): void => {
    console.log('✏️ Edit state:', state);
    setSelectedState(state);
    setStateFormData({
      name: state.name || '',
      country_id: state.country_id?.toString() || '',
      active: state.active !== undefined ? state.active : true
    });
    setShowEditStateModal(true);
  };

  const handleDeleteState = (state: any): void => {
    console.log('🗑️ Delete state:', state);
    setSelectedState(state);
    setShowDeleteStateModal(true);
  };

  const submitCreateState = async (): Promise<void> => {
    if (!stateFormData.name.trim()) {
      alert('State name is required');
      return;
    }
    if (!stateFormData.country_id) {
      alert('Country selection is required');
      return;
    }

    setStateLoading(true);
    try {
      const response = await fetch(apiUrls.countries.createState, {
        method: 'POST',
        headers: getHttpHeaders(),
        body: JSON.stringify({
          name: stateFormData.name.trim(),
          country_id: parseInt(stateFormData.country_id)
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        console.log('✅ State created successfully');
        setShowCreateStateModal(false);
        setStateFormData({ name: '', country_id: '', active: true });
        fetchLocations(); // Refresh the list
        alert('State created successfully!');
      } else {
        // Show detailed error message including server-side details
        const errorDetails = data.details ? ` Details: ${data.details.join(', ')}` : '';
        throw new Error(data.error || data.message || `Failed to create state${errorDetails}`);
      }
    } catch (error) {
      console.error('❌ Error creating state:', error);
      alert(`Error creating state: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setStateLoading(false);
    }
  };

  const submitEditState = async (): Promise<void> => {
    if (!stateFormData.name.trim()) {
      alert('State name is required');
      return;
    }
    if (!stateFormData.country_id) {
      alert('Country selection is required');
      return;
    }

    setStateLoading(true);
    try {
      const response = await fetch(apiUrls.countries.updateState(selectedState.id), {
        method: 'PUT',
        headers: getHttpHeaders(),
        body: JSON.stringify({
          name: stateFormData.name.trim(),
          country_id: parseInt(stateFormData.country_id),
          active: stateFormData.active
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        console.log('✅ State updated successfully');
        setShowEditStateModal(false);
        setStateFormData({ name: '', country_id: '', active: true });
        setSelectedState(null);
        fetchLocations(); // Refresh the list
        alert('State updated successfully!');
      } else {
        throw new Error(data.error || 'Failed to update state');
      }
    } catch (error) {
      console.error('❌ Error updating state:', error);
      alert(`Error updating state: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setStateLoading(false);
    }
  };

  const submitDeleteState = async (): Promise<void> => {
    setStateLoading(true);
    try {
      const response = await fetch(apiUrls.countries.deleteState(selectedState.id), {
        method: 'DELETE',
        headers: getHttpHeaders()
      });

      const data = await response.json();

      if (response.ok && data.success) {
        console.log('✅ State deleted successfully');
        setShowDeleteStateModal(false);
        setSelectedState(null);
        fetchLocations(); // Refresh the list
        alert('State deleted successfully!');
      } else {
        throw new Error(data.error || 'Failed to delete state');
      }
    } catch (error) {
      console.error('❌ Error deleting state:', error);
      alert(`Error deleting state: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setStateLoading(false);
    }
  };

  // Description editor handlers
  const openDescriptionEditor = (): void => {
    setTempDescription(bookFormData.description);
    setShowDescriptionEditor(true);
  };

  const saveDescription = (): void => {
    setBookFormData(prev => ({ ...prev, description: tempDescription }));
    setShowDescriptionEditor(false);
    setTempDescription('');
  };

  const cancelDescriptionEditor = (): void => {
    setShowDescriptionEditor(false);
    setTempDescription('');
  };

  // Blog content editor handlers
  const openBlogContentEditor = (): void => {
    setTempBlogContent(blogFormData.description);
    setShowBlogContentEditor(true);
  };

  const saveBlogContent = (): void => {
    setBlogFormData(prev => ({ ...prev, description: tempBlogContent }));
    setShowBlogContentEditor(false);
    setTempBlogContent('');
  };

  const cancelBlogContentEditor = (): void => {
    setShowBlogContentEditor(false);
    setTempBlogContent('');
  };

  // ============================================================================
  // BLOG CRUD HANDLERS
  // ============================================================================

  const handleCreateBlog = (): void => {
    console.log('➕ Create new blog');
    setBlogFormData({
      title: '',
      description: '',
      author: 'math-knots',
      status: 'Active',
      seo: '',
      image: '',
      content_images: []
    });
    setSelectedBlogImageFile(null);
    setActiveSection('create-blog');
  };

  const handleEditBlog = (blog: any): void => {
    console.log('✏️ Edit blog:', blog);
    setSelectedBlog(blog);
    setBlogFormData({
      title: blog.title || '',
      description: blog.description || '',
      author: blog.author || '',
      status: blog.status || 'Active',
      seo: blog.seo || '',
      image: blog.image || '',
      content_images: blog.content_images || []
    });
    setSelectedBlogImageFile(null);
    setActiveSection('edit-blog');
  };

  const handleDeleteBlog = (blog: any): void => {
    console.log('🗑️ Delete blog:', blog);
    setSelectedBlog(blog);
    setShowDeleteBlogModal(true);
  };

  const submitCreateBlog = async (): Promise<void> => {
    if (!blogFormData.title.trim()) {
      alert('Blog title is required');
      return;
    }

    setBlogLoading(true);
    try {
      let finalImageName = blogFormData.image.trim();

      // If user selected a file, upload it first
      if (selectedBlogImageFile) {
        console.log('📤 Uploading selected blog image file first...');
        const uploadedImageName = await handleBlogImageUpload();

        if (!uploadedImageName) {
          alert('❌ Failed to upload image. Please try again.');
          return;
        }

        finalImageName = uploadedImageName;
        console.log('✅ Blog image uploaded, using filename:', finalImageName);
      }

      // Extract content images from description
      const contentImages: Array<{ filename: string; alt_text: string }> = [];
      const imageMatches = blogFormData.description.match(/\[IMAGE:([^\]]+)\]/g);
      if (imageMatches) {
        imageMatches.forEach(match => {
          const filename = match.replace(/\[IMAGE:([^\]]+)\]/, '$1');
          contentImages.push({
            filename: filename,
            alt_text: `Blog content image`
          });
        });
      }

      const response = await fetch(apiUrls.blogs.admin, {
        method: 'POST',
        headers: getHttpHeaders(),
        body: JSON.stringify({
          title: blogFormData.title.trim(),
          description: blogFormData.description.trim(),
          author: blogFormData.author.trim() || 'Admin',
          status: blogFormData.status,
          seo: blogFormData.seo.trim() || null,
          image: finalImageName || null,
          content_images: contentImages
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        console.log('✅ Blog created successfully');
        setShowCreateBlogModal(false);
        setBlogFormData({ title: '', description: '', author: '', status: 'Active', seo: '', image: '', content_images: [] });
        setSelectedBlogImageFile(null); // Clear selected image file
        fetchBlogs(); // Refresh the list
        setActiveSection('blogs'); // Navigate back to blog management
        alert('Blog created successfully!');
      } else {
        throw new Error(data.error || 'Failed to create blog');
      }
    } catch (error) {
      console.error('❌ Error creating blog:', error);
      alert(`Error creating blog: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setBlogLoading(false);
    }
  };

  const submitEditBlog = async (): Promise<void> => {
    if (!blogFormData.title.trim()) {
      alert('Blog title is required');
      return;
    }

    setBlogLoading(true);
    try {
      let finalImageName = blogFormData.image.trim();

      // If user selected a new file, upload it first
      if (selectedBlogImageFile) {
        console.log('📤 Uploading selected blog image file first...');
        const uploadedImageName = await handleBlogImageUpload();

        if (!uploadedImageName) {
          alert('❌ Failed to upload image. Please try again.');
          setBlogLoading(false);
          return;
        }

        finalImageName = uploadedImageName;
        console.log('✅ Blog image uploaded, using filename:', finalImageName);
      }

      // Extract content images from description
      const contentImages: Array<{ filename: string; alt_text: string }> = [];
      const imageMatches = blogFormData.description.match(/\[IMAGE:([^\]]+)\]/g);
      if (imageMatches) {
        imageMatches.forEach(match => {
          const filename = match.replace(/\[IMAGE:([^\]]+)\]/, '$1');
          contentImages.push({
            filename: filename,
            alt_text: `Blog content image`
          });
        });
      }

      const response = await fetch(`${apiUrls.blogs.admin}/${selectedBlog.id}`, {
        method: 'PUT',
        headers: getHttpHeaders(),
        body: JSON.stringify({
          title: blogFormData.title.trim(),
          description: blogFormData.description.trim(),
          author: blogFormData.author.trim() || 'Admin',
          status: blogFormData.status,
          seo: blogFormData.seo.trim() || null,
          image: finalImageName || null,
          content_images: contentImages
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        console.log('✅ Blog updated successfully');
        setShowEditBlogModal(false);
        setBlogFormData({ title: '', description: '', author: '', status: 'Active', seo: '', image: '', content_images: [] });
        setSelectedBlogImageFile(null); // Clear selected image file
        setSelectedBlog(null);
        fetchBlogs(); // Refresh the list
        setActiveSection('blogs'); // Navigate back to blog management
        alert('Blog updated successfully!');
      } else {
        throw new Error(data.error || 'Failed to update blog');
      }
    } catch (error) {
      console.error('❌ Error updating blog:', error);
      alert(`Error updating blog: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setBlogLoading(false);
    }
  };

  const submitDeleteBlog = async (): Promise<void> => {
    setBlogLoading(true);
    try {
      const response = await fetch(`${apiUrls.blogs.admin}/${selectedBlog.id}`, {
        method: 'DELETE',
        headers: getHttpHeaders()
      });

      const data = await response.json();

      if (response.ok && data.success) {
        console.log('✅ Blog deleted successfully');
        setShowDeleteBlogModal(false);
        setSelectedBlog(null);
        fetchBlogs(); // Refresh the list
        setActiveSection('blogs'); // Navigate back to blog management
        alert('Blog deleted successfully!');
      } else {
        throw new Error(data.error || 'Failed to delete blog');
      }
    } catch (error) {
      console.error('❌ Error deleting blog:', error);
      alert(`Error deleting blog: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setBlogLoading(false);
    }
  };

  // ============================================================================
  // FAQ CRUD HANDLERS
  // ============================================================================

  const handleCreateFaq = (): void => {
    console.log('➕ Create new FAQ');
    setFaqFormData({
      question: '',
      answer_html: '',
      category: 'General',
      tags: [],
      status: 'active',
      display_order: 0,
      locale: 'en'
    });
    setSelectedFaq(null);
    setShowCreateFaqModal(true);
  };

  const handleEditFaq = (faq: any): void => {
    console.log('✏️ Edit FAQ:', faq);
    setSelectedFaq(faq);
    setFaqFormData({
      question: faq.question || '',
      answer_html: faq.answer_html || '',
      category: faq.category || 'General',
      tags: faq.tags || [],
      status: faq.status || 'draft',
      display_order: faq.display_order || 0,
      locale: faq.locale || 'en'
    });
    setShowEditFaqModal(true);
  };

  const handleDeleteFaq = (faq: any): void => {
    console.log('🗑️ Delete FAQ:', faq);
    setSelectedFaq(faq);
    setShowDeleteFaqModal(true);
  };

  const submitCreateFaq = async (): Promise<void> => {
    if (!faqFormData.question.trim()) {
      alert('FAQ question is required');
      return;
    }
    if (!faqFormData.answer_html.trim()) {
      alert('FAQ answer is required');
      return;
    }

    setFaqLoading(true);
    try {
      console.log('🔄 Creating FAQ...');
      const response = await fetch(apiUrls.faqs.create, {
        method: 'POST',
        headers: getHttpHeaders(),
        body: JSON.stringify({
          question: faqFormData.question.trim(),
          answer_html: faqFormData.answer_html.trim(),
          category: faqFormData.category.trim() || 'General',
          status: faqFormData.status
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        console.log('✅ FAQ created successfully');
        setShowCreateFaqModal(false);
        setFaqFormData({
          question: '',
          answer_html: '',
          category: 'General',
          tags: [],
          status: 'active',
          display_order: 0,
          locale: 'en'
        });
        fetchFaqs(); // Refresh the list
        alert('FAQ created successfully!');
      } else {
        throw new Error(data.error || 'Failed to create FAQ');
      }
    } catch (error) {
      console.error('❌ Error creating FAQ:', error);
      alert(`Error creating FAQ: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setFaqLoading(false);
    }
  };

  const submitEditFaq = async (): Promise<void> => {
    if (!faqFormData.question.trim()) {
      alert('FAQ question is required');
      return;
    }
    if (!faqFormData.answer_html.trim()) {
      alert('FAQ answer is required');
      return;
    }

    setFaqLoading(true);
    try {
      console.log('🔄 Updating FAQ...');
      const response = await fetch(apiUrls.faqs.update(selectedFaq.id), {
        method: 'PUT',
        headers: getHttpHeaders(),
        body: JSON.stringify({
          question: faqFormData.question.trim(),
          answer_html: faqFormData.answer_html.trim(),
          category: faqFormData.category.trim() || 'General',
          status: faqFormData.status
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        console.log('✅ FAQ updated successfully');
        setShowEditFaqModal(false);
        setFaqFormData({
          question: '',
          answer_html: '',
          category: 'General',
          tags: [],
          status: 'active',
          display_order: 0,
          locale: 'en'
        });
        setSelectedFaq(null);
        fetchFaqs(); // Refresh the list
        alert('FAQ updated successfully!');
      } else {
        throw new Error(data.error || 'Failed to update FAQ');
      }
    } catch (error) {
      console.error('❌ Error updating FAQ:', error);
      alert(`Error updating FAQ: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setFaqLoading(false);
    }
  };

  const submitDeleteFaq = async (): Promise<void> => {
    setFaqLoading(true);
    try {
      const response = await fetch(apiUrls.faqs.delete(selectedFaq.id), {
        method: 'DELETE',
        headers: getHttpHeaders()
      });

      const data = await response.json();

      if (response.ok && data.success) {
        console.log('✅ FAQ deleted successfully');
        setShowDeleteFaqModal(false);
        setSelectedFaq(null);
        fetchFaqs(); // Refresh the list
        alert('FAQ deleted successfully!');
      } else {
        throw new Error(data.error || 'Failed to delete FAQ');
      }
    } catch (error) {
      console.error('❌ Error deleting FAQ:', error);
      alert(`Error deleting FAQ: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setFaqLoading(false);
    }
  };

  // ============================================================================
  // FEEDBACK MANAGEMENT HANDLERS
  // ============================================================================

  const handleViewFeedback = (feedbackItem: any): void => {
    console.log('👁️ View feedback:', feedbackItem);
    setSelectedFeedback(feedbackItem);
    setShowViewFeedbackModal(true);
  };

  const handleDeleteFeedback = (feedbackItem: any): void => {
    console.log('🗑️ Delete feedback:', feedbackItem);
    setSelectedFeedback(feedbackItem);
    setShowDeleteFeedbackModal(true);
  };

  const submitDeleteFeedback = async (): Promise<void> => {
    setFeedbackLoading(true);
    try {
      const response = await fetch(apiUrls.feedback.delete(selectedFeedback.id), {
        method: 'DELETE',
        headers: getHttpHeaders()
      });

      const data = await response.json();

      if (response.ok && data.success) {
        console.log('✅ Feedback deleted successfully');
        setShowDeleteFeedbackModal(false);
        setSelectedFeedback(null);
        fetchFeedback(); // Refresh the list
        alert('Feedback deleted successfully!');
      } else {
        throw new Error(data.error || 'Failed to delete feedback');
      }
    } catch (error) {
      console.error('❌ Error deleting feedback:', error);
      alert(`Error deleting feedback: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setFeedbackLoading(false);
    }
  };

  const updateFeedbackStatus = async (status: string): Promise<void> => {
    if (!selectedFeedback) return;

    setFeedbackLoading(true);
    try {
      const response = await fetch(apiUrls.feedback.updateStatus(selectedFeedback.id), {
        method: 'PUT',
        headers: getHttpHeaders(),
        body: JSON.stringify({ status })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        console.log('✅ Feedback status updated successfully');
        fetchFeedback(); // Refresh the list
        alert(`Feedback marked as ${status}!`);
      } else {
        throw new Error(data.error || 'Failed to update feedback status');
      }
    } catch (error) {
      console.error('❌ Error updating feedback status:', error);
      alert(`Error updating feedback status: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setFeedbackLoading(false);
    }
  };

  // ============================================================================
  // SUCCESS STORIES HANDLERS
  // ============================================================================

  const fetchSuccessStories = async (): Promise<void> => {
    try {
      console.log('📖 Fetching success stories...');
      const response = await fetch(apiUrls.successStories.admin, {
        headers: getHttpHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('📖 Success stories fetched:', data);
        setSuccessStories(data.stories || []);
      } else {
        console.error('Failed to fetch success stories');
        setSuccessStories([]);
      }
    } catch (error) {
      console.error('Error fetching success stories:', error);
      setSuccessStories([]);
    }
  };

  const handleCreateSuccessStory = (): void => {
    setSuccessStoryFormData({
      name: '',
      grade: '',
      avatar: '',
      content: '',
      improvement: '',
      time_spent: '',
      display_order: successStories.length + 1,
      is_active: true
    });
    setShowCreateSuccessStoryModal(true);
  };

  const handleEditSuccessStory = (story: any): void => {
    setSelectedSuccessStory(story);
    setSuccessStoryFormData({
      name: story.name || '',
      grade: story.grade || '',
      avatar: story.avatar || '',
      content: story.content || '',
      improvement: story.improvement || '',
      time_spent: story.time_spent || '',
      display_order: story.display_order || 0,
      is_active: story.is_active !== undefined ? story.is_active : true
    });
    setShowEditSuccessStoryModal(true);
  };

  const handleDeleteSuccessStory = (story: any): void => {
    setSelectedSuccessStory(story);
    setShowDeleteSuccessStoryModal(true);
  };

  const submitCreateSuccessStory = async (): Promise<void> => {
    if (!successStoryFormData.name.trim()) {
      alert('Name is required');
      return;
    }
    if (!successStoryFormData.grade.trim()) {
      alert('Grade is required');
      return;
    }
    if (!successStoryFormData.content.trim()) {
      alert('Testimonial content is required');
      return;
    }

    setSuccessStoryLoading(true);
    try {
      const response = await fetch(apiUrls.successStories.create, {
        method: 'POST',
        headers: getHttpHeaders(),
        body: JSON.stringify(successStoryFormData)
      });

      const data = await response.json();

      if (response.ok && data.success) {
        console.log('✅ Success story created');
        setShowCreateSuccessStoryModal(false);
        fetchSuccessStories();
        alert('Success story created successfully!');
      } else {
        throw new Error(data.error || 'Failed to create success story');
      }
    } catch (error) {
      console.error('❌ Error creating success story:', error);
      alert(`Error creating success story: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setSuccessStoryLoading(false);
    }
  };

  const submitEditSuccessStory = async (): Promise<void> => {
    if (!successStoryFormData.name.trim()) {
      alert('Name is required');
      return;
    }
    if (!successStoryFormData.grade.trim()) {
      alert('Grade is required');
      return;
    }
    if (!successStoryFormData.content.trim()) {
      alert('Testimonial content is required');
      return;
    }

    setSuccessStoryLoading(true);
    try {
      const response = await fetch(apiUrls.successStories.update(selectedSuccessStory.id), {
        method: 'PUT',
        headers: getHttpHeaders(),
        body: JSON.stringify(successStoryFormData)
      });

      const data = await response.json();

      if (response.ok && data.success) {
        console.log('✅ Success story updated');
        setShowEditSuccessStoryModal(false);
        setSelectedSuccessStory(null);
        fetchSuccessStories();
        alert('Success story updated successfully!');
      } else {
        throw new Error(data.error || 'Failed to update success story');
      }
    } catch (error) {
      console.error('❌ Error updating success story:', error);
      alert(`Error updating success story: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setSuccessStoryLoading(false);
    }
  };

  const submitDeleteSuccessStory = async (): Promise<void> => {
    setSuccessStoryLoading(true);
    try {
      const response = await fetch(apiUrls.successStories.delete(selectedSuccessStory.id), {
        method: 'DELETE',
        headers: getHttpHeaders()
      });

      const data = await response.json();

      if (response.ok && data.success) {
        console.log('✅ Success story deleted');
        setShowDeleteSuccessStoryModal(false);
        setSelectedSuccessStory(null);
        fetchSuccessStories();
        alert('Success story deleted successfully!');
      } else {
        throw new Error(data.error || 'Failed to delete success story');
      }
    } catch (error) {
      console.error('❌ Error deleting success story:', error);
      alert(`Error deleting success story: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setSuccessStoryLoading(false);
    }
  };

  const toggleSuccessStoryStatus = async (story: any): Promise<void> => {
    try {
      const response = await fetch(apiUrls.successStories.toggle(story.id), {
        method: 'PATCH',
        headers: getHttpHeaders()
      });

      const data = await response.json();

      if (response.ok && data.success) {
        fetchSuccessStories();
      } else {
        throw new Error(data.error || 'Failed to toggle status');
      }
    } catch (error) {
      console.error('❌ Error toggling success story status:', error);
      alert(`Error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const fetchCountries = async (): Promise<void> => {
    try {
      console.log('🔍 Fetching active countries for book form...');
      const response = await fetch(`${apiUrls.countries.list}?active=true`, {
        headers: getHttpHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Countries data:', data);

        if (data.success && Array.isArray(data.countries)) {
          setCountries(data.countries);
        } else if (Array.isArray(data)) {
          setCountries(data);
        } else {
          console.warn('Unexpected countries data format:', data);
          setCountries([]);
        }
      } else {
        console.error('Failed to fetch countries');
        setCountries([]);
      }
    } catch (error) {
      console.error('Error fetching countries:', error);
      setCountries([]);
    }
  };

  const fetchStates = async (countryId?: string): Promise<void> => {
    try {
      if (!countryId) {
        console.log('🔍 No country selected, clearing states...');
        setStates([]);
        return;
      }

      console.log('🔍 Fetching states for country:', countryId);
      const response = await fetch(`${apiUrls.countries.list}/${countryId}/states`, {
        headers: getHttpHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        console.log('✅ States data for country', countryId, ':', data);

        if (data.success && Array.isArray(data.states)) {
          setStates(data.states);
        } else if (Array.isArray(data)) {
          setStates(data);
        } else {
          console.warn('Unexpected states data format:', data);
          setStates([]);
        }
      } else {
        console.error('Failed to fetch states for country:', countryId);
        setStates([]);
      }
    } catch (error) {
      console.error('Error fetching states:', error);
      setStates([]);
    }
  };

  // Book CRUD handlers
  const handleCreateBook = (): void => {
    console.log('➕ Create new book');
    setBookFormData({
      title: '',
      image: '',
      description: '',
      price: '',
      discount_price: '',
      referal_link: '',
      video_link: '',
      file: '',
      subject_id: '',
      shared_content_group_id: '',
      sub_subject_id: '',
      country: '',
      state_id: '',
      state_ids: '',
      grade_id: '',
      short_description: '',
      seo: '',
      is_best_seller: false,
      vendor_id: '',
      vendors: [], // Add missing vendors array
      other_link: ''
    });
    setActiveSection('create-book');
  };

  const handleEditBook = (book: any): void => {
    console.log('✏️ Edit book:', book);
    
    const tabId = `edit-${book.id}`;
    
    // Check if tab already exists
    const existingTab = bookTabs.find(tab => tab.id === tabId);
    if (existingTab) {
      // Tab already open, just switch to it
      setActiveBookTab(tabId);
      setSelectedBook(book);
      return;
    }
    
    // Set form data for this book
    setSelectedBook(book);
    
    // Debug: Log book data to see what state_ids contains
    console.log('📖 Edit Book - Full book data:', book);
    console.log('📖 Edit Book - state_id:', book.state_id);
    console.log('📖 Edit Book - state_ids:', book.state_ids);
    console.log('📖 Edit Book - state_ids type:', typeof book.state_ids);
    
    // Initialize state_ids from existing data
    // If state_ids exists and is valid, use it; otherwise, create from state_id
    let initialStateIds = '';
    try {
      const existingStateIds = JSON.parse(book.state_ids || '[]');
      console.log('📖 Edit Book - Parsed state_ids:', existingStateIds);
      if (Array.isArray(existingStateIds) && existingStateIds.length > 0) {
        initialStateIds = book.state_ids;
        console.log('📖 Edit Book - Using existing state_ids:', initialStateIds);
      } else if (book.state_id) {
        // Convert single state_id to state_ids array
        initialStateIds = JSON.stringify([String(book.state_id)]);
        console.log('📖 Edit Book - Converted state_id to state_ids:', initialStateIds);
      }
    } catch (e) {
      // If parsing fails and we have a state_id, use it
      console.log('📖 Edit Book - Parse error:', e);
      if (book.state_id) {
        initialStateIds = JSON.stringify([String(book.state_id)]);
      }
    }
    console.log('📖 Edit Book - Final initialStateIds:', initialStateIds);
    
    setBookFormData({
      title: book.title || '',
      image: book.image || '',
      description: book.description || '',
      price: book.price || '',
      discount_price: book.discount_price || '',
      referal_link: book.referal_link || '',
      video_link: book.video_link || '',
      file: book.file || '',
      subject_id: book.subject_id?.toString() || '',
      shared_content_group_id: book.shared_content_group_id || '',
      sub_subject_id: book.sub_subject_id || '',
      country: book.country || '',
      state_id: book.state_id || '',
      state_ids: initialStateIds,
      grade_id: book.grade_id || '',
      short_description: book.short_description || '',
      seo: book.seo || '',
      is_best_seller: book.is_best_seller || false,
      vendor_id: book.vendor_id || '',
      vendors: parseVendorsFromBook(book), // Parse vendors from book data
      other_link: book.other_link || ''
    });

    // Fetch states for the book's country to populate the states dropdown
    if (book.country) {
      console.log('🏛️ Fetching states for book country:', book.country);
      fetchStates(book.country.toString());
    }

    // Add new tab and switch to it
    const newTab: BookTab = {
      id: tabId,
      type: 'edit',
      title: `✏️ ${book.title?.substring(0, 20)}${book.title?.length > 20 ? '...' : ''}`,
      book: book
    };
    setBookTabs(prev => [...prev, newTab]);
    setActiveBookTab(tabId);
  };
  
  // Close a book tab
  const closeBookTab = (tabId: string, e: React.MouseEvent): void => {
    e.stopPropagation();
    if (tabId === 'list') return; // Can't close the main list tab
    
    setBookTabs(prev => prev.filter(tab => tab.id !== tabId));
    
    // If closing the active tab, switch to the list tab
    if (activeBookTab === tabId) {
      setActiveBookTab('list');
      setSelectedBook(null);
      setSelectedBookImageFile(null);
    }
  };
  
  // Switch to a book tab
  const switchToBookTab = (tabId: string): void => {
    setActiveBookTab(tabId);
    
    // If switching to an edit tab, load that book's data
    const tab = bookTabs.find(t => t.id === tabId);
    if (tab?.type === 'edit' && tab.book) {
      setSelectedBook(tab.book);
      
      // Initialize state_ids from existing data
      let initialStateIds = '';
      try {
        const existingStateIds = JSON.parse(tab.book.state_ids || '[]');
        if (Array.isArray(existingStateIds) && existingStateIds.length > 0) {
          initialStateIds = tab.book.state_ids;
        } else if (tab.book.state_id) {
          initialStateIds = JSON.stringify([String(tab.book.state_id)]);
        }
      } catch {
        if (tab.book.state_id) {
          initialStateIds = JSON.stringify([String(tab.book.state_id)]);
        }
      }
      
      setBookFormData({
        title: tab.book.title || '',
        image: tab.book.image || '',
        description: tab.book.description || '',
        price: tab.book.price || '',
        discount_price: tab.book.discount_price || '',
        referal_link: tab.book.referal_link || '',
        video_link: tab.book.video_link || '',
        file: tab.book.file || '',
        subject_id: tab.book.subject_id?.toString() || '',
        shared_content_group_id: tab.book.shared_content_group_id || '',
        sub_subject_id: tab.book.sub_subject_id || '',
        country: tab.book.country || '',
        state_id: tab.book.state_id || '',
        state_ids: initialStateIds,
        grade_id: tab.book.grade_id || '',
        short_description: tab.book.short_description || '',
        seo: tab.book.seo || '',
        is_best_seller: tab.book.is_best_seller || false,
        vendor_id: tab.book.vendor_id || '',
        vendors: parseVendorsFromBook(tab.book),
        other_link: tab.book.other_link || ''
      });
      if (tab.book.country) {
        fetchStates(tab.book.country.toString());
      }
    }
  };

  const handleDeleteBook = (book: any): void => {
    console.log('🗑️ Delete book:', book);
    setSelectedBook(book);
    setShowDeleteBookModal(true);
  };

  const submitCreateBook = async (): Promise<void> => {
    // Enhanced validation for required fields
    const requiredFields = {
      title: bookFormData.title.trim(),
      description: bookFormData.description.trim(),
      price: bookFormData.price.trim(),
      image: bookFormData.image.trim(),
      video_link: bookFormData.video_link.trim(),
      subject_id: bookFormData.subject_id,
      grade_id: bookFormData.grade_id,
      country: bookFormData.country
    };

    const missingFields = Object.entries(requiredFields)
      .filter(([key, value]) => !value)
      .map(([key]) => key.replace('_', ' '));

    // Check if at least one vendor is selected (either vendors array or vendor_id)
    if (bookFormData.vendors.length === 0 && !bookFormData.vendor_id) {
      missingFields.push('vendor (select at least one)');
    }

    if (missingFields.length > 0) {
      alert(`Required fields missing: ${missingFields.join(', ')}`);
      return;
    }

    setBookLoading(true);
    try {
      let finalImageName = bookFormData.image.trim();

      // If user selected a file, upload it first
      if (selectedBookImageFile) {
        console.log('📤 Uploading selected image file first...');
        const uploadedImageName = await handleBookImageUpload();

        if (!uploadedImageName) {
          alert('❌ Failed to upload image. Please try again.');
          return;
        }

        finalImageName = uploadedImageName;
        console.log('✅ Image uploaded, using filename:', finalImageName);
      }

      console.log('🔍 Creating book with URL:', apiUrls.books.admin);
      const response = await fetch(apiUrls.books.admin, {
        method: 'POST',
        headers: getHttpHeaders(),
        body: JSON.stringify({
          title: bookFormData.title.trim(),
          image: finalImageName,
          description: bookFormData.description.trim(),
          price: bookFormData.price.trim(),
          discount_price: bookFormData.discount_price.trim() || null,
          referal_link: bookFormData.referal_link.trim() || null,
          video_link: bookFormData.video_link.trim(),
          file: bookFormData.file.trim() || null,
          subject_id: parseInt(bookFormData.subject_id),
          shared_content_group_id: parseInt(bookFormData.shared_content_group_id) || null,
          sub_subject_id: parseInt(bookFormData.sub_subject_id) || null,
          country: parseInt(bookFormData.country),
          state_id: parseInt(bookFormData.state_id) || null,
          state_ids: bookFormData.state_ids.trim() || "[]",
          grade_id: parseInt(bookFormData.grade_id),
          short_description: bookFormData.short_description.trim() || null,
          seo: bookFormData.seo.trim() || null,
          is_best_seller: bookFormData.is_best_seller ? 1 : 0,
          vendors: bookFormData.vendors.length > 0 ? bookFormData.vendors : undefined, // Send vendors array if selected
          vendor_id: bookFormData.vendor_id ? parseInt(bookFormData.vendor_id) : undefined, // Fallback to single vendor
          other_link: bookFormData.other_link.trim() || null
        })
      });

      const data = await response.json();
      console.log('🔍 Book creation response:', data);
      console.log('🔍 Response status:', response.status);
      console.log('🔍 Response ok:', response.ok);

      if (response.ok && data.success) {
        console.log('✅ Book created successfully');
        setBookFormData({
          title: '',
          image: '',
          description: '',
          price: '',
          discount_price: '',
          referal_link: '',
          video_link: '',
          file: '',
          subject_id: '',
          shared_content_group_id: '',
          sub_subject_id: '',
          country: '',
          state_id: '',
          state_ids: '',
          grade_id: '',
          short_description: '',
          seo: '',
          is_best_seller: false,
          vendor_id: '',
          vendors: [], // Reset vendors array
          other_link: ''
        });
        setSelectedBookImageFile(null); // Clear selected image file
        fetchBooks(); // Refresh the list
        alert('Book created successfully!');
        setActiveSection('books'); // Navigate back to book management
      } else {
        throw new Error(data.error || 'Failed to create book');
      }
    } catch (error) {
      console.error('❌ Error creating book:', error);
      alert(`Error creating book: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setBookLoading(false);
    }
  };

  const submitEditBook = async (): Promise<void> => {
    // Enhanced validation for required fields (matching backend expectations)
    const requiredFields = {
      title: bookFormData.title.trim(),
      description: bookFormData.description.trim(),
      price: bookFormData.price.trim(),
      image: bookFormData.image.trim(),
      video_link: bookFormData.video_link.trim(),
      subject_id: bookFormData.subject_id,
      grade_id: bookFormData.grade_id,
      country: bookFormData.country
    };

    const missingFields = Object.entries(requiredFields)
      .filter(([key, value]) => !value)
      .map(([key]) => key.replace('_', ' '));

    // Check if at least one vendor is selected (either vendors array or vendor_id)
    if (bookFormData.vendors.length === 0 && !bookFormData.vendor_id) {
      missingFields.push('vendor (select at least one)');
    }

    if (missingFields.length > 0) {
      alert(`Required fields missing: ${missingFields.join(', ')}`);
      return;
    }

    setBookLoading(true);
    try {
      // If user selected a new image file, upload it first
      let imageToSave = bookFormData.image.trim();
      if (selectedBookImageFile) {
        console.log('📤 Uploading selected image file first...');
        const uploadedImageName = await handleBookImageUpload();

        if (!uploadedImageName) {
          setBookLoading(false);
          return; // Upload failed, error already shown
        }
        imageToSave = uploadedImageName;
      }

      const response = await fetch(apiUrls.booksExtra.bookById(selectedBook.id), {
        method: 'PUT',
        headers: getHttpHeaders(),
        body: JSON.stringify({
          title: bookFormData.title.trim(),
          image: imageToSave,
          description: bookFormData.description.trim(),
          price: bookFormData.price.trim(),
          discount_price: bookFormData.discount_price.trim() || null,
          referal_link: bookFormData.referal_link.trim() || null,
          video_link: bookFormData.video_link.trim(),
          file: bookFormData.file.trim() || null,
          subject_id: parseInt(bookFormData.subject_id),
          shared_content_group_id: parseInt(bookFormData.shared_content_group_id) || null,
          sub_subject_id: parseInt(bookFormData.sub_subject_id) || null,
          country: parseInt(bookFormData.country),
          state_id: parseInt(bookFormData.state_id) || null,
          state_ids: bookFormData.state_ids.trim() || "[]",
          grade_id: parseInt(bookFormData.grade_id),
          short_description: bookFormData.short_description.trim() || null,
          seo: bookFormData.seo.trim() || null,
          is_best_seller: bookFormData.is_best_seller ? 1 : 0,
          vendors: bookFormData.vendors.length > 0 ? bookFormData.vendors : undefined, // Send vendors array if selected
          vendor_id: bookFormData.vendor_id ? parseInt(bookFormData.vendor_id) : undefined, // Fallback to single vendor
          other_link: bookFormData.other_link.trim() || null
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        console.log('✅ Book updated successfully');
        
        // Check if we're in the tabbed view
        const isInTabbedView = activeBookTab && activeBookTab.startsWith('edit-');
        
        // Refresh the books list in the background
        fetchBooks();
        
        if (isInTabbedView) {
          // Keep the edit tab open - form already has the current data
          // Update the tab title if the book title changed
          const newTitle = bookFormData.title.trim();
          setBookTabs(prev => prev.map(tab => 
            tab.id === activeBookTab 
              ? { 
                  ...tab, 
                  title: `✏️ ${newTitle.substring(0, 20)}${newTitle.length > 20 ? '...' : ''}`,
                  book: { ...tab.book, ...bookFormData, image: imageToSave }
                }
              : tab
          ));
          
          // Update selectedBook with current form data
          setSelectedBook((prev: any) => prev ? { ...prev, ...bookFormData, image: imageToSave } : prev);
          
          // Update the image field in form data if a new image was uploaded
          if (selectedBookImageFile) {
            setBookFormData(prev => ({ ...prev, image: imageToSave }));
          }
          
          setSelectedBookImageFile(null); // Clear selected image file
          alert('Book updated successfully!');
        } else {
          // Old behavior for non-tabbed view
          setBookFormData({
            title: '',
            image: '',
            description: '',
            price: '',
            discount_price: '',
            referal_link: '',
            video_link: '',
            file: '',
            subject_id: '',
            shared_content_group_id: '',
            sub_subject_id: '',
            country: '',
            state_id: '',
            state_ids: '',
            grade_id: '',
            short_description: '',
            seo: '',
            is_best_seller: false,
            vendor_id: '',
            vendors: [],
            other_link: ''
          });
          setSelectedBookImageFile(null);
          setSelectedBook(null);
          alert('Book updated successfully!');
          setActiveSection('books');
        }
      } else {
        throw new Error(data.error || 'Failed to update book');
      }
    } catch (error) {
      console.error('❌ Error updating book:', error);
      alert(`Error updating book: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setBookLoading(false);
    }
  };

  const submitDeleteBook = async (): Promise<void> => {
    setBookLoading(true);
    try {
      const response = await fetch(apiUrls.booksExtra.bookById(selectedBook.id), {
        method: 'DELETE',
        headers: getHttpHeaders()
      });

      const data = await response.json();

      if (response.ok && data.success) {
        console.log('✅ Book deleted successfully');
        setShowDeleteBookModal(false);
        setSelectedBook(null);
        fetchBooks(); // Refresh the list
        alert('Book deleted successfully!');
      } else {
        throw new Error(data.error || 'Failed to delete book');
      }
    } catch (error) {
      console.error('❌ Error deleting book:', error);
      alert(`Error deleting book: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setBookLoading(false);
    }
  };

  // Video CRUD handlers
  const handleCreateVideo = (): void => {
    console.log('➕ Create new video package');
    setVideoFormData({
      book_details_id: '',
      subject_id: '',
      grade_id: '',
      six_month: '',
      twelve_month: '',
      is_publish: false
    });
    setActiveSection('create-video');
  };

  const handleEditVideo = (video: any): void => {
    console.log('✏️ Edit video package:', video);
    setSelectedVideo(video);
    setVideoFormData({
      book_details_id: video.book_details_id || '',
      subject_id: video.subject_id || '',
      grade_id: video.grade_id || '',
      six_month: video.six_month || '',
      twelve_month: video.twelve_month || '',
      is_publish: video.active === 'Published'
    });
    setActiveSection('edit-video');
  };

  const handleDeleteVideo = (video: any): void => {
    console.log('🗑️ Delete video:', video);
    setSelectedVideo(video);
    setShowDeleteVideoModal(true);
  };

  const submitCreateVideo = async (): Promise<void> => {
    if (!videoFormData.book_details_id || !videoFormData.subject_id || !videoFormData.grade_id) {
      alert('Book details ID, category, and grade are required');
      return;
    }

    setVideoLoading(true);
    try {
      const response = await fetch(apiUrls.videos.admin, {
        method: 'POST',
        headers: getHttpHeaders(),
        body: JSON.stringify({
          book_details_id: parseInt(videoFormData.book_details_id) || 1,
          subject_id: parseInt(videoFormData.subject_id) || 1,
          grade_id: parseInt(videoFormData.grade_id) || 1,
          six_month: (videoFormData.six_month || '').toString().trim(),
          twelve_month: (videoFormData.twelve_month || '').toString().trim(),
          is_publish: videoFormData.is_publish ? 1 : 0
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        console.log('✅ Content package created successfully');
        setVideoFormData({ book_details_id: '', subject_id: '', grade_id: '', six_month: '', twelve_month: '', is_publish: false });
        fetchVideos(); // Refresh the list
        alert('Content package created successfully!');
        setActiveSection('videos'); // Navigate back to video management
      } else {
        throw new Error(data.error || 'Failed to create content package');
      }
    } catch (error) {
      console.error('❌ Error creating video package:', error);
      alert(`Error creating video package: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setVideoLoading(false);
    }
  };

  const submitEditVideo = async (): Promise<void> => {
    if (!videoFormData.book_details_id || !videoFormData.subject_id || !videoFormData.grade_id) {
      alert('Book details ID, category, and grade are required');
      return;
    }

    setVideoLoading(true);
    try {
      const response = await fetch(apiUrls.videos.videoById(selectedVideo.id), {
        method: 'PUT',
        headers: getHttpHeaders(),
        body: JSON.stringify({
          book_details_id: parseInt(videoFormData.book_details_id) || selectedVideo.book_details_id,
          subject_id: parseInt(videoFormData.subject_id) || selectedVideo.subject_id,
          grade_id: parseInt(videoFormData.grade_id) || selectedVideo.grade_id,
          six_month: (videoFormData.six_month || '').toString().trim(),
          twelve_month: (videoFormData.twelve_month || '').toString().trim(),
          is_publish: videoFormData.is_publish ? 1 : 0
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        console.log('✅ Content package updated successfully');
        setVideoFormData({ book_details_id: '', subject_id: '', grade_id: '', six_month: '', twelve_month: '', is_publish: false });
        setSelectedVideo(null);
        fetchVideos(); // Refresh the list
        alert('Content package updated successfully!');
        setActiveSection('videos'); // Navigate back to video management
      } else {
        throw new Error(data.error || 'Failed to update content package');
      }
    } catch (error) {
      console.error('❌ Error updating video package:', error);
      alert(`Error updating video package: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setVideoLoading(false);
    }
  };

  const submitDeleteVideo = async (): Promise<void> => {
    setVideoLoading(true);
    try {
      const response = await fetch(apiUrls.videos.videoById(selectedVideo.id), {
        method: 'DELETE',
        headers: getHttpHeaders()
      });

      const data = await response.json();

      if (response.ok && data.success) {
        console.log('✅ Video deleted successfully');
        setShowDeleteVideoModal(false);
        setSelectedVideo(null);
        fetchVideos(); // Refresh the list
        alert('Video deleted successfully!');
      } else {
        throw new Error(data.error || 'Failed to delete video');
      }
    } catch (error) {
      console.error('❌ Error deleting video:', error);
      alert(`Error deleting video: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setVideoLoading(false);
    }
  };

  // Filter users based on search and role
  const filteredUsers = users.filter(user => {
    const matchesSearch = user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = roleFilter === 'all' ||
      (roleFilter === 'admin' && (user.role === 'Admin' || user.role_name === 'admin')) ||
      (roleFilter === 'user' && (user.role !== 'Admin' && user.role_name !== 'admin'));
    return matchesSearch && matchesRole;
  });

  const renderDashboard = (): React.JSX.Element => (
    <>
      <AdminHeader>
        <div>
          <AdminTitle>Admin Dashboard</AdminTitle>
          <AdminSubtitle>Manage your MathKnots platform content and users</AdminSubtitle>
          <div style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '5px', fontStyle: 'italic' }}>
            💡 Click on any stat card below to navigate to that section
          </div>
        </div>
        <DashboardControls>
          <RefreshButton
            onClick={fetchStats}
            disabled={statsLoading}
          >
            {statsLoading ? (
              <LoadingSpinner><FiLoader /></LoadingSpinner>
            ) : (
              <FiRefreshCw />
            )}
            {statsLoading ? 'Loading...' : 'Refresh Stats'}
          </RefreshButton>
        </DashboardControls>
      </AdminHeader>

      {error && (
        <ErrorMessage>
          {error}
        </ErrorMessage>
      )}

      <StatsGrid>
        <StatCard
          clickable
          onClick={() => handleStatCardClick('users')}
          title="Click to view user management"
        >
          <StatIcon color="#3b82f6"><FiUsers /></StatIcon>
          <StatNumber>
            {statsLoading ? <LoadingSpinner><FiLoader /></LoadingSpinner> : stats.users}
          </StatNumber>
          <StatLabel>Total Users</StatLabel>
        </StatCard>
        <StatCard
          clickable
          onClick={() => handleStatCardClick('books')}
          title="Click to view book management"
        >
          <StatIcon color="#10b981"><FiBook /></StatIcon>
          <StatNumber>
            {statsLoading ? <LoadingSpinner><FiLoader /></LoadingSpinner> : stats.books}
          </StatNumber>
          <StatLabel>Total Books</StatLabel>
        </StatCard>
        {/* Videos stat card removed - Video Learning System deprecated */}
        <StatCard
          clickable
          onClick={() => handleStatCardClick('packages')}
          title="Click to view package management"
        >
          <StatIcon color="#8b5cf6"><FiPackage /></StatIcon>
          <StatNumber>
            {statsLoading ? <LoadingSpinner><FiLoader /></LoadingSpinner> : stats.packages}
          </StatNumber>
          <StatLabel>Content Packages</StatLabel>
        </StatCard>
        <StatCard
          clickable
          onClick={() => handleStatCardClick('subscriptions')}
          title="Click to view subscription management"
        >
          <StatIcon color="#06b6d4"><FiCreditCard /></StatIcon>
          <StatNumber>
            {statsLoading ? <LoadingSpinner><FiLoader /></LoadingSpinner> : stats.subscriptions}
          </StatNumber>
          <StatLabel>Subscriptions</StatLabel>
        </StatCard>
        <StatCard
          clickable
          onClick={() => handleStatCardClick('coupons')}
          title="Click to view coupon management"
        >
          <StatIcon color="#10b981"><FiTag /></StatIcon>
          <StatNumber>
            {statsLoading ? <LoadingSpinner><FiLoader /></LoadingSpinner> : (stats.coupons || 0)}
          </StatNumber>
          <StatLabel>Active Coupons</StatLabel>
        </StatCard>
        <StatCard
          clickable
          onClick={() => handleStatCardClick('blogs')}
          title="Click to view blog management"
        >
          <StatIcon color="#ec4899"><FiBarChart2 /></StatIcon>
          <StatNumber>
            {statsLoading ? <LoadingSpinner><FiLoader /></LoadingSpinner> : stats.blogs}
          </StatNumber>
          <StatLabel>Total Blogs</StatLabel>
        </StatCard>
        <StatCard
          clickable
          onClick={() => handleStatCardClick('subjects')}
          title="Click to view subject management"
        >
          <StatIcon color="#84cc16"><FiTag /></StatIcon>
          <StatNumber>
            {statsLoading ? <LoadingSpinner><FiLoader /></LoadingSpinner> : stats.subjects}
          </StatNumber>
          <StatLabel>Subjects</StatLabel>
        </StatCard>
        <StatCard
          clickable
          onClick={() => handleStatCardClick('grades')}
          title="Click to view grade management"
        >
          <StatIcon color="#f97316"><FiAward /></StatIcon>
          <StatNumber>
            {statsLoading ? <LoadingSpinner><FiLoader /></LoadingSpinner> : stats.grades}
          </StatNumber>
          <StatLabel>Grades</StatLabel>
        </StatCard>
        <StatCard
          clickable
          onClick={() => handleStatCardClick('countries')}
          title="Click to view location management"
        >
          <StatIcon color="#6366f1"><FiMapPin /></StatIcon>
          <StatNumber>
            {statsLoading ? <LoadingSpinner><FiLoader /></LoadingSpinner> : stats.countries}
          </StatNumber>
          <StatLabel>Countries</StatLabel>
        </StatCard>
        <StatCard
          clickable
          onClick={() => handleStatCardClick('states')}
          title="Click to view location management"
        >
          <StatIcon color="#8b5cf6"><FiMapPin /></StatIcon>
          <StatNumber>
            {statsLoading ? <LoadingSpinner><FiLoader /></LoadingSpinner> : stats.states}
          </StatNumber>
          <StatLabel>States</StatLabel>
        </StatCard>

        {/* Academy Stats */}
        <StatCard
          clickable
          onClick={() => handleStatCardClick('onlineClasses')}
          title="Click to view online classes management"
        >
          <StatIcon color="#7c3aed"><FiBookOpen /></StatIcon>
          <StatNumber>
            {statsLoading ? <LoadingSpinner><FiLoader /></LoadingSpinner> : stats.onlineClasses}
          </StatNumber>
          <StatLabel>Online Classes</StatLabel>
        </StatCard>
        <StatCard
          clickable
          onClick={() => handleStatCardClick('inPersonClasses')}
          title="Click to view in-person classes management"
        >
          <StatIcon color="#dc2626"><FiCalendar /></StatIcon>
          <StatNumber>
            {statsLoading ? <LoadingSpinner><FiLoader /></LoadingSpinner> : stats.inPersonClasses}
          </StatNumber>
          <StatLabel>InPerson Classes</StatLabel>
        </StatCard>
        <StatCard
          clickable
          onClick={() => handleStatCardClick('totalBatches')}
          title="Click to view batches management"
        >
          <StatIcon color="#059669"><FiUsersGroup /></StatIcon>
          <StatNumber>
            {statsLoading ? <LoadingSpinner><FiLoader /></LoadingSpinner> : stats.totalBatches}
          </StatNumber>
          <StatLabel>Total Batches</StatLabel>
        </StatCard>
      </StatsGrid>
    </>
  );

  const renderCRUDSection = (title: string, actions: Array<{ label: string; action: string; variant: 'create' | 'read' | 'update' | 'delete' }>): React.JSX.Element => (
    <CRUDSection>
      <CRUDTitle>{title}</CRUDTitle>
      <CRUDGrid>
        {actions.map((action) => (
          <CRUDButton
            key={action.action}
            variant={action.variant}
            onClick={() => handleSubMenuClick(action.action)}
          >
            {action.variant === 'create' && <FiPlus />}
            {action.variant === 'read' && <FiEye />}
            {action.variant === 'update' && <FiEdit />}
            {action.variant === 'delete' && <FiTrash2 />}
            {action.label}
          </CRUDButton>
        ))}
      </CRUDGrid>
    </CRUDSection>
  );

  const renderCreateBookPage = (): React.JSX.Element => (
    <FullPageContainer>
      <FullPageHeader>
        <FullPageTitle>➕ Create New Book</FullPageTitle>
        <BackButton onClick={() => setActiveSection('books')}>
          <FiArrowLeft />
          Back to Book Management
        </BackButton>
      </FullPageHeader>

      <FormContainer>
        {/* Basic Information */}
        <FormSection>
          <FormSectionTitle>📚 Basic Information</FormSectionTitle>
          <FormRow>
            <FormField>
              <FormLabel>
                Title: <span style={{ color: '#dc2626' }}>*</span>
              </FormLabel>
              <FormInput
                type="text"
                value={bookFormData.title}
                onChange={(e) => setBookFormData(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Enter book title..."
              />
            </FormField>
            <FormField>
              <FormLabel>
                Upload Image: <span style={{ color: '#dc2626' }}>*</span>
              </FormLabel>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        setSelectedBookImageFile(file);
                        setBookFormData(prev => ({ ...prev, image: file.name }));
                      }
                    }}
                    style={{ display: 'none' }}
                    id="book-image-upload"
                  />
                  <label
                    htmlFor="book-image-upload"
                    style={{
                      backgroundColor: '#3b82f6',
                      color: 'white',
                      padding: '8px 16px',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      border: 'none',
                      fontSize: '14px'
                    }}
                  >
                    Browse
                  </label>
                  <FormInput
                    type="text"
                    value={selectedBookImageFile ? selectedBookImageFile.name : bookFormData.image}
                    onChange={(e) => setBookFormData(prev => ({ ...prev, image: e.target.value }))}
                    placeholder="Select image file or enter image filename..."
                    style={{ flex: 1 }}
                  />
                </div>
                {isUploadingBookImage && (
                  <div style={{ width: '100%', backgroundColor: '#f3f4f6', borderRadius: '4px', height: '8px' }}>
                    <div
                      style={{
                        width: `${bookImageUploadProgress}%`,
                        height: '100%',
                        backgroundColor: '#3b82f6',
                        borderRadius: '4px',
                        transition: 'width 0.3s ease'
                      }}
                    />
                  </div>
                )}
                {bookImageUploadError && (
                  <div style={{ color: '#dc2626', fontSize: '14px' }}>
                    {bookImageUploadError}
                  </div>
                )}
              </div>
            </FormField>
          </FormRow>
          <FormField style={{ width: '100%' }}>
            <FormLabel>Short Description:</FormLabel>
            <RichTextEditor
              value={bookFormData.short_description}
              onChange={(value) => setBookFormData(prev => ({ ...prev, short_description: value }))}
              placeholder="Enter a brief description with formatting..."
              showImageUpload={false}
              minHeight="150px"
            />
          </FormField>
          <FormField>
            <FormLabel>
              Description: <span style={{ color: '#dc2626' }}>*</span>
            </FormLabel>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              <FormTextarea
                value={bookFormData.description}
                placeholder="Click 'Edit Description' for full editor..."
                rows={3}
                style={{ flex: 1, backgroundColor: '#f9fafb', cursor: 'pointer' }}
                readOnly
                onClick={openDescriptionEditor}
              />
              <button
                type="button"
                onClick={openDescriptionEditor}
                style={{
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  whiteSpace: 'nowrap',
                  height: 'fit-content',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                📝 Edit Description
              </button>
            </div>
            {bookFormData.description && (
              <div style={{
                marginTop: '8px',
                padding: '8px 12px',
                backgroundColor: '#f0f9ff',
                borderRadius: '6px',
                border: '1px solid #bae6fd',
                fontSize: '12px',
                color: '#0369a1'
              }}>
                <strong>Characters:</strong> {bookFormData.description.length} | <strong>Words:</strong> {bookFormData.description.split(' ').filter(w => w.length > 0).length}
                {bookFormData.description.length > 100 && (
                  <span style={{ marginLeft: '8px', color: '#059669' }}>✓ Good length</span>
                )}
              </div>
            )}
          </FormField>
        </FormSection>

        {/* Pricing Information */}
        <FormSection>
          <FormSectionTitle>💰 Pricing Information</FormSectionTitle>
          <FormRow>
            <FormField>
              <FormLabel>
                Price: <span style={{ color: '#dc2626' }}>*</span>
              </FormLabel>
              <FormInput
                type="text"
                value={bookFormData.price}
                onChange={(e) => setBookFormData(prev => ({ ...prev, price: e.target.value }))}
                placeholder="Enter price..."
              />
            </FormField>
            <FormField>
              <FormLabel>Discount Price:</FormLabel>
              <FormInput
                type="text"
                value={bookFormData.discount_price}
                onChange={(e) => setBookFormData(prev => ({ ...prev, discount_price: e.target.value }))}
                placeholder="Enter discount price..."
              />
            </FormField>
          </FormRow>
        </FormSection>

        {/* Content Links */}
        <FormSection>
          <FormSectionTitle>🔗 Content Links</FormSectionTitle>
          <FormRow>
            <FormField>
              <FormLabel>
                Youtube Link: <span style={{ color: '#dc2626' }}>*</span>
              </FormLabel>
              <FormInput
                type="text"
                value={bookFormData.video_link}
                onChange={(e) => setBookFormData(prev => ({ ...prev, video_link: e.target.value }))}
                placeholder="Enter video URL..."
              />
            </FormField>
            <FormField>
              <FormLabel>Amazon Book Link:</FormLabel>
              <FormInput
                type="text"
                value={bookFormData.referal_link}
                onChange={(e) => setBookFormData(prev => ({ ...prev, referal_link: e.target.value }))}
                placeholder="Enter referral link..."
              />
            </FormField>
          </FormRow>
          <FormRow>
            <FormField>
              <FormLabel>S3 Location:</FormLabel>
              <FormInput
                type="text"
                value={bookFormData.file}
                onChange={(e) => setBookFormData(prev => ({ ...prev, file: e.target.value }))}
                placeholder="Enter file URL..."
              />
            </FormField>
            <FormField>
              <FormLabel>Barnes & Nobel Link:</FormLabel>
              <FormInput
                type="text"
                value={bookFormData.other_link}
                onChange={(e) => setBookFormData(prev => ({ ...prev, other_link: e.target.value }))}
                placeholder="Enter other link..."
              />
            </FormField>
          </FormRow>
        </FormSection>

        {/* Categories and Location */}
        <FormSection>
          <FormSectionTitle>📂 Categories & Location</FormSectionTitle>
          <FormRow>
            <FormField>
              <FormLabel>
                Subject: <span style={{ color: '#dc2626' }}>*</span>
              </FormLabel>
              <FormSelect
                value={bookFormData.subject_id}
                onChange={(e) => setBookFormData(prev => ({ ...prev, subject_id: e.target.value }))}
              >
                <option value="">Select Subject</option>
                {subjects.map(subject => (
                  <option key={subject.id} value={subject.id}>{subject.name}</option>
                ))}
              </FormSelect>
            </FormField>
            <FormField>
              <FormLabel>
                Grade: <span style={{ color: '#dc2626' }}>*</span>
              </FormLabel>
              <FormSelect
                value={bookFormData.grade_id}
                onChange={(e) => setBookFormData(prev => ({ ...prev, grade_id: e.target.value }))}
              >
                <option value="">Select Grade</option>
                {grades.map(grade => (
                  <option key={grade.id} value={grade.id}>{grade.name}</option>
                ))}
              </FormSelect>
            </FormField>
          </FormRow>
          <FormRow>
            <FormField>
              <FormLabel>
                Country: <span style={{ color: '#dc2626' }}>*</span>
              </FormLabel>
              <FormSelect
                value={bookFormData.country}
                onChange={(e) => {
                  const countryId = e.target.value;
                  setBookFormData(prev => ({
                    ...prev,
                    country: countryId,
                    state_id: '', // Reset state when country changes
                    state_ids: '[]' // Reset multi-select states
                  }));
                  fetchStates(countryId); // Load states for selected country
                }}
              >
                <option value="">Select Country</option>
                {countries.map(country => (
                  <option key={country.id} value={country.id}>{country.name}</option>
                ))}
              </FormSelect>
            </FormField>
            <FormField>
              <FormLabel>States: <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: 'normal' }}>(Select multiple)</span></FormLabel>
              <div style={{
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                maxHeight: '200px',
                overflowY: 'auto',
                padding: '8px',
                backgroundColor: states.length === 0 ? '#f9fafb' : 'white'
              }}>
                {states.length === 0 ? (
                  <div style={{ color: '#9ca3af', fontSize: '14px', padding: '8px' }}>
                    {bookFormData.country ? 'No states available' : 'Select a country first'}
                  </div>
                ) : (
                  states.map(state => {
                    const selectedStates = (() => {
                      try {
                        const parsed = JSON.parse(bookFormData.state_ids || '[]');
                        return Array.isArray(parsed) ? parsed.map(String) : [];
                      } catch { return []; }
                    })();
                    const isChecked = selectedStates.includes(String(state.id));
                    return (
                      <label
                        key={state.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '8px',
                          cursor: 'pointer',
                          borderRadius: '4px',
                          backgroundColor: isChecked ? '#eff6ff' : 'transparent',
                          marginBottom: '4px',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = isChecked ? '#dbeafe' : '#f3f4f6'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = isChecked ? '#eff6ff' : 'transparent'}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={(e) => {
                            const stateId = String(state.id);
                            let newSelectedStates: string[];
                            if (e.target.checked) {
                              newSelectedStates = [...selectedStates, stateId];
                            } else {
                              newSelectedStates = selectedStates.filter(id => id !== stateId);
                            }
                            setBookFormData(prev => ({
                              ...prev,
                              state_ids: JSON.stringify(newSelectedStates),
                              state_id: newSelectedStates.length > 0 ? newSelectedStates[0] : ''
                            }));
                          }}
                          style={{ marginRight: '10px', width: '18px', height: '18px', cursor: 'pointer' }}
                        />
                        <span style={{ fontSize: '14px', color: '#374151' }}>{state.name}</span>
                      </label>
                    );
                  })
                )}
              </div>
              {(() => {
                try {
                  const selected = JSON.parse(bookFormData.state_ids || '[]');
                  if (Array.isArray(selected) && selected.length > 0) {
                    return (
                      <div style={{ marginTop: '8px', fontSize: '12px', color: '#059669' }}>
                        ✓ {selected.length} state{selected.length > 1 ? 's' : ''} selected
                      </div>
                    );
                  }
                } catch { }
                return null;
              })()}
            </FormField>
          </FormRow>
        </FormSection>

        {/* Additional Settings */}
        <FormSection>
          <FormSectionTitle>⚙️ Additional Settings</FormSectionTitle>
          <FormRow>
            <FormField>
              <FormLabel>
                Vendors: <span style={{ color: '#dc2626' }}>*</span>
              </FormLabel>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                padding: '12px',
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                backgroundColor: '#f9fafb'
              }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={bookFormData.vendors.includes('amazon')}
                    onChange={(e) => {
                      const vendors = e.target.checked
                        ? [...bookFormData.vendors, 'amazon']
                        : bookFormData.vendors.filter(v => v !== 'amazon');
                      setBookFormData(prev => ({ ...prev, vendors }));
                    }}
                  />
                  <span>Amazon</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={bookFormData.vendors.includes('barnesandnoble')}
                    onChange={(e) => {
                      const vendors = e.target.checked
                        ? [...bookFormData.vendors, 'barnesandnoble']
                        : bookFormData.vendors.filter(v => v !== 'barnesandnoble');
                      setBookFormData(prev => ({ ...prev, vendors }));
                    }}
                  />
                  <span>Barnes & Noble</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={bookFormData.vendors.includes('mathknots')}
                    onChange={(e) => {
                      const vendors = e.target.checked
                        ? [...bookFormData.vendors, 'mathknots']
                        : bookFormData.vendors.filter(v => v !== 'mathknots');
                      setBookFormData(prev => ({ ...prev, vendors }));
                    }}
                  />
                  <span>MathKnots</span>
                </label>
                {bookFormData.vendors.length === 0 && (
                  <div style={{ color: '#dc2626', fontSize: '12px', fontStyle: 'italic' }}>
                    Please select at least one vendor
                  </div>
                )}
              </div>
            </FormField>
            <FormField>
              <FormLabel>SEO Keywords:</FormLabel>
              <textarea
                value={bookFormData.seo}
                onChange={(e) => setBookFormData(prev => ({ ...prev, seo: e.target.value }))}
                placeholder="Enter SEO keywords, tags, or search terms (one per line or comma-separated)..."
                rows={6}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '14px',
                  resize: 'vertical',
                  fontFamily: 'inherit'
                }}
              />
            </FormField>
          </FormRow>
          <FormField>
            <FormLabel style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input
                type="checkbox"
                checked={bookFormData.is_best_seller}
                onChange={(e) => setBookFormData(prev => ({ ...prev, is_best_seller: e.target.checked }))}
              />
              Mark as Best Seller
            </FormLabel>
          </FormField>
        </FormSection>

        <FormActions>
          <CancelButton
            type="button"
            onClick={() => setActiveSection('books')}
            loading={bookLoading}
          >
            Cancel
          </CancelButton>
          <SubmitButton
            type="button"
            onClick={submitCreateBook}
            loading={bookLoading}
            disabled={bookLoading || !bookFormData.title.trim() || !bookFormData.description.trim() || !bookFormData.price.trim() || !bookFormData.image.trim() || !bookFormData.video_link.trim() || !bookFormData.subject_id || !bookFormData.grade_id || !bookFormData.country || (bookFormData.vendors.length === 0 && !bookFormData.vendor_id)}
          >
            {bookLoading ? 'Creating...' : 'Create Book'}
          </SubmitButton>
        </FormActions>
      </FormContainer>
    </FullPageContainer>
  );

  const renderEditBookPage = (): React.JSX.Element => (
    <FullPageContainer>
      <FullPageHeader>
        <FullPageTitle>✏️ Edit Book: {selectedBook?.title}</FullPageTitle>
        <BackButton onClick={() => setActiveSection('books')}>
          <FiArrowLeft />
          Back to Book Management
        </BackButton>
      </FullPageHeader>

      <FormContainer>
        {/* Basic Information */}
        <FormSection>
          <FormSectionTitle>📚 Basic Information</FormSectionTitle>
          <FormRow>
            <FormField>
              <FormLabel>
                Title: <span style={{ color: '#dc2626' }}>*</span>
              </FormLabel>
              <FormInput
                type="text"
                value={bookFormData.title}
                onChange={(e) => setBookFormData(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Enter book title..."
              />
            </FormField>
            <FormField>
              <FormLabel>
                Upload Image: <span style={{ color: '#dc2626' }}>*</span>
              </FormLabel>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        setSelectedBookImageFile(file);
                        setBookFormData(prev => ({ ...prev, image: file.name }));
                      }
                    }}
                    style={{ display: 'none' }}
                    id="edit-book-image-upload"
                  />
                  <label
                    htmlFor="edit-book-image-upload"
                    style={{
                      backgroundColor: '#3b82f6',
                      color: 'white',
                      padding: '8px 16px',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      border: 'none',
                      fontSize: '14px'
                    }}
                  >
                    Browse
                  </label>
                  <FormInput
                    type="text"
                    value={selectedBookImageFile ? selectedBookImageFile.name : bookFormData.image}
                    onChange={(e) => setBookFormData(prev => ({ ...prev, image: e.target.value }))}
                    placeholder="Select image file or enter image filename..."
                    style={{ flex: 1 }}
                  />
                  {/* Show existing book image preview with zoom on hover */}
                  {(selectedBook?.image || bookFormData.image) && !selectedBookImageFile && (
                    <div 
                      style={{
                        position: 'relative',
                        width: '50px',
                        height: '50px',
                        borderRadius: '6px',
                        overflow: 'visible',
                        border: '2px solid #e5e7eb',
                        flexShrink: 0,
                        cursor: 'pointer'
                      }}
                      className="image-preview-container"
                    >
                      <img
                        src={`/images/${selectedBook?.image || bookFormData.image}`}
                        alt="Current book"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          borderRadius: '4px'
                        }}
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = '/images/placeholder-book.png';
                        }}
                      />
                      {/* Zoomed image on hover */}
                      <div
                        style={{
                          position: 'absolute',
                          top: '60px',
                          right: '0',
                          width: '200px',
                          height: '200px',
                          borderRadius: '8px',
                          overflow: 'hidden',
                          border: '3px solid #3b82f6',
                          boxShadow: '0 10px 25px rgba(0, 0, 0, 0.3)',
                          backgroundColor: 'white',
                          zIndex: 1000,
                          opacity: 0,
                          visibility: 'hidden',
                          transition: 'opacity 0.2s ease, visibility 0.2s ease',
                          pointerEvents: 'none'
                        }}
                        className="image-zoom-preview"
                      >
                        <img
                          src={`/images/${selectedBook?.image || bookFormData.image}`}
                          alt="Current book zoomed"
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain'
                          }}
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = '/images/placeholder-book.png';
                          }}
                        />
                      </div>
                      <style>{`
                        .image-preview-container:hover .image-zoom-preview {
                          opacity: 1 !important;
                          visibility: visible !important;
                        }
                      `}</style>
                    </div>
                  )}
                  {/* Show selected file preview with zoom on hover */}
                  {selectedBookImageFile && (
                    <div 
                      style={{
                        position: 'relative',
                        width: '50px',
                        height: '50px',
                        borderRadius: '6px',
                        overflow: 'visible',
                        border: '2px solid #3b82f6',
                        flexShrink: 0,
                        cursor: 'pointer'
                      }}
                      className="new-image-preview-container"
                    >
                      <img
                        src={URL.createObjectURL(selectedBookImageFile)}
                        alt="New book"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          borderRadius: '4px'
                        }}
                      />
                      {/* Zoomed image on hover for new file */}
                      <div
                        style={{
                          position: 'absolute',
                          top: '60px',
                          right: '0',
                          width: '200px',
                          height: '200px',
                          borderRadius: '8px',
                          overflow: 'hidden',
                          border: '3px solid #3b82f6',
                          boxShadow: '0 10px 25px rgba(0, 0, 0, 0.3)',
                          backgroundColor: 'white',
                          zIndex: 1000,
                          opacity: 0,
                          visibility: 'hidden',
                          transition: 'opacity 0.2s ease, visibility 0.2s ease',
                          pointerEvents: 'none'
                        }}
                        className="new-image-zoom-preview"
                      >
                        <img
                          src={URL.createObjectURL(selectedBookImageFile)}
                          alt="New book zoomed"
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain'
                          }}
                        />
                      </div>
                      <style>{`
                        .new-image-preview-container:hover .new-image-zoom-preview {
                          opacity: 1 !important;
                          visibility: visible !important;
                        }
                      `}</style>
                    </div>
                  )}
                </div>
                {isUploadingBookImage && (
                  <div style={{ width: '100%', backgroundColor: '#f3f4f6', borderRadius: '4px', height: '8px' }}>
                    <div
                      style={{
                        width: `${bookImageUploadProgress}%`,
                        height: '100%',
                        backgroundColor: '#3b82f6',
                        borderRadius: '4px',
                        transition: 'width 0.3s ease'
                      }}
                    />
                  </div>
                )}
                {bookImageUploadError && (
                  <div style={{ color: '#dc2626', fontSize: '14px' }}>
                    {bookImageUploadError}
                  </div>
                )}
              </div>
            </FormField>
          </FormRow>
          <FormField style={{ width: '100%' }}>
            <FormLabel>Short Description:</FormLabel>
            <RichTextEditor
              value={bookFormData.short_description}
              onChange={(value) => setBookFormData(prev => ({ ...prev, short_description: value }))}
              placeholder="Enter a brief description with formatting..."
              showImageUpload={false}
              minHeight="150px"
            />
          </FormField>
          <FormField>
            <FormLabel>
              Description: <span style={{ color: '#dc2626' }}>*</span>
            </FormLabel>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              <FormTextarea
                value={bookFormData.description}
                placeholder="Click 'Edit Description' for full editor..."
                rows={3}
                style={{ flex: 1, backgroundColor: '#f9fafb', cursor: 'pointer' }}
                readOnly
                onClick={openDescriptionEditor}
              />
              <button
                type="button"
                onClick={openDescriptionEditor}
                style={{
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  whiteSpace: 'nowrap',
                  height: 'fit-content',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                📝 Edit Description
              </button>
            </div>
            {bookFormData.description && (
              <div style={{
                marginTop: '8px',
                padding: '8px 12px',
                backgroundColor: '#f0f9ff',
                borderRadius: '6px',
                border: '1px solid #bae6fd',
                fontSize: '12px',
                color: '#0369a1'
              }}>
                <strong>Characters:</strong> {bookFormData.description.length} | <strong>Words:</strong> {bookFormData.description.split(' ').filter(w => w.length > 0).length}
                {bookFormData.description.length > 100 && (
                  <span style={{ marginLeft: '8px', color: '#059669' }}>✓ Good length</span>
                )}
              </div>
            )}
          </FormField>
        </FormSection>

        {/* Pricing Information */}
        <FormSection>
          <FormSectionTitle>💰 Pricing Information</FormSectionTitle>
          <FormRow>
            <FormField>
              <FormLabel>
                Price: <span style={{ color: '#dc2626' }}>*</span>
              </FormLabel>
              <FormInput
                type="text"
                value={bookFormData.price}
                onChange={(e) => setBookFormData(prev => ({ ...prev, price: e.target.value }))}
                placeholder="Enter price..."
              />
            </FormField>
            <FormField>
              <FormLabel>Discount Price:</FormLabel>
              <FormInput
                type="text"
                value={bookFormData.discount_price}
                onChange={(e) => setBookFormData(prev => ({ ...prev, discount_price: e.target.value }))}
                placeholder="Enter discount price..."
              />
            </FormField>
          </FormRow>
        </FormSection>

        {/* Content Links */}
        <FormSection>
          <FormSectionTitle>🔗 Content Links</FormSectionTitle>
          <FormRow>
            <FormField>
              <FormLabel>
                Youtube Link: <span style={{ color: '#dc2626' }}>*</span>
              </FormLabel>
              <FormInput
                type="text"
                value={bookFormData.video_link}
                onChange={(e) => setBookFormData(prev => ({ ...prev, video_link: e.target.value }))}
                placeholder="Enter video URL..."
              />
            </FormField>
            <FormField>
              <FormLabel>Amazon Book Link:</FormLabel>
              <FormInput
                type="text"
                value={bookFormData.referal_link}
                onChange={(e) => setBookFormData(prev => ({ ...prev, referal_link: e.target.value }))}
                placeholder="Enter referral link..."
              />
            </FormField>
          </FormRow>
          <FormRow>
            <FormField>
              <FormLabel>S3 Location:</FormLabel>
              <FormInput
                type="text"
                value={bookFormData.file}
                onChange={(e) => setBookFormData(prev => ({ ...prev, file: e.target.value }))}
                placeholder="Enter file URL..."
              />
            </FormField>
            <FormField>
              <FormLabel>Barnes & Nobel Link:</FormLabel>
              <FormInput
                type="text"
                value={bookFormData.other_link}
                onChange={(e) => setBookFormData(prev => ({ ...prev, other_link: e.target.value }))}
                placeholder="Enter other link..."
              />
            </FormField>
          </FormRow>
        </FormSection>

        {/* Categories and Location */}
        <FormSection>
          <FormSectionTitle>📂 Categories & Location</FormSectionTitle>
          <FormRow>
            <FormField>
              <FormLabel>
                Subject: <span style={{ color: '#dc2626' }}>*</span>
              </FormLabel>
              <FormSelect
                value={bookFormData.subject_id}
                onChange={(e) => setBookFormData(prev => ({ ...prev, subject_id: e.target.value }))}
              >
                <option value="">Select Subject</option>
                {subjects.map(subject => (
                  <option key={subject.id} value={subject.id}>{subject.name}</option>
                ))}
              </FormSelect>
            </FormField>
            <FormField>
              <FormLabel>
                Grade: <span style={{ color: '#dc2626' }}>*</span>
              </FormLabel>
              <FormSelect
                value={bookFormData.grade_id}
                onChange={(e) => setBookFormData(prev => ({ ...prev, grade_id: e.target.value }))}
              >
                <option value="">Select Grade</option>
                {grades.map(grade => (
                  <option key={grade.id} value={grade.id}>{grade.name}</option>
                ))}
              </FormSelect>
            </FormField>
          </FormRow>
          <FormRow>
            <FormField>
              <FormLabel>
                Country: <span style={{ color: '#dc2626' }}>*</span>
              </FormLabel>
              <FormSelect
                value={bookFormData.country}
                onChange={(e) => {
                  const countryId = e.target.value;
                  setBookFormData(prev => ({
                    ...prev,
                    country: countryId,
                    state_id: '', // Reset state when country changes
                    state_ids: '[]' // Reset multi-select states
                  }));
                  fetchStates(countryId); // Load states for selected country
                }}
              >
                <option value="">Select Country</option>
                {countries.map(country => (
                  <option key={country.id} value={country.id}>{country.name}</option>
                ))}
              </FormSelect>
            </FormField>
            <FormField>
              <FormLabel>States: <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: 'normal' }}>(Select multiple)</span></FormLabel>
              <div style={{
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                maxHeight: '200px',
                overflowY: 'auto',
                padding: '8px',
                backgroundColor: states.length === 0 ? '#f9fafb' : 'white'
              }}>
                {states.length === 0 ? (
                  <div style={{ color: '#9ca3af', fontSize: '14px', padding: '8px' }}>
                    {bookFormData.country ? 'No states available' : 'Select a country first'}
                  </div>
                ) : (
                  states.map(state => {
                    const selectedStates = (() => {
                      try {
                        const parsed = JSON.parse(bookFormData.state_ids || '[]');
                        return Array.isArray(parsed) ? parsed.map(String) : [];
                      } catch { return []; }
                    })();
                    const isChecked = selectedStates.includes(String(state.id));
                    return (
                      <label
                        key={state.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '8px',
                          cursor: 'pointer',
                          borderRadius: '4px',
                          backgroundColor: isChecked ? '#eff6ff' : 'transparent',
                          marginBottom: '4px',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = isChecked ? '#dbeafe' : '#f3f4f6'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = isChecked ? '#eff6ff' : 'transparent'}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={(e) => {
                            const stateId = String(state.id);
                            let newSelectedStates: string[];
                            if (e.target.checked) {
                              newSelectedStates = [...selectedStates, stateId];
                            } else {
                              newSelectedStates = selectedStates.filter(id => id !== stateId);
                            }
                            setBookFormData(prev => ({
                              ...prev,
                              state_ids: JSON.stringify(newSelectedStates),
                              state_id: newSelectedStates.length > 0 ? newSelectedStates[0] : ''
                            }));
                          }}
                          style={{ marginRight: '10px', width: '18px', height: '18px', cursor: 'pointer' }}
                        />
                        <span style={{ fontSize: '14px', color: '#374151' }}>{state.name}</span>
                      </label>
                    );
                  })
                )}
              </div>
              {(() => {
                try {
                  const selected = JSON.parse(bookFormData.state_ids || '[]');
                  if (Array.isArray(selected) && selected.length > 0) {
                    return (
                      <div style={{ marginTop: '8px', fontSize: '12px', color: '#059669' }}>
                        ✓ {selected.length} state{selected.length > 1 ? 's' : ''} selected
                      </div>
                    );
                  }
                } catch { }
                return null;
              })()}
            </FormField>
          </FormRow>
        </FormSection>

        {/* Additional Settings */}
        <FormSection>
          <FormSectionTitle>⚙️ Additional Settings</FormSectionTitle>
          <FormRow>
            <FormField>
              <FormLabel>
                Vendors: <span style={{ color: '#dc2626' }}>*</span>
              </FormLabel>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                padding: '12px',
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                backgroundColor: '#f9fafb'
              }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={bookFormData.vendors.includes('amazon')}
                    onChange={(e) => {
                      const vendors = e.target.checked
                        ? [...bookFormData.vendors, 'amazon']
                        : bookFormData.vendors.filter(v => v !== 'amazon');
                      setBookFormData(prev => ({ ...prev, vendors }));
                    }}
                  />
                  <span>Amazon</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={bookFormData.vendors.includes('barnesandnoble')}
                    onChange={(e) => {
                      const vendors = e.target.checked
                        ? [...bookFormData.vendors, 'barnesandnoble']
                        : bookFormData.vendors.filter(v => v !== 'barnesandnoble');
                      setBookFormData(prev => ({ ...prev, vendors }));
                    }}
                  />
                  <span>Barnes & Noble</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={bookFormData.vendors.includes('mathknots')}
                    onChange={(e) => {
                      const vendors = e.target.checked
                        ? [...bookFormData.vendors, 'mathknots']
                        : bookFormData.vendors.filter(v => v !== 'mathknots');
                      setBookFormData(prev => ({ ...prev, vendors }));
                    }}
                  />
                  <span>MathKnots</span>
                </label>
                {bookFormData.vendors.length === 0 && (
                  <div style={{ color: '#dc2626', fontSize: '12px', fontStyle: 'italic' }}>
                    Please select at least one vendor
                  </div>
                )}
              </div>
            </FormField>
            <FormField>
              <FormLabel>SEO Keywords:</FormLabel>
              <textarea
                value={bookFormData.seo}
                onChange={(e) => setBookFormData(prev => ({ ...prev, seo: e.target.value }))}
                placeholder="Enter SEO keywords, tags, or search terms (one per line or comma-separated)..."
                rows={6}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '14px',
                  resize: 'vertical',
                  fontFamily: 'inherit'
                }}
              />
            </FormField>
          </FormRow>
          <FormField>
            <FormLabel style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input
                type="checkbox"
                checked={bookFormData.is_best_seller}
                onChange={(e) => setBookFormData(prev => ({ ...prev, is_best_seller: e.target.checked }))}
              />
              Mark as Best Seller
            </FormLabel>
          </FormField>
        </FormSection>

        <FormActions>
          <CancelButton
            type="button"
            onClick={() => setActiveSection('books')}
            loading={bookLoading}
          >
            Cancel
          </CancelButton>
          <SubmitButton
            type="button"
            onClick={submitEditBook}
            loading={bookLoading}
            disabled={bookLoading || !bookFormData.title.trim() || !bookFormData.description.trim() || !bookFormData.price.trim() || !bookFormData.image.trim() || !bookFormData.video_link.trim() || !bookFormData.subject_id || !bookFormData.grade_id || !bookFormData.country || !bookFormData.vendor_id}
          >
            {bookLoading ? 'Updating...' : 'Update Book'}
          </SubmitButton>
        </FormActions>
      </FormContainer>
    </FullPageContainer>
  );

  // ============================================================================
  // BLOG FORM PAGES
  // ============================================================================

  const renderCreateBlogPage = (): React.JSX.Element => (
    <FullPageContainer>
      <FullPageHeader>
        <FullPageTitle>➕ Create New Blog</FullPageTitle>
        <BackButton onClick={() => setActiveSection('blogs')}>
          <FiArrowLeft />
          Back to Blog Management
        </BackButton>
      </FullPageHeader>

      <FormContainer>
        {/* Basic Information */}
        <FormSection>
          <FormSectionTitle>📝 Basic Information</FormSectionTitle>
          <FormRow>
            <FormField>
              <FormLabel>
                Title: <span style={{ color: '#dc2626' }}>*</span>
              </FormLabel>
              <FormInput
                type="text"
                value={blogFormData.title}
                onChange={(e) => setBlogFormData(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Enter blog title..."
              />
            </FormField>
            <FormField>
              <FormLabel>
                Author: <span style={{ color: '#dc2626' }}>*</span>
              </FormLabel>
              <FormInput
                type="text"
                value={blogFormData.author}
                onChange={(e) => setBlogFormData(prev => ({ ...prev, author: e.target.value }))}
                placeholder="Enter author name..."
              />
            </FormField>
          </FormRow>
          <FormRow>
            <FormField>
              <FormLabel>
                Status: <span style={{ color: '#dc2626' }}>*</span>
              </FormLabel>
              <FormSelect
                value={blogFormData.status}
                onChange={(e) => setBlogFormData(prev => ({ ...prev, status: e.target.value }))}
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
                <option value="Draft">Draft</option>
              </FormSelect>
            </FormField>
            <FormField>
              <FormLabel>
                Upload Image: <span style={{ color: '#dc2626' }}>*</span>
              </FormLabel>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        setSelectedBlogImageFile(file);
                        setBlogFormData(prev => ({ ...prev, image: file.name }));
                      }
                    }}
                    style={{ display: 'none' }}
                    id="blog-image-upload"
                  />
                  <label
                    htmlFor="blog-image-upload"
                    style={{
                      backgroundColor: '#3b82f6',
                      color: 'white',
                      padding: '8px 16px',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      border: 'none',
                      fontSize: '14px'
                    }}
                  >
                    Browse
                  </label>
                  <FormInput
                    type="text"
                    value={selectedBlogImageFile ? selectedBlogImageFile.name : blogFormData.image}
                    onChange={(e) => setBlogFormData(prev => ({ ...prev, image: e.target.value }))}
                    placeholder="Select image file or enter image filename..."
                    style={{ flex: 1 }}
                  />
                </div>
                {isUploadingBlogImage && (
                  <div style={{ width: '100%', backgroundColor: '#f3f4f6', borderRadius: '4px', height: '8px' }}>
                    <div
                      style={{
                        width: `${blogImageUploadProgress}%`,
                        height: '100%',
                        backgroundColor: '#3b82f6',
                        borderRadius: '4px',
                        transition: 'width 0.3s ease'
                      }}
                    />
                  </div>
                )}
                {blogImageUploadError && (
                  <div style={{ color: '#dc2626', fontSize: '14px' }}>
                    {blogImageUploadError}
                  </div>
                )}
              </div>
            </FormField>
          </FormRow>
          <FormField>
            <FormLabel>
              Content: <span style={{ color: '#dc2626' }}>*</span>
            </FormLabel>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              <FormTextarea
                value={blogFormData.description}
                placeholder="Click 'Edit Content' for full editor..."
                rows={3}
                style={{ flex: 1, backgroundColor: '#f9fafb', cursor: 'pointer' }}
                readOnly
                onClick={openBlogContentEditor}
              />
              <button
                type="button"
                onClick={openBlogContentEditor}
                style={{
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  whiteSpace: 'nowrap',
                  height: 'fit-content',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                📝 Edit Content
              </button>
            </div>
            {blogFormData.description && (
              <div style={{
                marginTop: '8px',
                padding: '8px 12px',
                backgroundColor: '#f0f9ff',
                borderRadius: '6px',
                border: '1px solid #bae6fd',
                fontSize: '12px',
                color: '#0369a1'
              }}>
                <strong>Characters:</strong> {blogFormData.description.length} | <strong>Words:</strong> {blogFormData.description.split(' ').filter(w => w.length > 0).length}
                {blogFormData.description.length > 100 && (
                  <span style={{ marginLeft: '8px', color: '#059669' }}>✓ Good length</span>
                )}
              </div>
            )}
          </FormField>
        </FormSection>

        {/* SEO Information */}
        <FormSection>
          <FormSectionTitle>🔍 SEO Information</FormSectionTitle>
          <FormField>
            <FormLabel>SEO Keywords:</FormLabel>
            <textarea
              value={blogFormData.seo}
              onChange={(e) => setBlogFormData(prev => ({ ...prev, seo: e.target.value }))}
              placeholder="Enter SEO keywords, tags, or search terms (one per line or comma-separated)..."
              rows={6}
              style={{
                width: '100%',
                padding: '12px',
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '14px',
                resize: 'vertical',
                fontFamily: 'inherit'
              }}
            />
          </FormField>
        </FormSection>

        <FormActions>
          <CancelButton
            type="button"
            onClick={() => setActiveSection('blogs')}
            loading={blogLoading}
          >
            Cancel
          </CancelButton>
          <SubmitButton
            type="button"
            onClick={submitCreateBlog}
            loading={blogLoading}
            disabled={blogLoading || !blogFormData.title.trim() || !blogFormData.description.trim() || !blogFormData.author.trim() || !blogFormData.image.trim()}
          >
            {blogLoading ? 'Creating...' : 'Create Blog'}
          </SubmitButton>
        </FormActions>
      </FormContainer>
    </FullPageContainer>
  );

  const renderEditBlogPage = (): React.JSX.Element => (
    <FullPageContainer>
      <FullPageHeader>
        <FullPageTitle>✏️ Edit Blog: {selectedBlog?.title}</FullPageTitle>
        <BackButton onClick={() => setActiveSection('blogs')}>
          <FiArrowLeft />
          Back to Blog Management
        </BackButton>
      </FullPageHeader>

      <FormContainer>
        {/* Basic Information */}
        <FormSection>
          <FormSectionTitle>📝 Basic Information</FormSectionTitle>
          <FormRow>
            <FormField>
              <FormLabel>
                Title: <span style={{ color: '#dc2626' }}>*</span>
              </FormLabel>
              <FormInput
                type="text"
                value={blogFormData.title}
                onChange={(e) => setBlogFormData(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Enter blog title..."
              />
            </FormField>
            <FormField>
              <FormLabel>
                Author: <span style={{ color: '#dc2626' }}>*</span>
              </FormLabel>
              <FormInput
                type="text"
                value={blogFormData.author}
                onChange={(e) => setBlogFormData(prev => ({ ...prev, author: e.target.value }))}
                placeholder="Enter author name..."
              />
            </FormField>
          </FormRow>
          <FormRow>
            <FormField>
              <FormLabel>
                Status: <span style={{ color: '#dc2626' }}>*</span>
              </FormLabel>
              <FormSelect
                value={blogFormData.status}
                onChange={(e) => setBlogFormData(prev => ({ ...prev, status: e.target.value }))}
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
                <option value="Draft">Draft</option>
              </FormSelect>
            </FormField>
            <FormField>
              <FormLabel>
                Upload Image: <span style={{ color: '#dc2626' }}>*</span>
              </FormLabel>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        setSelectedBlogImageFile(file);
                        setBlogFormData(prev => ({ ...prev, image: file.name }));
                      }
                    }}
                    style={{ display: 'none' }}
                    id="blog-image-upload-edit"
                  />
                  <label
                    htmlFor="blog-image-upload-edit"
                    style={{
                      backgroundColor: '#3b82f6',
                      color: 'white',
                      padding: '8px 16px',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      border: 'none',
                      fontSize: '14px'
                    }}
                  >
                    Browse
                  </label>
                  <FormInput
                    type="text"
                    value={selectedBlogImageFile ? selectedBlogImageFile.name : blogFormData.image}
                    onChange={(e) => setBlogFormData(prev => ({ ...prev, image: e.target.value }))}
                    placeholder="Select image file or enter image filename..."
                    style={{ flex: 1 }}
                  />
                </div>
                {isUploadingBlogImage && (
                  <div style={{ width: '100%', backgroundColor: '#f3f4f6', borderRadius: '4px', height: '8px' }}>
                    <div
                      style={{
                        width: `${blogImageUploadProgress}%`,
                        height: '100%',
                        backgroundColor: '#3b82f6',
                        borderRadius: '4px',
                        transition: 'width 0.3s ease'
                      }}
                    />
                  </div>
                )}
                {blogImageUploadError && (
                  <div style={{ color: '#dc2626', fontSize: '14px' }}>
                    {blogImageUploadError}
                  </div>
                )}
              </div>
            </FormField>
          </FormRow>
          <FormField>
            <FormLabel>
              Content: <span style={{ color: '#dc2626' }}>*</span>
            </FormLabel>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              <FormTextarea
                value={blogFormData.description}
                placeholder="Click 'Edit Content' for full editor..."
                rows={3}
                style={{ flex: 1, backgroundColor: '#f9fafb', cursor: 'pointer' }}
                readOnly
                onClick={openBlogContentEditor}
              />
              <button
                type="button"
                onClick={openBlogContentEditor}
                style={{
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  whiteSpace: 'nowrap',
                  height: 'fit-content',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                📝 Edit Content
              </button>
            </div>
            {blogFormData.description && (
              <div style={{
                marginTop: '8px',
                padding: '8px 12px',
                backgroundColor: '#f0f9ff',
                borderRadius: '6px',
                border: '1px solid #bae6fd',
                fontSize: '12px',
                color: '#0369a1'
              }}>
                <strong>Characters:</strong> {blogFormData.description.length} | <strong>Words:</strong> {blogFormData.description.split(' ').filter(w => w.length > 0).length}
                {blogFormData.description.length > 100 && (
                  <span style={{ marginLeft: '8px', color: '#059669' }}>✓ Good length</span>
                )}
              </div>
            )}
          </FormField>
        </FormSection>

        {/* SEO Information */}
        <FormSection>
          <FormSectionTitle>🔍 SEO Information</FormSectionTitle>
          <FormField>
            <FormLabel>SEO Keywords:</FormLabel>
            <textarea
              value={blogFormData.seo}
              onChange={(e) => setBlogFormData(prev => ({ ...prev, seo: e.target.value }))}
              placeholder="Enter SEO keywords, tags, or search terms (one per line or comma-separated)..."
              rows={6}
              style={{
                width: '100%',
                padding: '12px',
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '14px',
                resize: 'vertical',
                fontFamily: 'inherit'
              }}
            />
          </FormField>
        </FormSection>

        <FormActions>
          <CancelButton
            type="button"
            onClick={() => setActiveSection('blogs')}
            loading={blogLoading}
          >
            Cancel
          </CancelButton>
          <SubmitButton
            type="button"
            onClick={submitEditBlog}
            loading={blogLoading}
            disabled={blogLoading || !blogFormData.title.trim() || !blogFormData.description.trim()}
          >
            {blogLoading ? 'Updating...' : 'Update Blog'}
          </SubmitButton>
        </FormActions>
      </FormContainer>
    </FullPageContainer>
  );

  const renderCreateVideoPage = (): React.JSX.Element => (
    <FullPageContainer>
      <FullPageHeader>
        <FullPageTitle>➕ Create New Video Package</FullPageTitle>
        <BackButton onClick={() => setActiveSection('videos')}>
          <FiArrowLeft />
          Back to Video Management
        </BackButton>
      </FullPageHeader>

      <FormContainer>
        {/* Basic Information */}
        <FormSection>
          <FormSectionTitle>🎥 Video Package Information</FormSectionTitle>
          <FormRow>
            <FormField>
              <FormLabel>
                Book Details ID: <span style={{ color: '#dc2626' }}>*</span>
              </FormLabel>
              <FormInput
                type="number"
                value={videoFormData.book_details_id}
                onChange={(e) => setVideoFormData(prev => ({ ...prev, book_details_id: e.target.value }))}
                placeholder="Enter book details ID..."
              />
            </FormField>
            <FormField>
              <FormLabel>
                Subject: <span style={{ color: '#dc2626' }}>*</span>
              </FormLabel>
              <FormSelect
                value={videoFormData.subject_id}
                onChange={(e) => setVideoFormData(prev => ({ ...prev, subject_id: e.target.value }))}
              >
                <option value="">Select Subject</option>
                {subjects.map(subject => (
                  <option key={subject.id} value={subject.id}>{subject.name}</option>
                ))}
              </FormSelect>
            </FormField>
          </FormRow>
          <FormRow>
            <FormField>
              <FormLabel>
                Grade: <span style={{ color: '#dc2626' }}>*</span>
              </FormLabel>
              <FormSelect
                value={videoFormData.grade_id}
                onChange={(e) => setVideoFormData(prev => ({ ...prev, grade_id: e.target.value }))}
              >
                <option value="">Select Grade</option>
                {grades.map(grade => (
                  <option key={grade.id} value={grade.id}>{grade.name}</option>
                ))}
              </FormSelect>
            </FormField>
            <FormField>
              <FormLabel style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  checked={videoFormData.is_publish}
                  onChange={(e) => setVideoFormData(prev => ({ ...prev, is_publish: e.target.checked }))}
                />
                Publish Video
              </FormLabel>
            </FormField>
          </FormRow>
        </FormSection>

        {/* Subscription Options */}
        <FormSection>
          <FormSectionTitle>💳 Subscription Options</FormSectionTitle>
          <FormRow>
            <FormField>
              <FormLabel>Six Month Subscription:</FormLabel>
              <FormInput
                type="text"
                value={videoFormData.six_month}
                onChange={(e) => setVideoFormData(prev => ({ ...prev, six_month: e.target.value }))}
                placeholder="Enter 6-month subscription details..."
              />
            </FormField>
            <FormField>
              <FormLabel>Twelve Month Subscription:</FormLabel>
              <FormInput
                type="text"
                value={videoFormData.twelve_month}
                onChange={(e) => setVideoFormData(prev => ({ ...prev, twelve_month: e.target.value }))}
                placeholder="Enter 12-month subscription details..."
              />
            </FormField>
          </FormRow>
        </FormSection>

        <FormActions>
          <CancelButton
            type="button"
            onClick={() => setActiveSection('videos')}
            loading={videoLoading}
          >
            Cancel
          </CancelButton>
          <SubmitButton
            type="button"
            onClick={submitCreateVideo}
            loading={videoLoading}
            disabled={videoLoading || !videoFormData.book_details_id || !videoFormData.subject_id || !videoFormData.grade_id}
          >
            {videoLoading ? 'Creating...' : 'Create Video Package'}
          </SubmitButton>
        </FormActions>
      </FormContainer>
    </FullPageContainer>
  );

  const renderEditVideoPage = (): React.JSX.Element => (
    <FullPageContainer>
      <FullPageHeader>
        <FullPageTitle>✏️ Edit Video Package #{selectedVideo?.id}</FullPageTitle>
        <BackButton onClick={() => setActiveSection('videos')}>
          <FiArrowLeft />
          Back to Video Management
        </BackButton>
      </FullPageHeader>

      <FormContainer>
        {/* Basic Information */}
        <FormSection>
          <FormSectionTitle>🎥 Video Package Information</FormSectionTitle>
          <FormRow>
            <FormField>
              <FormLabel>
                Book Details ID: <span style={{ color: '#dc2626' }}>*</span>
              </FormLabel>
              <FormInput
                type="number"
                value={videoFormData.book_details_id}
                onChange={(e) => setVideoFormData(prev => ({ ...prev, book_details_id: e.target.value }))}
                placeholder="Enter book details ID..."
              />
            </FormField>
            <FormField>
              <FormLabel>
                Subject: <span style={{ color: '#dc2626' }}>*</span>
              </FormLabel>
              <FormSelect
                value={videoFormData.subject_id}
                onChange={(e) => setVideoFormData(prev => ({ ...prev, subject_id: e.target.value }))}
              >
                <option value="">Select Subject</option>
                {subjects.map(subject => (
                  <option key={subject.id} value={subject.id}>{subject.name}</option>
                ))}
              </FormSelect>
            </FormField>
          </FormRow>
          <FormRow>
            <FormField>
              <FormLabel>
                Grade: <span style={{ color: '#dc2626' }}>*</span>
              </FormLabel>
              <FormSelect
                value={videoFormData.grade_id}
                onChange={(e) => setVideoFormData(prev => ({ ...prev, grade_id: e.target.value }))}
              >
                <option value="">Select Grade</option>
                {grades.map(grade => (
                  <option key={grade.id} value={grade.id}>{grade.name}</option>
                ))}
              </FormSelect>
            </FormField>
            <FormField>
              <FormLabel style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  checked={videoFormData.is_publish}
                  onChange={(e) => setVideoFormData(prev => ({ ...prev, is_publish: e.target.checked }))}
                />
                Publish Video
              </FormLabel>
            </FormField>
          </FormRow>
        </FormSection>

        {/* Subscription Options */}
        <FormSection>
          <FormSectionTitle>💳 Subscription Options</FormSectionTitle>
          <FormRow>
            <FormField>
              <FormLabel>Six Month Subscription:</FormLabel>
              <FormInput
                type="text"
                value={videoFormData.six_month}
                onChange={(e) => setVideoFormData(prev => ({ ...prev, six_month: e.target.value }))}
                placeholder="Enter 6-month subscription details..."
              />
            </FormField>
            <FormField>
              <FormLabel>Twelve Month Subscription:</FormLabel>
              <FormInput
                type="text"
                value={videoFormData.twelve_month}
                onChange={(e) => setVideoFormData(prev => ({ ...prev, twelve_month: e.target.value }))}
                placeholder="Enter 12-month subscription details..."
              />
            </FormField>
          </FormRow>
        </FormSection>

        <FormActions>
          <CancelButton
            type="button"
            onClick={() => setActiveSection('videos')}
            loading={videoLoading}
          >
            Cancel
          </CancelButton>
          <SubmitButton
            type="button"
            onClick={submitEditVideo}
            loading={videoLoading}
            disabled={videoLoading || !videoFormData.book_details_id || !videoFormData.subject_id || !videoFormData.grade_id}
          >
            {videoLoading ? 'Updating...' : 'Update Video Package'}
          </SubmitButton>
        </FormActions>
      </FormContainer>
    </FullPageContainer>
  );

  const renderCreatePackagePage = (): React.JSX.Element => (
    <FullPageContainer>
      <FullPageHeader>
        <FullPageTitle>➕ Create New Video Package</FullPageTitle>
        <BackButton onClick={() => setActiveSection('packages')}>
          <FiArrowLeft />
          Back to Package Management
        </BackButton>
      </FullPageHeader>

      <FormContainer>
        {/* Basic Information */}
        <FormSection>
          <FormSectionTitle>📦 Package Information</FormSectionTitle>
          <FormRow>
            <FormField>
              <FormLabel>
                Package Name: <span style={{ color: '#dc2626' }}>*</span>
              </FormLabel>
              <FormInput
                type="text"
                value={packageFormData.name}
                onChange={(e) => setPackageFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Enter package name..."
              />
            </FormField>
            <FormField>
              <FormLabel>
                Annual Price: <span style={{ color: '#dc2626' }}>*</span>
              </FormLabel>
              <FormInput
                type="number"
                value={packageFormData.annual_price}
                onChange={(e) => setPackageFormData(prev => ({ ...prev, annual_price: e.target.value }))}
                placeholder="Enter annual price..."
              />
            </FormField>
          </FormRow>
          <FormRow>
            <FormField>
              <FormLabel>Discount Price:</FormLabel>
              <FormInput
                type="number"
                value={packageFormData.discount_price}
                onChange={(e) => setPackageFormData(prev => ({ ...prev, discount_price: e.target.value }))}
                placeholder="Enter discount price..."
              />
            </FormField>
            <FormField>
              <FormLabel style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  checked={packageFormData.is_active}
                  onChange={(e) => setPackageFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                />
                Active Package
              </FormLabel>
            </FormField>
          </FormRow>
          <FormField>
            <FormLabel>Description:</FormLabel>
            <FormTextarea
              value={packageFormData.description}
              onChange={(e) => setPackageFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Enter package description..."
              rows={3}
            />
          </FormField>
        </FormSection>

        {/* Categories and Location */}
        <FormSection>
          <FormSectionTitle>📂 Categories & Location</FormSectionTitle>
          <FormRow>
            <FormField>
              <FormLabel>
                Subject: <span style={{ color: '#dc2626' }}>*</span>
              </FormLabel>
              <FormSelect
                value={packageFormData.subject_id}
                onChange={(e) => setPackageFormData(prev => ({ ...prev, subject_id: e.target.value }))}
              >
                <option value="">Select Subject</option>
                {subjects.map(subject => (
                  <option key={subject.id} value={subject.id}>{subject.name}</option>
                ))}
              </FormSelect>
            </FormField>
            <FormField>
              <FormLabel>
                Grade: <span style={{ color: '#dc2626' }}>*</span>
              </FormLabel>
              <FormSelect
                value={packageFormData.grade_id}
                onChange={(e) => setPackageFormData(prev => ({ ...prev, grade_id: e.target.value }))}
              >
                <option value="">Select Grade</option>
                {grades.map(grade => (
                  <option key={grade.id} value={grade.id}>{grade.name}</option>
                ))}
              </FormSelect>
            </FormField>
          </FormRow>
          <FormRow>
            <FormField>
              <FormLabel>
                Country: <span style={{ color: '#dc2626' }}>*</span>
              </FormLabel>
              <FormSelect
                value={packageFormData.country_id}
                onChange={(e) => setPackageFormData(prev => ({ ...prev, country_id: e.target.value }))}
              >
                <option value="">Select Country</option>
                {countries.map(country => (
                  <option key={country.id} value={country.id}>{country.name}</option>
                ))}
              </FormSelect>
            </FormField>
            <FormField>
              <FormLabel>State:</FormLabel>
              <FormInput
                type="text"
                value={packageFormData.state_id}
                onChange={(e) => setPackageFormData(prev => ({ ...prev, state_id: e.target.value }))}
                placeholder="Enter state ID..."
              />
            </FormField>
          </FormRow>
        </FormSection>

        {/* Additional Information */}
        <FormSection>
          <FormSectionTitle>⚙️ Additional Information</FormSectionTitle>
          <FormRow>
            <FormField>
              <FormLabel>Features:</FormLabel>
              <FormTextarea
                value={packageFormData.features}
                onChange={(e) => setPackageFormData(prev => ({ ...prev, features: e.target.value }))}
                placeholder="Enter package features..."
                rows={3}
              />
            </FormField>
            <FormField>
              <FormLabel>Image URL:</FormLabel>
              <FormInput
                type="text"
                value={packageFormData.image}
                onChange={(e) => setPackageFormData(prev => ({ ...prev, image: e.target.value }))}
                placeholder="Enter image URL..."
              />
            </FormField>
          </FormRow>
          <FormField>
            <FormLabel>Metadata (JSON):</FormLabel>
            <FormTextarea
              value={packageFormData.metadata}
              onChange={(e) => setPackageFormData(prev => ({ ...prev, metadata: e.target.value }))}
              placeholder="Enter metadata in JSON format..."
              rows={2}
            />
          </FormField>
        </FormSection>

        <FormActions>
          <CancelButton
            type="button"
            onClick={() => setActiveSection('packages')}
            loading={packageLoading}
          >
            Cancel
          </CancelButton>
          <SubmitButton
            type="button"
            onClick={submitCreatePackage}
            loading={packageLoading}
            disabled={packageLoading || !packageFormData.name.trim() || !packageFormData.annual_price.trim() || !packageFormData.country_id || !packageFormData.grade_id || !packageFormData.subject_id}
          >
            {packageLoading ? 'Creating...' : 'Create Package'}
          </SubmitButton>
        </FormActions>
      </FormContainer>
    </FullPageContainer>
  );

  const renderEditPackagePage = (): React.JSX.Element => (
    <FullPageContainer>
      <FullPageHeader>
        <FullPageTitle>✏️ Edit Video Package: {selectedPackage?.name}</FullPageTitle>
        <BackButton onClick={() => setActiveSection('packages')}>
          <FiArrowLeft />
          Back to Package Management
        </BackButton>
      </FullPageHeader>

      <FormContainer>
        {/* Basic Information */}
        <FormSection>
          <FormSectionTitle>📦 Package Information</FormSectionTitle>
          <FormRow>
            <FormField>
              <FormLabel>
                Package Name: <span style={{ color: '#dc2626' }}>*</span>
              </FormLabel>
              <FormInput
                type="text"
                value={packageFormData.name}
                onChange={(e) => setPackageFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Enter package name..."
              />
            </FormField>
            <FormField>
              <FormLabel>
                Annual Price: <span style={{ color: '#dc2626' }}>*</span>
              </FormLabel>
              <FormInput
                type="number"
                value={packageFormData.annual_price}
                onChange={(e) => setPackageFormData(prev => ({ ...prev, annual_price: e.target.value }))}
                placeholder="Enter annual price..."
              />
            </FormField>
          </FormRow>
          <FormRow>
            <FormField>
              <FormLabel>Discount Price:</FormLabel>
              <FormInput
                type="number"
                value={packageFormData.discount_price}
                onChange={(e) => setPackageFormData(prev => ({ ...prev, discount_price: e.target.value }))}
                placeholder="Enter discount price..."
              />
            </FormField>
            <FormField>
              <FormLabel style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  checked={packageFormData.is_active}
                  onChange={(e) => setPackageFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                />
                Active Package
              </FormLabel>
            </FormField>
          </FormRow>
          <FormField>
            <FormLabel>Description:</FormLabel>
            <FormTextarea
              value={packageFormData.description}
              onChange={(e) => setPackageFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Enter package description..."
              rows={3}
            />
          </FormField>
        </FormSection>

        {/* Categories and Location */}
        <FormSection>
          <FormSectionTitle>📂 Categories & Location</FormSectionTitle>
          <FormRow>
            <FormField>
              <FormLabel>
                Subject: <span style={{ color: '#dc2626' }}>*</span>
              </FormLabel>
              <FormSelect
                value={packageFormData.subject_id}
                onChange={(e) => setPackageFormData(prev => ({ ...prev, subject_id: e.target.value }))}
              >
                <option value="">Select Subject</option>
                {subjects.map(subject => (
                  <option key={subject.id} value={subject.id}>{subject.name}</option>
                ))}
              </FormSelect>
            </FormField>
            <FormField>
              <FormLabel>
                Grade: <span style={{ color: '#dc2626' }}>*</span>
              </FormLabel>
              <FormSelect
                value={packageFormData.grade_id}
                onChange={(e) => setPackageFormData(prev => ({ ...prev, grade_id: e.target.value }))}
              >
                <option value="">Select Grade</option>
                {grades.map(grade => (
                  <option key={grade.id} value={grade.id}>{grade.name}</option>
                ))}
              </FormSelect>
            </FormField>
          </FormRow>
          <FormRow>
            <FormField>
              <FormLabel>
                Country: <span style={{ color: '#dc2626' }}>*</span>
              </FormLabel>
              <FormSelect
                value={packageFormData.country_id}
                onChange={(e) => setPackageFormData(prev => ({ ...prev, country_id: e.target.value }))}
              >
                <option value="">Select Country</option>
                {countries.map(country => (
                  <option key={country.id} value={country.id}>{country.name}</option>
                ))}
              </FormSelect>
            </FormField>
            <FormField>
              <FormLabel>State:</FormLabel>
              <FormInput
                type="text"
                value={packageFormData.state_id}
                onChange={(e) => setPackageFormData(prev => ({ ...prev, state_id: e.target.value }))}
                placeholder="Enter state ID..."
              />
            </FormField>
          </FormRow>
        </FormSection>

        {/* Additional Information */}
        <FormSection>
          <FormSectionTitle>⚙️ Additional Information</FormSectionTitle>
          <FormRow>
            <FormField>
              <FormLabel>Features:</FormLabel>
              <FormTextarea
                value={packageFormData.features}
                onChange={(e) => setPackageFormData(prev => ({ ...prev, features: e.target.value }))}
                placeholder="Enter package features..."
                rows={3}
              />
            </FormField>
            <FormField>
              <FormLabel>Image URL:</FormLabel>
              <FormInput
                type="text"
                value={packageFormData.image}
                onChange={(e) => setPackageFormData(prev => ({ ...prev, image: e.target.value }))}
                placeholder="Enter image URL..."
              />
            </FormField>
          </FormRow>
          <FormField>
            <FormLabel>Metadata (JSON):</FormLabel>
            <FormTextarea
              value={packageFormData.metadata}
              onChange={(e) => setPackageFormData(prev => ({ ...prev, metadata: e.target.value }))}
              placeholder="Enter metadata in JSON format..."
              rows={2}
            />
          </FormField>
        </FormSection>

        <FormActions>
          <CancelButton
            type="button"
            onClick={() => setActiveSection('packages')}
            loading={packageLoading}
          >
            Cancel
          </CancelButton>
          <SubmitButton
            type="button"
            onClick={submitEditPackage}
            loading={packageLoading}
            disabled={packageLoading || !packageFormData.name.trim() || !packageFormData.annual_price.trim() || !packageFormData.country_id || !packageFormData.grade_id || !packageFormData.subject_id}
          >
            {packageLoading ? 'Updating...' : 'Update Package'}
          </SubmitButton>
        </FormActions>
      </FormContainer>
    </FullPageContainer>
  );

  const renderCreateOnlineClassPage = (): React.JSX.Element => (
    <FullPageContainer>
      <FullPageHeader>
        <FullPageTitle>➕ Create New Online Class</FullPageTitle>
        <BackButton onClick={() => setActiveSection('online-classes')}>
          <FiArrowLeft />
          Back to Online Classes
        </BackButton>
      </FullPageHeader>

      <FormContainer>
        {/* Basic Information */}
        <FormSection>
          <FormSectionTitle>💻 Course Information</FormSectionTitle>
          <FormRow>
            <FormField>
              <FormLabel>
                Course Name: <span style={{ color: '#dc2626' }}>*</span>
                <span style={{ marginLeft: '4px', color: '#6b7280', cursor: 'help' }} title="Enter the full course name including grade, subject, and year (e.g., Algebra 1 (2024-2025) - Online)">ℹ️</span>
              </FormLabel>
              <FormInput
                type="text"
                value={onlineClassFormData.course_name}
                onChange={(e) => setOnlineClassFormData(prev => ({ ...prev, course_name: e.target.value }))}
                placeholder="e.g., Algebra 1 (2024-2025) - Online..."
                title="Enter the full course name including grade, subject, and year"
              />
            </FormField>
            <FormField>
              <FormLabel>
                Program: <span style={{ color: '#dc2626' }}>*</span>
                <span style={{ marginLeft: '4px', color: '#6b7280', cursor: 'help' }} title="Enter the program name (e.g., Advanced Math, English Literature)">ℹ️</span>
              </FormLabel>
              <FormInput
                type="text"
                value={onlineClassFormData.program}
                onChange={(e) => setOnlineClassFormData(prev => ({ ...prev, program: e.target.value }))}
                placeholder="e.g., Advanced Math, SAT Prep..."
                title="Enter the program name (e.g., Advanced Math, English Literature)"
              />
            </FormField>
          </FormRow>
          <FormRow>
            <FormField>
              <FormLabel>
                Instructor:
                <span style={{ marginLeft: '4px', color: '#6b7280', cursor: 'help' }} title="Enter the instructor's full name">ℹ️</span>
              </FormLabel>
              <FormInput
                type="text"
                value={onlineClassFormData.instructor}
                onChange={(e) => setOnlineClassFormData(prev => ({ ...prev, instructor: e.target.value }))}
                placeholder="e.g., John Smith..."
                title="Enter the instructor's full name"
              />
            </FormField>
            <FormField>
              <FormLabel>
                Level:
                <span style={{ marginLeft: '4px', color: '#6b7280', cursor: 'help' }} title="Enter the course level (e.g., Beginner, Intermediate, Advanced, Grade 9-10)">ℹ️</span>
              </FormLabel>
              <FormInput
                type="text"
                value={onlineClassFormData.level}
                onChange={(e) => setOnlineClassFormData(prev => ({ ...prev, level: e.target.value }))}
                placeholder="e.g., Beginner, Grade 9-10..."
                title="Enter the course level (e.g., Beginner, Intermediate, Advanced, Grade 9-10)"
              />
            </FormField>
          </FormRow>
          <FormField>
            <FormLabel>Description:</FormLabel>
            <RichTextEditor
              value={onlineClassFormData.description}
              onChange={(value) => setOnlineClassFormData(prev => ({ ...prev, description: value }))}
              placeholder="Enter course description with formatting..."
              showImageUpload={false}
              minHeight="150px"
            />
          </FormField>
        </FormSection>

        {/* Schedule & Timing */}
        <FormSection>
          <FormSectionTitle>📅 Schedule & Timing</FormSectionTitle>
          <FormRow>
            <FormField>
              <FormLabel>
                Day:
                <span style={{ marginLeft: '4px', color: '#6b7280', cursor: 'help' }} title="Select the day(s) of the week for this class">ℹ️</span>
              </FormLabel>
              <FormSelect
                value={onlineClassFormData.day}
                onChange={(e) => setOnlineClassFormData(prev => ({ ...prev, day: e.target.value }))}
                title="Select the day(s) of the week for this class"
              >
                <option value="">Select Day...</option>
                <option value="Monday">Monday</option>
                <option value="Tuesday">Tuesday</option>
                <option value="Wednesday">Wednesday</option>
                <option value="Thursday">Thursday</option>
                <option value="Friday">Friday</option>
                <option value="Saturday">Saturday</option>
                <option value="Sunday">Sunday</option>
                <option value="Monday & Wednesday">Monday & Wednesday</option>
                <option value="Tuesday & Thursday">Tuesday & Thursday</option>
                <option value="Monday, Wednesday & Friday">Monday, Wednesday & Friday</option>
                <option value="Weekdays">Weekdays (Mon-Fri)</option>
                <option value="Weekends">Weekends (Sat-Sun)</option>
              </FormSelect>
            </FormField>
            <FormField>
              <FormLabel>Year:</FormLabel>
              <FormInput
                type="text"
                value={onlineClassFormData.year}
                onChange={(e) => setOnlineClassFormData(prev => ({ ...prev, year: e.target.value }))}
                placeholder="Enter year..."
              />
            </FormField>
          </FormRow>
          <FormRow>
            <FormField>
              <FormLabel>Start Time:</FormLabel>
              <FormInput
                type="text"
                value={onlineClassFormData.time1}
                onChange={(e) => setOnlineClassFormData(prev => ({ ...prev, time1: e.target.value }))}
                placeholder="Enter start time..."
              />
            </FormField>
            <FormField>
              <FormLabel>End Time:</FormLabel>
              <FormInput
                type="text"
                value={onlineClassFormData.time2}
                onChange={(e) => setOnlineClassFormData(prev => ({ ...prev, time2: e.target.value }))}
                placeholder="Enter end time..."
              />
            </FormField>
          </FormRow>
          <FormRow>
            <FormField>
              <FormLabel>Start Date:</FormLabel>
              <FormInput
                type="date"
                value={onlineClassFormData.date1}
                onChange={(e) => setOnlineClassFormData(prev => ({ ...prev, date1: e.target.value }))}
              />
            </FormField>
            <FormField>
              <FormLabel>End Date:</FormLabel>
              <FormInput
                type="date"
                value={onlineClassFormData.date2}
                onChange={(e) => setOnlineClassFormData(prev => ({ ...prev, date2: e.target.value }))}
              />
            </FormField>
          </FormRow>
        </FormSection>

        {/* Pricing & Capacity */}
        <FormSection>
          <FormSectionTitle>💰 Pricing & Capacity</FormSectionTitle>
          <FormRow>
            <FormField>
              <FormLabel>Fee:</FormLabel>
              <FormInput
                type="number"
                value={onlineClassFormData.fee}
                onChange={(e) => setOnlineClassFormData(prev => ({ ...prev, fee: e.target.value }))}
                placeholder="Enter fee..."
              />
            </FormField>
            <FormField>
              <FormLabel>Available Slots:</FormLabel>
              <FormInput
                type="number"
                value={onlineClassFormData.slots}
                onChange={(e) => setOnlineClassFormData(prev => ({ ...prev, slots: parseInt(e.target.value) || 20 }))}
                placeholder="Enter number of slots..."
              />
            </FormField>
          </FormRow>
          <FormRow>
            <FormField>
              <FormLabel>Total Hours:</FormLabel>
              <FormInput
                type="text"
                value={onlineClassFormData.total_hours}
                onChange={(e) => setOnlineClassFormData(prev => ({ ...prev, total_hours: e.target.value }))}
                placeholder="Enter total hours..."
              />
            </FormField>
            <FormField>
              <FormLabel>Zoom Link:</FormLabel>
              <FormInput
                type="url"
                value={onlineClassFormData.zoom_link}
                onChange={(e) => setOnlineClassFormData(prev => ({ ...prev, zoom_link: e.target.value }))}
                placeholder="Enter Zoom meeting link..."
              />
            </FormField>
          </FormRow>
        </FormSection>

        <FormActions>
          <CancelButton
            type="button"
            onClick={() => setActiveSection('online-classes')}
            loading={loading}
          >
            Cancel
          </CancelButton>
          <SubmitButton
            type="button"
            onClick={submitCreateOnlineClass}
            loading={loading}
            disabled={loading || !onlineClassFormData.course_name.trim()}
          >
            {loading ? 'Creating...' : 'Create Online Class'}
          </SubmitButton>
        </FormActions>
      </FormContainer>
    </FullPageContainer>
  );

  const renderEditOnlineClassPage = (): React.JSX.Element => (
    <FullPageContainer>
      <FullPageHeader>
        <FullPageTitle>✏️ Edit Online Class: {selectedOnlineClass?.title || selectedOnlineClass?.course_name}</FullPageTitle>
        <BackButton onClick={() => setActiveSection('online-classes')}>
          <FiArrowLeft />
          Back to Online Classes
        </BackButton>
      </FullPageHeader>

      <FormContainer>
        {/* Basic Information */}
        <FormSection>
          <FormSectionTitle>💻 Course Information</FormSectionTitle>
          <FormRow>
            <FormField>
              <FormLabel>
                Course Name: <span style={{ color: '#dc2626' }}>*</span>
              </FormLabel>
              <FormInput
                type="text"
                value={onlineClassFormData.course_name}
                onChange={(e) => setOnlineClassFormData(prev => ({ ...prev, course_name: e.target.value }))}
                placeholder="Enter course name..."
              />
            </FormField>
            <FormField>
              <FormLabel>Program:</FormLabel>
              <FormInput
                type="text"
                value={onlineClassFormData.program}
                onChange={(e) => setOnlineClassFormData(prev => ({ ...prev, program: e.target.value }))}
                placeholder="Enter program..."
              />
            </FormField>
          </FormRow>
          <FormRow>
            <FormField>
              <FormLabel>Instructor:</FormLabel>
              <FormInput
                type="text"
                value={onlineClassFormData.instructor}
                onChange={(e) => setOnlineClassFormData(prev => ({ ...prev, instructor: e.target.value }))}
                placeholder="Enter instructor name..."
              />
            </FormField>
            <FormField>
              <FormLabel>Level:</FormLabel>
              <FormInput
                type="text"
                value={onlineClassFormData.level}
                onChange={(e) => setOnlineClassFormData(prev => ({ ...prev, level: e.target.value }))}
                placeholder="Enter level..."
              />
            </FormField>
          </FormRow>
          <FormField>
            <FormLabel>Description:</FormLabel>
            <RichTextEditor
              value={onlineClassFormData.description}
              onChange={(value) => setOnlineClassFormData(prev => ({ ...prev, description: value }))}
              placeholder="Enter course description with formatting..."
              showImageUpload={false}
              minHeight="150px"
            />
          </FormField>
        </FormSection>

        {/* Schedule & Timing */}
        <FormSection>
          <FormSectionTitle>📅 Schedule & Timing</FormSectionTitle>
          <FormRow>
            <FormField>
              <FormLabel>Day:</FormLabel>
              <FormInput
                type="text"
                value={onlineClassFormData.day}
                onChange={(e) => setOnlineClassFormData(prev => ({ ...prev, day: e.target.value }))}
                placeholder="Enter day..."
              />
            </FormField>
            <FormField>
              <FormLabel>Year:</FormLabel>
              <FormInput
                type="text"
                value={onlineClassFormData.year}
                onChange={(e) => setOnlineClassFormData(prev => ({ ...prev, year: e.target.value }))}
                placeholder="Enter year..."
              />
            </FormField>
          </FormRow>
          <FormRow>
            <FormField>
              <FormLabel>Start Time:</FormLabel>
              <FormInput
                type="text"
                value={onlineClassFormData.time1}
                onChange={(e) => setOnlineClassFormData(prev => ({ ...prev, time1: e.target.value }))}
                placeholder="Enter start time..."
              />
            </FormField>
            <FormField>
              <FormLabel>End Time:</FormLabel>
              <FormInput
                type="text"
                value={onlineClassFormData.time2}
                onChange={(e) => setOnlineClassFormData(prev => ({ ...prev, time2: e.target.value }))}
                placeholder="Enter end time..."
              />
            </FormField>
          </FormRow>
          <FormRow>
            <FormField>
              <FormLabel>Start Date:</FormLabel>
              <FormInput
                type="date"
                value={onlineClassFormData.date1}
                onChange={(e) => setOnlineClassFormData(prev => ({ ...prev, date1: e.target.value }))}
              />
            </FormField>
            <FormField>
              <FormLabel>End Date:</FormLabel>
              <FormInput
                type="date"
                value={onlineClassFormData.date2}
                onChange={(e) => setOnlineClassFormData(prev => ({ ...prev, date2: e.target.value }))}
              />
            </FormField>
          </FormRow>
        </FormSection>

        {/* Pricing & Capacity */}
        <FormSection>
          <FormSectionTitle>💰 Pricing & Capacity</FormSectionTitle>
          <FormRow>
            <FormField>
              <FormLabel>Fee:</FormLabel>
              <FormInput
                type="number"
                value={onlineClassFormData.fee}
                onChange={(e) => setOnlineClassFormData(prev => ({ ...prev, fee: e.target.value }))}
                placeholder="Enter fee..."
              />
            </FormField>
            <FormField>
              <FormLabel>Available Slots:</FormLabel>
              <FormInput
                type="number"
                value={onlineClassFormData.slots}
                onChange={(e) => setOnlineClassFormData(prev => ({ ...prev, slots: parseInt(e.target.value) || 20 }))}
                placeholder="Enter number of slots..."
              />
            </FormField>
          </FormRow>
          <FormRow>
            <FormField>
              <FormLabel>Total Hours:</FormLabel>
              <FormInput
                type="text"
                value={onlineClassFormData.total_hours}
                onChange={(e) => setOnlineClassFormData(prev => ({ ...prev, total_hours: e.target.value }))}
                placeholder="Enter total hours..."
              />
            </FormField>
            <FormField>
              <FormLabel>Zoom Link:</FormLabel>
              <FormInput
                type="url"
                value={onlineClassFormData.zoom_link}
                onChange={(e) => setOnlineClassFormData(prev => ({ ...prev, zoom_link: e.target.value }))}
                placeholder="Enter Zoom meeting link..."
              />
            </FormField>
          </FormRow>
        </FormSection>

        <FormActions>
          <CancelButton
            type="button"
            onClick={() => setActiveSection('online-classes')}
            loading={loading}
          >
            Cancel
          </CancelButton>
          <SubmitButton
            type="button"
            onClick={submitEditOnlineClass}
            loading={loading}
            disabled={loading || !onlineClassFormData.course_name.trim()}
          >
            {loading ? 'Updating...' : 'Update Online Class'}
          </SubmitButton>
        </FormActions>
      </FormContainer>
    </FullPageContainer>
  );

  const renderCreateInPersonClassPage = (): React.JSX.Element => (
    <FullPageContainer>
      <FullPageHeader>
        <FullPageTitle>➕ Create New In-Person Class</FullPageTitle>
        <BackButton onClick={() => setActiveSection('inperson-classes')}>
          <FiArrowLeft />
          Back to In-Person Classes
        </BackButton>
      </FullPageHeader>

      <FormContainer>
        {/* Basic Information */}
        <FormSection>
          <FormSectionTitle>🏫 Course Information</FormSectionTitle>
          <FormRow>
            <FormField>
              <FormLabel>
                Course Name: <span style={{ color: '#dc2626' }}>*</span>
              </FormLabel>
              <FormInput
                type="text"
                value={inPersonClassFormData.course_name}
                onChange={(e) => setInPersonClassFormData(prev => ({ ...prev, course_name: e.target.value }))}
                placeholder="Enter course name..."
              />
            </FormField>
            <FormField>
              <FormLabel>Program:</FormLabel>
              <FormInput
                type="text"
                value={inPersonClassFormData.program}
                onChange={(e) => setInPersonClassFormData(prev => ({ ...prev, program: e.target.value }))}
                placeholder="Enter program..."
              />
            </FormField>
          </FormRow>
          <FormRow>
            <FormField>
              <FormLabel>Instructor:</FormLabel>
              <FormInput
                type="text"
                value={inPersonClassFormData.instructor}
                onChange={(e) => setInPersonClassFormData(prev => ({ ...prev, instructor: e.target.value }))}
                placeholder="Enter instructor name..."
              />
            </FormField>
            <FormField>
              <FormLabel>Level:</FormLabel>
              <FormInput
                type="text"
                value={inPersonClassFormData.level}
                onChange={(e) => setInPersonClassFormData(prev => ({ ...prev, level: e.target.value }))}
                placeholder="Enter level..."
              />
            </FormField>
          </FormRow>
          <FormField>
            <FormLabel>Description:</FormLabel>
            <RichTextEditor
              value={inPersonClassFormData.description}
              onChange={(value) => setInPersonClassFormData(prev => ({ ...prev, description: value }))}
              placeholder="Enter course description with formatting..."
              showImageUpload={false}
              minHeight="150px"
            />
          </FormField>
        </FormSection>

        {/* Schedule & Timing */}
        <FormSection>
          <FormSectionTitle>📅 Schedule & Timing</FormSectionTitle>
          <FormRow>
            <FormField>
              <FormLabel>Day:</FormLabel>
              <FormInput
                type="text"
                value={inPersonClassFormData.day}
                onChange={(e) => setInPersonClassFormData(prev => ({ ...prev, day: e.target.value }))}
                placeholder="Enter day..."
              />
            </FormField>
            <FormField>
              <FormLabel>Year:</FormLabel>
              <FormInput
                type="text"
                value={inPersonClassFormData.year}
                onChange={(e) => setInPersonClassFormData(prev => ({ ...prev, year: e.target.value }))}
                placeholder="Enter year..."
              />
            </FormField>
          </FormRow>
          <FormRow>
            <FormField>
              <FormLabel>Start Time:</FormLabel>
              <FormInput
                type="text"
                value={inPersonClassFormData.time1}
                onChange={(e) => setInPersonClassFormData(prev => ({ ...prev, time1: e.target.value }))}
                placeholder="Enter start time..."
              />
            </FormField>
            <FormField>
              <FormLabel>End Time:</FormLabel>
              <FormInput
                type="text"
                value={inPersonClassFormData.time2}
                onChange={(e) => setInPersonClassFormData(prev => ({ ...prev, time2: e.target.value }))}
                placeholder="Enter end time..."
              />
            </FormField>
          </FormRow>
          <FormRow>
            <FormField>
              <FormLabel>Start Date:</FormLabel>
              <FormInput
                type="date"
                value={inPersonClassFormData.date1}
                onChange={(e) => setInPersonClassFormData(prev => ({ ...prev, date1: e.target.value }))}
              />
            </FormField>
            <FormField>
              <FormLabel>End Date:</FormLabel>
              <FormInput
                type="date"
                value={inPersonClassFormData.date2}
                onChange={(e) => setInPersonClassFormData(prev => ({ ...prev, date2: e.target.value }))}
              />
            </FormField>
          </FormRow>
        </FormSection>

        {/* Pricing & Capacity */}
        <FormSection>
          <FormSectionTitle>💰 Pricing & Capacity</FormSectionTitle>
          <FormRow>
            <FormField>
              <FormLabel>Fee:</FormLabel>
              <FormInput
                type="number"
                value={inPersonClassFormData.fee}
                onChange={(e) => setInPersonClassFormData(prev => ({ ...prev, fee: e.target.value }))}
                placeholder="Enter fee..."
              />
            </FormField>
            <FormField>
              <FormLabel>Available Slots:</FormLabel>
              <FormInput
                type="number"
                value={inPersonClassFormData.slots}
                onChange={(e) => setInPersonClassFormData(prev => ({ ...prev, slots: parseInt(e.target.value) || 20 }))}
                placeholder="Enter number of slots..."
              />
            </FormField>
          </FormRow>
          <FormField>
            <FormLabel>Total Hours:</FormLabel>
            <FormInput
              type="text"
              value={inPersonClassFormData.total_hours}
              onChange={(e) => setInPersonClassFormData(prev => ({ ...prev, total_hours: e.target.value }))}
              placeholder="Enter total hours..."
            />
          </FormField>
        </FormSection>

        <FormActions>
          <CancelButton
            type="button"
            onClick={() => setActiveSection('inperson-classes')}
            loading={loading}
          >
            Cancel
          </CancelButton>
          <SubmitButton
            type="button"
            onClick={submitCreateInPersonClass}
            loading={loading}
            disabled={loading || !inPersonClassFormData.course_name.trim()}
          >
            {loading ? 'Creating...' : 'Create In-Person Class'}
          </SubmitButton>
        </FormActions>
      </FormContainer>
    </FullPageContainer>
  );

  const renderEditInPersonClassPage = (): React.JSX.Element => (
    <FullPageContainer>
      <FullPageHeader>
        <FullPageTitle>✏️ Edit In-Person Class: {selectedInPersonClass?.title || selectedInPersonClass?.course_name}</FullPageTitle>
        <BackButton onClick={() => setActiveSection('inperson-classes')}>
          <FiArrowLeft />
          Back to In-Person Classes
        </BackButton>
      </FullPageHeader>

      <FormContainer>
        {/* Basic Information */}
        <FormSection>
          <FormSectionTitle>🏫 Course Information</FormSectionTitle>
          <FormRow>
            <FormField>
              <FormLabel>
                Course Name: <span style={{ color: '#dc2626' }}>*</span>
              </FormLabel>
              <FormInput
                type="text"
                value={inPersonClassFormData.course_name}
                onChange={(e) => setInPersonClassFormData(prev => ({ ...prev, course_name: e.target.value }))}
                placeholder="Enter course name..."
              />
            </FormField>
            <FormField>
              <FormLabel>Program:</FormLabel>
              <FormInput
                type="text"
                value={inPersonClassFormData.program}
                onChange={(e) => setInPersonClassFormData(prev => ({ ...prev, program: e.target.value }))}
                placeholder="Enter program..."
              />
            </FormField>
          </FormRow>
          <FormRow>
            <FormField>
              <FormLabel>Instructor:</FormLabel>
              <FormInput
                type="text"
                value={inPersonClassFormData.instructor}
                onChange={(e) => setInPersonClassFormData(prev => ({ ...prev, instructor: e.target.value }))}
                placeholder="Enter instructor name..."
              />
            </FormField>
            <FormField>
              <FormLabel>Level:</FormLabel>
              <FormInput
                type="text"
                value={inPersonClassFormData.level}
                onChange={(e) => setInPersonClassFormData(prev => ({ ...prev, level: e.target.value }))}
                placeholder="Enter level..."
              />
            </FormField>
          </FormRow>
          <FormField>
            <FormLabel>Description:</FormLabel>
            <RichTextEditor
              value={inPersonClassFormData.description}
              onChange={(value) => setInPersonClassFormData(prev => ({ ...prev, description: value }))}
              placeholder="Enter course description with formatting..."
              showImageUpload={false}
              minHeight="150px"
            />
          </FormField>
        </FormSection>

        {/* Schedule & Timing */}
        <FormSection>
          <FormSectionTitle>📅 Schedule & Timing</FormSectionTitle>
          <FormRow>
            <FormField>
              <FormLabel>Day:</FormLabel>
              <FormInput
                type="text"
                value={inPersonClassFormData.day}
                onChange={(e) => setInPersonClassFormData(prev => ({ ...prev, day: e.target.value }))}
                placeholder="Enter day..."
              />
            </FormField>
            <FormField>
              <FormLabel>Year:</FormLabel>
              <FormInput
                type="text"
                value={inPersonClassFormData.year}
                onChange={(e) => setInPersonClassFormData(prev => ({ ...prev, year: e.target.value }))}
                placeholder="Enter year..."
              />
            </FormField>
          </FormRow>
          <FormRow>
            <FormField>
              <FormLabel>Start Time:</FormLabel>
              <FormInput
                type="text"
                value={inPersonClassFormData.time1}
                onChange={(e) => setInPersonClassFormData(prev => ({ ...prev, time1: e.target.value }))}
                placeholder="Enter start time..."
              />
            </FormField>
            <FormField>
              <FormLabel>End Time:</FormLabel>
              <FormInput
                type="text"
                value={inPersonClassFormData.time2}
                onChange={(e) => setInPersonClassFormData(prev => ({ ...prev, time2: e.target.value }))}
                placeholder="Enter end time..."
              />
            </FormField>
          </FormRow>
          <FormRow>
            <FormField>
              <FormLabel>Start Date:</FormLabel>
              <FormInput
                type="date"
                value={inPersonClassFormData.date1}
                onChange={(e) => setInPersonClassFormData(prev => ({ ...prev, date1: e.target.value }))}
              />
            </FormField>
            <FormField>
              <FormLabel>End Date:</FormLabel>
              <FormInput
                type="date"
                value={inPersonClassFormData.date2}
                onChange={(e) => setInPersonClassFormData(prev => ({ ...prev, date2: e.target.value }))}
              />
            </FormField>
          </FormRow>
        </FormSection>

        {/* Pricing & Capacity */}
        <FormSection>
          <FormSectionTitle>💰 Pricing & Capacity</FormSectionTitle>
          <FormRow>
            <FormField>
              <FormLabel>Fee:</FormLabel>
              <FormInput
                type="number"
                value={inPersonClassFormData.fee}
                onChange={(e) => setInPersonClassFormData(prev => ({ ...prev, fee: e.target.value }))}
                placeholder="Enter fee..."
              />
            </FormField>
            <FormField>
              <FormLabel>Available Slots:</FormLabel>
              <FormInput
                type="number"
                value={inPersonClassFormData.slots}
                onChange={(e) => setInPersonClassFormData(prev => ({ ...prev, slots: parseInt(e.target.value) || 20 }))}
                placeholder="Enter number of slots..."
              />
            </FormField>
          </FormRow>
          <FormField>
            <FormLabel>Total Hours:</FormLabel>
            <FormInput
              type="text"
              value={inPersonClassFormData.total_hours}
              onChange={(e) => setInPersonClassFormData(prev => ({ ...prev, total_hours: e.target.value }))}
              placeholder="Enter total hours..."
            />
          </FormField>
        </FormSection>

        <FormActions>
          <CancelButton
            type="button"
            onClick={() => setActiveSection('inperson-classes')}
            loading={loading}
          >
            Cancel
          </CancelButton>
          <SubmitButton
            type="button"
            onClick={submitEditInPersonClass}
            loading={loading}
            disabled={loading || !inPersonClassFormData.course_name.trim()}
          >
            {loading ? 'Updating...' : 'Update In-Person Class'}
          </SubmitButton>
        </FormActions>
      </FormContainer>
    </FullPageContainer>
  );

  const renderCreateBatchPage = (): React.JSX.Element => (
    <FullPageContainer>
      <FullPageHeader>
        <FullPageTitle>➕ Create New Batch</FullPageTitle>
        <BackButton onClick={() => setActiveSection('batches')}>
          <FiArrowLeft />
          Back to Batches
        </BackButton>
      </FullPageHeader>

      <FormContainer>
        {/* Basic Information */}
        <FormSection>
          <FormSectionTitle>📚 Batch Information</FormSectionTitle>
          <FormRow>
            <FormField>
              <FormLabel>
                Batch Name: <span style={{ color: '#dc2626' }}>*</span>
              </FormLabel>
              <FormInput
                type="text"
                value={batchFormData.batch_name}
                onChange={(e) => setBatchFormData(prev => ({ ...prev, batch_name: e.target.value }))}
                placeholder="Enter batch name..."
              />
            </FormField>
            <FormField>
              <FormLabel>
                Program: <span style={{ color: '#dc2626' }}>*</span>
              </FormLabel>
              <FormInput
                type="text"
                value={batchFormData.program}
                onChange={(e) => setBatchFormData(prev => ({ ...prev, program: e.target.value }))}
                placeholder="Enter program name..."
              />
            </FormField>
          </FormRow>
          <FormRow>
            <FormField>
              <FormLabel>
                Instructor: <span style={{ color: '#dc2626' }}>*</span>
              </FormLabel>
              <FormInput
                type="text"
                value={batchFormData.instructor}
                onChange={(e) => setBatchFormData(prev => ({ ...prev, instructor: e.target.value }))}
                placeholder="Enter instructor name..."
              />
            </FormField>
            <FormField>
              <FormLabel>Max Students:</FormLabel>
              <FormInput
                type="number"
                value={batchFormData.max_students}
                onChange={(e) => setBatchFormData(prev => ({ ...prev, max_students: parseInt(e.target.value) || 20 }))}
                placeholder="Enter maximum students..."
              />
            </FormField>
          </FormRow>
          <FormField>
            <FormLabel>Description:</FormLabel>
            <RichTextEditor
              value={batchFormData.description}
              onChange={(value) => setBatchFormData(prev => ({ ...prev, description: value }))}
              placeholder="Enter batch description with formatting..."
              showImageUpload={false}
              minHeight="150px"
            />
          </FormField>
        </FormSection>

        {/* Schedule */}
        <FormSection>
          <FormSectionTitle>📅 Schedule & Status</FormSectionTitle>
          <FormRow>
            <FormField>
              <FormLabel>
                Start Date: <span style={{ color: '#dc2626' }}>*</span>
              </FormLabel>
              <FormInput
                type="date"
                value={batchFormData.start_date}
                onChange={(e) => setBatchFormData(prev => ({ ...prev, start_date: e.target.value }))}
              />
            </FormField>
            <FormField>
              <FormLabel>
                End Date: <span style={{ color: '#dc2626' }}>*</span>
              </FormLabel>
              <FormInput
                type="date"
                value={batchFormData.end_date}
                onChange={(e) => setBatchFormData(prev => ({ ...prev, end_date: e.target.value }))}
              />
            </FormField>
          </FormRow>
          <FormField>
            <FormLabel>Status:</FormLabel>
            <FormSelect
              value={batchFormData.status}
              onChange={(e) => setBatchFormData(prev => ({ ...prev, status: e.target.value }))}
            >
              <option value="Active">Active</option>
              <option value="Completed">Completed</option>
              <option value="Cancelled">Cancelled</option>
            </FormSelect>
          </FormField>
        </FormSection>

        <FormActions>
          <CancelButton
            type="button"
            onClick={() => setActiveSection('batches')}
            loading={loading}
          >
            Cancel
          </CancelButton>
          <SubmitButton
            type="button"
            onClick={submitCreateBatch}
            loading={loading}
            disabled={loading || !batchFormData.batch_name.trim() || !batchFormData.program.trim()}
          >
            {loading ? 'Creating...' : 'Create Batch'}
          </SubmitButton>
        </FormActions>
      </FormContainer>
    </FullPageContainer>
  );

  const renderEditBatchPage = (): React.JSX.Element => (
    <FullPageContainer>
      <FullPageHeader>
        <FullPageTitle>✏️ Edit Batch: {selectedBatch?.batch_name || selectedBatch?.name}</FullPageTitle>
        <BackButton onClick={() => setActiveSection('batches')}>
          <FiArrowLeft />
          Back to Batches
        </BackButton>
      </FullPageHeader>

      <FormContainer>
        {/* Basic Information */}
        <FormSection>
          <FormSectionTitle>📚 Batch Information</FormSectionTitle>
          <FormRow>
            <FormField>
              <FormLabel>
                Batch Name: <span style={{ color: '#dc2626' }}>*</span>
              </FormLabel>
              <FormInput
                type="text"
                value={batchFormData.batch_name}
                onChange={(e) => setBatchFormData(prev => ({ ...prev, batch_name: e.target.value }))}
                placeholder="Enter batch name..."
              />
            </FormField>
            <FormField>
              <FormLabel>
                Program: <span style={{ color: '#dc2626' }}>*</span>
              </FormLabel>
              <FormInput
                type="text"
                value={batchFormData.program}
                onChange={(e) => setBatchFormData(prev => ({ ...prev, program: e.target.value }))}
                placeholder="Enter program name..."
              />
            </FormField>
          </FormRow>
          <FormRow>
            <FormField>
              <FormLabel>
                Instructor: <span style={{ color: '#dc2626' }}>*</span>
              </FormLabel>
              <FormInput
                type="text"
                value={batchFormData.instructor}
                onChange={(e) => setBatchFormData(prev => ({ ...prev, instructor: e.target.value }))}
                placeholder="Enter instructor name..."
              />
            </FormField>
            <FormField>
              <FormLabel>Max Students:</FormLabel>
              <FormInput
                type="number"
                value={batchFormData.max_students}
                onChange={(e) => setBatchFormData(prev => ({ ...prev, max_students: parseInt(e.target.value) || 20 }))}
                placeholder="Enter maximum students..."
              />
            </FormField>
          </FormRow>
          <FormField>
            <FormLabel>Description:</FormLabel>
            <RichTextEditor
              value={batchFormData.description}
              onChange={(value) => setBatchFormData(prev => ({ ...prev, description: value }))}
              placeholder="Enter batch description with formatting..."
              showImageUpload={false}
              minHeight="150px"
            />
          </FormField>
        </FormSection>

        {/* Schedule */}
        <FormSection>
          <FormSectionTitle>📅 Schedule & Status</FormSectionTitle>
          <FormRow>
            <FormField>
              <FormLabel>
                Start Date: <span style={{ color: '#dc2626' }}>*</span>
              </FormLabel>
              <FormInput
                type="date"
                value={batchFormData.start_date}
                onChange={(e) => setBatchFormData(prev => ({ ...prev, start_date: e.target.value }))}
              />
            </FormField>
            <FormField>
              <FormLabel>
                End Date: <span style={{ color: '#dc2626' }}>*</span>
              </FormLabel>
              <FormInput
                type="date"
                value={batchFormData.end_date}
                onChange={(e) => setBatchFormData(prev => ({ ...prev, end_date: e.target.value }))}
              />
            </FormField>
          </FormRow>
          <FormField>
            <FormLabel>Status:</FormLabel>
            <FormSelect
              value={batchFormData.status}
              onChange={(e) => setBatchFormData(prev => ({ ...prev, status: e.target.value }))}
            >
              <option value="Active">Active</option>
              <option value="Completed">Completed</option>
              <option value="Cancelled">Cancelled</option>
            </FormSelect>
          </FormField>
        </FormSection>

        <FormActions>
          <CancelButton
            type="button"
            onClick={() => setActiveSection('batches')}
            loading={loading}
          >
            Cancel
          </CancelButton>
          <SubmitButton
            type="button"
            onClick={submitEditBatch}
            loading={loading}
            disabled={loading || !batchFormData.batch_name.trim() || !batchFormData.program.trim()}
          >
            {loading ? 'Updating...' : 'Update Batch'}
          </SubmitButton>
        </FormActions>
      </FormContainer>
    </FullPageContainer>
  );

  const renderCreateBatchStudentPage = (): React.JSX.Element => (
    <FullPageContainer>
      <FullPageHeader>
        <FullPageTitle>➕ Add Student to Batch</FullPageTitle>
        <BackButton onClick={() => setActiveSection('batch-students')}>
          <FiArrowLeft />
          Back to Batch Students
        </BackButton>
      </FullPageHeader>

      <FormContainer>
        {/* Student Information */}
        <FormSection>
          <FormSectionTitle>👥 Student Information</FormSectionTitle>
          <FormRow>
            <FormField>
              <FormLabel>
                Student Name: <span style={{ color: '#dc2626' }}>*</span>
              </FormLabel>
              <FormInput
                type="text"
                value={batchStudentFormData.student_name}
                onChange={(e) => setBatchStudentFormData(prev => ({ ...prev, student_name: e.target.value }))}
                placeholder="Enter student name..."
              />
            </FormField>
            <FormField>
              <FormLabel>
                Email: <span style={{ color: '#dc2626' }}>*</span>
              </FormLabel>
              <FormInput
                type="email"
                value={batchStudentFormData.email}
                onChange={(e) => setBatchStudentFormData(prev => ({ ...prev, email: e.target.value }))}
                placeholder="Enter student email..."
              />
            </FormField>
          </FormRow>
          <FormRow>
            <FormField>
              <FormLabel>
                Batch: <span style={{ color: '#dc2626' }}>*</span>
              </FormLabel>
              <FormSelect
                value={batchStudentFormData.batch_id}
                onChange={(e) => setBatchStudentFormData(prev => ({ ...prev, batch_id: e.target.value }))}
              >
                <option value="">Select Batch</option>
                {batches.map(batch => (
                  <option key={batch.id || batch.batch_id} value={batch.id || batch.batch_id}>
                    {batch.batch_name || batch.name}
                  </option>
                ))}
              </FormSelect>
            </FormField>
            <FormField>
              <FormLabel>Enrollment Date:</FormLabel>
              <FormInput
                type="date"
                value={batchStudentFormData.enrollment_date}
                onChange={(e) => setBatchStudentFormData(prev => ({ ...prev, enrollment_date: e.target.value }))}
              />
            </FormField>
          </FormRow>
        </FormSection>

        {/* Progress & Status */}
        <FormSection>
          <FormSectionTitle>📊 Progress & Status</FormSectionTitle>
          <FormRow>
            <FormField>
              <FormLabel>Status:</FormLabel>
              <FormSelect
                value={batchStudentFormData.status}
                onChange={(e) => setBatchStudentFormData(prev => ({ ...prev, status: e.target.value }))}
              >
                <option value="Active">Active</option>
                <option value="Completed">Completed</option>
                <option value="Dropped">Dropped</option>
                <option value="Suspended">Suspended</option>
              </FormSelect>
            </FormField>
            <FormField>
              <FormLabel>Progress (%):</FormLabel>
              <FormInput
                type="number"
                min="0"
                max="100"
                value={batchStudentFormData.progress}
                onChange={(e) => setBatchStudentFormData(prev => ({ ...prev, progress: parseInt(e.target.value) || 0 }))}
                placeholder="Enter progress percentage..."
              />
            </FormField>
          </FormRow>
          <FormField>
            <FormLabel>Grade:</FormLabel>
            <FormInput
              type="text"
              value={batchStudentFormData.grade}
              onChange={(e) => setBatchStudentFormData(prev => ({ ...prev, grade: e.target.value }))}
              placeholder="Enter grade (e.g., A, B+, 85%)..."
            />
          </FormField>
        </FormSection>

        <FormActions>
          <CancelButton
            type="button"
            onClick={() => setActiveSection('batch-students')}
            loading={loading}
          >
            Cancel
          </CancelButton>
          <SubmitButton
            type="button"
            onClick={submitCreateBatchStudent}
            loading={loading}
            disabled={loading || !batchStudentFormData.student_name.trim() || !batchStudentFormData.email.trim() || !batchStudentFormData.batch_id}
          >
            {loading ? 'Adding...' : 'Add Student to Batch'}
          </SubmitButton>
        </FormActions>
      </FormContainer>
    </FullPageContainer>
  );

  const renderEditBatchStudentPage = (): React.JSX.Element => (
    <FullPageContainer>
      <FullPageHeader>
        <FullPageTitle>✏️ Edit Batch Student: {selectedBatchStudent?.student_name || selectedBatchStudent?.name}</FullPageTitle>
        <BackButton onClick={() => setActiveSection('batch-students')}>
          <FiArrowLeft />
          Back to Batch Students
        </BackButton>
      </FullPageHeader>

      <FormContainer>
        {/* Student Information */}
        <FormSection>
          <FormSectionTitle>👥 Student Information</FormSectionTitle>
          <FormRow>
            <FormField>
              <FormLabel>
                Student Name: <span style={{ color: '#dc2626' }}>*</span>
              </FormLabel>
              <FormInput
                type="text"
                value={batchStudentFormData.student_name}
                onChange={(e) => setBatchStudentFormData(prev => ({ ...prev, student_name: e.target.value }))}
                placeholder="Enter student name..."
              />
            </FormField>
            <FormField>
              <FormLabel>
                Email: <span style={{ color: '#dc2626' }}>*</span>
              </FormLabel>
              <FormInput
                type="email"
                value={batchStudentFormData.email}
                onChange={(e) => setBatchStudentFormData(prev => ({ ...prev, email: e.target.value }))}
                placeholder="Enter student email..."
              />
            </FormField>
          </FormRow>
          <FormRow>
            <FormField>
              <FormLabel>
                Batch: <span style={{ color: '#dc2626' }}>*</span>
              </FormLabel>
              <FormSelect
                value={batchStudentFormData.batch_id}
                onChange={(e) => setBatchStudentFormData(prev => ({ ...prev, batch_id: e.target.value }))}
              >
                <option value="">Select Batch</option>
                {batches.map(batch => (
                  <option key={batch.id || batch.batch_id} value={batch.id || batch.batch_id}>
                    {batch.batch_name || batch.name}
                  </option>
                ))}
              </FormSelect>
            </FormField>
            <FormField>
              <FormLabel>Enrollment Date:</FormLabel>
              <FormInput
                type="date"
                value={batchStudentFormData.enrollment_date}
                onChange={(e) => setBatchStudentFormData(prev => ({ ...prev, enrollment_date: e.target.value }))}
              />
            </FormField>
          </FormRow>
        </FormSection>

        {/* Progress & Status */}
        <FormSection>
          <FormSectionTitle>📊 Progress & Status</FormSectionTitle>
          <FormRow>
            <FormField>
              <FormLabel>Status:</FormLabel>
              <FormSelect
                value={batchStudentFormData.status}
                onChange={(e) => setBatchStudentFormData(prev => ({ ...prev, status: e.target.value }))}
              >
                <option value="Active">Active</option>
                <option value="Completed">Completed</option>
                <option value="Dropped">Dropped</option>
                <option value="Suspended">Suspended</option>
              </FormSelect>
            </FormField>
            <FormField>
              <FormLabel>Progress (%):</FormLabel>
              <FormInput
                type="number"
                min="0"
                max="100"
                value={batchStudentFormData.progress}
                onChange={(e) => setBatchStudentFormData(prev => ({ ...prev, progress: parseInt(e.target.value) || 0 }))}
                placeholder="Enter progress percentage..."
              />
            </FormField>
          </FormRow>
          <FormField>
            <FormLabel>Grade:</FormLabel>
            <FormInput
              type="text"
              value={batchStudentFormData.grade}
              onChange={(e) => setBatchStudentFormData(prev => ({ ...prev, grade: e.target.value }))}
              placeholder="Enter grade (e.g., A, B+, 85%)..."
            />
          </FormField>
        </FormSection>

        <FormActions>
          <CancelButton
            type="button"
            onClick={() => setActiveSection('batch-students')}
            loading={loading}
          >
            Cancel
          </CancelButton>
          <SubmitButton
            type="button"
            onClick={submitEditBatchStudent}
            loading={loading}
            disabled={loading || !batchStudentFormData.student_name.trim() || !batchStudentFormData.email.trim() || !batchStudentFormData.batch_id}
          >
            {loading ? 'Updating...' : 'Update Student'}
          </SubmitButton>
        </FormActions>
      </FormContainer>
    </FullPageContainer>
  );

  const renderDataTable = (title: string, data: any[], columns: string[]): React.JSX.Element => (
    <div style={{ padding: '20px' }}>
      <h2 style={{ marginBottom: '20px', color: '#253C8C' }}>{title}</h2>

      {loading && <div>Loading...</div>}
      {error && <div style={{ color: 'red', marginBottom: '20px' }}>{error}</div>}

      {!loading && !error && data.length === 0 && (
        <div>No data found.</div>
      )}

      {!loading && !error && data.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{
            width: '100%',
            borderCollapse: 'collapse',
            backgroundColor: 'white',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            borderRadius: '8px'
          }}>
            <thead>
              <tr style={{ backgroundColor: '#f8f9fa' }}>
                {columns.map(column => (
                  <th key={column} style={{
                    padding: '12px',
                    textAlign: 'left',
                    borderBottom: '2px solid #dee2e6',
                    fontWeight: 'bold',
                    color: '#495057'
                  }}>
                    {column}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.map((item, index) => (
                <tr key={item.id || index} style={{
                  borderBottom: '1px solid #dee2e6'
                }}>
                  {columns.map(column => (
                    <td key={column} style={{
                      padding: '12px',
                      color: '#495057'
                    }}>
                      {item[column.toLowerCase()] || item[column] || 'N/A'}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  // Success Stories Management Interface
  const renderSuccessStoriesManagement = (): React.JSX.Element => (
    <div style={{ padding: '24px' }}>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '24px' 
      }}>
        <h2 style={{ margin: 0, color: '#253C8C', fontSize: '24px' }}>⭐ Success Stories Management</h2>
        <button
          onClick={handleCreateSuccessStory}
          style={{
            backgroundColor: '#253C8C',
            color: 'white',
            border: 'none',
            padding: '12px 20px',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <FiPlus /> Add Success Story
        </button>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>Loading...</div>
      ) : successStories.length === 0 ? (
        <div style={{ 
          textAlign: 'center', 
          padding: '60px', 
          backgroundColor: '#f9fafb', 
          borderRadius: '12px',
          color: '#6b7280'
        }}>
          <FiStar size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
          <h3 style={{ margin: '0 0 8px 0' }}>No Success Stories Yet</h3>
          <p style={{ margin: 0 }}>Click "Add Success Story" to create your first testimonial.</p>
        </div>
      ) : (
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', 
          gap: '20px' 
        }}>
          {successStories.map((story) => (
            <div 
              key={story.id}
              style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: '20px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                border: story.is_active ? '2px solid #10b981' : '2px solid #e5e7eb',
                position: 'relative'
              }}
            >
              {/* Status Badge */}
              <div style={{
                position: 'absolute',
                top: '12px',
                right: '12px',
                backgroundColor: story.is_active ? '#dcfce7' : '#f3f4f6',
                color: story.is_active ? '#166534' : '#6b7280',
                padding: '4px 10px',
                borderRadius: '20px',
                fontSize: '12px',
                fontWeight: '600'
              }}>
                {story.is_active ? 'Active' : 'Inactive'}
              </div>

              {/* Header with Avatar */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                <div style={{
                  width: '50px',
                  height: '50px',
                  borderRadius: '50%',
                  backgroundColor: '#253C8C',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 'bold',
                  fontSize: '18px'
                }}>
                  {story.avatar || story.name?.split(' ').map((n: string) => n[0]).join('').toUpperCase().substring(0, 2)}
                </div>
                <div>
                  <h3 style={{ margin: 0, color: '#1f2937', fontSize: '16px' }}>{story.name}</h3>
                  <p style={{ margin: '2px 0 0 0', color: '#6b7280', fontSize: '14px' }}>{story.grade}</p>
                </div>
              </div>

              {/* Content */}
              <p style={{ 
                color: '#4b5563', 
                fontSize: '14px', 
                lineHeight: '1.6',
                margin: '0 0 16px 0',
                minHeight: '60px'
              }}>
                {story.content?.length > 150 ? `${story.content.substring(0, 150)}...` : story.content}
              </p>

              {/* Stats */}
              <div style={{ 
                display: 'flex', 
                gap: '24px', 
                marginBottom: '16px',
                padding: '12px',
                backgroundColor: '#f9fafb',
                borderRadius: '8px'
              }}>
                {story.improvement && (
                  <div>
                    <div style={{ color: '#253C8C', fontWeight: 'bold', fontSize: '20px' }}>{story.improvement}</div>
                    <div style={{ color: '#6b7280', fontSize: '12px', textTransform: 'uppercase' }}>Improvement</div>
                  </div>
                )}
                {story.time_spent && (
                  <div>
                    <div style={{ color: '#253C8C', fontWeight: 'bold', fontSize: '20px' }}>{story.time_spent}</div>
                    <div style={{ color: '#6b7280', fontSize: '12px', textTransform: 'uppercase' }}>Time</div>
                  </div>
                )}
                <div>
                  <div style={{ color: '#253C8C', fontWeight: 'bold', fontSize: '20px' }}>#{story.display_order}</div>
                  <div style={{ color: '#6b7280', fontSize: '12px', textTransform: 'uppercase' }}>Order</div>
                </div>
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => toggleSuccessStoryStatus(story)}
                  style={{
                    backgroundColor: story.is_active ? '#fef3c7' : '#dcfce7',
                    color: story.is_active ? '#92400e' : '#166534',
                    border: 'none',
                    padding: '8px 12px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: '500'
                  }}
                >
                  {story.is_active ? 'Deactivate' : 'Activate'}
                </button>
                <button
                  onClick={() => handleEditSuccessStory(story)}
                  style={{
                    backgroundColor: '#dbeafe',
                    color: '#1d4ed8',
                    border: 'none',
                    padding: '8px 12px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  <FiEdit size={14} /> Edit
                </button>
                <button
                  onClick={() => handleDeleteSuccessStory(story)}
                  style={{
                    backgroundColor: '#fee2e2',
                    color: '#dc2626',
                    border: 'none',
                    padding: '8px 12px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  <FiTrash2 size={14} /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Modal */}
      {showCreateSuccessStoryModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <h3 style={{ margin: '0 0 20px 0', color: '#253C8C' }}>⭐ Add New Success Story</h3>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#374151' }}>
                  Name: <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <input
                  type="text"
                  value={successStoryFormData.name}
                  onChange={(e) => setSuccessStoryFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Student name"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#374151' }}>
                  Grade: <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <input
                  type="text"
                  value={successStoryFormData.grade}
                  onChange={(e) => setSuccessStoryFormData(prev => ({ ...prev, grade: e.target.value }))}
                  placeholder="e.g., Grade 10"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#374151' }}>
                Testimonial: <span style={{ color: '#dc2626' }}>*</span>
              </label>
              <textarea
                value={successStoryFormData.content}
                onChange={(e) => setSuccessStoryFormData(prev => ({ ...prev, content: e.target.value }))}
                placeholder="Write the student's testimonial here..."
                rows={4}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '14px',
                  resize: 'vertical'
                }}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#374151' }}>
                  Improvement
                </label>
                <input
                  type="text"
                  value={successStoryFormData.improvement}
                  onChange={(e) => setSuccessStoryFormData(prev => ({ ...prev, improvement: e.target.value }))}
                  placeholder="e.g., 85%"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#374151' }}>
                  Time Spent
                </label>
                <input
                  type="text"
                  value={successStoryFormData.time_spent}
                  onChange={(e) => setSuccessStoryFormData(prev => ({ ...prev, time_spent: e.target.value }))}
                  placeholder="e.g., 3 months"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#374151' }}>
                  Display Order
                </label>
                <input
                  type="number"
                  value={successStoryFormData.display_order}
                  onChange={(e) => setSuccessStoryFormData(prev => ({ ...prev, display_order: parseInt(e.target.value) || 0 }))}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={successStoryFormData.is_active}
                  onChange={(e) => setSuccessStoryFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                  style={{ width: '18px', height: '18px' }}
                />
                <span style={{ fontWeight: '600', color: '#374151' }}>Active (visible on website)</span>
              </label>
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowCreateSuccessStoryModal(false)}
                disabled={successStoryLoading}
                style={{
                  backgroundColor: '#6b7280',
                  color: 'white',
                  border: 'none',
                  padding: '12px 20px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                Cancel
              </button>
              <button
                onClick={submitCreateSuccessStory}
                disabled={successStoryLoading}
                style={{
                  backgroundColor: '#253C8C',
                  color: 'white',
                  border: 'none',
                  padding: '12px 20px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  opacity: successStoryLoading ? 0.7 : 1
                }}
              >
                {successStoryLoading ? 'Creating...' : 'Create Story'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditSuccessStoryModal && selectedSuccessStory && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <h3 style={{ margin: '0 0 20px 0', color: '#253C8C' }}>✏️ Edit Success Story</h3>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#374151' }}>
                  Name: <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <input
                  type="text"
                  value={successStoryFormData.name}
                  onChange={(e) => setSuccessStoryFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Student name"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#374151' }}>
                  Grade: <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <input
                  type="text"
                  value={successStoryFormData.grade}
                  onChange={(e) => setSuccessStoryFormData(prev => ({ ...prev, grade: e.target.value }))}
                  placeholder="e.g., Grade 10"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#374151' }}>
                Testimonial: <span style={{ color: '#dc2626' }}>*</span>
              </label>
              <textarea
                value={successStoryFormData.content}
                onChange={(e) => setSuccessStoryFormData(prev => ({ ...prev, content: e.target.value }))}
                placeholder="Write the student's testimonial here..."
                rows={4}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '14px',
                  resize: 'vertical'
                }}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#374151' }}>
                  Improvement
                </label>
                <input
                  type="text"
                  value={successStoryFormData.improvement}
                  onChange={(e) => setSuccessStoryFormData(prev => ({ ...prev, improvement: e.target.value }))}
                  placeholder="e.g., 85%"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#374151' }}>
                  Time Spent
                </label>
                <input
                  type="text"
                  value={successStoryFormData.time_spent}
                  onChange={(e) => setSuccessStoryFormData(prev => ({ ...prev, time_spent: e.target.value }))}
                  placeholder="e.g., 3 months"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#374151' }}>
                  Display Order
                </label>
                <input
                  type="number"
                  value={successStoryFormData.display_order}
                  onChange={(e) => setSuccessStoryFormData(prev => ({ ...prev, display_order: parseInt(e.target.value) || 0 }))}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={successStoryFormData.is_active}
                  onChange={(e) => setSuccessStoryFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                  style={{ width: '18px', height: '18px' }}
                />
                <span style={{ fontWeight: '600', color: '#374151' }}>Active (visible on website)</span>
              </label>
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowEditSuccessStoryModal(false);
                  setSelectedSuccessStory(null);
                }}
                disabled={successStoryLoading}
                style={{
                  backgroundColor: '#6b7280',
                  color: 'white',
                  border: 'none',
                  padding: '12px 20px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                Cancel
              </button>
              <button
                onClick={submitEditSuccessStory}
                disabled={successStoryLoading}
                style={{
                  backgroundColor: '#253C8C',
                  color: 'white',
                  border: 'none',
                  padding: '12px 20px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  opacity: successStoryLoading ? 0.7 : 1
                }}
              >
                {successStoryLoading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteSuccessStoryModal && selectedSuccessStory && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '400px',
            width: '90%'
          }}>
            <h3 style={{ margin: '0 0 16px 0', color: '#dc2626' }}>🗑️ Delete Success Story</h3>
            <p style={{ marginBottom: '20px', color: '#374151' }}>
              Are you sure you want to delete <strong>{selectedSuccessStory.name}'s</strong> success story?
            </p>
            <div style={{
              backgroundColor: '#fef2f2',
              padding: '12px',
              borderRadius: '8px',
              marginBottom: '20px',
              border: '1px solid #fecaca'
            }}>
              <strong style={{ color: '#dc2626' }}>⚠️ Warning:</strong>
              <span style={{ color: '#dc2626' }}> This action cannot be undone.</span>
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowDeleteSuccessStoryModal(false);
                  setSelectedSuccessStory(null);
                }}
                disabled={successStoryLoading}
                style={{
                  backgroundColor: '#6b7280',
                  color: 'white',
                  border: 'none',
                  padding: '12px 20px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                Cancel
              </button>
              <button
                onClick={submitDeleteSuccessStory}
                disabled={successStoryLoading}
                style={{
                  backgroundColor: '#dc2626',
                  color: 'white',
                  border: 'none',
                  padding: '12px 20px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  opacity: successStoryLoading ? 0.7 : 1
                }}
              >
                {successStoryLoading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // Generic modern management interface
  const renderModernManagement = (
    title: string,
    data: any[],
    columns: string[],
    searchFields: string[],
    filterField?: string,
    filterOptions?: string[],
    onEdit?: (item: any) => void,
    onDelete?: (item: any) => void,
    onPasswordReset?: (item: any) => void,
    onCreate?: () => void,
    customActionLabel?: string,
    customActionIcon?: React.ReactNode
  ): React.JSX.Element => {
    // Ensure data is an array before filtering
    const safeData = Array.isArray(data) ? data : [];
    let filteredData = safeData.filter(item => {
      const matchesSearch = searchFields.some(field =>
        item[field]?.toString().toLowerCase().includes(searchTerm.toLowerCase())
      );
      const matchesFilter = !filterField || roleFilter === 'all' ||
        item[filterField]?.toString().toLowerCase() === roleFilter.toLowerCase();
      return matchesSearch && matchesFilter;
    });

    // Apply sorting if a column is selected
    if (sortColumn) {
      filteredData = [...filteredData].sort((a, b) => {
        const columnKey = sortColumn.toLowerCase().replace(/ /g, '_');
        let aValue = a[columnKey] || a[sortColumn.toLowerCase()] || a[sortColumn] || '';
        let bValue = b[columnKey] || b[sortColumn.toLowerCase()] || b[sortColumn] || '';

        // Handle special column mappings
        if (sortColumn === 'Subject' && !aValue && a.category) aValue = a.category;
        if (sortColumn === 'Subject' && !bValue && b.category) bValue = b.category;
        if (sortColumn === 'Title' && !aValue && a.course_name) aValue = a.course_name;
        if (sortColumn === 'Title' && !bValue && b.course_name) bValue = b.course_name;

        // Handle Status column (dynamically calculated for classes)
        if (sortColumn === 'Status') {
          // Calculate status for sorting (same logic as display)
          const calculateStatus = (item: any): string => {
            if (!item.start_date && !item.end_date) {
              return item.status || '';
            }
            
            const now = new Date();
            const startDate = item.start_date ? new Date(item.start_date) : null;
            const endDate = item.end_date ? new Date(item.end_date) : null;
            
            if (startDate && endDate) {
              if (now < startDate) return 'Upcoming';
              if (now >= startDate && now <= endDate) return 'Active';
              if (now > endDate) return 'Completed';
            }
            return 'Active';
          };
          
          aValue = calculateStatus(a);
          bValue = calculateStatus(b);
        }
        
        // Handle date columns
        if (sortColumn === 'Created Date') {
          aValue = a.created_at ? new Date(a.created_at).getTime() : 0;
          bValue = b.created_at ? new Date(b.created_at).getTime() : 0;
        } else if (sortColumn === 'Updated Date') {
          aValue = a.updated_at ? new Date(a.updated_at).getTime() : 0;
          bValue = b.updated_at ? new Date(b.updated_at).getTime() : 0;
        } else if (sortColumn === 'Start Date') {
          aValue = a.start_date ? new Date(a.start_date).getTime() : 0;
          bValue = b.start_date ? new Date(b.start_date).getTime() : 0;
        } else if (sortColumn === 'End Date') {
          aValue = a.end_date ? new Date(a.end_date).getTime() : 0;
          bValue = b.end_date ? new Date(b.end_date).getTime() : 0;
        }

        // Handle numeric values (ID, Price, etc.)
        const aNum = parseFloat(aValue);
        const bNum = parseFloat(bValue);
        if (!isNaN(aNum) && !isNaN(bNum)) {
          return sortDirection === 'asc' ? aNum - bNum : bNum - aNum;
        }

        // Handle string values
        const aStr = String(aValue).toLowerCase();
        const bStr = String(bValue).toLowerCase();
        
        if (sortDirection === 'asc') {
          return aStr.localeCompare(bStr);
        } else {
          return bStr.localeCompare(aStr);
        }
      });
    }

    // Calculate pagination
    const totalPages = Math.ceil(filteredData.length / itemsPerPage);
    const safePage = currentPage > totalPages && totalPages > 0 ? 1 : currentPage;
    const startIndex = (safePage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedData = filteredData.slice(startIndex, endIndex);

    return (
      <div style={{ padding: '24px' }}>
        {/* Sticky Header with Create Button and Pagination */}
        <div style={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          backgroundColor: '#f8f9ff',
          paddingBottom: '16px',
          marginBottom: '8px',
          borderBottom: '2px solid #e5e7eb'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '16px',
            marginBottom: '16px',
            flexWrap: 'wrap'
          }}>
            {/* Left side: Create button and title */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              {onCreate && (
                <button
                  onClick={onCreate}
                  style={{
                    backgroundColor: '#059669',
                    color: 'white',
                    border: 'none',
                    padding: '12px 24px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontWeight: 'bold',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    flexShrink: 0
                  }}
                >
                  <FiPlus size={18} /> Create
                </button>
              )}
              <h2 style={{ margin: 0, color: '#253C8C', fontSize: '28px', fontWeight: 'bold' }}>
                {title}
              </h2>
            </div>

            {/* Right side: Pagination controls */}
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
              {/* Items per page selector */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <label style={{ fontSize: '14px', color: '#374151', fontWeight: '500' }}>
                  Show:
                </label>
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  style={{
                    padding: '6px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    backgroundColor: 'white',
                    cursor: 'pointer',
                    fontWeight: '500'
                  }}
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <span style={{ fontSize: '14px', color: '#6b7280' }}>per page</span>
              </div>

              {totalPages > 1 && (
                <>
                  <div style={{ width: '1px', height: '24px', backgroundColor: '#e5e7eb', margin: '0 4px' }} />
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={safePage === 1}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: safePage === 1 ? '#e5e7eb' : '#3b82f6',
                      color: safePage === 1 ? '#9ca3af' : 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: safePage === 1 ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                      fontWeight: '600',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      transition: 'all 0.2s'
                    }}
                  >
                    <FiChevronLeft size={16} />
                    Previous
                  </button>

                  <div style={{ 
                    padding: '8px 16px', 
                    backgroundColor: 'white',
                    border: '2px solid #3b82f6',
                    borderRadius: '6px',
                    color: '#3b82f6',
                    fontSize: '14px',
                    fontWeight: '700',
                    whiteSpace: 'nowrap'
                  }}>
                    Page {safePage} of {totalPages}
                  </div>

                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={safePage === totalPages}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: safePage === totalPages ? '#e5e7eb' : '#3b82f6',
                      color: safePage === totalPages ? '#9ca3af' : 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: safePage === totalPages ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                      fontWeight: '600',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      transition: 'all 0.2s'
                    }}
                  >
                    Next
                    <FiChevronRight size={16} />
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Filters and Search */}
        <div style={{
          backgroundColor: 'white',
          padding: '20px',
          borderRadius: '12px',
          marginBottom: '24px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            <div style={{ flex: '1', minWidth: '300px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#374151' }}>
                🔍 Search
              </label>
              <input
                type="text"
                placeholder={`Search by ${searchFields.join(', ')}...`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '16px',
                  outline: 'none',
                  transition: 'border-color 0.2s'
                }}
                onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
              />
            </div>
            {filterField && filterOptions && (
              <div style={{ minWidth: '200px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#374151' }}>
                  📊 Filter
                </label>
                <select
                  value={roleFilter}
                  onChange={(e) => setRoleFilter(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '16px',
                    backgroundColor: 'white',
                    outline: 'none'
                  }}
                >
                  <option value="all">All {filterField}</option>
                  {filterOptions.map(option => (
                    <option key={option} value={option.toLowerCase()}>{option}</option>
                  ))}
                </select>
              </div>
            )}
            <div style={{ minWidth: '150px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#374151' }}>
                📄 Show
              </label>
              <select
                value={itemsPerPage}
                onChange={(e) => setItemsPerPage(parseInt(e.target.value))}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '16px',
                  backgroundColor: 'white',
                  outline: 'none'
                }}
              >
                <option value="10">10 items</option>
                <option value="20">20 items</option>
                <option value="50">50 items</option>
                <option value="100">100 items</option>
              </select>
            </div>
          </div>
        </div>

        {/* Data Table */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          overflow: 'hidden',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          {loading && (
            <div style={{ padding: '40px', textAlign: 'center' }}>
              <div style={{ fontSize: '18px', color: '#6b7280' }}>Loading...</div>
            </div>
          )}

          {error && (
            <div style={{ padding: '20px', backgroundColor: '#fef2f2', color: '#dc2626', textAlign: 'center' }}>
              <strong>Error:</strong> {error}
            </div>
          )}

          {!loading && !error && filteredData.length === 0 && (
            <div style={{ padding: '40px', textAlign: 'center' }}>
              <div style={{ fontSize: '18px', color: '#6b7280' }}>
                {searchTerm || roleFilter !== 'all' ? 'No items match your search criteria.' : 'No data found.'}
              </div>
            </div>
          )}

          {!loading && !error && filteredData.length > 0 && (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                    {columns.map(column => (
                      <th 
                        key={column} 
                        onClick={() => {
                          if (sortColumn === column) {
                            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
                          } else {
                            setSortColumn(column);
                            setSortDirection('asc');
                          }
                        }}
                        style={{ 
                          padding: '16px', 
                          textAlign: 'left', 
                          fontWeight: 'bold', 
                          color: '#374151',
                          cursor: 'pointer',
                          userSelect: 'none',
                          position: 'relative',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#f3f4f6';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                        title={`Click to sort by ${column}`}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span>{column}</span>
                          <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minWidth: '16px' }}>
                            {sortColumn === column ? (
                              sortDirection === 'asc' ? (
                                <FiArrowUp size={14} color="#3b82f6" />
                              ) : (
                                <FiArrowDown size={14} color="#3b82f6" />
                              )
                            ) : (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '0px', fontSize: '10px', color: '#9ca3af', lineHeight: '1' }}>
                                <span>▲</span>
                                <span>▼</span>
                              </div>
                            )}
                          </span>
                        </div>
                      </th>
                    ))}
                    {(onEdit || onDelete || onPasswordReset) && (
                      <th style={{ padding: '16px', textAlign: 'center', fontWeight: 'bold', color: '#374151' }}>Actions</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {paginatedData.map((item, index) => (
                    <tr
                      key={item.id || index}
                      style={{
                        borderBottom: '1px solid #e5e7eb',
                        backgroundColor: index % 2 === 0 ? '#ffffff' : '#f9fafb'
                      }}
                    >
                      {columns.map(column => (
                        <td key={column} style={{ padding: '16px', color: '#374151' }}>
                          {(() => {
                            const columnKey = column.toLowerCase().replace(/ /g, '_');
                            let value = item[columnKey] || item[column.toLowerCase()] || item[column];

                            // Handle date columns
                            if (column === 'Created Date' && item.created_at) {
                              value = new Date(item.created_at).toLocaleDateString();
                            } else if (column === 'Updated Date' && item.updated_at) {
                              value = new Date(item.updated_at).toLocaleDateString();
                            } else if (column === 'Start Date' && item.start_date) {
                              value = new Date(item.start_date).toLocaleDateString();
                            } else if (column === 'End Date' && item.end_date) {
                              value = new Date(item.end_date).toLocaleDateString();
                            }

                            // Handle subject column (map from category field for backward compatibility)
                            if (column === 'Subject' && !value && item.category) {
                              value = item.category;
                            }
                            
                            // Handle Title column (for courses and classes)
                            if (column === 'Title' && !value && item.course_name) {
                              value = item.course_name;
                            }

                            // Handle grade-related columns for subjects
                            if (column === 'Linked Grades' && item.grades) {
                              value = item.grades.map((grade: any) => grade.name).join(', ') || 'None';
                            } else if (column === 'Grades Count' && item.grades_count !== undefined) {
                              value = item.grades_count;
                            }

                            // Handle status column for online classes (only if item has start_date/end_date)
                            if (column === 'Status' && !value && (item.start_date || item.end_date)) {
                              // Determine status based on dates and enrollment
                              const now = new Date();
                              const startDate = item.start_date ? new Date(item.start_date) : null;
                              const endDate = item.end_date ? new Date(item.end_date) : null;

                              if (startDate && endDate) {
                                if (now < startDate) {
                                  value = 'Upcoming';
                                } else if (now >= startDate && now <= endDate) {
                                  value = 'Active';
                                } else if (now > endDate) {
                                  value = 'Completed';
                                }
                              } else {
                                value = 'Active'; // Default status for classes
                              }
                            }

                            // Handle students column for online classes
                            if (column === 'Students' && !value) {
                              const enrolled = item.enrolled_students || 0;
                              const maxStudents = item.max_students || item.slots || 0;
                              value = maxStudents > 0 ? `${enrolled}/${maxStudents}` : enrolled.toString();
                            }

                            // Handle links
                            if (typeof value === 'string' && value.startsWith('http')) {
                              return (
                                <a href={value} target="_blank" rel="noopener noreferrer" style={{ color: '#3b82f6' }}>
                                  Link
                                </a>
                              );
                            }

                            return value || 'N/A';
                          })()}
                        </td>
                      ))}
                      {(onEdit || onDelete || onPasswordReset) && (
                        <td style={{ padding: '16px', textAlign: 'center' }}>
                          <div style={{ display: 'flex', gap: '6px', justifyContent: 'center', flexWrap: 'wrap' }}>
                            {onEdit && (
                              <button
                                onClick={() => onEdit(item)}
                                style={{
                                  backgroundColor: '#3b82f6',
                                  color: 'white',
                                  border: 'none',
                                  padding: '8px 12px',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  fontSize: '14px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '4px'
                                }}
                                title="Edit"
                              >
                                <FiEdit size={14} />
                                Edit
                              </button>
                            )}
                            {onPasswordReset && (
                              <button
                                onClick={() => onPasswordReset(item)}
                                style={{
                                  backgroundColor: customActionLabel ? '#10b981' : '#f59e0b',
                                  color: 'white',
                                  border: 'none',
                                  padding: '8px 12px',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  fontSize: '14px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '4px'
                                }}
                                title={customActionLabel || "Reset Password"}
                              >
                                {customActionIcon || <FiKey size={14} />}
                                {customActionLabel || "Reset"}
                              </button>
                            )}
                            {onDelete && (
                              <button
                                onClick={() => onDelete(item)}
                                style={{
                                  backgroundColor: '#ef4444',
                                  color: 'white',
                                  border: 'none',
                                  padding: '8px 12px',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  fontSize: '14px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '4px'
                                }}
                                title="Delete"
                              >
                                <FiTrash2 size={14} />
                                Delete
                              </button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Item Count at Bottom */}
          {!loading && !error && filteredData.length > 0 && (
            <div style={{
              padding: '12px 20px',
              borderTop: '1px solid #e5e7eb',
              backgroundColor: '#f9fafb',
              color: '#6b7280',
              fontSize: '14px',
              textAlign: 'center'
            }}>
              Showing <strong>{startIndex + 1}-{Math.min(endIndex, filteredData.length)}</strong> of <strong>{filteredData.length}</strong> items
              {(searchTerm || roleFilter !== 'all') && ' (filtered)'}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderModernUserManagement = (): React.JSX.Element => {
    // Filter users
    const filteredUsers = users.filter(user => {
      const matchesSearch = 
        user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesRole = roleFilter === 'all' || 
        (roleFilter === 'admin' && (user.role === 'Admin' || user.role_name === 'admin')) ||
        (roleFilter === 'user' && (user.role !== 'Admin' && user.role_name !== 'admin'));
      return matchesSearch && matchesRole;
    });

    // Apply sorting
    let sortedUsers = [...filteredUsers];
    if (sortColumn) {
      sortedUsers.sort((a, b) => {
        let aValue: any = '';
        let bValue: any = '';

        switch (sortColumn) {
          case 'ID':
            aValue = a.id || 0;
            bValue = b.id || 0;
            break;
          case 'Name':
            aValue = (a.name || '').toLowerCase();
            bValue = (b.name || '').toLowerCase();
            break;
          case 'Email':
            aValue = (a.email || '').toLowerCase();
            bValue = (b.email || '').toLowerCase();
            break;
          case 'Role':
            aValue = (a.role || (a.user_role === 2 ? 'Admin' : 'User')).toLowerCase();
            bValue = (b.role || (b.user_role === 2 ? 'Admin' : 'User')).toLowerCase();
            break;
          case 'Created':
            aValue = a.created_at ? new Date(a.created_at).getTime() : 0;
            bValue = b.created_at ? new Date(b.created_at).getTime() : 0;
            break;
          default:
            return 0;
        }

        if (typeof aValue === 'number' && typeof bValue === 'number') {
          return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
        }
        
        const aStr = String(aValue);
        const bStr = String(bValue);
        return sortDirection === 'asc' ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr);
      });
    }

    // Calculate pagination
    const totalPages = Math.ceil(sortedUsers.length / itemsPerPage);
    const safePage = currentPage > totalPages && totalPages > 0 ? 1 : currentPage;
    const startIndex = (safePage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedUsers = sortedUsers.slice(startIndex, endIndex);

    const userColumns = ['ID', 'Name', 'Email', 'Role', 'Created'];

    return (
    <div style={{ padding: '24px' }}>
      {/* Header with Create Button and Pagination */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '24px',
        flexWrap: 'wrap',
        gap: '16px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button
            onClick={handleCreateUser}
            style={{
              backgroundColor: '#059669',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontWeight: 'bold',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}
          >
            <FiPlus size={18} /> Create User
          </button>
          <h2 style={{ margin: 0, color: '#253C8C', fontSize: '28px', fontWeight: 'bold' }}>
            👥 User Management
          </h2>
        </div>

        {/* Pagination Controls */}
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <label style={{ fontSize: '14px', color: '#374151', fontWeight: '500' }}>
              Show:
            </label>
            <select
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              style={{
                padding: '6px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                backgroundColor: 'white',
                cursor: 'pointer',
                fontWeight: '500'
              }}
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <span style={{ fontSize: '14px', color: '#6b7280' }}>per page</span>
          </div>

          {totalPages > 1 && (
            <>
              <div style={{ width: '1px', height: '24px', backgroundColor: '#e5e7eb', margin: '0 4px' }} />
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={safePage === 1}
                style={{
                  padding: '8px 16px',
                  backgroundColor: safePage === 1 ? '#e5e7eb' : '#3b82f6',
                  color: safePage === 1 ? '#9ca3af' : 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: safePage === 1 ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  transition: 'all 0.2s'
                }}
              >
                <FiChevronLeft size={16} />
                Previous
              </button>

              <div style={{ 
                padding: '8px 16px', 
                backgroundColor: 'white',
                border: '2px solid #3b82f6',
                borderRadius: '6px',
                color: '#3b82f6',
                fontSize: '14px',
                fontWeight: '700',
                whiteSpace: 'nowrap'
              }}>
                Page {safePage} of {totalPages}
              </div>

              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={safePage === totalPages}
                style={{
                  padding: '8px 16px',
                  backgroundColor: safePage === totalPages ? '#e5e7eb' : '#3b82f6',
                  color: safePage === totalPages ? '#9ca3af' : 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: safePage === totalPages ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  transition: 'all 0.2s'
                }}
              >
                Next
                <FiChevronRight size={16} />
              </button>
            </>
          )}
        </div>
      </div>

      {/* Filters and Search */}
      <div style={{
        backgroundColor: 'white',
        padding: '20px',
        borderRadius: '12px',
        marginBottom: '24px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
          <div style={{ flex: '1', minWidth: '300px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#374151' }}>
              🔍 Search Users
            </label>
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '12px',
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '16px',
                outline: 'none',
                transition: 'border-color 0.2s'
              }}
              onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
              onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
            />
          </div>
          <div style={{ minWidth: '200px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#374151' }}>
              👤 Filter by Role
            </label>
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              style={{
                width: '100%',
                padding: '12px',
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '16px',
                backgroundColor: 'white',
                outline: 'none'
              }}
            >
              <option value="all">All Roles</option>
              <option value="admin">Admin</option>
              <option value="user">User</option>
            </select>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        overflow: 'hidden',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        {loading && (
          <div style={{ padding: '40px', textAlign: 'center' }}>
            <div style={{ fontSize: '18px', color: '#6b7280' }}>Loading users...</div>
          </div>
        )}

        {error && (
          <div style={{ padding: '20px', backgroundColor: '#fef2f2', color: '#dc2626', textAlign: 'center' }}>
            <strong>Error:</strong> {error}
          </div>
        )}

        {!loading && !error && filteredUsers.length === 0 && (
          <div style={{ padding: '40px', textAlign: 'center' }}>
            <div style={{ fontSize: '18px', color: '#6b7280' }}>
              {searchTerm || roleFilter !== 'all' ? 'No users match your search criteria.' : 'No users found.'}
            </div>
          </div>
        )}

        {!loading && !error && filteredUsers.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                  {userColumns.map(column => (
                    <th 
                      key={column}
                      onClick={() => {
                        if (sortColumn === column) {
                          setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
                        } else {
                          setSortColumn(column);
                          setSortDirection('asc');
                        }
                      }}
                      style={{ 
                        padding: '16px', 
                        textAlign: 'left', 
                        fontWeight: 'bold', 
                        color: '#374151',
                        cursor: 'pointer',
                        userSelect: 'none',
                        transition: 'background-color 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#f3f4f6';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                      title={`Click to sort by ${column}`}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span>{column}</span>
                        <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minWidth: '16px' }}>
                          {sortColumn === column ? (
                            sortDirection === 'asc' ? (
                              <FiArrowUp size={14} color="#3b82f6" />
                            ) : (
                              <FiArrowDown size={14} color="#3b82f6" />
                            )
                          ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0px', fontSize: '10px', color: '#9ca3af', lineHeight: '1' }}>
                              <span>▲</span>
                              <span>▼</span>
                            </div>
                          )}
                        </span>
                      </div>
                    </th>
                  ))}
                  <th style={{ padding: '16px', textAlign: 'center', fontWeight: 'bold', color: '#374151' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedUsers.map((user, index) => (
                  <tr
                    key={user.id || index}
                    style={{
                      borderBottom: '1px solid #e5e7eb',
                      backgroundColor: index % 2 === 0 ? '#ffffff' : '#f9fafb'
                    }}
                  >
                    <td style={{ padding: '16px', color: '#374151' }}>{user.id || 'N/A'}</td>
                    <td style={{ padding: '16px', color: '#374151', fontWeight: 'medium' }}>{user.name || 'N/A'}</td>
                    <td style={{ padding: '16px', color: '#374151' }}>{user.email || 'N/A'}</td>
                    <td style={{ padding: '16px' }}>
                      <span style={{
                        padding: '4px 12px',
                        borderRadius: '20px',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        backgroundColor: (user.role === 'Admin' || user.role_name === 'admin') ? '#fef3c7' : '#e0f2fe',
                        color: (user.role === 'Admin' || user.role_name === 'admin') ? '#d97706' : '#0369a1'
                      }}>
                        {user.role || 'User'}
                      </span>
                    </td>
                    <td style={{ padding: '16px', color: '#6b7280', fontSize: '14px' }}>
                      {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                    </td>
                    <td style={{ padding: '16px', textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: '6px', justifyContent: 'center', flexWrap: 'wrap' }}>
                        <button
                          onClick={() => handleEditUser(user)}
                          style={{
                            backgroundColor: '#3b82f6',
                            color: 'white',
                            border: 'none',
                            padding: '8px 12px',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                          title="Edit User"
                        >
                          <FiEdit size={14} />
                          Edit
                        </button>
                        <button
                          onClick={() => handlePasswordReset(user)}
                          style={{
                            backgroundColor: '#f59e0b',
                            color: 'white',
                            border: 'none',
                            padding: '8px 12px',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                          title="Reset Password"
                        >
                          <FiKey size={14} />
                          Reset
                        </button>
                        <button
                          onClick={() => handleDeleteUser(user)}
                          style={{
                            backgroundColor: '#ef4444',
                            color: 'white',
                            border: 'none',
                            padding: '8px 12px',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                          title="Delete User"
                        >
                          <FiTrash2 size={14} />
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Results Summary */}
        {!loading && !error && sortedUsers.length > 0 && (
          <div style={{
            padding: '16px',
            borderTop: '1px solid #e5e7eb',
            backgroundColor: '#f9fafb',
            color: '#6b7280',
            fontSize: '14px',
            textAlign: 'center'
          }}>
            Showing <strong>{startIndex + 1}-{Math.min(endIndex, sortedUsers.length)}</strong> of <strong>{sortedUsers.length}</strong> users
            {(searchTerm || roleFilter !== 'all') && ' (filtered)'}
          </div>
        )}
      </div>
    </div>
    );
  };

  // Custom Book Management with Multiple Filters
  const renderBookManagement = (): React.JSX.Element => {
    // Filter books by search, subject, grade, country, and state
    const filteredBooks = books.filter(book => {
      const matchesSearch = book.title?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesSubject = roleFilter === 'all' || 
        book.category?.toLowerCase() === roleFilter.toLowerCase() ||
        book.subjectInfo?.name?.toLowerCase() === roleFilter.toLowerCase();
      
      const matchesGrade = bookGradeFilter === 'all' || 
        book.grade_id?.toString() === bookGradeFilter ||
        book.gradeInfo?.id?.toString() === bookGradeFilter;
      
      const matchesCountry = bookCountryFilter === 'all' || 
        book.country?.toString() === bookCountryFilter;
      
      const matchesState = bookStateFilter === 'all' || 
        book.state_id?.toString() === bookStateFilter ||
        book.state?.toLowerCase() === bookStateFilter.toLowerCase() ||
        (() => {
          // Check if state is in the state_ids array (multi-state books)
          try {
            const stateIds = JSON.parse(book.state_ids || '[]');
            if (Array.isArray(stateIds)) {
              return stateIds.map(String).includes(bookStateFilter);
            }
          } catch { }
          return false;
        })();
      
      return matchesSearch && matchesSubject && matchesGrade && matchesCountry && matchesState;
    });

    // Apply sorting
    let sortedBooks = [...filteredBooks];
    if (sortColumn) {
      sortedBooks.sort((a, b) => {
        let aValue: any = '';
        let bValue: any = '';

        switch (sortColumn) {
          case 'ID':
            aValue = a.id || 0;
            bValue = b.id || 0;
            break;
          case 'Title':
            aValue = (a.title || '').toLowerCase();
            bValue = (b.title || '').toLowerCase();
            break;
          case 'Subject':
            aValue = (a.category || a.subjectInfo?.name || '').toLowerCase();
            bValue = (b.category || b.subjectInfo?.name || '').toLowerCase();
            break;
          case 'Grade':
            aValue = (a.gradeInfo?.name || '').toLowerCase();
            bValue = (b.gradeInfo?.name || '').toLowerCase();
            break;
          case 'Price':
            aValue = parseFloat(a.price) || 0;
            bValue = parseFloat(b.price) || 0;
            break;
          case 'Created Date':
            aValue = a.created_at ? new Date(a.created_at).getTime() : 0;
            bValue = b.created_at ? new Date(b.created_at).getTime() : 0;
            break;
          case 'Updated Date':
            aValue = a.updated_at ? new Date(a.updated_at).getTime() : 0;
            bValue = b.updated_at ? new Date(b.updated_at).getTime() : 0;
            break;
          default:
            return 0;
        }

        if (typeof aValue === 'number' && typeof bValue === 'number') {
          return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
        }
        
        return sortDirection === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
      });
    }

    // Calculate pagination
    const totalPages = Math.ceil(sortedBooks.length / itemsPerPage);
    const safePage = currentPage > totalPages && totalPages > 0 ? 1 : currentPage;
    const startIndex = (safePage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedBooks = sortedBooks.slice(startIndex, endIndex);

    const bookColumns = ['ID', 'Title', 'Subject', 'Grade', 'Price', 'Created Date', 'Updated Date'];

    // Render the edit form content for a tab
    const renderEditTabContent = () => {
      const currentTab = bookTabs.find(t => t.id === activeBookTab);
      if (!currentTab || currentTab.type !== 'edit' || !selectedBook) return null;

      return (
        <div style={{ padding: '0' }}>
          <FormContainer style={{ maxWidth: '100%', margin: 0 }}>
            {/* Basic Information */}
            <FormSection>
              <FormSectionTitle>📚 Basic Information</FormSectionTitle>
              <FormRow>
                <FormField>
                  <FormLabel>
                    Title: <span style={{ color: '#dc2626' }}>*</span>
                  </FormLabel>
                  <FormInput
                    type="text"
                    value={bookFormData.title}
                    onChange={(e) => setBookFormData(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Enter book title..."
                  />
                </FormField>
                <FormField>
                  <FormLabel>
                    Upload Image: <span style={{ color: '#dc2626' }}>*</span>
                  </FormLabel>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            setSelectedBookImageFile(file);
                            setBookFormData(prev => ({ ...prev, image: file.name }));
                          }
                        }}
                        style={{ display: 'none' }}
                        id={`tab-edit-book-image-upload-${activeBookTab}`}
                      />
                      <label
                        htmlFor={`tab-edit-book-image-upload-${activeBookTab}`}
                        style={{
                          backgroundColor: '#3b82f6',
                          color: 'white',
                          padding: '8px 16px',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          border: 'none',
                          fontSize: '14px'
                        }}
                      >
                        Browse
                      </label>
                      <FormInput
                        type="text"
                        value={selectedBookImageFile ? selectedBookImageFile.name : bookFormData.image}
                        onChange={(e) => setBookFormData(prev => ({ ...prev, image: e.target.value }))}
                        placeholder="Select image file or enter image filename..."
                        style={{ flex: 1 }}
                      />
                      {/* Show existing book image preview with zoom on hover */}
                      {(selectedBook?.image || bookFormData.image) && !selectedBookImageFile && (
                        <div 
                          style={{
                            position: 'relative',
                            width: '50px',
                            height: '50px',
                            borderRadius: '6px',
                            overflow: 'visible',
                            border: '2px solid #e5e7eb',
                            flexShrink: 0,
                            cursor: 'pointer'
                          }}
                          className="tab-image-preview-container"
                        >
                          <img
                            src={`/images/${selectedBook?.image || bookFormData.image}`}
                            alt="Current book"
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                              borderRadius: '4px'
                            }}
                            onError={(e) => {
                              (e.target as HTMLImageElement).src = '/images/placeholder-book.png';
                            }}
                          />
                          {/* Zoomed image on hover */}
                          <div
                            style={{
                              position: 'absolute',
                              top: '60px',
                              right: '0',
                              width: '200px',
                              height: '200px',
                              borderRadius: '8px',
                              overflow: 'hidden',
                              border: '3px solid #3b82f6',
                              boxShadow: '0 10px 25px rgba(0, 0, 0, 0.3)',
                              backgroundColor: 'white',
                              zIndex: 1000,
                              opacity: 0,
                              visibility: 'hidden',
                              transition: 'opacity 0.2s ease, visibility 0.2s ease',
                              pointerEvents: 'none'
                            }}
                            className="tab-image-zoom-preview"
                          >
                            <img
                              src={`/images/${selectedBook?.image || bookFormData.image}`}
                              alt="Current book zoomed"
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'contain'
                              }}
                              onError={(e) => {
                                (e.target as HTMLImageElement).src = '/images/placeholder-book.png';
                              }}
                            />
                          </div>
                          <style>{`
                            .tab-image-preview-container:hover .tab-image-zoom-preview {
                              opacity: 1 !important;
                              visibility: visible !important;
                            }
                          `}</style>
                        </div>
                      )}
                      {/* Show selected file preview with zoom on hover */}
                      {selectedBookImageFile && (
                        <div 
                          style={{
                            position: 'relative',
                            width: '50px',
                            height: '50px',
                            borderRadius: '6px',
                            overflow: 'visible',
                            border: '2px solid #3b82f6',
                            flexShrink: 0,
                            cursor: 'pointer'
                          }}
                          className="tab-new-image-preview-container"
                        >
                          <img
                            src={URL.createObjectURL(selectedBookImageFile)}
                            alt="New book"
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                              borderRadius: '4px'
                            }}
                          />
                          {/* Zoomed image on hover for new file */}
                          <div
                            style={{
                              position: 'absolute',
                              top: '60px',
                              right: '0',
                              width: '200px',
                              height: '200px',
                              borderRadius: '8px',
                              overflow: 'hidden',
                              border: '3px solid #3b82f6',
                              boxShadow: '0 10px 25px rgba(0, 0, 0, 0.3)',
                              backgroundColor: 'white',
                              zIndex: 1000,
                              opacity: 0,
                              visibility: 'hidden',
                              transition: 'opacity 0.2s ease, visibility 0.2s ease',
                              pointerEvents: 'none'
                            }}
                            className="tab-new-image-zoom-preview"
                          >
                            <img
                              src={URL.createObjectURL(selectedBookImageFile)}
                              alt="New book zoomed"
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'contain'
                              }}
                            />
                          </div>
                          <style>{`
                            .tab-new-image-preview-container:hover .tab-new-image-zoom-preview {
                              opacity: 1 !important;
                              visibility: visible !important;
                            }
                          `}</style>
                        </div>
                      )}
                    </div>
                  </div>
                </FormField>
              </FormRow>

              <FormRow style={{ gridTemplateColumns: '1fr' }}>
                <FormField>
                  <FormLabel>
                    Short Description:
                  </FormLabel>
                  <RichTextEditor
                    value={bookFormData.short_description}
                    onChange={(value: string) => setBookFormData(prev => ({ ...prev, short_description: value }))}
                    placeholder="Enter a brief description with formatting..."
                    showImageUpload={false}
                    minHeight="150px"
                  />
                </FormField>
              </FormRow>

              <FormRow style={{ gridTemplateColumns: '1fr' }}>
                <FormField>
                  <FormLabel>
                    Description: <span style={{ color: '#dc2626' }}>*</span>
                  </FormLabel>
                  <RichTextEditor
                    value={bookFormData.description}
                    onChange={(value: string) => setBookFormData(prev => ({ ...prev, description: value }))}
                    placeholder="Enter book description..."
                    showImageUpload={false}
                    minHeight="250px"
                    maxHeight="250px"
                  />
                </FormField>
              </FormRow>
            </FormSection>

            {/* Classification */}
            <FormSection>
              <FormSectionTitle>📊 Classification</FormSectionTitle>
              <FormRow>
                <FormField>
                  <FormLabel>
                    Subject: <span style={{ color: '#dc2626' }}>*</span>
                  </FormLabel>
                  <FormSelect
                    value={bookFormData.subject_id}
                    onChange={(e) => setBookFormData(prev => ({ ...prev, subject_id: e.target.value }))}
                  >
                    <option value="">Select Subject...</option>
                    {subjects.map(subject => (
                      <option key={subject.id} value={subject.id}>{subject.name}</option>
                    ))}
                  </FormSelect>
                </FormField>

                <FormField>
                  <FormLabel>
                    Grade: <span style={{ color: '#dc2626' }}>*</span>
                  </FormLabel>
                  <FormSelect
                    value={bookFormData.grade_id}
                    onChange={(e) => setBookFormData(prev => ({ ...prev, grade_id: e.target.value }))}
                  >
                    <option value="">Select Grade...</option>
                    {grades.map(grade => (
                      <option key={grade.id} value={grade.id}>{grade.name}</option>
                    ))}
                  </FormSelect>
                </FormField>
              </FormRow>

              <FormRow>
                <FormField>
                  <FormLabel>
                    Country: <span style={{ color: '#dc2626' }}>*</span>
                  </FormLabel>
                  <FormSelect
                    value={bookFormData.country}
                    onChange={(e) => {
                      const countryId = e.target.value;
                      setBookFormData(prev => ({ ...prev, country: countryId, state_id: '', state_ids: '[]' }));
                      if (countryId) {
                        fetchStates(countryId);
                      } else {
                        setStates([]);
                      }
                    }}
                  >
                    <option value="">Select Country...</option>
                    {countries.map(country => (
                      <option key={country.id} value={country.id}>{country.name}</option>
                    ))}
                  </FormSelect>
                </FormField>

                <FormField>
                  <FormLabel>States: <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: 'normal' }}>(Select multiple)</span></FormLabel>
                  <div style={{
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    maxHeight: '200px',
                    overflowY: 'auto',
                    padding: '8px',
                    backgroundColor: states.length === 0 ? '#f9fafb' : 'white'
                  }}>
                    {states.length === 0 ? (
                      <div style={{ color: '#9ca3af', fontSize: '14px', padding: '8px' }}>
                        {bookFormData.country ? 'No states available' : 'Select a country first'}
                      </div>
                    ) : (
                      states.map(state => {
                        const selectedStates = (() => {
                          try {
                            const parsed = JSON.parse(bookFormData.state_ids || '[]');
                            return Array.isArray(parsed) ? parsed.map(String) : [];
                          } catch { return []; }
                        })();
                        const isChecked = selectedStates.includes(String(state.id));
                        return (
                          <label
                            key={state.id}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              padding: '8px',
                              cursor: 'pointer',
                              borderRadius: '4px',
                              backgroundColor: isChecked ? '#eff6ff' : 'transparent',
                              marginBottom: '4px',
                              transition: 'background-color 0.2s'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = isChecked ? '#dbeafe' : '#f3f4f6'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = isChecked ? '#eff6ff' : 'transparent'}
                          >
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={(e) => {
                                const stateId = String(state.id);
                                let newSelectedStates: string[];
                                if (e.target.checked) {
                                  newSelectedStates = [...selectedStates, stateId];
                                } else {
                                  newSelectedStates = selectedStates.filter(id => id !== stateId);
                                }
                                setBookFormData(prev => ({
                                  ...prev,
                                  state_ids: JSON.stringify(newSelectedStates),
                                  state_id: newSelectedStates.length > 0 ? newSelectedStates[0] : ''
                                }));
                              }}
                              style={{ marginRight: '10px', width: '18px', height: '18px', cursor: 'pointer' }}
                            />
                            <span style={{ fontSize: '14px', color: '#374151' }}>{state.name}</span>
                          </label>
                        );
                      })
                    )}
                  </div>
                  {(() => {
                    try {
                      const selected = JSON.parse(bookFormData.state_ids || '[]');
                      if (Array.isArray(selected) && selected.length > 0) {
                        return (
                          <div style={{ marginTop: '8px', fontSize: '12px', color: '#059669' }}>
                            ✓ {selected.length} state{selected.length > 1 ? 's' : ''} selected
                          </div>
                        );
                      }
                    } catch { }
                    return null;
                  })()}
                </FormField>
              </FormRow>
            </FormSection>

            {/* Pricing */}
            <FormSection>
              <FormSectionTitle>💰 Pricing</FormSectionTitle>
              <FormRow>
                <FormField>
                  <FormLabel>
                    Price: <span style={{ color: '#dc2626' }}>*</span>
                  </FormLabel>
                  <FormInput
                    type="text"
                    value={bookFormData.price}
                    onChange={(e) => setBookFormData(prev => ({ ...prev, price: e.target.value }))}
                    placeholder="e.g., 29.99"
                  />
                </FormField>

                <FormField>
                  <FormLabel>Discount Price:</FormLabel>
                  <FormInput
                    type="text"
                    value={bookFormData.discount_price}
                    onChange={(e) => setBookFormData(prev => ({ ...prev, discount_price: e.target.value }))}
                    placeholder="e.g., 19.99"
                  />
                </FormField>
              </FormRow>
            </FormSection>

            {/* Links */}
            <FormSection>
              <FormSectionTitle>🔗 Links</FormSectionTitle>
              <FormRow>
                <FormField>
                  <FormLabel>
                    Video Link: <span style={{ color: '#dc2626' }}>*</span>
                  </FormLabel>
                  <FormInput
                    type="text"
                    value={bookFormData.video_link}
                    onChange={(e) => setBookFormData(prev => ({ ...prev, video_link: e.target.value }))}
                    placeholder="e.g., https://youtube.com/..."
                  />
                </FormField>

                <FormField>
                  <FormLabel>File:</FormLabel>
                  <FormInput
                    type="text"
                    value={bookFormData.file}
                    onChange={(e) => setBookFormData(prev => ({ ...prev, file: e.target.value }))}
                    placeholder="e.g., sample.pdf"
                  />
                </FormField>
              </FormRow>

              <FormRow>
                <FormField>
                  <FormLabel>Referral Link:</FormLabel>
                  <FormInput
                    type="text"
                    value={bookFormData.referal_link}
                    onChange={(e) => setBookFormData(prev => ({ ...prev, referal_link: e.target.value }))}
                    placeholder="e.g., https://..."
                  />
                </FormField>

                <FormField>
                  <FormLabel>Other Link:</FormLabel>
                  <FormInput
                    type="text"
                    value={bookFormData.other_link}
                    onChange={(e) => setBookFormData(prev => ({ ...prev, other_link: e.target.value }))}
                    placeholder="e.g., https://..."
                  />
                </FormField>
              </FormRow>
            </FormSection>

            {/* Vendors */}
            <FormSection>
              <FormSectionTitle>🏪 Vendors</FormSectionTitle>
              <FormRow>
                <FormField style={{ flex: '1 1 100%' }}>
                  <FormLabel>
                    Select Vendors: <span style={{ color: '#dc2626' }}>*</span>
                  </FormLabel>
                  <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                    {['mathknots', 'amazon', 'barnesandnoble'].map(vendor => (
                      <label key={vendor} style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={bookFormData.vendors.includes(vendor)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setBookFormData(prev => ({ ...prev, vendors: [...prev.vendors, vendor] }));
                            } else {
                              setBookFormData(prev => ({ ...prev, vendors: prev.vendors.filter(v => v !== vendor) }));
                            }
                          }}
                        />
                        <span style={{ textTransform: 'capitalize' }}>{vendor === 'barnesandnoble' ? 'Barnes & Noble' : vendor}</span>
                      </label>
                    ))}
                  </div>
                </FormField>
              </FormRow>
            </FormSection>

            {/* SEO */}
            <FormSection>
              <FormSectionTitle>🔍 SEO</FormSectionTitle>
              <FormRow>
                <FormField style={{ flex: '1 1 100%' }}>
                  <FormLabel>SEO Keywords:</FormLabel>
                  <FormInput
                    type="text"
                    value={bookFormData.seo}
                    onChange={(e) => setBookFormData(prev => ({ ...prev, seo: e.target.value }))}
                    placeholder="e.g., math, education, workbook..."
                  />
                </FormField>
              </FormRow>
            </FormSection>

            {/* Actions */}
            <FormActions>
              <CancelButton 
                onClick={() => {
                  closeBookTab(activeBookTab, { stopPropagation: () => {} } as React.MouseEvent);
                }}
              >
                Cancel
              </CancelButton>
              <SubmitButton
                onClick={submitEditBook}
                loading={bookLoading}
                disabled={bookLoading || !bookFormData.title.trim() || !bookFormData.description.trim() || !bookFormData.price.trim() || !bookFormData.image.trim() || !bookFormData.video_link.trim() || !bookFormData.subject_id || !bookFormData.grade_id || !bookFormData.country || (bookFormData.vendors.length === 0 && !bookFormData.vendor_id)}
              >
                {bookLoading ? 'Updating...' : 'Update Book'}
              </SubmitButton>
            </FormActions>
          </FormContainer>
        </div>
      );
    };

    return (
      <div style={{ padding: '24px' }}>
        {/* Browser-like Tab Bar */}
        <div style={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: '2px',
          marginBottom: '0',
          borderBottom: '2px solid #e5e7eb',
          backgroundColor: '#f3f4f6',
          padding: '8px 8px 0 8px',
          borderRadius: '8px 8px 0 0',
          overflowX: 'auto'
        }}>
          {bookTabs.map(tab => (
            <div
              key={tab.id}
              onClick={() => switchToBookTab(tab.id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 16px',
                backgroundColor: activeBookTab === tab.id ? 'white' : '#e5e7eb',
                borderRadius: '8px 8px 0 0',
                cursor: 'pointer',
                border: activeBookTab === tab.id ? '2px solid #e5e7eb' : '1px solid #d1d5db',
                borderBottom: activeBookTab === tab.id ? '2px solid white' : '1px solid #d1d5db',
                marginBottom: activeBookTab === tab.id ? '-2px' : '0',
                fontSize: '14px',
                fontWeight: activeBookTab === tab.id ? '600' : '500',
                color: activeBookTab === tab.id ? '#253C8C' : '#6b7280',
                transition: 'all 0.15s ease',
                whiteSpace: 'nowrap',
                minWidth: tab.type === 'list' ? '120px' : '150px',
                maxWidth: '250px'
              }}
            >
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{tab.title}</span>
              {tab.type === 'edit' && (
                <button
                  onClick={(e) => closeBookTab(tab.id, e)}
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: '2px 4px',
                    cursor: 'pointer',
                    color: '#9ca3af',
                    borderRadius: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '16px',
                    lineHeight: 1
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#fee2e2';
                    e.currentTarget.style.color = '#dc2626';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = '#9ca3af';
                  }}
                  title="Close tab"
                >
                  ×
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Tab Content Container */}
        <div style={{
          backgroundColor: 'white',
          border: '2px solid #e5e7eb',
          borderTop: 'none',
          borderRadius: '0 0 8px 8px',
          padding: '16px'
        }}>
          {/* Show Books List if on list tab */}
          {activeBookTab === 'list' && (
            <>
              {/* Compact Header with Title, Create Button, and Pagination on Same Line */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '16px',
                flexWrap: 'wrap',
                gap: '12px'
              }}>
                {/* Left: Create Button + Title */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <button
                    onClick={handleCreateBook}
                    style={{
                      backgroundColor: '#059669',
                      color: 'white',
                      border: 'none',
                      padding: '8px 16px',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      fontWeight: '600',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    <FiPlus size={16} /> Create
                  </button>
                  <h2 style={{ margin: 0, color: '#253C8C', fontSize: '24px', fontWeight: 'bold' }}>
                    📚 Book Management
                  </h2>
                </div>

          {/* Right: Pagination Controls */}
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <label style={{ fontSize: '13px', color: '#374151', fontWeight: '500' }}>
                Show:
              </label>
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                style={{
                  padding: '4px 8px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '13px',
                  backgroundColor: 'white',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>

            {totalPages > 1 && (
              <>
                <div style={{ width: '1px', height: '20px', backgroundColor: '#e5e7eb', margin: '0 4px' }} />
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={safePage === 1}
                  style={{
                    padding: '6px 12px',
                    backgroundColor: safePage === 1 ? '#e5e7eb' : '#3b82f6',
                    color: safePage === 1 ? '#9ca3af' : 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: safePage === 1 ? 'not-allowed' : 'pointer',
                    fontSize: '13px',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    transition: 'all 0.2s'
                  }}
                >
                  <FiChevronLeft size={14} />
                  Previous
                </button>

                <div style={{ 
                  padding: '6px 12px', 
                  backgroundColor: 'white',
                  border: '2px solid #3b82f6',
                  borderRadius: '6px',
                  color: '#3b82f6',
                  fontSize: '13px',
                  fontWeight: '700',
                  whiteSpace: 'nowrap'
                }}>
                  Page {safePage} of {totalPages}
                </div>

                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={safePage === totalPages}
                  style={{
                    padding: '6px 12px',
                    backgroundColor: safePage === totalPages ? '#e5e7eb' : '#3b82f6',
                    color: safePage === totalPages ? '#9ca3af' : 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: safePage === totalPages ? 'not-allowed' : 'pointer',
                    fontSize: '13px',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    transition: 'all 0.2s'
                  }}
                >
                  Next
                  <FiChevronRight size={14} />
                </button>
              </>
            )}
          </div>
        </div>

        {/* Compact Filters and Search */}
        <div style={{
          backgroundColor: 'white',
          padding: '12px 16px',
          borderRadius: '8px',
          marginBottom: '12px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          border: '1px solid #e5e7eb'
        }}>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
            gap: '12px',
            alignItems: 'end'
          }}>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', color: '#374151', fontSize: '12px' }}>
                🔍 Search
              </label>
              <input
                type="text"
                placeholder="Search by title..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  outline: 'none',
                  transition: 'border-color 0.2s',
                  height: '36px'
                }}
                onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', color: '#374151', fontSize: '12px' }}>
                📊 Filter by Subject
              </label>
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  backgroundColor: 'white',
                  outline: 'none',
                  height: '36px',
                  cursor: 'pointer'
                }}
              >
                <option value="all">All Subjects</option>
                {subjects.map(subject => (
                  <option key={subject.id} value={subject.name.toLowerCase()}>
                    {subject.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', color: '#374151', fontSize: '12px' }}>
                🎓 Filter by Grade
              </label>
              <select
                value={bookGradeFilter}
                onChange={(e) => setBookGradeFilter(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  backgroundColor: 'white',
                  outline: 'none',
                  height: '36px',
                  cursor: 'pointer'
                }}
              >
                <option value="all">All Grades</option>
                {grades.map(grade => (
                  <option key={grade.id} value={grade.id}>
                    {grade.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', color: '#374151', fontSize: '12px' }}>
                🌍 Filter by Country
              </label>
              <select
                value={bookCountryFilter}
                onChange={(e) => {
                  const countryId = e.target.value;
                  setBookCountryFilter(countryId);
                  setBookStateFilter('all'); // Reset state when country changes
                  if (countryId && countryId !== 'all') {
                    fetchStates(countryId); // Load states for selected country
                  } else {
                    setStates([]); // Clear states if no country selected
                  }
                }}
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  backgroundColor: 'white',
                  outline: 'none',
                  height: '36px',
                  cursor: 'pointer'
                }}
              >
                <option value="all">All Countries</option>
                {countries.map(country => (
                  <option key={country.id} value={country.id}>
                    {country.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', color: '#374151', fontSize: '12px' }}>
                📍 Filter by State
              </label>
              <select
                value={bookStateFilter}
                onChange={(e) => setBookStateFilter(e.target.value)}
                disabled={bookCountryFilter === 'all' || states.length === 0}
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  backgroundColor: bookCountryFilter === 'all' ? '#f3f4f6' : 'white',
                  outline: 'none',
                  height: '36px',
                  cursor: bookCountryFilter === 'all' ? 'not-allowed' : 'pointer',
                  opacity: bookCountryFilter === 'all' ? 0.6 : 1
                }}
              >
                <option value="all">{bookCountryFilter === 'all' ? 'Select Country First' : 'All States'}</option>
                {states.map(state => (
                  <option key={state.id} value={state.id}>
                    {state.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Books Table */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          overflow: 'hidden',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          {loading && (
            <div style={{ padding: '40px', textAlign: 'center' }}>
              <div style={{ fontSize: '18px', color: '#6b7280' }}>Loading books...</div>
            </div>
          )}

          {error && (
            <div style={{ padding: '20px', backgroundColor: '#fef2f2', color: '#dc2626', textAlign: 'center' }}>
              <strong>Error:</strong> {error}
            </div>
          )}

          {!loading && !error && sortedBooks.length === 0 && (
            <div style={{ padding: '40px', textAlign: 'center' }}>
              <div style={{ fontSize: '18px', color: '#6b7280' }}>
                {searchTerm || roleFilter !== 'all' || bookGradeFilter !== 'all' || bookCountryFilter !== 'all' || bookStateFilter !== 'all' 
                  ? 'No books match your search criteria.' 
                  : 'No books found.'}
              </div>
            </div>
          )}

          {!loading && !error && sortedBooks.length > 0 && (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                    {bookColumns.map(column => (
                      <th 
                        key={column}
                        onClick={() => {
                          if (sortColumn === column) {
                            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
                          } else {
                            setSortColumn(column);
                            setSortDirection('asc');
                          }
                        }}
                        style={{ 
                          padding: '16px', 
                          textAlign: 'left', 
                          fontWeight: 'bold', 
                          color: '#374151',
                          cursor: 'pointer',
                          userSelect: 'none',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#f3f4f6';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                        title={`Click to sort by ${column}`}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span>{column}</span>
                          <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minWidth: '16px' }}>
                            {sortColumn === column ? (
                              sortDirection === 'asc' ? (
                                <FiArrowUp size={14} color="#10b981" />
                              ) : (
                                <FiArrowDown size={14} color="#10b981" />
                              )
                            ) : (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '0px', fontSize: '10px', color: '#9ca3af', lineHeight: '1' }}>
                                <span>▲</span>
                                <span>▼</span>
                              </div>
                            )}
                          </span>
                        </div>
                      </th>
                    ))}
                    <th style={{ padding: '16px', textAlign: 'center', fontWeight: 'bold', color: '#374151' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedBooks.map((book, index) => (
                    <tr
                      key={book.id || index}
                      style={{
                        borderBottom: '1px solid #e5e7eb',
                        backgroundColor: index % 2 === 0 ? '#ffffff' : '#f9fafb'
                      }}
                    >
                      <td style={{ padding: '16px', color: '#374151' }}>{book.id || 'N/A'}</td>
                      <td style={{ padding: '16px', color: '#374151', fontWeight: '500' }}>{book.title || 'N/A'}</td>
                      <td style={{ padding: '16px', color: '#374151' }}>{book.category || book.subjectInfo?.name || 'N/A'}</td>
                      <td style={{ padding: '16px', color: '#374151' }}>{book.gradeInfo?.name || 'N/A'}</td>
                      <td style={{ padding: '16px', color: '#374151' }}>${book.price || '0.00'}</td>
                      <td style={{ padding: '16px', color: '#374151' }}>
                        {book.created_at ? new Date(book.created_at).toLocaleDateString() : 'N/A'}
                      </td>
                      <td style={{ padding: '16px', color: '#374151' }}>
                        {book.updated_at ? new Date(book.updated_at).toLocaleDateString() : 'N/A'}
                      </td>
                      <td style={{ padding: '16px', textAlign: 'center' }}>
                        <div style={{ display: 'flex', gap: '6px', justifyContent: 'center', flexWrap: 'wrap' }}>
                          <button
                            onClick={() => handleEditBook(book)}
                            style={{
                              backgroundColor: '#3b82f6',
                              color: 'white',
                              border: 'none',
                              padding: '8px 12px',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '14px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}
                            title="Edit"
                          >
                            <FiEdit size={14} />
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteBook(book)}
                            style={{
                              backgroundColor: '#ef4444',
                              color: 'white',
                              border: 'none',
                              padding: '8px 12px',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '14px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}
                            title="Delete"
                          >
                            <FiTrash2 size={14} />
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Results Summary */}
          {!loading && !error && sortedBooks.length > 0 && (
            <div style={{
              padding: '16px',
              borderTop: '1px solid #e5e7eb',
              backgroundColor: '#f9fafb',
              color: '#6b7280',
              fontSize: '14px',
              textAlign: 'center'
            }}>
              Showing <strong>{startIndex + 1}-{Math.min(endIndex, sortedBooks.length)}</strong> of <strong>{sortedBooks.length}</strong> books
              {(searchTerm || roleFilter !== 'all' || bookGradeFilter !== 'all' || bookCountryFilter !== 'all' || bookStateFilter !== 'all') && ' (filtered)'}
            </div>
          )}
              </div>
            </>
          )}

          {/* Show Edit Form if on an edit tab */}
          {activeBookTab !== 'list' && renderEditTabContent()}
        </div>
      </div>
    );
  };

  // Package CRUD handlers
  const handleCreatePackage = (): void => {
    console.log('➕ Create new video package with ALL fields');
    setPackageFormData({
      name: '',
      description: '',
      annual_price: '',
      discount_price: '',
      country_id: '',
      state_id: '',
      grade_id: '',
      subject_id: '',
      book_id: '', // Legacy single book field
      is_active: true,
      features: '',
      image: '',
      metadata: '',
      deleted_at: '',
      book_ids: [], // Many-to-many book associations
      sort_orders: [], // Sort orders for each book
      junction_active: [] // Active status for each book association
    });
    setActiveSection('create-package');
  };

  const handleEditPackage = (pkg: any): void => {
    console.log('✏️ Edit video package with ALL fields:', pkg);
    setSelectedPackage(pkg);
    setPackageFormData({
      name: pkg.name || '',
      description: pkg.description || '',
      annual_price: pkg.annual_price?.toString() || '',
      discount_price: pkg.raw_discount_price?.toString() || '',
      country_id: pkg.country_id?.toString() || '',
      state_id: pkg.state_id?.toString() || '',
      grade_id: pkg.grade_id?.toString() || '',
      subject_id: pkg.subject_id?.toString() || '',
      book_id: pkg.book_id?.toString() || '', // Legacy single book field
      is_active: pkg.is_active || true,
      features: pkg.features || '',
      image: pkg.image || '',
      metadata: typeof pkg.metadata === 'object' ? JSON.stringify(pkg.metadata) : pkg.metadata || '',
      deleted_at: pkg.deleted_at || '',
      // Junction table data for many-to-many book associations
      book_ids: pkg.associated_books?.map((book: any) => book.book_id.toString()) || [],
      sort_orders: pkg.associated_books?.map((book: any) => book.sort_order.toString()) || [],
      junction_active: pkg.associated_books?.map((book: any) => book.is_active) || []
    });
    setActiveSection('edit-package');
  };

  const handleDeletePackage = (pkg: any): void => {
    console.log('🗑️ Delete video package:', pkg);
    setSelectedPackage(pkg);
    setShowDeletePackageModal(true);
  };

  // Online Classes Handlers
  // CRUD handlers for courses
  const handleCreateCourse = (): void => {
    alert('Course creation will be implemented. For now, please use the Online Classes or In-Person Classes sections to create courses.');
  };

  const handleEditCourse = (course: any): void => {
    alert(`Editing course: ${course.title || course.course_name}. Please use the Online Classes or In-Person Classes sections to edit courses.`);
  };

  const handleDeleteCourse = (course: any): void => {
    if (window.confirm(`Are you sure you want to delete the course "${course.title || course.course_name}"?`)) {
      fetch(apiUrls.academy.deleteCourse(course.id), {
        method: 'DELETE',
        headers: getHttpHeaders()
      })
        .then(response => response.json())
        .then(data => {
          if (data.success) {
            alert('Course deleted successfully!');
            fetchCourses();
          } else {
            throw new Error(data.error || 'Failed to delete course');
          }
        })
        .catch(error => {
          console.error('Error deleting course:', error);
          alert(`Error deleting course: ${error.message}`);
        });
    }
  };

  const handleCreateOnlineClass = (): void => {
    setSelectedOnlineClass(null);
    // Reset form data
    setOnlineClassFormData({
      course_name: '',
      program: '',
      instructor: '',
      level: '',
      day: '',
      time1: '',
      time2: '',
      date1: '',
      date2: '',
      fee: '',
      description: '',
      slots: 20,
      zoom_link: '',
      total_hours: '',
      year: new Date().getFullYear().toString(),
      mode: 'online'
    });
    setActiveSection('create-online-class');
  };

  const handleEditOnlineClass = (onlineClass: any): void => {
    setSelectedOnlineClass(onlineClass);
    // Populate form with existing data
    setOnlineClassFormData({
      course_name: onlineClass.title || onlineClass.course_name || '',
      program: onlineClass.program || '',
      instructor: onlineClass.instructor || '',
      level: onlineClass.level || '',
      day: onlineClass.day || '',
      time1: onlineClass.time1 || '',
      time2: onlineClass.time2 || '',
      date1: onlineClass.date1 || onlineClass.start_date || '',
      date2: onlineClass.date2 || onlineClass.end_date || '',
      fee: onlineClass.fee || onlineClass.price || '',
      description: onlineClass.description || '',
      slots: onlineClass.slots || onlineClass.max_students || 20,
      zoom_link: onlineClass.zoom_link || '',
      total_hours: onlineClass.total_hours || onlineClass.duration || '',
      year: onlineClass.year || new Date().getFullYear().toString(),
      mode: 'online'
    } as OnlineClassFormData);
    setActiveSection('edit-online-class');
  };

  const handleDeleteOnlineClass = (onlineClass: any): void => {
    setSelectedOnlineClass(onlineClass);
    setShowDeleteOnlineClassModal(true);
  };

  // Submit functions for online classes
  const submitCreateOnlineClass = async (): Promise<void> => {
    try {
      setLoading(true);

      // Validate required fields with detailed error message
      const missingFields = [];
      if (!onlineClassFormData.course_name?.trim()) missingFields.push('Course Name');
      if (!onlineClassFormData.program?.trim()) missingFields.push('Program');
      if (!onlineClassFormData.instructor?.trim()) missingFields.push('Instructor');
      if (!onlineClassFormData.level?.trim()) missingFields.push('Level');
      if (!onlineClassFormData.day?.trim()) missingFields.push('Day');
      if (!onlineClassFormData.time1?.trim()) missingFields.push('Start Time');
      if (!onlineClassFormData.time2?.trim()) missingFields.push('End Time');
      if (!onlineClassFormData.date1) missingFields.push('Start Date');
      if (!onlineClassFormData.date2) missingFields.push('End Date');
      if (!onlineClassFormData.fee?.trim()) missingFields.push('Fee');

      if (missingFields.length > 0) {
        alert(`Please fill in the following required fields:\n\n${missingFields.join('\n')}`);
        setLoading(false);
        return;
      }

      const response = await fetch(apiUrls.academy.createCourse, {
        method: 'POST',
        headers: getHttpHeaders(),
        body: JSON.stringify(onlineClassFormData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create course');
      }

      const result = await response.json();
      console.log('✅ Course created successfully:', result);

      // Refresh the online classes data
      await fetchOnlineClasses();

      // Navigate back to online classes management
      setActiveSection('online-classes');

      alert('Online class created successfully!');

    } catch (error) {
      console.error('❌ Error creating course:', error);
      alert(`Failed to create course: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const submitEditOnlineClass = async (): Promise<void> => {
    try {
      setLoading(true);

      if (!selectedOnlineClass || !selectedOnlineClass.course_id) {
        alert('No course selected for editing');
        return;
      }

      const response = await fetch(apiUrls.academy.updateCourse(selectedOnlineClass.course_id), {
        method: 'PUT',
        headers: getHttpHeaders(),
        body: JSON.stringify(onlineClassFormData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update course');
      }

      const result = await response.json();
      console.log('✅ Course updated successfully:', result);

      // Refresh the online classes data
      await fetchOnlineClasses();

      // Navigate back to online classes management
      setActiveSection('online-classes');

      alert('Online class updated successfully!');

    } catch (error) {
      console.error('❌ Error updating course:', error);
      alert(`Failed to update course: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const submitDeleteOnlineClass = async (): Promise<void> => {
    try {
      setLoading(true);

      if (!selectedOnlineClass || !selectedOnlineClass.course_id) {
        alert('No course selected for deletion');
        return;
      }

      const response = await fetch(apiUrls.academy.deleteCourse(selectedOnlineClass.course_id), {
        method: 'DELETE',
        headers: getHttpHeaders()
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete course');
      }

      const result = await response.json();
      console.log('✅ Course deleted successfully:', result);

      // Refresh the online classes data
      await fetchOnlineClasses();

      // Close modal
      setShowDeleteOnlineClassModal(false);
      setSelectedOnlineClass(null);

      alert('Online class deleted successfully!');

    } catch (error) {
      console.error('❌ Error deleting course:', error);
      alert(`Failed to delete course: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  // In-Person Classes Handlers
  const handleCreateInPersonClass = (): void => {
    setSelectedInPersonClass(null);
    // Reset form data
    setInPersonClassFormData({
      course_name: '',
      program: '',
      instructor: '',
      level: '',
      day: '',
      time1: '',
      time2: '',
      date1: '',
      date2: '',
      fee: '',
      description: '',
      slots: 20,
      zoom_link: '', // Not needed for in-person but keeping for consistency
      total_hours: '',
      year: new Date().getFullYear().toString(),
      mode: 'in-person'
    } as OnlineClassFormData);
    setActiveSection('create-inperson-class');
  };

  const handleEditInPersonClass = (inPersonClass: any): void => {
    setSelectedInPersonClass(inPersonClass);
    // Populate form with existing data
    setInPersonClassFormData({
      course_name: inPersonClass.title || inPersonClass.course_name || '',
      program: inPersonClass.program || '',
      instructor: inPersonClass.instructor || '',
      level: inPersonClass.level || '',
      day: inPersonClass.day || '',
      time1: inPersonClass.time1 || '',
      time2: inPersonClass.time2 || '',
      date1: inPersonClass.date1 || inPersonClass.start_date || '',
      date2: inPersonClass.date2 || inPersonClass.end_date || '',
      fee: inPersonClass.fee || inPersonClass.price || '',
      description: inPersonClass.description || '',
      slots: inPersonClass.slots || inPersonClass.max_students || 20,
      zoom_link: '', // In-person classes don't need zoom links
      total_hours: inPersonClass.total_hours || inPersonClass.duration || '',
      year: inPersonClass.year || new Date().getFullYear().toString(),
      mode: 'in-person'
    } as OnlineClassFormData);
    setActiveSection('edit-inperson-class');
  };

  const handleDeleteInPersonClass = (inPersonClass: any): void => {
    setSelectedInPersonClass(inPersonClass);
    setShowDeleteInPersonClassModal(true);
  };

  // Submit functions for in-person classes
  const submitCreateInPersonClass = async (): Promise<void> => {
    try {
      setLoading(true);

      // Validate required fields
      if (!inPersonClassFormData.course_name || !inPersonClassFormData.program || !inPersonClassFormData.instructor ||
        !inPersonClassFormData.level || !inPersonClassFormData.day || !inPersonClassFormData.time1 ||
        !inPersonClassFormData.time2 || !inPersonClassFormData.date1 || !inPersonClassFormData.date2 ||
        !inPersonClassFormData.fee) {
        alert('Please fill in all required fields');
        return;
      }

      const response = await fetch(apiUrls.academy.createCourse, {
        method: 'POST',
        headers: getHttpHeaders(),
        body: JSON.stringify(inPersonClassFormData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create course');
      }

      const result = await response.json();
      console.log('✅ In-person class created successfully:', result);

      // Refresh the in-person classes data
      await fetchInPersonClasses();

      // Navigate back to in-person classes management
      setActiveSection('inperson-classes');

      alert('In-person class created successfully!');

    } catch (error) {
      console.error('❌ Error creating in-person class:', error);
      alert(`Failed to create in-person class: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const submitEditInPersonClass = async (): Promise<void> => {
    try {
      setLoading(true);

      if (!selectedInPersonClass || !selectedInPersonClass.course_id) {
        alert('No course selected for editing');
        return;
      }

      const response = await fetch(apiUrls.academy.updateCourse(selectedInPersonClass.course_id), {
        method: 'PUT',
        headers: getHttpHeaders(),
        body: JSON.stringify(inPersonClassFormData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update course');
      }

      const result = await response.json();
      console.log('✅ In-person class updated successfully:', result);

      // Refresh the in-person classes data
      await fetchInPersonClasses();

      // Navigate back to in-person classes management
      setActiveSection('inperson-classes');

      alert('In-person class updated successfully!');

    } catch (error) {
      console.error('❌ Error updating in-person class:', error);
      alert(`Failed to update in-person class: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  // Batch Handlers
  const handleCreateBatch = (): void => {
    setSelectedBatch(null);
    // Reset form data
    setBatchFormData({
      batch_name: '',
      program: '',
      instructor: '',
      description: '',
      start_date: '',
      end_date: '',
      max_students: 20,
      status: 'Active',
      courses: []
    });
    setActiveSection('create-batch');
  };

  const handleEditBatch = (batch: any): void => {
    setSelectedBatch(batch);
    // Populate form with existing data
    setBatchFormData({
      batch_name: batch.batch_name || batch.name || '',
      program: batch.program || '',
      instructor: batch.instructor || '',
      description: batch.description || '',
      start_date: batch.start_date || '',
      end_date: batch.end_date || '',
      max_students: batch.max_students || 20,
      status: batch.status || 'Active',
      courses: batch.courses || []
    });
    setActiveSection('edit-batch');
  };

  const handleDeleteBatch = (batch: any): void => {
    console.log('🗑️ Delete batch:', batch);
    setSelectedBatch(batch);
    // Note: You can implement a delete modal here if needed
  };

  // Batch Student Handlers
  const handleCreateBatchStudent = (): void => {
    setSelectedBatchStudent(null);
    // Reset form data
    setBatchStudentFormData({
      student_name: '',
      email: '',
      batch_id: '',
      enrollment_date: new Date().toISOString().split('T')[0],
      status: 'Active',
      progress: 0,
      grade: ''
    });
    setActiveSection('create-batch-student');
  };

  const handleEditBatchStudent = (batchStudent: any): void => {
    setSelectedBatchStudent(batchStudent);
    // Populate form with existing data
    setBatchStudentFormData({
      student_name: batchStudent.student_name || batchStudent.name || '',
      email: batchStudent.email || '',
      batch_id: batchStudent.batch_id || '',
      enrollment_date: batchStudent.enrollment_date || new Date().toISOString().split('T')[0],
      status: batchStudent.status || 'Active',
      progress: batchStudent.progress || 0,
      grade: batchStudent.grade || ''
    });
    setActiveSection('edit-batch-student');
  };

  const handleDeleteBatchStudent = (batchStudent: any): void => {
    console.log('🗑️ Remove batch student:', batchStudent);
    setSelectedBatchStudent(batchStudent);
    // Note: You can implement a delete modal here if needed
  };

  // Submit functions for batches
  const submitCreateBatch = async (): Promise<void> => {
    try {
      setLoading(true);

      // Validate required fields
      if (!batchFormData.batch_name || !batchFormData.program || !batchFormData.instructor ||
        !batchFormData.start_date || !batchFormData.end_date) {
        alert('Please fill in all required fields');
        return;
      }

      const response = await fetch(apiUrls.academy.batches, {
        method: 'POST',
        headers: getHttpHeaders(),
        body: JSON.stringify(batchFormData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create batch');
      }

      const result = await response.json();
      console.log('✅ Batch created successfully:', result);

      // Refresh the batches data
      await fetchBatches();

      // Navigate back to batches management
      setActiveSection('batches');

      alert('Batch created successfully!');

    } catch (error) {
      console.error('❌ Error creating batch:', error);
      alert(`Failed to create batch: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const submitEditBatch = async (): Promise<void> => {
    try {
      setLoading(true);

      if (!selectedBatch || !selectedBatch.id) {
        alert('No batch selected for editing');
        return;
      }

      const response = await fetch(apiUrls.academy.batchById(selectedBatch.id), {
        method: 'PUT',
        headers: getHttpHeaders(),
        body: JSON.stringify(batchFormData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update batch');
      }

      const result = await response.json();
      console.log('✅ Batch updated successfully:', result);

      // Refresh the batches data
      await fetchBatches();

      // Navigate back to batches management
      setActiveSection('batches');

      alert('Batch updated successfully!');

    } catch (error) {
      console.error('❌ Error updating batch:', error);
      alert(`Failed to update batch: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  // Submit functions for batch students
  const submitCreateBatchStudent = async (): Promise<void> => {
    try {
      setLoading(true);

      // Validate required fields
      if (!batchStudentFormData.student_name || !batchStudentFormData.email || !batchStudentFormData.batch_id) {
        alert('Please fill in all required fields');
        return;
      }

      const response = await fetch(apiUrls.academy.batchStudentsAdmin, {
        method: 'POST',
        headers: getHttpHeaders(),
        body: JSON.stringify(batchStudentFormData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to add student to batch');
      }

      const result = await response.json();
      console.log('✅ Student added to batch successfully:', result);

      // Refresh the batch students data
      await fetchBatchStudents();

      // Navigate back to batch students management
      setActiveSection('batch-students');

      alert('Student added to batch successfully!');

    } catch (error) {
      console.error('❌ Error adding student to batch:', error);
      alert(`Failed to add student to batch: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const submitEditBatchStudent = async (): Promise<void> => {
    try {
      setLoading(true);

      if (!selectedBatchStudent || !selectedBatchStudent.id) {
        alert('No batch student selected for editing');
        return;
      }

      const response = await fetch(`${apiUrls.academy.batchStudentsAdmin}/${selectedBatchStudent.id}`, {
        method: 'PUT',
        headers: getHttpHeaders(),
        body: JSON.stringify(batchStudentFormData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update batch student');
      }

      const result = await response.json();
      console.log('✅ Batch student updated successfully:', result);

      // Refresh the batch students data
      await fetchBatchStudents();

      // Navigate back to batch students management
      setActiveSection('batch-students');

      alert('Batch student updated successfully!');

    } catch (error) {
      console.error('❌ Error updating batch student:', error);
      alert(`Failed to update batch student: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const submitDeleteInPersonClass = async (): Promise<void> => {
    try {
      setLoading(true);

      if (!selectedInPersonClass || !selectedInPersonClass.course_id) {
        alert('No course selected for deletion');
        return;
      }

      const response = await fetch(apiUrls.academy.deleteCourse(selectedInPersonClass.course_id), {
        method: 'DELETE',
        headers: getHttpHeaders()
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete course');
      }

      const result = await response.json();
      console.log('✅ In-person class deleted successfully:', result);

      // Refresh the in-person classes data
      await fetchInPersonClasses();

      // Close modal
      setShowDeleteInPersonClassModal(false);
      setSelectedInPersonClass(null);

      alert('In-person class deleted successfully!');

    } catch (error) {
      console.error('❌ Error deleting in-person class:', error);
      alert(`Failed to delete in-person class: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const submitCreatePackage = async (): Promise<void> => {
    if (!packageFormData.name.trim() || !packageFormData.annual_price.trim() || !packageFormData.country_id || !packageFormData.grade_id || !packageFormData.subject_id) {
      alert('Name, annual price, country, grade, and category are required');
      return;
    }

    setPackageLoading(true);
    try {
      const response = await fetch(apiUrls.packages.create, {
        method: 'POST',
        headers: getHttpHeaders(),
        body: JSON.stringify({
          name: packageFormData.name.trim(),
          description: packageFormData.description.trim(),
          annual_price: parseFloat(packageFormData.annual_price),
          discount_price: packageFormData.discount_price.trim() ? parseFloat(packageFormData.discount_price) : null,
          country_id: parseInt(packageFormData.country_id),
          state_id: packageFormData.state_id ? parseInt(packageFormData.state_id) : null,
          grade_id: parseInt(packageFormData.grade_id),
          subject_id: parseInt(packageFormData.subject_id),
          is_active: packageFormData.is_active ? 1 : 0,
          features: packageFormData.features.trim() || null,
          image: packageFormData.image.trim() || null,
          metadata: packageFormData.metadata.trim() ? JSON.parse(packageFormData.metadata) : null,
          book_ids: packageFormData.book_ids.map(id => parseInt(id))
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        console.log('✅ Content package created successfully');
        setPackageFormData({
          name: '',
          description: '',
          annual_price: '',
          discount_price: '',
          country_id: '',
          state_id: '',
          grade_id: '',
          subject_id: '',
          book_id: '', // Legacy single book field
          is_active: true,
          features: '',
          image: '',
          metadata: '',
          deleted_at: '',
          book_ids: [], // Many-to-many book associations
          sort_orders: [], // Sort orders for each book
          junction_active: [] // Active status for each book association
        });
        fetchPackages(); // Refresh the list
        alert('Content package created successfully!');
        setActiveSection('packages'); // Navigate back to package management
      } else {
        throw new Error(data.error || 'Failed to create content package');
      }
    } catch (error) {
      console.error('❌ Error creating video package:', error);
      alert(`Error creating video package: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setPackageLoading(false);
    }
  };

  const submitEditPackage = async (): Promise<void> => {
    if (!packageFormData.name.trim() || !packageFormData.annual_price.trim() || !packageFormData.country_id || !packageFormData.grade_id || !packageFormData.subject_id) {
      alert('Name, annual price, country, grade, and category are required');
      return;
    }

    setPackageLoading(true);
    try {
      const response = await fetch(apiUrls.packages.byId(selectedPackage.id), {
        method: 'PUT',
        headers: getHttpHeaders(),
        body: JSON.stringify({
          name: packageFormData.name.trim(),
          description: packageFormData.description.trim(),
          annual_price: parseFloat(packageFormData.annual_price),
          discount_price: packageFormData.discount_price.trim() ? parseFloat(packageFormData.discount_price) : null,
          country_id: parseInt(packageFormData.country_id),
          state_id: packageFormData.state_id ? parseInt(packageFormData.state_id) : null,
          grade_id: parseInt(packageFormData.grade_id),
          subject_id: parseInt(packageFormData.subject_id),
          is_active: packageFormData.is_active ? 1 : 0,
          features: packageFormData.features.trim() || null,
          image: packageFormData.image.trim() || null,
          metadata: packageFormData.metadata.trim() ? JSON.parse(packageFormData.metadata) : null,
          book_ids: packageFormData.book_ids.map(id => parseInt(id))
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        console.log('✅ Content package updated successfully');
        setPackageFormData({
          name: '',
          description: '',
          annual_price: '',
          discount_price: '',
          country_id: '',
          state_id: '',
          grade_id: '',
          subject_id: '',
          book_id: '', // Legacy single book field
          is_active: true,
          features: '',
          image: '',
          metadata: '',
          deleted_at: '',
          book_ids: [], // Many-to-many book associations
          sort_orders: [], // Sort orders for each book
          junction_active: [] // Active status for each book association
        });
        setSelectedPackage(null);
        fetchPackages(); // Refresh the list
        alert('Content package updated successfully!');
        setActiveSection('packages'); // Navigate back to package management
      } else {
        throw new Error(data.error || 'Failed to update content package');
      }
    } catch (error) {
      console.error('❌ Error updating video package:', error);
      alert(`Error updating video package: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setPackageLoading(false);
    }
  };

  const submitDeletePackage = async (): Promise<void> => {
    setPackageLoading(true);
    try {
      const response = await fetch(apiUrls.packages.byId(selectedPackage.id), {
        method: 'DELETE',
        headers: getHttpHeaders()
      });

      const data = await response.json();

      if (response.ok && data.success) {
        console.log('✅ Content package deleted successfully');
        setShowDeletePackageModal(false);
        setSelectedPackage(null);
        fetchPackages(); // Refresh the list
        alert('Content package deleted successfully!');
      } else {
        throw new Error(data.error || 'Failed to delete content package');
      }
    } catch (error) {
      console.error('❌ Error deleting content package:', error);
      alert(`Error deleting content package: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setPackageLoading(false);
    }
  };

  const renderContent = (): React.JSX.Element => {
    switch (activeSection) {
      case 'dashboard':
        return renderDashboard();

      // User Management
      case 'users':
        return renderModernUserManagement();

      case 'books':
        return renderBookManagement();

      case 'create-book':
        return renderCreateBookPage();

      case 'edit-book':
        return renderEditBookPage();

      // 'videos' case removed - Video Learning System deprecated

      // 'video-learning' case removed - VideoLearningManagement deprecated

      // 'create-video' and 'edit-video' cases removed - Video Learning System deprecated

      case 'packages':
        return <PackageManagement />;

      case 'legacy-packages':
        return renderModernManagement(
          '📦 Legacy Content Packages',
          packages,
          ['ID', 'Name', 'Description', 'Category', 'Grade', 'Price', 'Discount Price', 'Legacy Book ID', 'Associated Books', 'Books Count', 'Status', 'Features', 'Image', 'Created Date', 'Updated Date'],
          ['name', 'description', 'features'],
          'status',
          ['Active', 'Inactive'],
          handleEditPackage,
          handleDeletePackage,
          undefined,
          handleCreatePackage
        );

      case 'create-package':
        return renderCreatePackagePage();

      case 'edit-package':
        return renderEditPackagePage();

      case 'create-online-class':
        return renderCreateOnlineClassPage();

      case 'edit-online-class':
        return renderEditOnlineClassPage();

      case 'create-inperson-class':
        return renderCreateInPersonClassPage();

      case 'edit-inperson-class':
        return renderEditInPersonClassPage();

      case 'create-batch':
        return renderCreateBatchPage();

      case 'edit-batch':
        return renderEditBatchPage();

      case 'create-batch-student':
        return renderCreateBatchStudentPage();

      case 'edit-batch-student':
        return renderEditBatchStudentPage();

      case 'subscriptions':
        return renderModernManagement(
          '💳 Subscriptions',
          subscriptions,
          ['ID', 'User Name', 'Plan', 'Status', 'Start Date', 'Amount'],
          ['user_name', 'plan'],
          'status',
          ['Active', 'Expired', 'Cancelled'],
          (sub) => console.log('Edit subscription:', sub),
          (sub) => console.log('Cancel subscription:', sub),
          undefined,
          () => console.log('Create subscription')
        );
      
      case 'coupons':
        return <CouponManagement />;
      
      case 'payment-settings':
        return <PaymentSettings />;

      case 'create-blog':
        return renderCreateBlogPage();

      case 'edit-blog':
        return renderEditBlogPage();

      case 'blogs':
        return renderModernManagement(
          '📝 Blog Management',
          blogs,
          ['ID', 'Title', 'Author', 'Status', 'Created Date', 'Views'],
          ['title', 'author'],
          'status',
          ['Active', 'Inactive', 'Draft'],
          handleEditBlog,
          handleDeleteBlog,
          undefined,
          handleCreateBlog
        );

      case 'faqs':
        return renderModernManagement(
          '❓ FAQ Management',
          faqs,
          ['ID', 'Question', 'Category', 'Status', 'Updated Date'],
          ['question', 'category'],
          'status',
          ['active', 'inactive', 'draft'],
          handleEditFaq,
          handleDeleteFaq,
          undefined,
          handleCreateFaq
        );

      case 'feedback':
        return renderModernManagement(
          '💬 Feedback Management',
          feedback,
          ['ID', 'Name', 'Email', 'Type', 'Subject', 'Status', 'Priority', 'Created Date'],
          ['name', 'email', 'subject', 'message'],
          'status',
          ['new', 'in_review', 'resolved', 'closed'],
          handleViewFeedback,
          handleDeleteFeedback,
          undefined,
          undefined,
          'View Details',
          <FiEye />
        );

      case 'success-stories':
        return renderSuccessStoriesManagement();

      case 'subjects':
        return renderModernManagement(
          '🏷️ Subjects',
          subjects,
          ['ID', 'Name', 'Linked Grades', 'Grades Count', 'Created Date', 'Updated Date'],
          ['name'],
          undefined,
          undefined,
          handleEditSubject,
          handleDeleteSubject,
          handleManageSubjectGrades,
          handleCreateSubject,
          'Manage Grades',
          <FiLink size={14} />
        );

      case 'grades':
        return renderModernManagement(
          '🏆 Grades',
          grades,
          ['ID', 'Name', 'Subjects', 'Created Date', 'Updated Date'],
          ['name', 'subjects'],
          'active',
          ['Active'],
          handleEditGrade,
          handleDeleteGrade,
          undefined,
          handleCreateGrade
        );

      case 'locations':
        return (
          <div style={{ padding: '24px' }}>
            <h2 style={{ margin: '0 0 24px 0', color: '#253C8C', fontSize: '28px', fontWeight: 'bold' }}>
              📍 Location Management
            </h2>

            <div style={{ display: 'grid', gap: '24px', gridTemplateColumns: 'repeat(auto-fit, minmax(600px, 1fr))' }}>
              {/* Countries */}
              <div>
                {renderModernManagement(
                  '🌍 Countries',
                  countries,
                  ['ID', 'Name', 'Status', 'Created Date', 'Updated Date'],
                  ['name'],
                  'status',
                  ['Active', 'Inactive'],
                  handleEditCountry,
                  handleDeleteCountry,
                  undefined,
                  handleCreateCountry
                )}
              </div>

              {/* States */}
              <div>
                {renderModernManagement(
                  '🏛️ States',
                  states,
                  ['ID', 'Name', 'Country', 'Status', 'Created Date', 'Updated Date'],
                  ['name', 'country_name'],
                  'status',
                  ['Active', 'Inactive'],
                  handleEditState,
                  handleDeleteState,
                  undefined,
                  handleCreateState
                )}
              </div>
            </div>
          </div>
        );

      // Academy Management Cases
      case 'courses':
        return renderModernManagement(
          '📚 Course Management',
          courses,
          ['ID', 'Course Name', 'Created Date', 'Updated Date'],
          ['course_name'],
          undefined,
          undefined,
          handleEditCourse,
          handleDeleteCourse,
          undefined,
          handleCreateCourse
        );

      case 'online-classes':
        return renderModernManagement(
          '💻 Online Classes Management',
          onlineClasses,
          ['Title', 'Duration', 'Price', 'Students', 'Status', 'Start Date', 'Created Date'],
          ['title'],
          'status',
          ['Active', 'Upcoming', 'Completed'],
          handleEditOnlineClass,
          handleDeleteOnlineClass,
          undefined,
          handleCreateOnlineClass
        );

      case 'inperson-classes':
        return renderModernManagement(
          '🏫 InPerson Classes Management',
          inPersonClasses,
          ['Title', 'Location', 'Duration', 'Price', 'Capacity', 'Enrolled', 'Status', 'Start Date', 'Created Date'],
          ['title', 'location'],
          'status',
          ['Active', 'Upcoming', 'Full', 'Completed'],
          handleEditInPersonClass,
          handleDeleteInPersonClass,
          undefined,
          handleCreateInPersonClass
        );

      case 'batches':
        return renderModernManagement(
          '📚 Batches Management',
          batches,
          ['ID', 'Batch Name', 'Program', 'Instructor', 'Students Count', 'Start Date', 'End Date', 'Status', 'Created Date'],
          ['batch_name', 'program', 'instructor'],
          'status',
          ['Active', 'Completed', 'Cancelled'],
          handleEditBatch,
          handleDeleteBatch,
          undefined,
          handleCreateBatch
        );

      case 'batch-students':
        return renderModernManagement(
          '👥 Batch Students Management',
          batchStudents,
          ['ID', 'Student Name', 'Email', 'Batch Name', 'Enrollment Date', 'Status', 'Progress', 'Grade'],
          ['student_name', 'email', 'batch_name'],
          'status',
          ['Active', 'Completed', 'Dropped', 'Suspended'],
          handleEditBatchStudent,
          handleDeleteBatchStudent,
          undefined,
          handleCreateBatchStudent
        );

      case 'content-management':
        return (
          <div style={{ padding: '20px' }}>
            <h2 style={{ color: '#059669', marginBottom: '20px' }}>📋 Content Management</h2>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '20px',
              marginTop: '20px'
            }}>
              <div style={{
                padding: '20px',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                backgroundColor: '#f9fafb',
                textAlign: 'center'
              }}>
                <h3 style={{ color: '#059669', marginBottom: '10px' }}>📚 Content Library</h3>
                <p>Manage and organize your content assets</p>
                <button
                  onClick={() => handleSubMenuClick('content-library')}
                  style={{
                    marginTop: '15px',
                    padding: '8px 16px',
                    backgroundColor: '#059669',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer'
                  }}
                >
                  Go to Content Library
                </button>
              </div>

              <div style={{
                padding: '20px',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                backgroundColor: '#f9fafb',
                textAlign: 'center'
              }}>
                <h3 style={{ color: '#059669', marginBottom: '10px' }}>🏗️ Content Structure</h3>
                <p>Organize content into logical structures and link to books</p>
                <button
                  onClick={() => handleSubMenuClick('content-structure')}
                  style={{
                    marginTop: '15px',
                    padding: '8px 16px',
                    backgroundColor: '#059669',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer'
                  }}
                >
                  Go to Content Structure
                </button>
              </div>

              <div style={{
                padding: '20px',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                backgroundColor: '#f9fafb',
                textAlign: 'center'
              }}>
                <h3 style={{ color: '#059669', marginBottom: '10px' }}>📖 Book Content</h3>
                <p>View and manage book-content structure relationships</p>
                <button
                  onClick={() => handleSubMenuClick('book-content')}
                  style={{
                    marginTop: '15px',
                    padding: '8px 16px',
                    backgroundColor: '#059669',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer'
                  }}
                >
                  Go to Book Content
                </button>
              </div>

              <div style={{
                padding: '20px',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                backgroundColor: '#f9fafb',
                textAlign: 'center'
              }}>
                <h3 style={{ color: '#059669', marginBottom: '10px' }}>🏆 Grades</h3>
                <p>Manage grade levels and academic classifications</p>
                <button
                  onClick={() => handleSubMenuClick('grades')}
                  style={{
                    marginTop: '15px',
                    padding: '8px 16px',
                    backgroundColor: '#059669',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer'
                  }}
                >
                  Go to Grades
                </button>
              </div>

              <div style={{
                padding: '20px',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                backgroundColor: '#f9fafb',
                textAlign: 'center'
              }}>
                <h3 style={{ color: '#059669', marginBottom: '10px' }}>🏷️ Subjects</h3>
                <p>Manage subject categories and classifications</p>
                <button
                  onClick={() => handleSubMenuClick('subjects')}
                  style={{
                    marginTop: '15px',
                    padding: '8px 16px',
                    backgroundColor: '#059669',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer'
                  }}
                >
                  Go to Subjects
                </button>
              </div>

              <div style={{
                padding: '20px',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                backgroundColor: '#f9fafb',
                textAlign: 'center'
              }}>
                <h3 style={{ color: '#059669', marginBottom: '10px' }}>📦 Content Packages</h3>
                <p>Manage content packages and video collections</p>
                <button
                  onClick={() => handleSubMenuClick('packages')}
                  style={{
                    marginTop: '15px',
                    padding: '8px 16px',
                    backgroundColor: '#059669',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer'
                  }}
                >
                  Go to Content Packages
                </button>
              </div>

            </div>
          </div>
        );

      case 'content-structure':
        return <EnhancedContentStructure />;

      case 'book-content':
        return <BookContentLinks />;

      case 'content-library':
        return <ContentLibrary />;

      default:
        return (
          <div style={{ padding: '20px', textAlign: 'center' }}>
            <h2>Welcome to Admin Panel</h2>
            <p>Select a section from the sidebar to get started.</p>
          </div>
        );
    }
  };

  return (
    <>
      <Header
        onHomeClick={onHomeClick}
        onBooksClick={onBooksClick}
        onVideoLearningClick={onVideoLearningClick}
        onBlogsClick={onBlogsClick}
        onLoginClick={onLoginClick}
        onAdminClick={onAdminClick}
        onMkAcademyOnlineClick={onMkAcademyOnlineClick}
        onMkAcademyInPersonClick={onMkAcademyInPersonClick}
        isAuthenticated={isAuthenticated}
        isAdmin={isAdmin}
        user={user}
        onLogout={onLogout}
        currentPage="admin"
      />
      <AdminContainer>
        <Sidebar>
          <SidebarHeader>
            <SidebarTitle>Admin Panel</SidebarTitle>
          </SidebarHeader>

          <MenuSection>
            {getMenuItems().map((item: MenuItem) => (
              <div key={item.id}>
                <MenuItem
                  isActive={activeSection === item.id}
                  onClick={() => item.subItems ? handleMenuToggle(item.id) : handleMenuClick(item.id)}
                >
                  <MenuItemContent>
                    <MenuItemLeft>
                      <MenuIcon color={item.color}>
                        {item.icon}
                      </MenuIcon>
                      <MenuLabel>{item.label}</MenuLabel>
                    </MenuItemLeft>
                    {item.subItems && (
                      expandedMenus.has(item.id) ? <FiChevronDown size={16} /> : <FiChevronRight size={16} />
                    )}
                  </MenuItemContent>
                </MenuItem>

                {item.subItems && (
                  <SubMenu isExpanded={expandedMenus.has(item.id)}>
                    {item.subItems.map((subItem: { id: string; label: string; action: string }) => (
                      <SubMenuItem
                        key={subItem.id}
                        onClick={() => handleSubMenuClick(subItem.action)}
                      >
                        {subItem.label}
                      </SubMenuItem>
                    ))}
                  </SubMenu>
                )}
              </div>
            ))}
          </MenuSection>
        </Sidebar>

        <MainContent>
          <ContentArea>
            {renderContent()}
          </ContentArea>
        </MainContent>
      </AdminContainer>

      {/* Subject Modal Components */}
      {showCreateSubjectModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '80vh',
            overflowY: 'auto'
          }}>
            <h3 style={{ margin: '0 0 20px 0', color: '#253C8C' }}>➕ Create New Subject</h3>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#374151' }}>
                Subject Name: <span style={{ color: '#dc2626' }}>*</span>
              </label>
              <input
                type="text"
                value={subjectFormData.name}
                onChange={(e) => setSubjectFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Enter subject name..."
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '14px',
                  transition: 'border-color 0.2s'
                }}
                onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
              />
              <small style={{ color: '#6b7280', fontSize: '12px' }}>
                Required field - Unique name for the subject
              </small>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#374151' }}>
                Link to Grades (Optional)
              </label>
              <p style={{ color: '#6b7280', fontSize: '14px', margin: '0 0 12px 0' }}>
                Select which grades this subject should be available for. You can also link grades later.
              </p>

              <div style={{
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                padding: '16px',
                maxHeight: '200px',
                overflowY: 'auto',
                backgroundColor: '#f9fafb'
              }}>
                {availableGrades.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>
                    No grades available. Please create grades first.
                  </div>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '8px' }}>
                    {availableGrades.map((grade: any) => (
                      <label key={grade.id} style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '8px 12px',
                        border: '2px solid #e5e7eb',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        backgroundColor: subjectFormData.selectedGradeIds.includes(grade.id) ? '#eff6ff' : 'white',
                        borderColor: subjectFormData.selectedGradeIds.includes(grade.id) ? '#3b82f6' : '#e5e7eb',
                        transition: 'all 0.2s',
                        fontSize: '14px'
                      }}>
                        <input
                          type="checkbox"
                          checked={subjectFormData.selectedGradeIds.includes(grade.id)}
                          onChange={() => handleCreateSubjectGradeToggle(grade.id)}
                          style={{ marginRight: '8px', transform: 'scale(1.1)' }}
                        />
                        <span style={{
                          fontWeight: subjectFormData.selectedGradeIds.includes(grade.id) ? '600' : '400',
                          color: subjectFormData.selectedGradeIds.includes(grade.id) ? '#1e40af' : '#374151'
                        }}>
                          {grade.name}
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {subjectFormData.selectedGradeIds.length > 0 && (
                <div style={{
                  marginTop: '8px',
                  padding: '8px 12px',
                  backgroundColor: '#ecfdf5',
                  borderRadius: '6px',
                  border: '1px solid #d1fae5'
                }}>
                  <small style={{ color: '#065f46', fontSize: '12px' }}>
                    <strong>Selected:</strong> {subjectFormData.selectedGradeIds.length} grade{subjectFormData.selectedGradeIds.length !== 1 ? 's' : ''} - {availableGrades.filter(g => subjectFormData.selectedGradeIds.includes(g.id)).map(g => g.name).join(', ')}
                  </small>
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => setShowCreateSubjectModal(false)}
                disabled={subjectLoading}
                style={{
                  backgroundColor: '#6b7280',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  cursor: subjectLoading ? 'not-allowed' : 'pointer',
                  opacity: subjectLoading ? 0.6 : 1
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submitCreateSubject}
                disabled={subjectLoading || !subjectFormData.name.trim()}
                style={{
                  backgroundColor: '#059669',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  cursor: (subjectLoading || !subjectFormData.name.trim()) ? 'not-allowed' : 'pointer',
                  opacity: (subjectLoading || !subjectFormData.name.trim()) ? 0.6 : 1,
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                {subjectLoading ? 'Creating...' : 'Create Subject'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showEditSubjectModal && selectedSubject && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '80vh',
            overflowY: 'auto'
          }}>
            <h3 style={{ margin: '0 0 20px 0', color: '#253C8C' }}>✏️ Edit Subject: {selectedSubject.name}</h3>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#374151' }}>
                Subject ID:
              </label>
              <input
                type="text"
                value={selectedSubject.id}
                disabled
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  backgroundColor: '#f9fafb',
                  color: '#6b7280',
                  cursor: 'not-allowed'
                }}
              />
              <small style={{ color: '#6b7280', fontSize: '12px' }}>
                Auto-generated ID (read-only)
              </small>
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#374151' }}>
                Category Name: <span style={{ color: '#dc2626' }}>*</span>
              </label>
              <input
                type="text"
                value={subjectFormData.name}
                onChange={(e) => setSubjectFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Enter category name..."
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '14px',
                  transition: 'border-color 0.2s'
                }}
                onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
              />
              <small style={{ color: '#6b7280', fontSize: '12px' }}>
                Required field - Unique name for the subject
              </small>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#374151' }}>
                Metadata:
              </label>
              <div style={{
                backgroundColor: '#f9fafb',
                padding: '12px',
                borderRadius: '8px',
                border: '1px solid #e5e7eb'
              }}>

                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ color: '#6b7280', fontSize: '14px' }}>Created:</span>
                  <span style={{ color: '#374151', fontSize: '14px' }}>
                    {selectedSubject.created_at ? new Date(selectedSubject.created_at).toLocaleDateString() : 'N/A'}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#6b7280', fontSize: '14px' }}>Updated:</span>
                  <span style={{ color: '#374151', fontSize: '14px' }}>
                    {selectedSubject.updated_at ? new Date(selectedSubject.updated_at).toLocaleDateString() : 'N/A'}
                  </span>
                </div>
              </div>
              <small style={{ color: '#6b7280', fontSize: '12px' }}>
                Read-only metadata about this subject
              </small>
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => setShowEditSubjectModal(false)}
                disabled={subjectLoading}
                style={{
                  backgroundColor: '#6b7280',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  cursor: subjectLoading ? 'not-allowed' : 'pointer',
                  opacity: subjectLoading ? 0.6 : 1
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submitEditSubject}
                disabled={subjectLoading || !subjectFormData.name.trim()}
                style={{
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  cursor: (subjectLoading || !subjectFormData.name.trim()) ? 'not-allowed' : 'pointer',
                  opacity: (subjectLoading || !subjectFormData.name.trim()) ? 0.6 : 1,
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                {subjectLoading ? 'Updating...' : 'Update Subject'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteSubjectModal && selectedSubject && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '400px',
            width: '90%'
          }}>
            <h3 style={{ margin: '0 0 16px 0', color: '#dc2626' }}>🗑️ Delete Subject</h3>
            <p style={{ marginBottom: '16px', color: '#374151' }}>
              Are you sure you want to delete the subject <strong>"{selectedSubject.name}"</strong>?
            </p>
            <div style={{ backgroundColor: '#fef2f2', padding: '12px', borderRadius: '8px', marginBottom: '20px', border: '1px solid #fecaca' }}>
              <p style={{ margin: 0, color: '#dc2626', fontSize: '14px' }}>
                <strong>⚠️ Warning:</strong> This will be a soft delete. The subject will be hidden but can be restored if needed.
              </p>
              {selectedSubject.items_count > 0 && (
                <p style={{ margin: '8px 0 0 0', color: '#dc2626', fontSize: '14px' }}>
                  <strong>📚 Note:</strong> This subject has {selectedSubject.items_count} books assigned to it. Deletion will be prevented if books are still assigned.
                </p>
              )}
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => setShowDeleteSubjectModal(false)}
                disabled={subjectLoading}
                style={{
                  backgroundColor: '#6b7280',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  cursor: subjectLoading ? 'not-allowed' : 'pointer',
                  opacity: subjectLoading ? 0.6 : 1
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submitDeleteSubject}
                disabled={subjectLoading}
                style={{
                  backgroundColor: '#dc2626',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  cursor: subjectLoading ? 'not-allowed' : 'pointer',
                  opacity: subjectLoading ? 0.6 : 1
                }}
              >
                {subjectLoading ? 'Deleting...' : 'Delete Subject'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Subject-Grade Management Modal */}
      {showManageSubjectGradesModal && selectedSubjectForGrades && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '80vh',
            overflowY: 'auto'
          }}>
            <h3 style={{ margin: '0 0 20px 0', color: '#253C8C' }}>
              🔗 Manage Grades for "{selectedSubjectForGrades.name}"
            </h3>

            <div style={{ marginBottom: '20px' }}>
              <p style={{ color: '#6b7280', fontSize: '14px', margin: '0 0 16px 0' }}>
                Select which grades this subject should be available for. You can select multiple grades.
              </p>

              <div style={{
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                padding: '16px',
                maxHeight: '300px',
                overflowY: 'auto'
              }}>
                {subjectGradesLoading ? (
                  <div style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>
                    Loading grades...
                  </div>
                ) : availableGrades.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>
                    No grades available. Please create grades first.
                  </div>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px' }}>
                    {availableGrades.map((grade: any) => (
                      <label key={grade.id} style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '12px',
                        border: '2px solid #e5e7eb',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        backgroundColor: selectedGradeIds.includes(grade.id) ? '#eff6ff' : 'white',
                        borderColor: selectedGradeIds.includes(grade.id) ? '#3b82f6' : '#e5e7eb',
                        transition: 'all 0.2s'
                      }}>
                        <input
                          type="checkbox"
                          checked={selectedGradeIds.includes(grade.id)}
                          onChange={() => handleGradeToggle(grade.id)}
                          style={{ marginRight: '8px', transform: 'scale(1.2)' }}
                        />
                        <span style={{
                          fontWeight: selectedGradeIds.includes(grade.id) ? '600' : '400',
                          color: selectedGradeIds.includes(grade.id) ? '#1e40af' : '#374151'
                        }}>
                          {grade.name}
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div style={{
              backgroundColor: '#f3f4f6',
              padding: '12px',
              borderRadius: '8px',
              marginBottom: '20px'
            }}>
              <p style={{ margin: 0, fontSize: '14px', color: '#374151' }}>
                <strong>Selected:</strong> {selectedGradeIds.length} grade{selectedGradeIds.length !== 1 ? 's' : ''}
                {selectedGradeIds.length > 0 && (
                  <span style={{ marginLeft: '8px', color: '#6b7280' }}>
                    ({availableGrades.filter(g => selectedGradeIds.includes(g.id)).map(g => g.name).join(', ')})
                  </span>
                )}
              </p>
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => {
                  setShowManageSubjectGradesModal(false);
                  setSelectedSubjectForGrades(null);
                  setSelectedGradeIds([]);
                }}
                disabled={subjectGradesLoading}
                style={{
                  backgroundColor: '#6b7280',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  cursor: subjectGradesLoading ? 'not-allowed' : 'pointer',
                  opacity: subjectGradesLoading ? 0.6 : 1
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submitSubjectGrades}
                disabled={subjectGradesLoading}
                style={{
                  backgroundColor: '#059669',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  cursor: subjectGradesLoading ? 'not-allowed' : 'pointer',
                  opacity: subjectGradesLoading ? 0.6 : 1,
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                {subjectGradesLoading ? 'Saving...' : 'Save Grade Links'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Grade Modal Components */}
      {showCreateGradeModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '80vh',
            overflowY: 'auto'
          }}>
            <h3 style={{ margin: '0 0 20px 0', color: '#253C8C' }}>➕ Create New Grade</h3>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#374151' }}>
                Grade Name: <span style={{ color: '#dc2626' }}>*</span>
              </label>
              <input
                type="text"
                value={gradeFormData.name}
                onChange={(e) => setGradeFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Enter grade name (e.g., K, 1st, 2nd, 3rd...)"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '14px'
                }}
              />
              <small style={{ color: '#6b7280', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                Enter a grade level like K, 1, 2, 3, etc.
              </small>
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => setShowCreateGradeModal(false)}
                disabled={gradeLoading}
                style={{
                  backgroundColor: '#6b7280',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  cursor: gradeLoading ? 'not-allowed' : 'pointer',
                  opacity: gradeLoading ? 0.6 : 1
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submitCreateGrade}
                disabled={gradeLoading || !gradeFormData.name.trim()}
                style={{
                  backgroundColor: '#059669',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  cursor: (gradeLoading || !gradeFormData.name.trim()) ? 'not-allowed' : 'pointer',
                  opacity: (gradeLoading || !gradeFormData.name.trim()) ? 0.6 : 1
                }}
              >
                {gradeLoading ? 'Creating...' : 'Create Grade'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showEditGradeModal && selectedGrade && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '80vh',
            overflowY: 'auto'
          }}>
            <h3 style={{ margin: '0 0 20px 0', color: '#253C8C' }}>✏️ Edit Grade: {selectedGrade.name}</h3>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#374151' }}>
                Grade ID:
              </label>
              <input
                type="text"
                value={selectedGrade.id}
                disabled
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  backgroundColor: '#f9fafb',
                  color: '#6b7280',
                  cursor: 'not-allowed'
                }}
              />
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#374151' }}>
                Grade Name: <span style={{ color: '#dc2626' }}>*</span>
              </label>
              <input
                type="text"
                value={gradeFormData.name}
                onChange={(e) => setGradeFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Enter grade name..."
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '14px'
                }}
              />
            </div>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#374151' }}>
                Metadata:
              </label>
              <div style={{
                backgroundColor: '#f9fafb',
                padding: '12px',
                borderRadius: '8px',
                border: '1px solid #e5e7eb'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ color: '#6b7280', fontSize: '14px' }}>Videos Count:</span>
                  <span style={{ color: '#374151', fontSize: '14px', fontWeight: '600' }}>
                    {selectedGrade.videos_count || 0} videos
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ color: '#6b7280', fontSize: '14px' }}>Created:</span>
                  <span style={{ color: '#374151', fontSize: '14px' }}>
                    {selectedGrade.created_at || 'N/A'}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#6b7280', fontSize: '14px' }}>Updated:</span>
                  <span style={{ color: '#374151', fontSize: '14px' }}>
                    {selectedGrade.updated_at || 'N/A'}
                  </span>
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => setShowEditGradeModal(false)}
                disabled={gradeLoading}
                style={{
                  backgroundColor: '#6b7280',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  cursor: gradeLoading ? 'not-allowed' : 'pointer',
                  opacity: gradeLoading ? 0.6 : 1
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submitEditGrade}
                disabled={gradeLoading || !gradeFormData.name.trim()}
                style={{
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  cursor: (gradeLoading || !gradeFormData.name.trim()) ? 'not-allowed' : 'pointer',
                  opacity: (gradeLoading || !gradeFormData.name.trim()) ? 0.6 : 1
                }}
              >
                {gradeLoading ? 'Updating...' : 'Update Grade'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteGradeModal && selectedGrade && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '400px',
            width: '90%'
          }}>
            <h3 style={{ margin: '0 0 16px 0', color: '#dc2626' }}>🗑️ Delete Grade</h3>
            <p style={{ marginBottom: '16px', color: '#374151' }}>
              Are you sure you want to delete the grade <strong>"{selectedGrade.name}"</strong>?
            </p>
            <div style={{ backgroundColor: '#fef2f2', padding: '12px', borderRadius: '8px', marginBottom: '20px', border: '1px solid #fecaca' }}>
              <p style={{ margin: 0, color: '#dc2626', fontSize: '14px' }}>
                <strong>⚠️ Warning:</strong> This will permanently delete the grade. This action cannot be undone.
              </p>
              {selectedGrade.videos_count > 0 && (
                <p style={{ margin: '8px 0 0 0', color: '#dc2626', fontSize: '14px' }}>
                  <strong>🎥 Note:</strong> This grade has {selectedGrade.videos_count} videos assigned to it. Deletion will be prevented if videos are still assigned.
                </p>
              )}
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => setShowDeleteGradeModal(false)}
                disabled={gradeLoading}
                style={{
                  backgroundColor: '#6b7280',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  cursor: gradeLoading ? 'not-allowed' : 'pointer',
                  opacity: gradeLoading ? 0.6 : 1
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submitDeleteGrade}
                disabled={gradeLoading}
                style={{
                  backgroundColor: '#dc2626',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  cursor: gradeLoading ? 'not-allowed' : 'pointer',
                  opacity: gradeLoading ? 0.6 : 1
                }}
              >
                {gradeLoading ? 'Deleting...' : 'Delete Grade'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Book Modal */}
      {false && /* Modal disabled - using full page forms now */ (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '80vh',
            overflowY: 'auto'
          }}>
            <h3 style={{ margin: '0 0 20px 0', color: '#253C8C' }}>➕ Create New Book</h3>

            {/* Basic Information */}
            <div style={{ marginBottom: '24px', padding: '16px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
              <h4 style={{ margin: '0 0 16px 0', color: '#1e40af', fontSize: '16px' }}>📚 Basic Information</h4>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#374151' }}>
                  Title: <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <input
                  type="text"
                  value={bookFormData.title}
                  onChange={(e) => setBookFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Enter book title..."
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>
              <div style={{ marginBottom: '16px', width: '100%' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#374151' }}>
                  Short Description:
                </label>
                <RichTextEditor
                  value={bookFormData.short_description}
                  onChange={(value: string) => setBookFormData(prev => ({ ...prev, short_description: value }))}
                  placeholder="Enter a brief description with formatting..."
                  showImageUpload={false}
                  minHeight="150px"
                />
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#374151' }}>
                  Description: <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                  <textarea
                    value={bookFormData.description}
                    placeholder="Click 'Edit Description' for full editor..."
                    rows={3}
                    style={{
                      flex: 1,
                      padding: '12px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '8px',
                      fontSize: '14px',
                      backgroundColor: '#f9fafb',
                      cursor: 'pointer'
                    }}
                    readOnly
                    onClick={openDescriptionEditor}
                  />
                  <button
                    type="button"
                    onClick={openDescriptionEditor}
                    style={{
                      backgroundColor: '#3b82f6',
                      color: 'white',
                      border: 'none',
                      padding: '12px 16px',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '600',
                      whiteSpace: 'nowrap',
                      height: 'fit-content'
                    }}
                  >
                    📝 Edit Description
                  </button>
                </div>
                {bookFormData.description && (
                  <div style={{
                    marginTop: '8px',
                    padding: '8px 12px',
                    backgroundColor: '#f0f9ff',
                    borderRadius: '6px',
                    border: '1px solid #bae6fd',
                    fontSize: '12px',
                    color: '#0369a1'
                  }}>
                    <strong>Characters:</strong> {bookFormData.description.length} | <strong>Words:</strong> {bookFormData.description.split(' ').filter(w => w.length > 0).length}
                  </div>
                )}
              </div>
            </div>

            {/* Media & Links */}
            <div style={{ marginBottom: '24px', padding: '16px', backgroundColor: '#f0fdf4', borderRadius: '8px', border: '1px solid #bbf7d0' }}>
              <h4 style={{ margin: '0 0 16px 0', color: '#15803d', fontSize: '16px' }}>🎯 Media & Links</h4>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#374151' }}>
                  Upload Image: <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <input
                  type="url"
                  value={bookFormData.image}
                  onChange={(e) => setBookFormData(prev => ({ ...prev, image: e.target.value }))}
                  placeholder="https://example.com/book-cover.jpg"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#374151' }}>
                  Youtube Link: <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <input
                  type="url"
                  value={bookFormData.video_link}
                  onChange={(e) => setBookFormData(prev => ({ ...prev, video_link: e.target.value }))}
                  placeholder="https://youtube.com/watch?v=..."
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#374151' }}>
                  Amazon Book Link:
                </label>
                <input
                  type="url"
                  value={bookFormData.referal_link}
                  onChange={(e) => setBookFormData(prev => ({ ...prev, referal_link: e.target.value }))}
                  placeholder="https://affiliate-link.com/..."
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#374151' }}>
                  Barnes & Nobel Link:
                </label>
                <textarea
                  value={bookFormData.other_link}
                  onChange={(e) => setBookFormData(prev => ({ ...prev, other_link: e.target.value }))}
                  placeholder="Additional links or information..."
                  rows={2}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px',
                    resize: 'vertical'
                  }}
                />
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#374151' }}>
                  File Path:
                </label>
                <input
                  type="text"
                  value={bookFormData.file}
                  onChange={(e) => setBookFormData(prev => ({ ...prev, file: e.target.value }))}
                  placeholder="Path to book file..."
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>
            </div>

            {/* Pricing */}
            <div style={{ marginBottom: '24px', padding: '16px', backgroundColor: '#fefce8', borderRadius: '8px', border: '1px solid #fef08a' }}>
              <h4 style={{ margin: '0 0 16px 0', color: '#a16207', fontSize: '16px' }}>💰 Pricing</h4>
              <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#374151' }}>
                    Price: <span style={{ color: '#dc2626' }}>*</span>
                  </label>
                  <input
                    type="text"
                    value={bookFormData.price}
                    onChange={(e) => setBookFormData(prev => ({ ...prev, price: e.target.value }))}
                    placeholder="29.99"
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#374151' }}>
                    Discount Price:
                  </label>
                  <input
                    type="text"
                    value={bookFormData.discount_price}
                    onChange={(e) => setBookFormData(prev => ({ ...prev, discount_price: e.target.value }))}
                    placeholder="19.99 (optional)"
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}
                  />
                </div>
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#374151' }}>
                  Vendors: <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                  padding: '12px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  backgroundColor: '#f9fafb'
                }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={bookFormData.vendors.includes('amazon')}
                      onChange={(e) => {
                        const vendors = e.target.checked
                          ? [...bookFormData.vendors, 'amazon']
                          : bookFormData.vendors.filter(v => v !== 'amazon');
                        setBookFormData(prev => ({ ...prev, vendors }));
                      }}
                    />
                    <span>Amazon</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={bookFormData.vendors.includes('barnesandnoble')}
                      onChange={(e) => {
                        const vendors = e.target.checked
                          ? [...bookFormData.vendors, 'barnesandnoble']
                          : bookFormData.vendors.filter(v => v !== 'barnesandnoble');
                        setBookFormData(prev => ({ ...prev, vendors }));
                      }}
                    />
                    <span>Barnes & Noble</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={bookFormData.vendors.includes('mathknots')}
                      onChange={(e) => {
                        const vendors = e.target.checked
                          ? [...bookFormData.vendors, 'mathknots']
                          : bookFormData.vendors.filter(v => v !== 'mathknots');
                        setBookFormData(prev => ({ ...prev, vendors }));
                      }}
                    />
                    <span>MathKnots</span>
                  </label>
                  {bookFormData.vendors.length === 0 && (
                    <div style={{ color: '#dc2626', fontSize: '12px', fontStyle: 'italic' }}>
                      Please select at least one vendor
                    </div>
                  )}
                </div>
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'flex', alignItems: 'center', fontWeight: 'bold', color: '#374151' }}>
                  <input
                    type="checkbox"
                    checked={bookFormData.is_best_seller}
                    onChange={(e) => setBookFormData(prev => ({ ...prev, is_best_seller: e.target.checked }))}
                    style={{ marginRight: '8px' }}
                  />
                  Best Seller
                </label>
              </div>
            </div>

            {/* Categories & Location */}
            <div style={{ marginBottom: '24px', padding: '16px', backgroundColor: '#fef2f2', borderRadius: '8px', border: '1px solid #fecaca' }}>
              <h4 style={{ margin: '0 0 16px 0', color: '#dc2626', fontSize: '16px' }}>📍 Categories & Location</h4>
              <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#374151' }}>
                    Subject ID: <span style={{ color: '#dc2626' }}>*</span>
                  </label>
                  <input
                    type="number"
                    value={bookFormData.subject_id}
                    onChange={(e) => setBookFormData(prev => ({ ...prev, subject_id: e.target.value }))}
                    placeholder="Category ID"
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#374151' }}>
                    Sub Subject ID:
                  </label>
                  <input
                    type="number"
                    value={bookFormData.sub_subject_id}
                    onChange={(e) => setBookFormData(prev => ({ ...prev, sub_subject_id: e.target.value }))}
                    placeholder="Sub Category ID"
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}
                  />
                </div>
              </div>
              <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#374151' }}>
                    Grade ID: <span style={{ color: '#dc2626' }}>*</span>
                  </label>
                  <input
                    type="number"
                    value={bookFormData.grade_id}
                    onChange={(e) => setBookFormData(prev => ({ ...prev, grade_id: e.target.value }))}
                    placeholder="Grade ID"
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#374151' }}>
                    Country: <span style={{ color: '#dc2626' }}>*</span>
                  </label>
                  <input
                    type="number"
                    value={bookFormData.country}
                    onChange={(e) => setBookFormData(prev => ({ ...prev, country: e.target.value }))}
                    placeholder="Country ID"
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}
                  />
                </div>
              </div>
              <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#374151' }}>
                    State ID:
                  </label>
                  <input
                    type="number"
                    value={bookFormData.state_id}
                    onChange={(e) => setBookFormData(prev => ({ ...prev, state_id: e.target.value }))}
                    placeholder="State ID"
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#374151' }}>
                    State IDs (JSON):
                  </label>
                  <input
                    type="text"
                    value={bookFormData.state_ids}
                    onChange={(e) => setBookFormData(prev => ({ ...prev, state_ids: e.target.value }))}
                    placeholder="['1','2','3']"
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}
                  />
                </div>
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#374151' }}>
                  Shared Content Group ID:
                </label>
                <input
                  type="number"
                  value={bookFormData.shared_content_group_id}
                  onChange={(e) => setBookFormData(prev => ({ ...prev, shared_content_group_id: e.target.value }))}
                  placeholder="Shared Content Group ID"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>
            </div>

            {/* SEO */}
            <div style={{ marginBottom: '24px', padding: '16px', backgroundColor: '#f0f9ff', borderRadius: '8px', border: '1px solid #bae6fd' }}>
              <h4 style={{ margin: '0 0 16px 0', color: '#0369a1', fontSize: '16px' }}>🔍 SEO & Metadata</h4>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#374151' }}>
                  SEO:
                </label>
                <textarea
                  value={bookFormData.seo}
                  onChange={(e) => setBookFormData(prev => ({ ...prev, seo: e.target.value }))}
                  placeholder="SEO metadata, keywords, etc..."
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px',
                    resize: 'vertical'
                  }}
                />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => {/* setShowCreateBookModal(false) */ }}
                disabled={bookLoading}
                style={{
                  backgroundColor: '#6b7280',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  cursor: bookLoading ? 'not-allowed' : 'pointer',
                  opacity: bookLoading ? 0.6 : 1
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submitCreateBook}
                disabled={bookLoading || !bookFormData.title.trim() || !bookFormData.description.trim() || !bookFormData.price.trim() || !bookFormData.image.trim() || !bookFormData.video_link.trim() || !bookFormData.subject_id || !bookFormData.grade_id || !bookFormData.country || !bookFormData.vendor_id}
                style={{
                  backgroundColor: '#059669',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  cursor: (bookLoading || !bookFormData.title.trim()) ? 'not-allowed' : 'pointer',
                  opacity: (bookLoading || !bookFormData.title.trim()) ? 0.6 : 1
                }}
              >
                {bookLoading ? 'Creating...' : 'Create Book'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Book Modal */}
      {false && /* Modal disabled - using full page forms now */ (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '80vh',
            overflowY: 'auto'
          }}>
            <h3 style={{ margin: '0 0 20px 0', color: '#253C8C' }}>✏️ Edit Book: {selectedBook.title}</h3>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#374151' }}>
                Title: <span style={{ color: '#dc2626' }}>*</span>
              </label>
              <input
                type="text"
                value={bookFormData.title}
                onChange={(e) => setBookFormData(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Enter book title..."
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '14px'
                }}
              />
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#374151' }}>
                Description: <span style={{ color: '#dc2626' }}>*</span>
              </label>
              <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                <textarea
                  value={bookFormData.description}
                  placeholder="Click 'Edit Description' for full editor..."
                  rows={3}
                  style={{
                    flex: 1,
                    padding: '12px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px',
                    backgroundColor: '#f9fafb',
                    cursor: 'pointer'
                  }}
                  readOnly
                  onClick={openDescriptionEditor}
                />
                <button
                  type="button"
                  onClick={openDescriptionEditor}
                  style={{
                    backgroundColor: '#3b82f6',
                    color: 'white',
                    border: 'none',
                    padding: '12px 16px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600',
                    whiteSpace: 'nowrap',
                    height: 'fit-content'
                  }}
                >
                  📝 Edit Description
                </button>
              </div>
              {bookFormData.description && (
                <div style={{
                  marginTop: '8px',
                  padding: '8px 12px',
                  backgroundColor: '#f0f9ff',
                  borderRadius: '6px',
                  border: '1px solid #bae6fd',
                  fontSize: '12px',
                  color: '#0369a1'
                }}>
                  <strong>Characters:</strong> {bookFormData.description.length} | <strong>Words:</strong> {bookFormData.description.split(' ').filter(w => w.length > 0).length}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#374151' }}>
                  Price: <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <input
                  type="text"
                  value={bookFormData.price}
                  onChange={(e) => setBookFormData(prev => ({ ...prev, price: e.target.value }))}
                  placeholder="$29.99"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#374151' }}>
                  Discount Price:
                </label>
                <input
                  type="text"
                  value={bookFormData.discount_price}
                  onChange={(e) => setBookFormData(prev => ({ ...prev, discount_price: e.target.value }))}
                  placeholder="$19.99 (optional)"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#374151' }}>
                  Subject ID: <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <input
                  type="text"
                  value={bookFormData.subject_id}
                  onChange={(e) => setBookFormData(prev => ({ ...prev, subject_id: e.target.value }))}
                  placeholder="Category ID"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#374151' }}>
                  Grade ID: <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <input
                  type="text"
                  value={bookFormData.grade_id}
                  onChange={(e) => setBookFormData(prev => ({ ...prev, grade_id: e.target.value }))}
                  placeholder="Grade ID"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => {/* setShowEditBookModal(false) */ }}
                disabled={bookLoading}
                style={{
                  backgroundColor: '#6b7280',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  cursor: bookLoading ? 'not-allowed' : 'pointer',
                  opacity: bookLoading ? 0.6 : 1
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submitEditBook}
                disabled={bookLoading || !bookFormData.title.trim() || !bookFormData.description.trim() || !bookFormData.price.trim() || !bookFormData.image.trim() || !bookFormData.video_link.trim() || !bookFormData.subject_id || !bookFormData.grade_id || !bookFormData.country || !bookFormData.vendor_id}
                style={{
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  cursor: (bookLoading || !bookFormData.title.trim()) ? 'not-allowed' : 'pointer',
                  opacity: (bookLoading || !bookFormData.title.trim()) ? 0.6 : 1
                }}
              >
                {bookLoading ? 'Updating...' : 'Update Book'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Book Modal */}
      {showDeleteBookModal && selectedBook && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '400px',
            width: '90%'
          }}>
            <h3 style={{ margin: '0 0 16px 0', color: '#dc2626' }}>🗑️ Delete Book</h3>
            <p style={{ marginBottom: '20px', color: '#374151' }}>
              Are you sure you want to delete <strong>{selectedBook.title}</strong>?
            </p>
            <div style={{
              backgroundColor: '#fef2f2',
              padding: '12px',
              borderRadius: '8px',
              marginBottom: '20px',
              border: '1px solid #fecaca'
            }}>
              <strong style={{ color: '#dc2626' }}>⚠️ Warning:</strong>
              <span style={{ color: '#dc2626' }}> This action cannot be undone.</span>
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => setShowDeleteBookModal(false)}
                disabled={bookLoading}
                style={{
                  backgroundColor: '#6b7280',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  cursor: bookLoading ? 'not-allowed' : 'pointer',
                  opacity: bookLoading ? 0.6 : 1
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submitDeleteBook}
                disabled={bookLoading}
                style={{
                  backgroundColor: '#dc2626',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  cursor: bookLoading ? 'not-allowed' : 'pointer',
                  opacity: bookLoading ? 0.6 : 1
                }}
              >
                {bookLoading ? 'Deleting...' : 'Delete Book'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Video Modal Components */}
      {false && /* Video modal disabled - using full page forms now */ (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '80vh',
            overflowY: 'auto'
          }}>
            <h3 style={{ margin: '0 0 20px 0', color: '#253C8C' }}>➕ Create New Video Package</h3>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#374151' }}>
                Book Details ID: <span style={{ color: '#dc2626' }}>*</span>
              </label>
              <input
                type="number"
                value={videoFormData.book_details_id}
                onChange={(e) => setVideoFormData(prev => ({ ...prev, book_details_id: e.target.value }))}
                placeholder="Enter book details ID..."
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '14px'
                }}
              />
            </div>
            <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#374151' }}>
                  Subject ID: <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <input
                  type="number"
                  value={videoFormData.subject_id}
                  onChange={(e) => setVideoFormData(prev => ({ ...prev, subject_id: e.target.value }))}
                  placeholder="Category ID"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#374151' }}>
                  Grade ID: <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <input
                  type="number"
                  value={videoFormData.grade_id}
                  onChange={(e) => setVideoFormData(prev => ({ ...prev, grade_id: e.target.value }))}
                  placeholder="Grade ID"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#374151' }}>
                6-Month Package:
              </label>
              <textarea
                value={videoFormData.six_month}
                onChange={(e) => setVideoFormData(prev => ({ ...prev, six_month: e.target.value }))}
                placeholder="6-month package details..."
                rows={2}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '14px',
                  resize: 'vertical'
                }}
              />
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#374151' }}>
                12-Month Package:
              </label>
              <textarea
                value={videoFormData.twelve_month}
                onChange={(e) => setVideoFormData(prev => ({ ...prev, twelve_month: e.target.value }))}
                placeholder="12-month package details..."
                rows={2}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '14px',
                  resize: 'vertical'
                }}
              />
            </div>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'flex', alignItems: 'center', fontWeight: 'bold', color: '#374151' }}>
                <input
                  type="checkbox"
                  checked={videoFormData.is_publish}
                  onChange={(e) => setVideoFormData(prev => ({ ...prev, is_publish: e.target.checked }))}
                  style={{ marginRight: '8px' }}
                />
                Published
              </label>
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => {/* setShowCreateVideoModal(false) */ }}
                disabled={videoLoading}
                style={{
                  backgroundColor: '#6b7280',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  cursor: videoLoading ? 'not-allowed' : 'pointer',
                  opacity: videoLoading ? 0.6 : 1
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submitCreateVideo}
                disabled={videoLoading || !videoFormData.book_details_id || !videoFormData.subject_id || !videoFormData.grade_id}
                style={{
                  backgroundColor: '#059669',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  cursor: (videoLoading || !videoFormData.book_details_id) ? 'not-allowed' : 'pointer',
                  opacity: (videoLoading || !videoFormData.book_details_id) ? 0.6 : 1
                }}
              >
                {videoLoading ? 'Creating...' : 'Create Video Package'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Video Modal */}
      {false && /* Video edit modal disabled - using full page forms now */ (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '80vh',
            overflowY: 'auto'
          }}>
            <h3 style={{ margin: '0 0 20px 0', color: '#253C8C' }}>✏️ Edit Video Package #{selectedVideo.id}</h3>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#374151' }}>
                Book Details ID: <span style={{ color: '#dc2626' }}>*</span>
              </label>
              <input
                type="number"
                value={videoFormData.book_details_id}
                onChange={(e) => setVideoFormData(prev => ({ ...prev, book_details_id: e.target.value }))}
                placeholder="Enter book details ID..."
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '14px'
                }}
              />
            </div>
            <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#374151' }}>
                  Subject ID: <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <input
                  type="number"
                  value={videoFormData.subject_id}
                  onChange={(e) => setVideoFormData(prev => ({ ...prev, subject_id: e.target.value }))}
                  placeholder="Category ID"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#374151' }}>
                  Grade ID: <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <input
                  type="number"
                  value={videoFormData.grade_id}
                  onChange={(e) => setVideoFormData(prev => ({ ...prev, grade_id: e.target.value }))}
                  placeholder="Grade ID"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#374151' }}>
                6-Month Package:
              </label>
              <textarea
                value={videoFormData.six_month}
                onChange={(e) => setVideoFormData(prev => ({ ...prev, six_month: e.target.value }))}
                placeholder="6-month package details..."
                rows={2}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '14px',
                  resize: 'vertical'
                }}
              />
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#374151' }}>
                12-Month Package:
              </label>
              <textarea
                value={videoFormData.twelve_month}
                onChange={(e) => setVideoFormData(prev => ({ ...prev, twelve_month: e.target.value }))}
                placeholder="12-month package details..."
                rows={2}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '14px',
                  resize: 'vertical'
                }}
              />
            </div>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'flex', alignItems: 'center', fontWeight: 'bold', color: '#374151' }}>
                <input
                  type="checkbox"
                  checked={videoFormData.is_publish}
                  onChange={(e) => setVideoFormData(prev => ({ ...prev, is_publish: e.target.checked }))}
                  style={{ marginRight: '8px' }}
                />
                Published
              </label>
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => {/* setShowEditVideoModal(false) */ }}
                disabled={videoLoading}
                style={{
                  backgroundColor: '#6b7280',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  cursor: videoLoading ? 'not-allowed' : 'pointer',
                  opacity: videoLoading ? 0.6 : 1
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submitEditVideo}
                disabled={videoLoading || !videoFormData.book_details_id || !videoFormData.subject_id || !videoFormData.grade_id}
                style={{
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  cursor: (videoLoading || !videoFormData.book_details_id) ? 'not-allowed' : 'pointer',
                  opacity: (videoLoading || !videoFormData.book_details_id) ? 0.6 : 1
                }}
              >
                {videoLoading ? 'Updating...' : 'Update Video Package'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Video Modal */}
      {showDeleteVideoModal && selectedVideo && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '400px',
            width: '90%'
          }}>
            <h3 style={{ margin: '0 0 16px 0', color: '#dc2626' }}>🗑️ Delete Video Package</h3>
            <p style={{ marginBottom: '20px', color: '#374151' }}>
              Are you sure you want to delete <strong>Video Package #{selectedVideo.id}</strong>?
            </p>
            <div style={{
              backgroundColor: '#fef2f2',
              padding: '12px',
              borderRadius: '8px',
              marginBottom: '20px',
              border: '1px solid #fecaca'
            }}>
              <strong style={{ color: '#dc2626' }}>⚠️ Warning:</strong>
              <span style={{ color: '#dc2626' }}> This action cannot be undone.</span>
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => setShowDeleteVideoModal(false)}
                disabled={videoLoading}
                style={{
                  backgroundColor: '#6b7280',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  cursor: videoLoading ? 'not-allowed' : 'pointer',
                  opacity: videoLoading ? 0.6 : 1
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submitDeleteVideo}
                disabled={videoLoading}
                style={{
                  backgroundColor: '#dc2626',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  cursor: videoLoading ? 'not-allowed' : 'pointer',
                  opacity: videoLoading ? 0.6 : 1
                }}
              >
                {videoLoading ? 'Deleting...' : 'Delete Video Package'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* User Modal Components */}
      {showCreateModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '80vh',
            overflowY: 'auto'
          }}>
            <h3 style={{ margin: '0 0 20px 0', color: '#253C8C' }}>➕ Create New User</h3>
            
            {userFormError && (
              <div style={{ padding: '12px', backgroundColor: '#fee2e2', border: '1px solid #ef4444', borderRadius: '8px', marginBottom: '16px', color: '#991b1b' }}>
                {userFormError}
              </div>
            )}
            
            {userFormSuccess && (
              <div style={{ padding: '12px', backgroundColor: '#d1fae5', border: '1px solid #10b981', borderRadius: '8px', marginBottom: '16px', color: '#065f46' }}>
                {userFormSuccess}
              </div>
            )}
            
            <form onSubmit={handleSubmitCreateUser}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Name:</label>
                <input 
                  type="text" 
                  value={newUserForm.name}
                  onChange={(e) => setNewUserForm({...newUserForm, name: e.target.value})}
                  placeholder="Enter full name"
                  required
                  disabled={userFormLoading}
                  style={{ width: '100%', padding: '12px', border: '2px solid #e5e7eb', borderRadius: '8px' }} 
                />
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Email:</label>
                <input 
                  type="email" 
                  value={newUserForm.email}
                  onChange={(e) => setNewUserForm({...newUserForm, email: e.target.value})}
                  placeholder="user@example.com"
                  required
                  disabled={userFormLoading}
                  style={{ width: '100%', padding: '12px', border: '2px solid #e5e7eb', borderRadius: '8px' }} 
                />
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Password:</label>
                <input 
                  type="password" 
                  value={newUserForm.password}
                  onChange={(e) => setNewUserForm({...newUserForm, password: e.target.value})}
                  placeholder="Minimum 6 characters"
                  required
                  disabled={userFormLoading}
                  style={{ width: '100%', padding: '12px', border: '2px solid #e5e7eb', borderRadius: '8px' }} 
                />
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Role:</label>
                <select 
                  value={newUserForm.user_role}
                  onChange={(e) => setNewUserForm({...newUserForm, user_role: parseInt(e.target.value)})}
                  disabled={userFormLoading}
                  style={{ width: '100%', padding: '12px', border: '2px solid #e5e7eb', borderRadius: '8px' }}
                >
                  <option value="1">User</option>
                  <option value="2">Admin</option>
                </select>
              </div>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setShowCreateModal(false)} disabled={userFormLoading} style={{
                  backgroundColor: '#6b7280',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  cursor: userFormLoading ? 'not-allowed' : 'pointer',
                  opacity: userFormLoading ? 0.6 : 1
                }}>
                  Cancel
                </button>
                <button type="submit" disabled={userFormLoading} style={{
                  backgroundColor: '#059669',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  cursor: userFormLoading ? 'not-allowed' : 'pointer',
                  opacity: userFormLoading ? 0.6 : 1
                }}>
                  {userFormLoading ? 'Creating...' : 'Create User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showEditModal && selectedUser && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '80vh',
            overflowY: 'auto'
          }}>
            <h3 style={{ margin: '0 0 20px 0', color: '#253C8C' }}>✏️ Edit User: {selectedUser.name}</h3>
            {userFormError && (
              <div style={{ backgroundColor: '#fef2f2', color: '#dc2626', padding: '12px', borderRadius: '8px', marginBottom: '16px', border: '1px solid #fecaca' }}>
                {userFormError}
              </div>
            )}
            <form onSubmit={(e) => { e.preventDefault(); confirmUpdateUser(); }}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Name:</label>
                <input 
                  type="text" 
                  value={editUserForm.name}
                  onChange={(e) => setEditUserForm({...editUserForm, name: e.target.value})}
                  style={{ width: '100%', padding: '12px', border: '2px solid #e5e7eb', borderRadius: '8px' }} 
                />
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Email:</label>
                <input 
                  type="email" 
                  value={editUserForm.email}
                  onChange={(e) => setEditUserForm({...editUserForm, email: e.target.value})}
                  style={{ width: '100%', padding: '12px', border: '2px solid #e5e7eb', borderRadius: '8px' }} 
                />
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Role:</label>
                <select 
                  value={editUserForm.role_id}
                  onChange={(e) => setEditUserForm({...editUserForm, role_id: parseInt(e.target.value)})}
                  style={{ width: '100%', padding: '12px', border: '2px solid #e5e7eb', borderRadius: '8px' }}
                >
                  <option value={1}>User</option>
                  <option value={2}>Admin</option>
                </select>
              </div>

              {/* Password Reset Section */}
              <div style={{
                marginBottom: '20px',
                padding: '16px',
                backgroundColor: '#fef3c7',
                borderRadius: '8px',
                border: '1px solid #fbbf24'
              }}>
                <h4 style={{ margin: '0 0 12px 0', color: '#92400e', fontSize: '16px' }}>🔑 Password Management</h4>
                <p style={{ margin: '0 0 12px 0', color: '#92400e', fontSize: '14px' }}>
                  Reset this user's password to a temporary password. They will need to change it on next login.
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    handlePasswordReset(selectedUser);
                  }}
                  style={{
                    backgroundColor: '#f59e0b',
                    color: 'white',
                    border: 'none',
                    padding: '10px 20px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  <FiKey size={16} />
                  Reset Password
                </button>
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button 
                  type="button" 
                  onClick={() => setShowEditModal(false)} 
                  disabled={userFormLoading}
                  style={{
                    backgroundColor: '#6b7280',
                    color: 'white',
                    border: 'none',
                    padding: '12px 24px',
                    borderRadius: '8px',
                    cursor: userFormLoading ? 'not-allowed' : 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  disabled={userFormLoading}
                  style={{
                    backgroundColor: userFormLoading ? '#9ca3af' : '#3b82f6',
                    color: 'white',
                    border: 'none',
                    padding: '12px 24px',
                    borderRadius: '8px',
                    cursor: userFormLoading ? 'not-allowed' : 'pointer'
                  }}
                >
                  {userFormLoading ? 'Updating...' : 'Update User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showDeleteModal && selectedUser && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '400px',
            width: '90%'
          }}>
            <h3 style={{ margin: '0 0 16px 0', color: '#dc2626' }}>🗑️ Delete User</h3>
            <p style={{ marginBottom: '20px', color: '#374151' }}>
              Are you sure you want to delete <strong>{selectedUser.name}</strong> ({selectedUser.email})?
            </p>
            {userFormError && (
              <div style={{ backgroundColor: '#fef2f2', color: '#dc2626', padding: '12px', borderRadius: '8px', marginBottom: '16px', border: '1px solid #fecaca' }}>
                <strong>❌ Error:</strong> {userFormError}
              </div>
            )}
            <div style={{ backgroundColor: '#fef2f2', padding: '12px', borderRadius: '8px', marginBottom: '20px', border: '1px solid #fecaca' }}>
              <strong style={{ color: '#dc2626' }}>⚠️ Warning:</strong>
              <span style={{ color: '#dc2626' }}> This action cannot be undone.</span>
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => setShowDeleteModal(false)} style={{
                backgroundColor: '#6b7280',
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '8px',
                cursor: 'pointer'
              }}>
                Cancel
              </button>
              <button 
                type="button" 
                onClick={confirmDeleteUser}
                disabled={userFormLoading}
                style={{
                  backgroundColor: userFormLoading ? '#9ca3af' : '#dc2626',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  cursor: userFormLoading ? 'not-allowed' : 'pointer'
                }}
              >
                {userFormLoading ? 'Deleting...' : 'Delete User'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showPasswordResetModal && selectedUser && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '500px',
            width: '90%'
          }}>
            <h3 style={{ margin: '0 0 16px 0', color: '#f59e0b' }}>🔑 Reset Password</h3>
            <p style={{ marginBottom: '16px', color: '#374151' }}>
              Reset password for <strong>{selectedUser.name}</strong> ({selectedUser.email})?
            </p>

            {userFormError && (
              <div style={{ padding: '12px', backgroundColor: '#fee2e2', border: '1px solid #ef4444', borderRadius: '8px', marginBottom: '16px', color: '#991b1b' }}>
                {userFormError}
              </div>
            )}
            
            {userFormSuccess && (
              <div style={{ padding: '12px', backgroundColor: '#d1fae5', border: '1px solid #10b981', borderRadius: '8px', marginBottom: '16px', color: '#065f46' }}>
                {userFormSuccess}
              </div>
            )}

            {tempPasswordDisplay ? (
              <div style={{
                backgroundColor: '#fef3c7',
                padding: '16px',
                borderRadius: '8px',
                marginBottom: '20px',
                border: '1px solid #fbbf24'
              }}>
                <h4 style={{ margin: '0 0 8px 0', color: '#92400e', fontSize: '16px' }}>🔒 New Temporary Password</h4>
                <p style={{ margin: '0 0 12px 0', color: '#92400e', fontSize: '14px' }}>
                  Save this password! The user will need it to login.
                </p>
                <div style={{
                  backgroundColor: 'white',
                  padding: '12px',
                  borderRadius: '6px',
                  border: '1px solid #d97706',
                  fontFamily: 'monospace',
                  fontSize: '18px',
                  fontWeight: 'bold',
                  color: '#92400e',
                  textAlign: 'center',
                  userSelect: 'all'
                }}>
                  {tempPasswordDisplay}
                </div>
                <p style={{ margin: '12px 0 0 0', color: '#92400e', fontSize: '12px', fontStyle: 'italic' }}>
                  💡 Click to select and copy the password. User should change it on next login.
                </p>
              </div>
            ) : (
              <div style={{
                backgroundColor: '#fef3c7',
                padding: '16px',
                borderRadius: '8px',
                marginBottom: '20px',
                border: '1px solid #fbbf24'
              }}>
                <p style={{ margin: 0, color: '#92400e', fontSize: '14px' }}>
                  ⚠️ A random temporary password will be generated. Make sure to copy and share it with the user.
                </p>
              </div>
            )}

            <div style={{
              backgroundColor: '#e0f2fe',
              padding: '12px',
              borderRadius: '8px',
              marginBottom: '20px',
              border: '1px solid #0369a1'
            }}>
              <p style={{ margin: 0, color: '#0369a1', fontSize: '14px' }}>
                <strong>📧 Email Notification:</strong> Future feature - manual sharing required for now.
              </p>
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => {
                setShowPasswordResetModal(false);
                setTempPasswordDisplay('');
                setUserFormError('');
                setUserFormSuccess('');
              }} disabled={userFormLoading} style={{
                backgroundColor: '#6b7280',
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '8px',
                cursor: userFormLoading ? 'not-allowed' : 'pointer',
                opacity: userFormLoading ? 0.6 : 1
              }}>
                {tempPasswordDisplay ? 'Close' : 'Cancel'}
              </button>
              {!tempPasswordDisplay && (
                <button type="button" onClick={handleSubmitPasswordReset} disabled={userFormLoading} style={{
                  backgroundColor: '#f59e0b',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  cursor: userFormLoading ? 'not-allowed' : 'pointer',
                  opacity: userFormLoading ? 0.6 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <FiKey size={16} />
                  {userFormLoading ? 'Resetting...' : 'Reset Password'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Online Classes Modal Components */}
      {showCreateOnlineClassModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '32px',
            borderRadius: '16px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
            width: '90%',
            maxWidth: '500px',
            maxHeight: '80vh',
            overflow: 'auto'
          }}>
            <h2 style={{ margin: '0 0 20px 0', color: '#253C8C', fontSize: '24px' }}>
              💻 Create Online Class
            </h2>

            <div style={{ display: 'grid', gap: '16px', gridTemplateColumns: '1fr 1fr' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', color: '#374151' }}>
                  Course Name *
                </label>
                <input
                  type="text"
                  value={onlineClassFormData.course_name}
                  onChange={(e) => setOnlineClassFormData((prev: OnlineClassFormData) => ({ ...prev, course_name: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                  placeholder="Enter course name"
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', color: '#374151' }}>
                  Program *
                </label>
                <input
                  type="text"
                  value={onlineClassFormData.program}
                  onChange={(e) => setOnlineClassFormData((prev: OnlineClassFormData) => ({ ...prev, program: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                  placeholder="e.g., SAT Math, ACT English"
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', color: '#374151' }}>
                  Instructor *
                </label>
                <input
                  type="text"
                  value={onlineClassFormData.instructor}
                  onChange={(e) => setOnlineClassFormData((prev: OnlineClassFormData) => ({ ...prev, instructor: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                  placeholder="Instructor name"
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', color: '#374151' }}>
                  Level *
                </label>
                <select
                  value={onlineClassFormData.level}
                  onChange={(e) => setOnlineClassFormData((prev: OnlineClassFormData) => ({ ...prev, level: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                >
                  <option value="">Select Level</option>
                  <option value="Beginner">Beginner</option>
                  <option value="Intermediate">Intermediate</option>
                  <option value="Advanced">Advanced</option>
                  <option value="Grade 2">Grade 2</option>
                  <option value="Grade 3">Grade 3</option>
                  <option value="Grade 4">Grade 4</option>
                  <option value="Grade 5">Grade 5</option>
                  <option value="Grade 6">Grade 6</option>
                  <option value="Grade 7">Grade 7</option>
                  <option value="Grade 8">Grade 8</option>
                </select>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', color: '#374151' }}>
                  Day *
                </label>
                <select
                  value={onlineClassFormData.day}
                  onChange={(e) => setOnlineClassFormData((prev: OnlineClassFormData) => ({ ...prev, day: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                >
                  <option value="">Select Day</option>
                  <option value="Monday">Monday</option>
                  <option value="Tuesday">Tuesday</option>
                  <option value="Wednesday">Wednesday</option>
                  <option value="Thursday">Thursday</option>
                  <option value="Friday">Friday</option>
                  <option value="Saturday">Saturday</option>
                  <option value="Sunday">Sunday</option>
                  <option value="Mon, Wed, Fri">Mon, Wed, Fri</option>
                  <option value="Tue, Thu">Tue, Thu</option>
                  <option value="Sat, Sun">Sat, Sun</option>
                </select>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', color: '#374151' }}>
                  Start Time *
                </label>
                <input
                  type="time"
                  value={onlineClassFormData.time1}
                  onChange={(e) => setOnlineClassFormData((prev: OnlineClassFormData) => ({ ...prev, time1: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', color: '#374151' }}>
                  End Time *
                </label>
                <input
                  type="time"
                  value={onlineClassFormData.time2}
                  onChange={(e) => setOnlineClassFormData((prev: OnlineClassFormData) => ({ ...prev, time2: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', color: '#374151' }}>
                  Start Date *
                </label>
                <input
                  type="date"
                  value={onlineClassFormData.date1}
                  onChange={(e) => setOnlineClassFormData((prev: OnlineClassFormData) => ({ ...prev, date1: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', color: '#374151' }}>
                  End Date *
                </label>
                <input
                  type="date"
                  value={onlineClassFormData.date2}
                  onChange={(e) => setOnlineClassFormData((prev: OnlineClassFormData) => ({ ...prev, date2: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', color: '#374151' }}>
                  Fee *
                </label>
                <input
                  type="text"
                  value={onlineClassFormData.fee}
                  onChange={(e) => setOnlineClassFormData((prev: OnlineClassFormData) => ({ ...prev, fee: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                  placeholder="e.g., $1500, 1500"
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', color: '#374151' }}>
                  Max Students
                </label>
                <input
                  type="number"
                  value={onlineClassFormData.slots}
                  onChange={(e) => setOnlineClassFormData((prev: OnlineClassFormData) => ({ ...prev, slots: parseInt(e.target.value) || 20 }))}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                  min="1"
                  max="100"
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', color: '#374151' }}>
                  Total Hours
                </label>
                <input
                  type="number"
                  value={onlineClassFormData.total_hours}
                  onChange={(e) => setOnlineClassFormData((prev: OnlineClassFormData) => ({ ...prev, total_hours: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                  placeholder="Total course hours"
                />
              </div>

              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', color: '#374151' }}>
                  Zoom Link
                </label>
                <input
                  type="url"
                  value={onlineClassFormData.zoom_link}
                  onChange={(e) => setOnlineClassFormData((prev: OnlineClassFormData) => ({ ...prev, zoom_link: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                  placeholder="https://zoom.us/j/..."
                />
              </div>

              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', color: '#374151' }}>
                  Description
                </label>
                <textarea
                  value={onlineClassFormData.description}
                  onChange={(e) => setOnlineClassFormData((prev: OnlineClassFormData) => ({ ...prev, description: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '6px',
                    fontSize: '14px',
                    minHeight: '80px',
                    resize: 'vertical'
                  }}
                  placeholder="Course description, objectives, etc."
                />
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
              <button type="button" onClick={() => setShowCreateOnlineClassModal(false)} style={{
                padding: '12px 24px',
                backgroundColor: '#6b7280',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '16px'
              }}>
                Cancel
              </button>
              <button type="button" onClick={submitCreateOnlineClass} disabled={loading} style={{
                padding: '12px 24px',
                backgroundColor: loading ? '#9ca3af' : '#10b981',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: '16px'
              }}>
                {loading ? 'Creating...' : 'Create Course'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showEditOnlineClassModal && selectedOnlineClass && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '32px',
            borderRadius: '16px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
            width: '90%',
            maxWidth: '500px',
            maxHeight: '80vh',
            overflow: 'auto'
          }}>
            <h2 style={{ margin: '0 0 20px 0', color: '#253C8C', fontSize: '24px' }}>
              ✏️ Edit Online Class
            </h2>

            <div style={{ display: 'grid', gap: '16px', gridTemplateColumns: '1fr 1fr' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', color: '#374151' }}>
                  Course Name *
                </label>
                <input
                  type="text"
                  value={onlineClassFormData.course_name}
                  onChange={(e) => setOnlineClassFormData((prev: OnlineClassFormData) => ({ ...prev, course_name: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                  placeholder="Enter course name"
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', color: '#374151' }}>
                  Program *
                </label>
                <input
                  type="text"
                  value={onlineClassFormData.program}
                  onChange={(e) => setOnlineClassFormData((prev: OnlineClassFormData) => ({ ...prev, program: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                  placeholder="e.g., SAT Math, ACT English"
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', color: '#374151' }}>
                  Instructor *
                </label>
                <input
                  type="text"
                  value={onlineClassFormData.instructor}
                  onChange={(e) => setOnlineClassFormData((prev: OnlineClassFormData) => ({ ...prev, instructor: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                  placeholder="Instructor name"
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', color: '#374151' }}>
                  Level *
                </label>
                <select
                  value={onlineClassFormData.level}
                  onChange={(e) => setOnlineClassFormData((prev: OnlineClassFormData) => ({ ...prev, level: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                >
                  <option value="">Select Level</option>
                  <option value="Beginner">Beginner</option>
                  <option value="Intermediate">Intermediate</option>
                  <option value="Advanced">Advanced</option>
                  <option value="Grade 2">Grade 2</option>
                  <option value="Grade 3">Grade 3</option>
                  <option value="Grade 4">Grade 4</option>
                  <option value="Grade 5">Grade 5</option>
                  <option value="Grade 6">Grade 6</option>
                  <option value="Grade 7">Grade 7</option>
                  <option value="Grade 8">Grade 8</option>
                </select>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', color: '#374151' }}>
                  Day *
                </label>
                <select
                  value={onlineClassFormData.day}
                  onChange={(e) => setOnlineClassFormData((prev: OnlineClassFormData) => ({ ...prev, day: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                >
                  <option value="">Select Day</option>
                  <option value="Monday">Monday</option>
                  <option value="Tuesday">Tuesday</option>
                  <option value="Wednesday">Wednesday</option>
                  <option value="Thursday">Thursday</option>
                  <option value="Friday">Friday</option>
                  <option value="Saturday">Saturday</option>
                  <option value="Sunday">Sunday</option>
                  <option value="Mon, Wed, Fri">Mon, Wed, Fri</option>
                  <option value="Tue, Thu">Tue, Thu</option>
                  <option value="Sat, Sun">Sat, Sun</option>
                </select>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', color: '#374151' }}>
                  Start Time *
                </label>
                <input
                  type="time"
                  value={onlineClassFormData.time1}
                  onChange={(e) => setOnlineClassFormData((prev: OnlineClassFormData) => ({ ...prev, time1: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', color: '#374151' }}>
                  End Time *
                </label>
                <input
                  type="time"
                  value={onlineClassFormData.time2}
                  onChange={(e) => setOnlineClassFormData((prev: OnlineClassFormData) => ({ ...prev, time2: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', color: '#374151' }}>
                  Start Date *
                </label>
                <input
                  type="date"
                  value={onlineClassFormData.date1}
                  onChange={(e) => setOnlineClassFormData((prev: OnlineClassFormData) => ({ ...prev, date1: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', color: '#374151' }}>
                  End Date *
                </label>
                <input
                  type="date"
                  value={onlineClassFormData.date2}
                  onChange={(e) => setOnlineClassFormData((prev: OnlineClassFormData) => ({ ...prev, date2: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', color: '#374151' }}>
                  Fee *
                </label>
                <input
                  type="text"
                  value={onlineClassFormData.fee}
                  onChange={(e) => setOnlineClassFormData((prev: OnlineClassFormData) => ({ ...prev, fee: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                  placeholder="e.g., $1500, 1500"
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', color: '#374151' }}>
                  Max Students
                </label>
                <input
                  type="number"
                  value={onlineClassFormData.slots}
                  onChange={(e) => setOnlineClassFormData((prev: OnlineClassFormData) => ({ ...prev, slots: parseInt(e.target.value) || 20 }))}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                  min="1"
                  max="100"
                />
              </div>

              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', color: '#374151' }}>
                  Zoom Link
                </label>
                <input
                  type="url"
                  value={onlineClassFormData.zoom_link}
                  onChange={(e) => setOnlineClassFormData((prev: OnlineClassFormData) => ({ ...prev, zoom_link: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                  placeholder="https://zoom.us/j/..."
                />
              </div>

              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', color: '#374151' }}>
                  Description
                </label>
                <textarea
                  value={onlineClassFormData.description}
                  onChange={(e) => setOnlineClassFormData((prev: OnlineClassFormData) => ({ ...prev, description: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '6px',
                    fontSize: '14px',
                    minHeight: '80px',
                    resize: 'vertical'
                  }}
                  placeholder="Course description, objectives, etc."
                />
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
              <button type="button" onClick={() => setShowEditOnlineClassModal(false)} style={{
                padding: '12px 24px',
                backgroundColor: '#6b7280',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '16px'
              }}>
                Cancel
              </button>
              <button type="button" onClick={submitEditOnlineClass} disabled={loading} style={{
                padding: '12px 24px',
                backgroundColor: loading ? '#9ca3af' : '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: '16px'
              }}>
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteOnlineClassModal && selectedOnlineClass && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '32px',
            borderRadius: '16px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
            width: '90%',
            maxWidth: '500px',
            maxHeight: '80vh',
            overflow: 'auto'
          }}>
            <h2 style={{ margin: '0 0 20px 0', color: '#dc2626', fontSize: '24px' }}>
              🗑️ Delete Online Class
            </h2>
            <div style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '16px', marginBottom: '20px' }}>
              <p style={{ color: '#dc2626', marginBottom: '8px', fontWeight: 'bold' }}>
                ⚠️ Warning: This action cannot be undone!
              </p>
              <p style={{ color: '#991b1b', marginBottom: '8px' }}>
                <strong>Course:</strong> {selectedOnlineClass.title || selectedOnlineClass.course_name || 'N/A'}
              </p>
              <p style={{ color: '#991b1b', marginBottom: '0' }}>
                <strong>Instructor:</strong> {selectedOnlineClass.instructor || 'N/A'}
              </p>
            </div>
            <p style={{ color: '#666', marginBottom: '24px' }}>
              Are you sure you want to delete this online class? This will permanently remove the course and all its details from the system.
              If there are enrolled students or related batches, the deletion may be prevented to protect data integrity.
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => setShowDeleteOnlineClassModal(false)} style={{
                padding: '12px 24px',
                backgroundColor: '#6b7280',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '16px'
              }}>
                Cancel
              </button>
              <button type="button" onClick={submitDeleteOnlineClass} disabled={loading} style={{
                padding: '12px 24px',
                backgroundColor: loading ? '#9ca3af' : '#dc2626',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: '16px'
              }}>
                {loading ? 'Deleting...' : 'Delete Course'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* In-Person Classes Modals */}
      {showCreateInPersonClassModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '24px',
            borderRadius: '12px',
            width: '90%',
            maxWidth: '500px',
            maxHeight: '80vh',
            overflow: 'auto'
          }}>
            <h2 style={{ margin: '0 0 20px 0', color: '#253C8C', fontSize: '24px' }}>
              🏫 Create In-Person Class
            </h2>

            <div style={{ display: 'grid', gap: '16px', gridTemplateColumns: '1fr 1fr' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', color: '#374151' }}>
                  Course Name *
                </label>
                <input
                  type="text"
                  value={inPersonClassFormData.course_name}
                  onChange={(e) => setInPersonClassFormData((prev: OnlineClassFormData) => ({ ...prev, course_name: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                  placeholder="Enter course name"
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', color: '#374151' }}>
                  Program *
                </label>
                <input
                  type="text"
                  value={inPersonClassFormData.program}
                  onChange={(e) => setInPersonClassFormData((prev: OnlineClassFormData) => ({ ...prev, program: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                  placeholder="e.g., SAT Math, ACT English"
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', color: '#374151' }}>
                  Instructor *
                </label>
                <input
                  type="text"
                  value={inPersonClassFormData.instructor}
                  onChange={(e) => setInPersonClassFormData((prev: OnlineClassFormData) => ({ ...prev, instructor: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                  placeholder="Instructor name"
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', color: '#374151' }}>
                  Level *
                </label>
                <select
                  value={inPersonClassFormData.level}
                  onChange={(e) => setInPersonClassFormData((prev: OnlineClassFormData) => ({ ...prev, level: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                >
                  <option value="">Select Level</option>
                  <option value="Beginner">Beginner</option>
                  <option value="Intermediate">Intermediate</option>
                  <option value="Advanced">Advanced</option>
                  <option value="Grade 2">Grade 2</option>
                  <option value="Grade 3">Grade 3</option>
                  <option value="Grade 4">Grade 4</option>
                  <option value="Grade 5">Grade 5</option>
                  <option value="Grade 6">Grade 6</option>
                  <option value="Grade 7">Grade 7</option>
                  <option value="Grade 8">Grade 8</option>
                </select>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', color: '#374151' }}>
                  Day *
                </label>
                <select
                  value={inPersonClassFormData.day}
                  onChange={(e) => setInPersonClassFormData((prev: OnlineClassFormData) => ({ ...prev, day: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                >
                  <option value="">Select Day</option>
                  <option value="Monday">Monday</option>
                  <option value="Tuesday">Tuesday</option>
                  <option value="Wednesday">Wednesday</option>
                  <option value="Thursday">Thursday</option>
                  <option value="Friday">Friday</option>
                  <option value="Saturday">Saturday</option>
                  <option value="Sunday">Sunday</option>
                  <option value="Mon, Wed, Fri">Mon, Wed, Fri</option>
                  <option value="Tue, Thu">Tue, Thu</option>
                  <option value="Sat, Sun">Sat, Sun</option>
                </select>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', color: '#374151' }}>
                  Start Time *
                </label>
                <input
                  type="time"
                  value={inPersonClassFormData.time1}
                  onChange={(e) => setInPersonClassFormData((prev: OnlineClassFormData) => ({ ...prev, time1: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', color: '#374151' }}>
                  End Time *
                </label>
                <input
                  type="time"
                  value={inPersonClassFormData.time2}
                  onChange={(e) => setInPersonClassFormData((prev: OnlineClassFormData) => ({ ...prev, time2: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', color: '#374151' }}>
                  Start Date *
                </label>
                <input
                  type="date"
                  value={inPersonClassFormData.date1}
                  onChange={(e) => setInPersonClassFormData((prev: OnlineClassFormData) => ({ ...prev, date1: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', color: '#374151' }}>
                  End Date *
                </label>
                <input
                  type="date"
                  value={inPersonClassFormData.date2}
                  onChange={(e) => setInPersonClassFormData((prev: OnlineClassFormData) => ({ ...prev, date2: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', color: '#374151' }}>
                  Fee *
                </label>
                <input
                  type="text"
                  value={inPersonClassFormData.fee}
                  onChange={(e) => setInPersonClassFormData((prev: OnlineClassFormData) => ({ ...prev, fee: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                  placeholder="e.g., $1500, 1500"
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', color: '#374151' }}>
                  Max Students
                </label>
                <input
                  type="number"
                  value={inPersonClassFormData.slots}
                  onChange={(e) => setInPersonClassFormData((prev: OnlineClassFormData) => ({ ...prev, slots: parseInt(e.target.value) || 20 }))}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                  min="1"
                  max="100"
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', color: '#374151' }}>
                  Total Hours
                </label>
                <input
                  type="number"
                  value={inPersonClassFormData.total_hours}
                  onChange={(e) => setInPersonClassFormData((prev: OnlineClassFormData) => ({ ...prev, total_hours: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                  placeholder="Total course hours"
                />
              </div>

              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', color: '#374151' }}>
                  Description
                </label>
                <textarea
                  value={inPersonClassFormData.description}
                  onChange={(e) => setInPersonClassFormData((prev: OnlineClassFormData) => ({ ...prev, description: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '6px',
                    fontSize: '14px',
                    minHeight: '80px',
                    resize: 'vertical'
                  }}
                  placeholder="Course description, objectives, location details, etc."
                />
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
              <button type="button" onClick={() => setShowCreateInPersonClassModal(false)} style={{
                padding: '12px 24px',
                backgroundColor: '#6b7280',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '16px'
              }}>
                Cancel
              </button>
              <button type="button" onClick={submitCreateInPersonClass} disabled={loading} style={{
                padding: '12px 24px',
                backgroundColor: loading ? '#9ca3af' : '#10b981',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: '16px'
              }}>
                {loading ? 'Creating...' : 'Create In-Person Class'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showEditInPersonClassModal && selectedInPersonClass && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '24px',
            borderRadius: '12px',
            width: '90%',
            maxWidth: '500px',
            maxHeight: '80vh',
            overflow: 'auto'
          }}>
            <h2 style={{ margin: '0 0 20px 0', color: '#253C8C', fontSize: '24px' }}>
              ✏️ Edit In-Person Class
            </h2>
            <p style={{ color: '#666', marginBottom: '20px' }}>
              <strong>Class:</strong> {selectedInPersonClass.title || selectedInPersonClass.course_name || 'N/A'}
            </p>

            {/* Same form fields as Create modal - shortened for brevity */}
            <div style={{ display: 'grid', gap: '16px', gridTemplateColumns: '1fr 1fr' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', color: '#374151' }}>
                  Course Name *
                </label>
                <input
                  type="text"
                  value={inPersonClassFormData.course_name}
                  onChange={(e) => setInPersonClassFormData((prev: OnlineClassFormData) => ({ ...prev, course_name: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                  placeholder="Enter course name"
                />
              </div>

              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', color: '#374151' }}>
                  Description
                </label>
                <textarea
                  value={inPersonClassFormData.description}
                  onChange={(e) => setInPersonClassFormData((prev: OnlineClassFormData) => ({ ...prev, description: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '6px',
                    fontSize: '14px',
                    minHeight: '80px',
                    resize: 'vertical'
                  }}
                  placeholder="Course description, objectives, location details, etc."
                />
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
              <button type="button" onClick={() => setShowEditInPersonClassModal(false)} style={{
                padding: '12px 24px',
                backgroundColor: '#6b7280',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '16px'
              }}>
                Cancel
              </button>
              <button type="button" onClick={submitEditInPersonClass} disabled={loading} style={{
                padding: '12px 24px',
                backgroundColor: loading ? '#9ca3af' : '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: '16px'
              }}>
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteInPersonClassModal && selectedInPersonClass && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '24px',
            borderRadius: '12px',
            width: '90%',
            maxWidth: '500px',
            maxHeight: '80vh',
            overflow: 'auto'
          }}>
            <h2 style={{ margin: '0 0 20px 0', color: '#dc2626', fontSize: '24px' }}>
              🗑️ Delete In-Person Class
            </h2>
            <div style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '16px', marginBottom: '20px' }}>
              <p style={{ color: '#dc2626', marginBottom: '8px', fontWeight: 'bold' }}>
                ⚠️ Warning: This action cannot be undone!
              </p>
              <p style={{ color: '#991b1b', marginBottom: '8px' }}>
                <strong>Course:</strong> {selectedInPersonClass.title || selectedInPersonClass.course_name || 'N/A'}
              </p>
              <p style={{ color: '#991b1b', marginBottom: '0' }}>
                <strong>Instructor:</strong> {selectedInPersonClass.instructor || 'N/A'}
              </p>
            </div>
            <p style={{ color: '#666', marginBottom: '24px' }}>
              Are you sure you want to delete this in-person class? This will permanently remove the course and all its details from the system.
              If there are enrolled students or related batches, the deletion may be prevented to protect data integrity.
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => setShowDeleteInPersonClassModal(false)} style={{
                padding: '12px 24px',
                backgroundColor: '#6b7280',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '16px'
              }}>
                Cancel
              </button>
              <button type="button" onClick={submitDeleteInPersonClass} disabled={loading} style={{
                padding: '12px 24px',
                backgroundColor: loading ? '#9ca3af' : '#dc2626',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: '16px'
              }}>
                {loading ? 'Deleting...' : 'Delete Class'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============================================================================ */}
      {/* FULL-PAGE DESCRIPTION EDITOR */}
      {/* ============================================================================ */}

      {showDescriptionEditor && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'white',
          zIndex: 2000,
          display: 'flex',
          flexDirection: 'column'
        }}>
          {/* Header */}
          <div style={{
            padding: '20px 24px',
            borderBottom: '2px solid #e5e7eb',
            backgroundColor: '#f9fafb',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div>
              <h2 style={{ margin: 0, color: '#253C8C', fontSize: '24px', fontWeight: 'bold' }}>
                📝 Edit Book Description
              </h2>
              <p style={{ margin: '4px 0 0 0', color: '#6b7280', fontSize: '14px' }}>
                Write a detailed description for: <strong>{bookFormData.title || 'New Book'}</strong>
              </p>
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={cancelDescriptionEditor}
                style={{
                  backgroundColor: '#6b7280',
                  color: 'white',
                  border: 'none',
                  padding: '12px 20px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                Cancel
              </button>
              <button
                onClick={saveDescription}
                style={{
                  backgroundColor: '#059669',
                  color: 'white',
                  border: 'none',
                  padding: '12px 20px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                Save Description
              </button>
            </div>
          </div>

          {/* Editor Content */}
          <div style={{
            flex: 1,
            padding: '24px',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '16px'
            }}>
              <label style={{
                fontSize: '16px',
                fontWeight: 'bold',
                color: '#374151'
              }}>
                Book Description:
              </label>
              <div style={{
                display: 'flex',
                gap: '16px',
                fontSize: '14px',
                color: '#6b7280'
              }}>
                <span><strong>Characters:</strong> {tempDescription.length}</span>
                <span><strong>Words:</strong> {tempDescription.split(' ').filter(w => w.length > 0).length}</span>
                <span><strong>Lines:</strong> {tempDescription.split('\n').length}</span>
              </div>
            </div>

            <RichTextEditor
              value={tempDescription}
              onChange={setTempDescription}
              placeholder="Enter the detailed book description here... Use the toolbar for formatting."
              showImageUpload={false}
              minHeight="500px"
              maxHeight="500px"
            />

            <div style={{
              marginTop: '16px',
              padding: '12px',
              backgroundColor: '#f0f9ff',
              borderRadius: '8px',
              border: '1px solid #bae6fd'
            }}>
              <p style={{ margin: 0, fontSize: '14px', color: '#0369a1' }}>
                💡 <strong>Tip:</strong> Write a compelling description that highlights the book's key features, target audience, and learning outcomes.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Blog Content Editor Modal - Full Screen */}
      {showBlogContentEditor && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'white',
          zIndex: 2000,
          display: 'flex',
          flexDirection: 'column'
        }}>
          {/* Header */}
          <div style={{
            padding: '20px 24px',
            borderBottom: '2px solid #e5e7eb',
            backgroundColor: '#f9fafb',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div>
              <h2 style={{ margin: 0, color: '#253C8C', fontSize: '24px', fontWeight: 'bold' }}>
                📝 Edit Blog Content
              </h2>
              <p style={{ margin: '4px 0 0 0', color: '#6b7280', fontSize: '14px' }}>
                Write detailed content for: <strong>{blogFormData.title || 'New Blog'}</strong>
              </p>
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={cancelBlogContentEditor}
                style={{
                  backgroundColor: '#6b7280',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                Cancel
              </button>
              <button
                onClick={saveBlogContent}
                style={{
                  backgroundColor: '#059669',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                ✓ Save Content
              </button>
            </div>
          </div>

          {/* Editor Content */}
          <div style={{
            flex: 1,
            padding: '24px',
            overflow: 'auto',
            backgroundColor: '#ffffff'
          }}>
            <div style={{ maxWidth: '900px', margin: '0 auto' }}>
              <div style={{
                marginBottom: '16px',
                padding: '16px',
                backgroundColor: '#f0f9ff',
                borderRadius: '8px',
                border: '1px solid #bae6fd'
              }}>
                <h3 style={{ margin: '0 0 12px 0', color: '#0369a1', fontSize: '16px' }}>
                  💡 Content Formatting Tips
                </h3>
                <ul style={{ margin: 0, paddingLeft: '20px', color: '#075985', fontSize: '14px', lineHeight: '1.6' }}>
                  <li>Use **bold** for emphasis: <code>**bold text**</code></li>
                  <li>Use *italic* for subtle emphasis: <code>*italic text*</code></li>
                  <li>Insert images using the toolbar or drag & drop</li>
                  <li>Create lists with bullet points using the List button</li>
                  <li>Right-click in the editor for quick image insertion</li>
                </ul>
              </div>

              <BlogContentEditor
                value={tempBlogContent}
                onChange={setTempBlogContent}
                placeholder="Start writing your blog content here... You can format text, add images, and create lists."
              />

              <div style={{
                marginTop: '16px',
                padding: '12px 16px',
                backgroundColor: '#f0fdf4',
                borderRadius: '8px',
                border: '1px solid #bbf7d0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div style={{ fontSize: '14px', color: '#166534' }}>
                  <strong>Characters:</strong> {tempBlogContent.length} | 
                  <strong> Words:</strong> {tempBlogContent.split(/\s+/).filter(w => w.length > 0).length} |
                  <strong> Images:</strong> {(tempBlogContent.match(/\[IMAGE:/g) || []).length}
                </div>
                {tempBlogContent.length > 500 && (
                  <div style={{ color: '#059669', fontWeight: '600', fontSize: '14px' }}>
                    ✓ Great length for SEO!
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ============================================================================ */}
      {/* LOCATION MODAL COMPONENTS */}
      {/* ============================================================================ */}

      {/* Country Modals */}
      {showCreateCountryModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '500px',
            width: '90%'
          }}>
            <h3 style={{ margin: '0 0 20px 0', color: '#253C8C' }}>🌍 Create New Country</h3>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#374151' }}>
                Country Name: <span style={{ color: '#dc2626' }}>*</span>
              </label>
              <input
                type="text"
                value={countryFormData.name}
                onChange={(e) => setCountryFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Enter country name..."
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '14px'
                }}
              />
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowCreateCountryModal(false)}
                disabled={countryLoading}
                style={{
                  backgroundColor: '#6b7280',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  cursor: countryLoading ? 'not-allowed' : 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={submitCreateCountry}
                disabled={countryLoading || !countryFormData.name.trim()}
                style={{
                  backgroundColor: '#059669',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  cursor: (countryLoading || !countryFormData.name.trim()) ? 'not-allowed' : 'pointer'
                }}
              >
                {countryLoading ? 'Creating...' : 'Create Country'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showEditCountryModal && selectedCountry && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '500px',
            width: '90%'
          }}>
            <h3 style={{ margin: '0 0 20px 0', color: '#253C8C' }}>✏️ Edit Country</h3>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#374151' }}>
                Country Name: <span style={{ color: '#dc2626' }}>*</span>
              </label>
              <input
                type="text"
                value={countryFormData.name}
                onChange={(e) => setCountryFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Enter country name..."
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '14px'
                }}
              />
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={countryFormData.active}
                  onChange={(e) => setCountryFormData(prev => ({ ...prev, active: e.target.checked }))}
                  style={{ transform: 'scale(1.2)' }}
                />
                <span style={{ fontWeight: 'bold', color: '#374151' }}>Active</span>
              </label>
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowEditCountryModal(false)}
                disabled={countryLoading}
                style={{
                  backgroundColor: '#6b7280',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  cursor: countryLoading ? 'not-allowed' : 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={submitEditCountry}
                disabled={countryLoading || !countryFormData.name.trim()}
                style={{
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  cursor: (countryLoading || !countryFormData.name.trim()) ? 'not-allowed' : 'pointer'
                }}
              >
                {countryLoading ? 'Updating...' : 'Update Country'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteCountryModal && selectedCountry && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '400px',
            width: '90%'
          }}>
            <h3 style={{ margin: '0 0 16px 0', color: '#dc2626' }}>🗑️ Delete Country</h3>
            <p style={{ marginBottom: '16px', color: '#374151' }}>
              Are you sure you want to delete the country <strong>"{selectedCountry.name}"</strong>?
            </p>
            <div style={{ backgroundColor: '#fef2f2', padding: '12px', borderRadius: '8px', marginBottom: '20px', border: '1px solid #fecaca' }}>
              <p style={{ margin: 0, color: '#dc2626', fontSize: '14px' }}>
                <strong>⚠️ Warning:</strong> This will permanently delete the country and may affect associated states and data.
              </p>
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowDeleteCountryModal(false)}
                disabled={countryLoading}
                style={{
                  backgroundColor: '#6b7280',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  cursor: countryLoading ? 'not-allowed' : 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={submitDeleteCountry}
                disabled={countryLoading}
                style={{
                  backgroundColor: '#dc2626',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  cursor: countryLoading ? 'not-allowed' : 'pointer'
                }}
              >
                {countryLoading ? 'Deleting...' : 'Delete Country'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* State Modals */}
      {showCreateStateModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '500px',
            width: '90%'
          }}>
            <h3 style={{ margin: '0 0 20px 0', color: '#253C8C' }}>🏛️ Create New State</h3>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#374151' }}>
                State Name: <span style={{ color: '#dc2626' }}>*</span>
              </label>
              <input
                type="text"
                value={stateFormData.name}
                onChange={(e) => setStateFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Enter state name..."
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '14px'
                }}
              />
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#374151' }}>
                Country: <span style={{ color: '#dc2626' }}>*</span>
              </label>
              <select
                value={stateFormData.country_id}
                onChange={(e) => setStateFormData(prev => ({ ...prev, country_id: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '14px'
                }}
              >
                <option value="">Select a country...</option>
                {countries.map(country => (
                  <option key={country.id} value={country.id}>{country.name}</option>
                ))}
              </select>
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowCreateStateModal(false)}
                disabled={stateLoading}
                style={{
                  backgroundColor: '#6b7280',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  cursor: stateLoading ? 'not-allowed' : 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={submitCreateState}
                disabled={stateLoading || !stateFormData.name.trim() || !stateFormData.country_id}
                style={{
                  backgroundColor: '#059669',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  cursor: (stateLoading || !stateFormData.name.trim() || !stateFormData.country_id) ? 'not-allowed' : 'pointer'
                }}
              >
                {stateLoading ? 'Creating...' : 'Create State'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showEditStateModal && selectedState && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '500px',
            width: '90%'
          }}>
            <h3 style={{ margin: '0 0 20px 0', color: '#253C8C' }}>✏️ Edit State</h3>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#374151' }}>
                State Name: <span style={{ color: '#dc2626' }}>*</span>
              </label>
              <input
                type="text"
                value={stateFormData.name}
                onChange={(e) => setStateFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Enter state name..."
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '14px'
                }}
              />
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#374151' }}>
                Country: <span style={{ color: '#dc2626' }}>*</span>
              </label>
              <select
                value={stateFormData.country_id}
                onChange={(e) => setStateFormData(prev => ({ ...prev, country_id: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '14px'
                }}
              >
                <option value="">Select a country...</option>
                {countries.map(country => (
                  <option key={country.id} value={country.id}>{country.name}</option>
                ))}
              </select>
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={stateFormData.active}
                  onChange={(e) => setStateFormData(prev => ({ ...prev, active: e.target.checked }))}
                  style={{ transform: 'scale(1.2)' }}
                />
                <span style={{ fontWeight: 'bold', color: '#374151' }}>Active</span>
              </label>
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowEditStateModal(false)}
                disabled={stateLoading}
                style={{
                  backgroundColor: '#6b7280',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  cursor: stateLoading ? 'not-allowed' : 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={submitEditState}
                disabled={stateLoading || !stateFormData.name.trim() || !stateFormData.country_id}
                style={{
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  cursor: (stateLoading || !stateFormData.name.trim() || !stateFormData.country_id) ? 'not-allowed' : 'pointer'
                }}
              >
                {stateLoading ? 'Updating...' : 'Update State'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteStateModal && selectedState && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '400px',
            width: '90%'
          }}>
            <h3 style={{ margin: '0 0 16px 0', color: '#dc2626' }}>🗑️ Delete State</h3>
            <p style={{ marginBottom: '16px', color: '#374151' }}>
              Are you sure you want to delete the state <strong>"{selectedState.name}"</strong> from <strong>{selectedState.country_name}</strong>?
            </p>
            <div style={{ backgroundColor: '#fef2f2', padding: '12px', borderRadius: '8px', marginBottom: '20px', border: '1px solid #fecaca' }}>
              <p style={{ margin: 0, color: '#dc2626', fontSize: '14px' }}>
                <strong>⚠️ Warning:</strong> This action cannot be undone.
              </p>
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowDeleteStateModal(false)}
                disabled={stateLoading}
                style={{
                  backgroundColor: '#6b7280',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  cursor: stateLoading ? 'not-allowed' : 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={submitDeleteState}
                disabled={stateLoading}
                style={{
                  backgroundColor: '#dc2626',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  cursor: stateLoading ? 'not-allowed' : 'pointer'
                }}
              >
                {stateLoading ? 'Deleting...' : 'Delete State'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============================================================================ */}
      {/* BLOG MODAL COMPONENTS */}
      {/* ============================================================================ */}

      {/* Delete Blog Modal */}
      {showDeleteBlogModal && selectedBlog && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '400px',
            width: '90%'
          }}>
            <h3 style={{ margin: '0 0 16px 0', color: '#dc2626' }}>🗑️ Delete Blog</h3>
            <p style={{ marginBottom: '16px', color: '#374151' }}>
              Are you sure you want to delete the blog <strong>"{selectedBlog.title}"</strong>?
            </p>
            <div style={{ backgroundColor: '#fef2f2', padding: '12px', borderRadius: '8px', marginBottom: '20px', border: '1px solid #fecaca' }}>
              <p style={{ margin: 0, color: '#dc2626', fontSize: '14px' }}>
                <strong>⚠️ Warning:</strong> This action cannot be undone.
              </p>
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowDeleteBlogModal(false)}
                disabled={blogLoading}
                style={{
                  backgroundColor: '#6b7280',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  cursor: blogLoading ? 'not-allowed' : 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={submitDeleteBlog}
                disabled={blogLoading}
                style={{
                  backgroundColor: '#dc2626',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  cursor: blogLoading ? 'not-allowed' : 'pointer'
                }}
              >
                {blogLoading ? 'Deleting...' : 'Delete Blog'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create FAQ Modal */}
      {showCreateFaqModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '32px',
            borderRadius: '16px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
            width: '90%',
            maxWidth: '600px',
            maxHeight: '80vh',
            overflow: 'auto'
          }}>
            <h2 style={{ margin: '0 0 20px 0', color: '#253C8C', fontSize: '24px' }}>
              ➕ Create New FAQ
            </h2>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', color: '#374151' }}>
                Question *
              </label>
              <input
                type="text"
                value={faqFormData.question}
                onChange={(e) => setFaqFormData(prev => ({ ...prev, question: e.target.value }))}
                placeholder="Enter FAQ question"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '14px'
                }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', color: '#374151' }}>
                Answer *
              </label>
              <textarea
                value={faqFormData.answer_html}
                onChange={(e) => setFaqFormData(prev => ({ ...prev, answer_html: e.target.value }))}
                placeholder="Enter FAQ answer (HTML supported)"
                rows={6}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '14px',
                  resize: 'vertical'
                }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', color: '#374151' }}>
                Category:
              </label>
              <select
                value={faqFormData.category}
                onChange={(e) => setFaqFormData(prev => ({ ...prev, category: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '14px'
                }}
              >
                <option value="General">General</option>
                <option value="Account">Account</option>
                <option value="Subjects">Subjects</option>
                <option value="Support">Support</option>
                <option value="Billing">Billing</option>
                <option value="Technical">Technical</option>
              </select>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', color: '#374151' }}>
                Status:
              </label>
              <select
                value={faqFormData.status}
                onChange={(e) => setFaqFormData(prev => ({ ...prev, status: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '14px'
                }}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="draft">Draft</option>
              </select>
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
              <button
                type="button"
                onClick={() => setShowCreateFaqModal(false)}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#6b7280',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '16px'
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submitCreateFaq}
                disabled={faqLoading}
                style={{
                  padding: '12px 24px',
                  backgroundColor: faqLoading ? '#9ca3af' : '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: faqLoading ? 'not-allowed' : 'pointer',
                  fontSize: '16px'
                }}
              >
                {faqLoading ? 'Creating...' : 'Create FAQ'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit FAQ Modal */}
      {showEditFaqModal && selectedFaq && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '32px',
            borderRadius: '16px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
            width: '90%',
            maxWidth: '600px',
            maxHeight: '80vh',
            overflow: 'auto'
          }}>
            <h2 style={{ margin: '0 0 20px 0', color: '#253C8C', fontSize: '24px' }}>
              ✏️ Edit FAQ
            </h2>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', color: '#374151' }}>
                Question *
              </label>
              <input
                type="text"
                value={faqFormData.question}
                onChange={(e) => setFaqFormData(prev => ({ ...prev, question: e.target.value }))}
                placeholder="Enter FAQ question"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '14px'
                }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', color: '#374151' }}>
                Answer *
              </label>
              <textarea
                value={faqFormData.answer_html}
                onChange={(e) => setFaqFormData(prev => ({ ...prev, answer_html: e.target.value }))}
                placeholder="Enter FAQ answer (HTML supported)"
                rows={6}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '14px',
                  resize: 'vertical'
                }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', color: '#374151' }}>
                Category:
              </label>
              <select
                value={faqFormData.category}
                onChange={(e) => setFaqFormData(prev => ({ ...prev, category: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '14px'
                }}
              >
                <option value="General">General</option>
                <option value="Account">Account</option>
                <option value="Subjects">Subjects</option>
                <option value="Support">Support</option>
                <option value="Billing">Billing</option>
                <option value="Technical">Technical</option>
              </select>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', color: '#374151' }}>
                Status:
              </label>
              <select
                value={faqFormData.status}
                onChange={(e) => setFaqFormData(prev => ({ ...prev, status: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '14px'
                }}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="draft">Draft</option>
              </select>
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowEditFaqModal(false);
                  setSelectedFaq(null);
                }}
                disabled={faqLoading}
                style={{
                  backgroundColor: '#6b7280',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  cursor: faqLoading ? 'not-allowed' : 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={submitEditFaq}
                disabled={faqLoading}
                style={{
                  backgroundColor: faqLoading ? '#9ca3af' : '#3b82f6',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  cursor: faqLoading ? 'not-allowed' : 'pointer'
                }}
              >
                {faqLoading ? 'Updating...' : 'Update FAQ'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete FAQ Modal */}
      {showDeleteFaqModal && selectedFaq && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '32px',
            borderRadius: '16px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
            width: '90%',
            maxWidth: '500px'
          }}>
            <h2 style={{ margin: '0 0 20px 0', color: '#253C8C', fontSize: '24px' }}>
              🗑️ Delete FAQ
            </h2>

            <p style={{ marginBottom: '16px' }}>
              Are you sure you want to delete this FAQ?
            </p>

            <div style={{
              background: '#fee2e2',
              padding: '16px',
              borderRadius: '8px',
              marginBottom: '16px'
            }}>
              <strong>Question:</strong> {selectedFaq.question}
            </div>

            <p style={{ color: '#ef4444', marginBottom: '24px' }}>
              This action cannot be undone.
            </p>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowDeleteFaqModal(false);
                  setSelectedFaq(null);
                }}
                disabled={faqLoading}
                style={{
                  backgroundColor: '#6b7280',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  cursor: faqLoading ? 'not-allowed' : 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={submitDeleteFaq}
                disabled={faqLoading}
                style={{
                  backgroundColor: faqLoading ? '#9ca3af' : '#dc2626',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  cursor: faqLoading ? 'not-allowed' : 'pointer'
                }}
              >
                {faqLoading ? 'Deleting...' : 'Delete FAQ'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Feedback Modal */}
      {showViewFeedbackModal && selectedFeedback && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '32px',
            borderRadius: '16px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
            width: '90%',
            maxWidth: '700px',
            maxHeight: '80vh',
            overflow: 'auto'
          }}>
            <h2 style={{ margin: '0 0 20px 0', color: '#253C8C', fontSize: '24px' }}>
              👁️ Feedback Details
            </h2>

            <div style={{ marginBottom: '16px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div>
                <strong>Name:</strong> {selectedFeedback.name}
              </div>
              <div>
                <strong>Email:</strong> {selectedFeedback.email}
              </div>
              {selectedFeedback.phone && (
                <div>
                  <strong>Phone:</strong> {selectedFeedback.phone}
                </div>
              )}
              <div>
                <strong>Type:</strong> {selectedFeedback.feedback_type}
              </div>
              <div>
                <strong>Status:</strong>
                <span style={{
                  marginLeft: '8px',
                  padding: '4px 12px',
                  borderRadius: '20px',
                  fontSize: '12px',
                  fontWeight: '600',
                  textTransform: 'uppercase',
                  backgroundColor: selectedFeedback.status === 'resolved' ? '#10b981' :
                    selectedFeedback.status === 'in_review' ? '#f59e0b' :
                      selectedFeedback.status === 'closed' ? '#6b7280' : '#ef4444',
                  color: 'white'
                }}>
                  {selectedFeedback.status}
                </span>
              </div>
              <div>
                <strong>Priority:</strong>
                <span style={{
                  marginLeft: '8px',
                  padding: '4px 12px',
                  borderRadius: '20px',
                  fontSize: '12px',
                  fontWeight: '600',
                  textTransform: 'uppercase',
                  backgroundColor: selectedFeedback.priority === 'urgent' ? '#ef4444' :
                    selectedFeedback.priority === 'high' ? '#f59e0b' :
                      selectedFeedback.priority === 'medium' ? '#3b82f6' : '#10b981',
                  color: 'white'
                }}>
                  {selectedFeedback.priority}
                </span>
              </div>
            </div>

            <div style={{
              background: '#f8f9ff',
              padding: '20px',
              borderRadius: '8px',
              marginBottom: '16px'
            }}>
              <h3 style={{
                color: '#253C8C',
                marginBottom: '12px',
                fontSize: '18px'
              }}>
                {selectedFeedback.subject}
              </h3>
              <p style={{ lineHeight: '1.6', color: '#374151', margin: 0 }}>
                {selectedFeedback.message}
              </p>
            </div>

            {selectedFeedback.rating && (
              <div style={{ marginBottom: '16px' }}>
                <strong>Rating:</strong> {selectedFeedback.rating}/5 stars
              </div>
            )}

            <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '24px' }}>
              <div>Submitted: {new Date(selectedFeedback.created_at).toLocaleString()}</div>
              {selectedFeedback.page_source && (
                <div>From page: {selectedFeedback.page_source}</div>
              )}
            </div>

            {/* Status Update Buttons */}
            <div style={{ display: 'flex', gap: '8px', marginBottom: '24px', flexWrap: 'wrap' }}>
              <button
                onClick={() => updateFeedbackStatus('in_review')}
                disabled={feedbackLoading || selectedFeedback.status === 'in_review'}
                style={{
                  backgroundColor: selectedFeedback.status === 'in_review' ? '#9ca3af' : '#f59e0b',
                  color: 'white',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  cursor: selectedFeedback.status === 'in_review' ? 'not-allowed' : 'pointer',
                  fontSize: '14px'
                }}
              >
                Mark In Review
              </button>

              <button
                onClick={() => updateFeedbackStatus('resolved')}
                disabled={feedbackLoading || selectedFeedback.status === 'resolved'}
                style={{
                  backgroundColor: selectedFeedback.status === 'resolved' ? '#9ca3af' : '#10b981',
                  color: 'white',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  cursor: selectedFeedback.status === 'resolved' ? 'not-allowed' : 'pointer',
                  fontSize: '14px'
                }}
              >
                Mark Resolved
              </button>

              <button
                onClick={() => updateFeedbackStatus('closed')}
                disabled={feedbackLoading || selectedFeedback.status === 'closed'}
                style={{
                  backgroundColor: selectedFeedback.status === 'closed' ? '#9ca3af' : '#6b7280',
                  color: 'white',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  cursor: selectedFeedback.status === 'closed' ? 'not-allowed' : 'pointer',
                  fontSize: '14px'
                }}
              >
                Mark Closed
              </button>
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowViewFeedbackModal(false);
                  setSelectedFeedback(null);
                }}
                style={{
                  backgroundColor: '#6b7280',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Feedback Modal */}
      {showDeleteFeedbackModal && selectedFeedback && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '32px',
            borderRadius: '16px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
            width: '90%',
            maxWidth: '500px'
          }}>
            <h2 style={{ margin: '0 0 20px 0', color: '#253C8C', fontSize: '24px' }}>
              🗑️ Delete Feedback
            </h2>

            <p style={{ marginBottom: '16px' }}>
              Are you sure you want to delete this feedback?
            </p>

            <div style={{
              background: '#fee2e2',
              padding: '16px',
              borderRadius: '8px',
              marginBottom: '16px'
            }}>
              <strong>From:</strong> {selectedFeedback.name} ({selectedFeedback.email})<br />
              <strong>Subject:</strong> {selectedFeedback.subject}
            </div>

            <p style={{ color: '#ef4444', marginBottom: '24px' }}>
              This action cannot be undone.
            </p>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowDeleteFeedbackModal(false);
                  setSelectedFeedback(null);
                }}
                disabled={feedbackLoading}
                style={{
                  backgroundColor: '#6b7280',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  cursor: feedbackLoading ? 'not-allowed' : 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={submitDeleteFeedback}
                disabled={feedbackLoading}
                style={{
                  backgroundColor: feedbackLoading ? '#9ca3af' : '#dc2626',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  cursor: feedbackLoading ? 'not-allowed' : 'pointer'
                }}
              >
                {feedbackLoading ? 'Deleting...' : 'Delete Feedback'}
              </button>
            </div>
          </div>
        </div>
      )}

    </>
  );
};

export default AdminPage; 